(() => {
  let growth = null;
  const PIVOT_NATIVE = !!(window.PIVOT_NATIVE);

  let canvas = document.getElementById("game");
  // Bail out cleanly rather than throwing on line one. An uncaught error here
  // aborts the whole IIFE, taking down every other feature on the page — the
  // exact failure that left /custom/ blank for five months before anyone
  // noticed. tools-validate-shell-parity.js guards the shells, this guards the
  // page.
  if (!canvas) return;
  let ctx = canvas.getContext("2d");

  const GRID_ROWS = 3;
  const GRID_COLS = 6;
  const CENTER_ROW = 1;
  const CENTER_START_COL = 1;
  const CENTER_WORD_LENGTH = 4;
  const LEADERBOARD_PREFIX = "pivot_leaderboard_";
  const LEADERBOARD_TIMEOUT_MS = 8000;
  const LEADERBOARD_AUTO_RETRY_MS = 20000;
  const PROGRESS_PREFIX = "pivot_progress_";
  const PRACTICE_PROGRESS_PREFIX = "pivot_practice_progress_";
  // Declared here because safeSetItem/safeRemoveItem invalidate it.
  let _solvedCountCache = null;
  const NAME_PREFIX = "pivot_name_";
  const DEVICE_PREFIX = "pivot_device_id";
  const PLAYER_ID_KEY = "pivot_player_id_v1";
  const RECOVERY_CODE_KEY = "pivot_recovery_code_v1";
  const RECOVERY_PROMPT_KEY = "pivot_recovery_prompt_v1";
  const RECOVERED_HISTORY_KEY = "pivot_recovered_history_v1";
  const STREAK_PREFIX = "pivot_streak_state";
  const WORDLIST_VERSION_KEY = "pivot_wordlist_version";
  const WORDLIST_VERSION = "v2";
  const SHARE_DETAILS_KEY = "pivot_share_include_details";
  const PENDING_SUBMIT_PREFIX = "pivot_pending_submit_";
  const LANDING_VARIANT_KEY = "pivot_landing_variant_v1";
  const LANDING_EXPERIMENT_VERSION = "v1";
  const NOTIFY_PROMPT_KEY = "pivot_notify_prompt_v1";
  const STREAK_GRACE_DAYS = 7;
  const LONG_STREAK_GRACE_THRESHOLD = 30;
  // Widened from 6h -> 10h on 2026-05-29. The banner has an 89% CTR but
  // barely fires (9 shows / 7d at the 6h threshold); wider window gives it
  // a real shot at affecting retention without becoming nag-y.
  const STREAK_RISK_BANNER_WINDOW_MS = 10 * 60 * 60 * 1000;
  const ACTIVATION_NUDGE_DELAY_MS = 11000;
  // Let the completed result settle before asking for an optional leaderboard
  // name. The prompt is explicit and dismissible; no score is submitted until
  // the player taps Submit. Removing it (2026-09-21) halved posting, 47% -> 24%.
  const POST_SOLVE_LEADERBOARD_PROMPT_DELAY_MS = 3000;
  // Streak milestones. Players see a one-time celebration line on the win
  // screen when they cross any of these. The current tier (highest milestone
  // reached) also shows a small persistent badge on the win screen.
  const STREAK_MILESTONES = [7, 14, 30, 100, 365];
  const STREAK_MILESTONES_KEY = "pivot_streak_milestones_v1";
  const STREAK_MILESTONE_LABELS = {
    7: { label: "1 week", emoji: "✨" },
    14: { label: "2 weeks", emoji: "🌟" },
    30: { label: "30 days", emoji: "🏆" },
    100: { label: "100 days", emoji: "💯" },
    365: { label: "1 year", emoji: "👑" },
  };
  const UPDATES_LAST_SEEN_KEY = "pivot_updates_last_seen_v1";
  const UPDATES_BANNER_LAST_SHOWN_KEY = "pivot_updates_banner_last_shown_v1";
  const UPDATES_BANNER_AUTO_DISMISS_MS = 16000;
  // What's New entries — newest first. The `ts` is a unix-ms timestamp used
  // for the unseen indicator (anything newer than the user's last-seen ts
  // counts as unread). Keep entries short and player-facing — features and
  // changes someone would care about, not internal cleanups.
  const UPDATES_ENTRIES = [
    {
      ts: Date.UTC(2026, 7, 14, 16),
      emoji: "🏅",
      title: "Your circle rank, up front",
      body: "Leaderboards now have a My Circles view. See your weekly place and score immediately, switch between circles, and jump back to today's PIVOT without digging through circle settings.",
    },
    {
      ts: Date.UTC(2026, 7, 14, 1, 50),
      emoji: "🔗",
      title: "Clearer circles and challenges",
      body: "Friend Circles now confirm when you're joined, link straight to today's game, and share with a dedicated invitation preview. Challenges, Live Races, and player-made puzzles have their own PIVOT link previews too, while daily stats distinguish everyone who solved from players on the leaderboard.",
    },
    {
      ts: Date.UTC(2026, 7, 12, 22, 2),
      emoji: "✨",
      title: "Better races and smarter recaps",
      body: "Live Race now supports one game or best-of 3, 5, and 7 series, with steadier round finishes and disconnect handling. After a solve, recap cards can highlight your streak, rank, and challenge result.",
    },
    {
      ts: Date.UTC(2026, 7, 12, 22, 1),
      emoji: "🧩",
      title: "Make a PIVOT",
      body: "Choose a four-letter word, pick Easy, Medium, or Hard, and share your own verified puzzle. Every board is measured against its selected difficulty before its link is created.",
    },
    {
      ts: Date.UTC(2026, 7, 12, 22),
      emoji: "👥",
      title: "Play together",
      body: "Challenge a friend against your move-by-move ghost, start a private Friend Circle with weekly standings, or host a real-time race — all from one Play together menu.",
    },
    {
      ts: Date.UTC(2026, 4, 30, 18),
      emoji: "🏆",
      title: "Milestone streaks",
      body: "You'll see a one-time celebration when you cross 1 week, 2 weeks, 30 days, 100 days, and 1 year. A small badge stays on your win screen for as long as your streak holds.",
    },
    {
      ts: Date.UTC(2026, 4, 30, 18, 1),
      emoji: "⚔️",
      title: "See all your rivalries",
      body: "Your stats panel now lists every friend you've challenged — current record, current streak, who's ahead. Open Stats to check yours.",
    },
    {
      ts: Date.UTC(2026, 4, 26, 20),
      emoji: "⚔️",
      title: "Rivalries",
      body: "Challenge the same friend more than once and PIVOT remembers. After every challenge round, the win screen shows your head-to-head record — and your current winning streak against them.",
    },
    {
      ts: Date.UTC(2026, 4, 25, 12),
      emoji: "🔑",
      title: "Play on another device",
      body: "Your stats and streak now follow you. Find your recovery code in More → Your account, save it somewhere safe, and paste it on a new device to keep going.",
    },
    {
      ts: Date.UTC(2026, 4, 25),
      emoji: "🛡️",
      title: "Streak shields you can see",
      body: "When your streak is in trouble, the landing screen now tells you. When the shield catches a missed day, you'll know. Long streaks (30+) get a little more cushion.",
    },
    {
      ts: Date.UTC(2026, 4, 11),
      emoji: "📱",
      title: "PIVOT on iOS",
      body: "The native app is in the App Store. Daily push reminders, an app icon, and the same puzzle as the web.",
    },
    {
      ts: Date.UTC(2026, 3, 1),
      emoji: "🎯",
      title: "Practice mode",
      body: "Open practice from More to play unlimited puzzles without affecting your daily streak.",
    },
    {
      ts: Date.UTC(2026, 1, 1),
      emoji: "🌑",
      title: "Dark mode",
      body: "Toggle from the top right. We respect your system preference by default.",
    },
  ];
  const RELIABILITY_DEGRADED_TTL_MS = 6 * 60 * 1000;
  const MIN_KIND = "center_token_v2";
  const EVENT_PREFIX = "pivot_event_";
  const CHALLENGE_FEED_SEEN_KEY = "pivot_challenge_feed_seen_v1";
  const CHALLENGE_FEED_POLL_MS = 45 * 1000;
  // Substring matches — safe for any position (4+ chars, unlikely in real names)
  const BANNED_SUBSTRINGS = [
    "fuck","shit","bitch","cunt","nigg","spic","kike","chink",
    "gook","slut","whore","rape","nazi","dyke","trann",
    "retard","cock","dick","penis","pussy","boob",
    "anus","jizz","semen","porn","hentai","dildo",
    "erect","orgasm","masturbat","felch","goatse","chode","scrot",
    "piss","bollock","wank","tosser",
    "twat","prick","arse","queer","lesbo",
    "wetback","beaner","raghead","towelhead",
    "murder","genocide","holocaust",
    "terror","jihad","lynching",
  ];
  // Real names and words that contain a banned substring by accident. They are
  // removed before the substring check, so "Hancock" passes and "cock" fails.
  const NAME_SUBSTRING_ALLOWLIST = [
    "hancock","peacock","alcock","babcock","hitchcock","woodcock","glasscock",
    "draper","grape","therapist","janus","manus","uranus","spicer","nazir",
    "dickens","dickinson","dickson","dickey","dickerson","dickie","semenov",
    "arsenal","arsenio","scunthorpe",
  ];
  // Exact-word matches — short tokens that could appear inside normal names
  const BANNED_EXACT = [
    "fag","fags","vag","tit","tits","cum","ass","nig","nigs",
    "homo","anal","damn","hell","kill","hang","stab","bomb",
    "shoot",
  ];

  const TUTORIAL_SEEN_KEY = "pivot_tutorial_seen";

  const SUPABASE_URL = "https://mauwqoermwyacfmampyq.supabase.co";
  const SUPABASE_ANON_KEY = "sb_publishable_2SMv0yvPOOJyckBR_0grPQ_9K92PKvQ";
  const CANONICAL_URL = "https://pivotthegame.com";
  const VAPID_PUBLIC_KEY = "BOGMZst0dfHCP0tH5w95QARIJZfXTiP_UNezvfk15wAXeMlcT0Z0VC_54__rjCH3sMYUEhymXflJ2fmmW03Psc4";
  const VIDEO_REPLAY_VERSION = "20260216";

  function _d(s, i) {
    let r = '';
    for (let j = 0; j < s.length; j++) {
      const sh = (i * 7 + j * 13 + 42) % 26;
      r += String.fromCharCode(((s.charCodeAt(j) - 65 - sh + 26) % 26) + 65);
    }
    return r;
  }

  // 798 words exclusively for custom games. Entries are position-encoded, so
  // inserting/removing an item requires re-encoding every item after it.
  const CUSTOM_WORDS = [
    "QEBH","XMEO","ETMU","LACC","SLWI","ZUCQ","GERR","NLFO","UYWO","BFFO",
    "IMUT","PWCV","WDPX","DLLT","KTKV","SESI","ZLZJ","GSHC","NZUK","UGBZ",
    "BNKR","IUSX","PBZM","WIIL","DPPV","KWWG","RDHH","YKOX","FRWV","MYDQ",
    "TFLM","AQZP","HXGD","OENM","VLUU","CSEG","JZMA","QGTP","XNAA","EUOB",
    "LBVQ","SIEH","ZPLE","GWXL","NHMR","UODK","BVLY","ICAY","PMSX","WTJJ",
    "DAQR","KHXS","ROKH","YVRB","FFHP","MMWR","TTEG","AAMP","HHTX","OOBK",
    "VVIT","CCPH","JJZZ","QQGP","XXOB","EEWX","LLEQ","SSNP","ZCCO","GJJO",
    "NTXJ","UAER","BHMC","IOYH","PVGZ","WCUH","EPHT","LWPA","SDAH","ZKJO",
    "GRQG","NYYC","UFHX","BMQP","ITXX","PAET","WHML","DOTV","KVAO","RJPR",
    "YQWC","FXHV","MESK","TLZT","ASMA","HAYW","OHFX","VRTJ","CYAZ","JFHH",
    "QMOQ","XTVG","EAKE","LHXP","SOKE","ZYXV","GFEK","NMTJ","UTAS","BAKQ",
    "IHSU","POZE","WVHL","DCPY","KJXZ","RQEA","YXNM","FEUA","MLBK","TSJX",
    "AZQJ","HGZH","ONLX","VXTH","CEEJ","JLVJ","QSCX","XCGB","EJRH","LQAX",
    "SXHH","ZEOV","GLXV","OYOM","VFDJ","CMLQ","JTSI","QAEE","XHLR","EOSB",
    "LVZO","SCHJ","ZJPJ","GQWU","NXIP","UIRH","BPYQ","IWFD","PDMM","WKTX",
    "DRBG","KYJE","RFSN","YMZX","FTOD","MAWP","THDB","AOPU","HZGV","OGPB",
    "VNWY","CULQ","JBTX","QIET","XPMJ","EWTV","LDAF","SQTU","ZXJN","GERC",
    "NLYO","USJI","BZUY","IGEW","PQMF","WXTC","DEEJ","KLPJ","RSIN","YCVT",
    "FJEZ","MQNH","TXUV","AEIV","HLQS","OSXN","VZKY","CGSK","JRKW","RAPH",
    "YHLS","FOSB","MYQO","TNHG","AVEC","HDGI","OMSZ","VZVR","CGCC","JNND",
    "QWML","XDXI","EKIY","LRWI","SZSE","ZGZA","HPET","OWLP","VDTH","CKFV",
    "JROV","QYWJ","XFEJ","EMMS","LTXX","SAEM","ZHNL","GOXB","NZIM","UGPV",
    "BNAM","IUHB","PBOQ","WICP","DPJE","KWSV","RDES","YKNJ","FVAG","MCSF",
    "TJZN","AQIL","HXPT","OEAI","VLIW","CVXQ","JCEG","QJLR","XQSB","EXDP",
    "LEKP","SLVT","ZSIN","GZVL","NJIH","UQTU","BXEU","IEOT","PLVH","WSEH",
    "DZLE","KGTV","RNAK","YUHZ","FBRQ","MIYY","TPFL","AWMU","HDTI","OKDH",
    "VUUH","CBLW","JLIC","QSWJ","XZEJ","EGMP","LNTD","SUEY","ZBML","GITG",
    "OVQO","VCAG","CJJP","JQSU","QXBY","XEKI","ELTP","LSEW","SDMQ","ZKKS",
    "GVFG","NCSX","UJZM","BQNB","IXXT","PHJZ","WOYE","DVRO","KCYD","RLLR",
    "YSSB","FAZX","MHKL","TOYF","AVHL","HCSS","OJEI","VTPD","CAWV","JHDO",
    "QOOT","XVZQ","ECGY","LJNL","SQAV","ZAEL","GHRH","NOZN","UVHT","CIXS",
    "JPKA","QWRN","XDBH","EKIP","LRPC","SYWR","ZFFI","GMMS","NTXD","UAEM",
    "BHLW","IOTV","PVBZ","WCJN","DJRN","KQCO","RBKA","YIRP","FPYA","MWFJ",
    "TDQC","AKXR","HRIE","OYSX","VFZQ","CMHX","JTPH","QAAX","XHHG","EOOO",
    "LVVF","SGOC","ZNYM","GUJQ","NBRM","UIAD","BPLL","IWUG","PDDZ","WQPZ",
    "DXHN","KEPU","RLXB","YSFH","FZMV","MGUW","TNDM","AULZ","HBUE","OIDF",
    "VVPT","CCZL","JJNA","QQVL","XXDJ","EEKD","LLVC","SSCR","ZDES","HONZ",
    "OWKT","VDYE","CUWO","JBUC","QMWI","XUAG","ECAV","MQFA","TXNB","AEZP",
    "HLKM","OSWK","VZIR","CGSF","JRAA","QYZN","XPPT","EWFM","LDNI","SQMK",
    "ZXWH","GEJZ","NLRV","USCW","CJWS","JQDB","QXRI","XEZP","ELHA","LSQH",
    "SZGN","ZKRM","GRZB","NYOB","UFXD","BMJW","IYCP","PFJO","WMUS","DTHH",
    "KAOJ","SUJY","ZBQL","GIYG","NPKS","UWVA","BDDG","IKKO","PRVU","WYDR",
    "DFLJ","KMVZ","RXGW","YENK","FLUU","MSBD","TZVY","AGCU","HNJC","OUVI",
    "VBFV","CMRV","JTAV","QAJF","XHQR","EOXA","LVFR","SCRY","ZJAC","GQHM",
    "NXOE","UEWA","BLIW","ISSO","PFEU","WMLD","DTSS","KACQ","RHLM","YOTO",
    "FVHL","MCOU","TJWF","AQGF","HXNN","OEVI","VLCQ","CSKW","JZSO","QGAW",
    "XTOJ","EAER","LHMC","SOUA","ZVFF","GCMS","NJUW","UQBP","CDSH","JKAO",
    "QRMU","XYTJ","EFAS","LMJQ","STRX","ZAYL","GHFA","NOOS","UVVT","BCEU",
    "IJNN","PQUA","WXBP","DEJL","KLQV","RSXK","YZES","FGMK","MRAQ","TYHF",
    "AFOO","HMVB","OTGI","VAUZ","CHBW","JOJY","QVRU","XCYS","EJJJ","LQRT",
    "SXYL","ZIPA","GPWL","NWMR","UDTZ","BKAM","IRHU","PYPU","WFXA","DMEI",
    "KTQD","RAYW","YNMM","FUTZ","MBCX","TIKY","APZE","HWGB","ODPZ","VKXZ",
    "CRFH","JYOO","QFVE","XMDR","ETLM","LGBT","SNID","ZUYE","GBFS","NITS",
    "UPAC","BWHV","IHPQ","QQLB","XXWB","EEGI","LLTJ","SWFL","ZDOJ","GKXJ",
    "NRSG","UYDM","BFLU","IWIM","PDTB","WKBA","DRKQ","KEAO","RLQS","YSEQ",
    "GFLM","NNDK","UWJL","BKNY","ITYU","PAJH","WIKZ","DPTU","KWHH","RDWE",
    "YLJB","FTRP","MCYW","TJHS","AUQM","HBXX","PNCX","WUNY","DBWE","KIDT",
    "RPKC","YWRN","FDBH","MKIW","TRRV","AYYE","HFHF","OMQQ","VTXD","CAET",
    "JHMZ","QOTH","XVDZ","ECLP","LNWA","SUDJ","ZBMH","GIVP","NPCC","UWQL",
    "BDZC","IKGY","PROA","WYYE","DFGU","KQXS","RXGG","YEUA","FLBO","MSKU",
    "TZRK","AGAC","HNLD","OXZZ","VEKT","CLBD","JSID","QZPM","XGCB","ENJO",
    "LUQB","SEHC","ZLOQ","GSBI","NZJP","UGQD","BNXN","IUFV","PBOY","WIWY",
    "DPDT","KWMM","RDTZ","YKBA","FRIW","MYRB","TIEP","APPW","HWGI","ODNB",
    "VKUP","CRBX","JBWR","QIMD","XPUK","EWCM","LDNN","SKWI","ARKA","HYZX",
    "OFGK","WSHC","DZRS","KGZK","RNIQ","YUPF","FBYF","MIHX","TPPV","AWWP",
    "HDHH","OKPR","VRXV","CYGC","JJSI","QQZY","XXGI","EENR","LLYK","SSFT",
    "ZZMG","GGXP","NNHH","UUQJ","BBCQ","IMTI","PTAS","WAIW","DHRS","KOEK",
    "RVMR","YCUX","FJCU","MQNM","TXUZ","AKJZ","HRQP","OYXB","VFFV","CMOC",
    "JTDJ","QANR","XHUF","EOCE","LVPL","SIES","ZPQI","GWBR","NDJP","UKVU",
    "BRCE","IYJX","QLAV","XSLS","EZUC","LGDK","SNLR","ZUSN","GBBF","NIIO",
    "UPPZ","BWBD","IDLH","PKVC","WTES","DALL","KJSR","RQZD","YXGM","FEPT",
    "MLYR","TSFA","AZMI","HGAH","ONHU","VUPY","CBXA","JIEX","QPNE","XWBF",
    "EGQC","LNBS","SUIA","ZBVY","GICQ","NPJB","UWQL","BELG","ILTH","PSCD",
    "WZKY","DGRL","KNDQ","RUKF","YBTZ","FIAK","MPNL","TWAS","AFQO","HNPD",
    "OUWP","VBDC","CIKM","JPRX","QWCO","XDJO","EKUC","LRBS","SYIC","ZFPN",
    "GMCU","NTPO","UAWB","BIXC","IQQS","PXXQ","WEMG","DLZZ","KSMG","RZTS",
    "YHGD","FONP","MVUY","TCDY","AJLV","HQUC","OXEU","VEOT","CLVB","JSDI",
    "QZMY","XGWW","ENDS","LUNR","SBUE","ZJHH","GQSD","NXZL","UEKI","BLDJ",
    "ISKU","PDXL","WKEP","DRPK","KYWB","RFHD","YMUI","FTHB","MAOK","THVB",
    "GVZI","NGPT","UNHU","BEXH","JBKI","QIJX","XTLN","EGTE",
  ];

  function createEmptyMetrics(dayKey = "") {
    return {
      dayKey,
      solvers: null,
      boardEntries: null,
      median: null,
      best: null,
      percentileCache: {},
      percentileLoading: {},
    };
  }

  const state = {
    mode: "menu",
    grid: [],
    moves: 0,
    targetWord: "PLAY",
    hoverPivot: null,
    lastMove: null,
    solvedToday: false,
    nameInput: "",
    leaderboard: [],
    submitted: false,
    dailyKey: "",
    showReadme: false,
    leaderboardLoading: false,
    leaderboardError: "",
    minMoves: null,
    minExact: false,
    optimalPath: [],
    lowerBound: null,
    playerName: "",
    practiceMode: false,
    completed: false,
    completionLocked: false,
    alreadyCompleted: false,
    showLeaderboardOverlay: false,
    leaderboardPage: 0,
    winAcknowledged: false,
    streakCount: 0,
    streakDisplay: 0,
    milestoneJustReached: 0,
    milestoneJustReachedAt: 0,
    streakAnimating: false,
    streakAnimStart: 0,
    leaderboardEligible: true,
    redoMode: false,
    showTutorial: false,
    shareCooldownUntil: 0,
    shareToastUntil: 0,
    shareToastMessage: "",
    puzzleStartedAt: 0,
    firstMoveAt: 0,
    activationNudgeShown: false,
    anim: null,
    logo: null,
    tutorialIgnoreUntil: 0,
    highlightUntil: 0,
    sessionId: "",
    sessionStart: 0,
    metrics: createEmptyMetrics(""),
    adminMessage: "",
    adminMessageUpdatedAt: "",
    nextMessagePoll: 0,
    nextLeaderboardPoll: 0,
    shareOnly: false,
    shareIncludeDetails: safeGetItem(SHARE_DETAILS_KEY) === "1",
    winIgnoreCloseUntil: 0,
    landingVariant: "",
    shareLoopVariant: "",
    moveHistory: [],
    canonicalStartStr: "",
    leaderboardScope: "global",
    leaderboardTab: "today",
    friendCircles: null,
    friendCirclesLoading: false,
    friendCirclesError: "",
    friendCirclesFetchedAt: 0,
    selectedFriendCircleId: safeGetItem("pivot_selected_circle_v1") || "",
    weeklyLeaderboard: null,
    monthlyLeaderboard: null,
    weeklyLeaderboardLoading: false,
    monthlyLeaderboardLoading: false,
    hintUsed: false,
    hintDistanceVisible: false,
    hintDistance: null,
    hintPivotIndex: null,
    hintHighlightUntil: 0,
    hintSequence: null,       // Array of { pivotIdx, start, end } for multi-step hint
    hintSequenceUntil: 0,
    challengeFrom: null,
    challengeResolved: false,
    ghostStartedAt: 0,
    ghostMoveIndex: 0,
    ghostGrid: "",
    challengeFeedNextPoll: 0,
    shieldCelebrationVisible: false,
    achievementToastUntil: 0,
    achievementToastText: "",
    achievementToastIcon: "",
    isPersonalBest: false,
    winAnimStart: 0,
    sessionStats: { games: [] },
    // Iconic pivot animation state
    _lastPivotPulse: null,    // { pivotIdx, start }
    _settleCells: null,       // [{ r, c, start }]
    _pivotHoverT: {},         // { pivotIdx: { t, target, lastUpdate } }
    _gameEntryAnimStart: 0,
    // Custom game state
    customMode: false,
    customRoomCode: "",
    customDifficulty: "medium",
    customMultiplayer: false,
    customIsHost: false,
    customPlayerName: "",
    customPassword: "",
    customLobbyToken: "",      // host-only bearer secret for public listing mutations
    // N-player multiplayer state
    customPlayerId: "",
    customPhase: "lobby",       // "lobby" | "playing" | "results"
    customPlayers: {},           // Map<id, {id, name, moves, solved, grid, finishOrder, wins, losses, isHost, isObserver}>
    customSolveCount: 0,
    customRoundNumber: 1,
    customNextDifficulty: null,
    customIsObserver: false,     // true if joined mid-game
    customObserveMode: false,    // full observation mode (grid of all players, no own grid)
    customObserveFilter: "all",  // "all" | "active" | "solved"
    customObserveFocusPlayer: null, // name of watched player (null = auto)
    customObserveFocusLocked: false, // true if user manually selected
    customJoinRequested: false,  // observer has requested to join next round
    customJoinApproved: false,   // observer's request was approved by host
    customPendingJoins: [],      // host: list of {id, name, approved} requesting to join
    customRoomLocked: false,     // host: temporarily blocks new join requests
    customCountdownUntil: 0,     // performance.now() timestamp when countdown ends
    customCountdownPuzzle: null, // puzzle object for after countdown
    customCountdownRoomCode: "", // room code for after countdown
    customClassroomMode: false,  // true if host chose classroom mode
    customStartState: "",        // host-authoritative start state
    customWord: "",              // host-authoritative target word
    customRoundOutcomeKey: "",   // host: guard to score each round once
    customSeriesBestOf: 1,        // 1, 3, 5, or 7 games
    customSeriesWinsNeeded: 1,
    customSeriesComplete: false,
    customSeriesWinnerId: "",
    customCreatorName: "",
    customCommunityCode: "",
    customReconnecting: false,   // true during reconnection attempt
    customReconnectUntil: 0,     // performance.now() deadline for reconnection
    customMinToken: "",
    customLastKick: null,        // host-only undo data for the most recent kick
    customIsPublic: false,       // public lobby visibility
    // Chat state
    customChatMessages: [],      // [{id, name, text, ts}], capped at 50
    customChatOpen: false,
    customChatUnread: 0,
    customChatLastSendAt: 0,     // rate limit: 1 msg/sec
    pendingSubmitRetryAt: 0,
    reliabilityMode: "healthy",
    reliabilityReason: "",
    reliabilityUntil: 0,
    reliabilityChangedAt: 0,
    submissionReceiptId: "",
    submissionReceiptAt: "",
    yesterdayTopFirstName: "",
    yesterdayChampionKey: "",
  };

  const sessionId =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    `session_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  state.sessionId = sessionId;
  state.sessionStart = Date.now();

  const layout = {
    width: 720,
    height: 360,
    margin: 36,
    cellSize: 90,
    offsetX: 0,
    offsetY: 0,
    pivotRadius: 12,
    panelWidth: 160,
    panelHeight: 200,
    panelX: 0,
    panelY: 0,
    panelBelow: false,
    tabletMode: false,
    tabletPortraitStack: false,
    tabletWindowed: false,
    shortLandscape: false,
    gridWidth: 0,
    gridHeight: 0,
    readmeButton: { x: 0, y: 0, width: 0, height: 0 },
    archiveButton: { x: 0, y: 0, width: 0, height: 0 },
    fullLeaderboardLink: { x: 0, y: 0, width: 0, height: 0 },
    todayButton: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardPrev: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardNext: { x: 0, y: 0, width: 0, height: 0 },
    winClose: { x: 0, y: 0, width: 0, height: 0 },
    winShare: { x: 0, y: 0, width: 0, height: 0 },
    winShareResults: { x: 0, y: 0, width: 0, height: 0 },
    winShareImage: { x: 0, y: 0, width: 0, height: 0 },
    winShareVideo: { x: 0, y: 0, width: 0, height: 0 },
    winResubmit: { x: 0, y: 0, width: 0, height: 0 },
    winSkipLeaderboard: { x: 0, y: 0, width: 0, height: 0 },
    winWatchBest: { x: 0, y: 0, width: 0, height: 0 },
    winWatchOptimal: { x: 0, y: 0, width: 0, height: 0 },
    winPlayYesterday: { x: 0, y: 0, width: 0, height: 0 },
    winIosAppCard: { x: 0, y: 0, width: 0, height: 0 },
    winIosAppDismiss: { x: 0, y: 0, width: 0, height: 0 },
    winShareBluesky: { x: 0, y: 0, width: 0, height: 0 },
    readmeFeedbackButton: { x: 0, y: 0, width: 0, height: 0 },
    readmeMailRect: { x: 0, y: 0, width: 0, height: 0 },
    readmeMastoRect: { x: 0, y: 0, width: 0, height: 0 },
    readmeBlueRect: { x: 0, y: 0, width: 0, height: 0 },
    redoButton: { x: 0, y: 0, width: 0, height: 0 },
    shareButton: { x: 0, y: 0, width: 0, height: 0 },
    submitButton: { x: 0, y: 0, width: 0, height: 0 },
    statsButton: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardTabToday: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardTabWeek: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardTabMonth: { x: 0, y: 0, width: 0, height: 0 },
    leaderboardRetry: { x: 0, y: 0, width: 0, height: 0 },
    soundButton: { x: 0, y: 0, width: 0, height: 0 },
    notifyButton: { x: 0, y: 0, width: 0, height: 0 },
    hintButton: { x: 0, y: 0, width: 0, height: 0 },
    undoButton: { x: 0, y: 0, width: 0, height: 0 },
    hardButton: { x: 0, y: 0, width: 0, height: 0 },
    moreButton: { x: 0, y: 0, width: 0, height: 0 },
    challengeButton: { x: 0, y: 0, width: 0, height: 0 },
    challengeReplayLink: { x: 0, y: 0, width: 0, height: 0 },
    exitCustomButton: { x: 0, y: 0, width: 0, height: 0 },
    customPlayAgainButton: { x: 0, y: 0, width: 0, height: 0 },
    customExitWinButton: { x: 0, y: 0, width: 0, height: 0 },
    customNextRoundButton: { x: 0, y: 0, width: 0, height: 0 },
    customEndRoundButton: { x: 0, y: 0, width: 0, height: 0 },
    customObserveModeButton: { x: 0, y: 0, width: 0, height: 0 },
    chatButton: { x: 0, y: 0, width: 0, height: 0 },
    observeRequestJoinButton: { x: 0, y: 0, width: 0, height: 0 },
    observeApproveAllButton: { x: 0, y: 0, width: 0, height: 0 },
    observeDenyAllButton: { x: 0, y: 0, width: 0, height: 0 },
    observeApproveButtons: [],  // array of {x, y, width, height, playerId}
    observeDenyButtons: [],     // array of {x, y, width, height, playerId}
    observeFilterButtons: [],   // array of {x, y, width, height, filter}
    observePlayerRects: [],     // array of {x, y, width, height, playerName}
    observeKickButtons: [],     // array of {x, y, width, height, playerId}
    observeRosterKickButtons: [], // array of {x, y, width, height, playerId}
    observeRosterApproveButtons: [], // array of {x, y, width, height, playerId}
    observeRosterDenyButtons: [], // array of {x, y, width, height, playerId}
    observeHostActionButtons: [], // array of {x, y, width, height, action}
    observeUndoKickButton: { x: 0, y: 0, width: 0, height: 0 },
    observeAutoButton: { x: 0, y: 0, width: 0, height: 0 },
    menuCreateCustom: { x: 0, y: 0, width: 0, height: 0 },
    menuHall: { x: 0, y: 0, width: 0, height: 0 },
    menuAppStore: { x: 0, y: 0, width: 0, height: 0 },
    menuPlayToday: { x: 0, y: 0, width: 0, height: 0 },
    lobbyKickButtons: [],  // array of {x, y, width, height, playerId}
  };

  // ---------------------------------------------------------------------------
  // Theme system — centralized palette for canvas + CSS sync
  // ---------------------------------------------------------------------------
  const themes = {
    light: {
      bg: "#F5F2EA",
      ink: "#1D1D1F",
      tileText: "#4A4A4C",
      tileTextGold: "#3A3520",
      inkSoft: "rgba(29,29,31,0.78)",
      gold: "#E2C87A",
      goldEdge: "#C9A455",
      goldFill: "rgba(200,162,77,0.18)",
      goldFillHover: "rgba(200,162,77,0.32)",
      yellow: "rgba(200,162,77,0.22)",
      yellowEdge: "#B89444",
      muted: "#6B6B6B",
      mutedSoft: "#999999",
      mutedFaint: "#BBBBBB",
      mutedFaintest: "#DDDDDD",
      highlight: "#D4B35C",
      gridLine: "#3C3832",
      pivotDot: "#3C3C3C",
      pivotHover: "#C8A24D",
      panel: "rgba(27,27,27,0.065)",
      panelBorder: "rgba(27,27,27,0.08)",
      panelStroke: "rgba(27,27,27,0.16)",
      card: "rgba(27,27,27,0.05)",
      cardStroke: "rgba(27,27,27,0.16)",
      btnBg: "#FFFFFF",
      btnStroke: "#CCCCCC",
      btnPrimaryBg: "#1D1D1F",
      btnPrimaryText: "#F5F2EA",
      overlay: "rgba(27,27,27,0.72)",
      overlayText: "#FDF8F1",
      overlayTextSoft: "rgba(253,248,241,0.7)",
      overlayTextFaint: "rgba(253,248,241,0.55)",
      overlayBtnBg: "rgba(253,248,241,0.12)",
      overlayBtnStroke: "rgba(253,248,241,0.3)",
      overlayBtnText: "rgba(253,248,241,0.86)",
      overlayBtnPrimaryBg: "rgba(253,248,241,0.9)",
      overlayBtnPrimaryStroke: "rgba(253,248,241,0.95)",
      overlayBtnPrimaryText: "#1D1D1F",
      toastBg: "rgba(253,248,241,0.95)",
      toastStroke: "rgba(27,27,27,0.2)",
      leaderHighlight: "rgba(200,162,77,0.35)",
      statusDefault: { bg: "rgba(27,27,27,0.07)", stroke: "rgba(27,27,27,0.18)", text: "#666" },
      statusWarn: { bg: "rgba(199,167,66,0.16)", stroke: "rgba(199,167,66,0.45)", text: "#7A6B35" },
      statusError: { bg: "rgba(179,58,47,0.1)", stroke: "rgba(179,58,47,0.35)", text: "#9B2D24" },
      error: "#B33A2F",
      success: "#5BA85B",
      solvedTileBg: "rgba(91, 168, 91, 0.12)",
      solvedStroke: "rgba(91, 168, 91, 0.5)",
      warning: "#8A6A00",
    },
    dark: {
      bg: "#131313",
      ink: "#ECE9E1",
      tileText: "#B8B5AE",
      tileTextGold: "#FFFFFF",
      inkSoft: "rgba(236,233,225,0.78)",
      gold: "#D4AF37",
      goldEdge: "#AA8A2A",
      goldFill: "rgba(212,175,55,0.25)",
      goldFillHover: "rgba(212,175,55,0.42)",
      yellow: "rgba(212,175,55,0.25)",
      yellowEdge: "#AA8A2A",
      goldFaint: "rgba(212,175,55,0.18)",
      highlight: "#D4AF37",
      muted: "#A1A1A1",
      mutedSoft: "#888888",
      mutedFaint: "#666666",
      mutedFaintest: "#444444",
      gridLine: "#BFBFBF",
      pivotDot: "#A8A8A8",
      pivotHover: "#D4AF37",
      panel: "rgba(255,255,255,0.04)",
      panelBorder: "rgba(255,255,255,0.08)",
      panelStroke: "rgba(255,255,255,0.14)",
      card: "rgba(255,255,255,0.05)",
      cardStroke: "rgba(255,255,255,0.14)",
      btnBg: "#232323",
      btnStroke: "#444444",
      btnPrimaryBg: "#ECE9E1",
      btnPrimaryText: "#131313",
      overlay: "rgba(0,0,0,0.78)",
      overlayText: "#ECE9E1",
      overlayTextSoft: "rgba(236,233,225,0.7)",
      overlayTextFaint: "rgba(236,233,225,0.55)",
      overlayBtnBg: "rgba(236,233,225,0.1)",
      overlayBtnStroke: "rgba(236,233,225,0.25)",
      overlayBtnText: "rgba(236,233,225,0.86)",
      overlayBtnPrimaryBg: "rgba(236,233,225,0.9)",
      overlayBtnPrimaryStroke: "rgba(236,233,225,0.95)",
      overlayBtnPrimaryText: "#131313",
      toastBg: "rgba(30,30,30,0.95)",
      toastStroke: "rgba(255,255,255,0.15)",
      leaderHighlight: "rgba(212,175,55,0.3)",
      statusDefault: { bg: "rgba(255,255,255,0.06)", stroke: "rgba(255,255,255,0.14)", text: "#999" },
      statusWarn: { bg: "rgba(212,175,55,0.14)", stroke: "rgba(212,175,55,0.4)", text: "#D4AF37" },
      statusError: { bg: "rgba(220,80,60,0.12)", stroke: "rgba(220,80,60,0.35)", text: "#E06050" },
      error: "#E06050",
      success: "#6BC06B",
      solvedTileBg: "rgba(107, 192, 107, 0.15)",
      solvedStroke: "rgba(107, 192, 107, 0.5)",
      warning: "#D4AF37",
    },
    highContrast: {
      bg: "#000000",
      ink: "#FFFFFF",
      tileText: "#FFFFFF",
      tileTextGold: "#000000",
      inkSoft: "rgba(255,255,255,0.9)",
      gold: "#FFD700",
      goldEdge: "#FFD700",
      goldFill: "rgba(255,215,0,0.35)",
      goldFillHover: "rgba(255,215,0,0.5)",
      yellow: "rgba(255,215,0,0.35)",
      yellowEdge: "#FFD700",
      goldFaint: "rgba(255,215,0,0.25)",
      highlight: "#FFD700",
      muted: "#CCCCCC",
      mutedSoft: "#AAAAAA",
      mutedFaint: "#888888",
      mutedFaintest: "#666666",
      gridLine: "#FFFFFF",
      pivotDot: "#FFFFFF",
      pivotHover: "#FFD700",
      panel: "rgba(255,255,255,0.08)",
      panelBorder: "rgba(255,255,255,0.2)",
      panelStroke: "rgba(255,255,255,0.3)",
      card: "rgba(255,255,255,0.1)",
      cardStroke: "rgba(255,255,255,0.3)",
      btnBg: "#1A1A1A",
      btnStroke: "#FFFFFF",
      btnPrimaryBg: "#FFFFFF",
      btnPrimaryText: "#000000",
      overlay: "rgba(0,0,0,0.9)",
      overlayText: "#FFFFFF",
      overlayTextSoft: "rgba(255,255,255,0.85)",
      overlayTextFaint: "rgba(255,255,255,0.7)",
      overlayBtnBg: "rgba(255,255,255,0.15)",
      overlayBtnStroke: "rgba(255,255,255,0.5)",
      overlayBtnText: "#FFFFFF",
      overlayBtnPrimaryBg: "#FFFFFF",
      overlayBtnPrimaryStroke: "#FFFFFF",
      overlayBtnPrimaryText: "#000000",
      toastBg: "rgba(0,0,0,0.95)",
      toastStroke: "rgba(255,255,255,0.4)",
      leaderHighlight: "rgba(255,215,0,0.4)",
      statusDefault: { bg: "rgba(255,255,255,0.1)", stroke: "rgba(255,255,255,0.3)", text: "#CCCCCC" },
      statusWarn: { bg: "rgba(255,215,0,0.2)", stroke: "rgba(255,215,0,0.6)", text: "#FFD700" },
      statusError: { bg: "rgba(255,80,80,0.2)", stroke: "rgba(255,80,80,0.6)", text: "#FF5050" },
      error: "#FF5050",
      success: "#50FF50",
      solvedTileBg: "rgba(80, 255, 80, 0.2)",
      solvedStroke: "rgba(80, 255, 80, 0.6)",
      warning: "#FFD700",
    },
  };

  let _currentTheme = "light";
  /** @type {typeof themes.light} */
  let C = themes.light;

  // Background texture cache (declared early so applyTheme can invalidate)
  let _bgTextureLight = null;
  let _bgTextureDark = null;
  let _bgTextureTheme = null;
  let _bgTextureW = 0;
  let _bgTextureH = 0;

  // Legacy compat shim — any remaining `colors.X` still works
  const colors = new Proxy({}, {
    get(_, prop) { return C[prop]; },
  });

  // --- Font stacks (swappable for OpenDyslexic) ---
  const _SERIF_DEFAULT = `Georgia, "Times New Roman", serif`;
  const _UI_SERIF_DEFAULT = `ui-serif, "Iowan Old Style", Palatino, Georgia, "Times New Roman", serif`;
  const _SERIF_DYSLEXIC = `"OpenDyslexic", Georgia, "Times New Roman", serif`;
  const _UI_SERIF_DYSLEXIC = `"OpenDyslexic", ui-serif, "Iowan Old Style", Palatino, Georgia, "Times New Roman", serif`;
  let _fontSerif = _SERIF_DEFAULT;
  let _fontUI = _UI_SERIF_DEFAULT;
  let _dyslexicFont = false;

  function _loadDyslexicPref() {
    try { return localStorage.getItem("pivot_dyslexic") === "1"; } catch { return false; }
  }

  let _dyslexicFontLoaded = false;

  function _ensureDyslexicFont() {
    if (_dyslexicFontLoaded) return Promise.resolve();
    if (!document.fonts) return Promise.resolve();
    return Promise.all([
      document.fonts.load('400 16px "OpenDyslexic"'),
      document.fonts.load('600 16px "OpenDyslexic"'),
      document.fonts.load('italic 16px "OpenDyslexic"'),
    ]).then(() => { _dyslexicFontLoaded = true; }).catch(() => {});
  }

  function setDyslexicFont(on) {
    _dyslexicFont = on;
    _fontSerif = on ? _SERIF_DYSLEXIC : _SERIF_DEFAULT;
    _fontUI = on ? _UI_SERIF_DYSLEXIC : _UI_SERIF_DEFAULT;
    document.documentElement.classList.toggle("dyslexic-font", on);
    try { localStorage.setItem("pivot_dyslexic", on ? "1" : "0"); } catch {}
    if (on) _ensureDyslexicFont().then(() => { if (typeof render === "function") render(); });
  }

  // Init on load
  if (_loadDyslexicPref()) setDyslexicFont(true);

  function loadThemePreference() {
    try {
      const saved = localStorage.getItem("pivot_theme");
      if (saved === "dark" || saved === "light" || saved === "highContrast") return saved;
    } catch {}
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
    return "light";
  }

  function applyTheme(theme, persist) {
    _currentTheme = theme;
    C = themes[theme];
    document.documentElement.setAttribute("data-theme", theme);
    // Sync meta theme-color
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute("content", C.bg);
    // Invalidate background texture cache on theme change
    _bgTextureTheme = null;
    if (persist) { try { localStorage.setItem("pivot_theme", theme); } catch {} }
    // Update theme toggle button aria-label
    const toggleBtn = document.getElementById("theme-toggle");
    if (toggleBtn) {
      const nextLabel = theme === "light" ? "Switch to dark mode" : "Switch to light mode";
      toggleBtn.setAttribute("aria-label", nextLabel);
    }
  }

  function toggleTheme() {
    const next = _currentTheme === "light" ? "dark" : "light";
    applyTheme(next, true);
  }

  // Initialize theme immediately
  applyTheme(loadThemePreference());

  // Listen for system theme changes (auto-switch only if no manual preference)
  try {
    const _themeMedia = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
    if (!_themeMedia) throw new Error("no_match_media");
    const _onThemeChange = (e) => {
      try {
        const saved = localStorage.getItem("pivot_theme");
        if (!saved) applyTheme(e.matches ? "dark" : "light");
      } catch {}
    };
    if (typeof _themeMedia.addEventListener === "function") {
      _themeMedia.addEventListener("change", _onThemeChange);
    } else if (typeof _themeMedia.addListener === "function") {
      _themeMedia.addListener(_onThemeChange);
    }
  } catch {}

  const _prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const brandIcon = new Image();
  brandIcon.src = "/favicon.svg";

  // ---------------------------------------------------------------------------
  // Sound Effects — procedural Web Audio API (no files to download)
  // ---------------------------------------------------------------------------
  let _audioCtx = null;
  let _masterGain = null;
  let _soundMuted = true;
  try { const _sm = localStorage.getItem("pivot_sound_muted"); _soundMuted = _sm === null ? true : _sm === "1"; } catch {}
  let _winFocusIndex = -1; // keyboard focus index for win screen buttons (0=Share, 1=Replay, 2=Challenge)
  let _canvasLastMouse = null; // { x, y } for tooltip hover detection
  let _medalTooltipUntil = 0; // timestamp for tap-triggered medal tooltip
  const _isCustomPage = !!window.PIVOT_CUSTOM_MODE;
  let _customChannel = null; // Supabase Realtime channel for custom multiplayer
  // The room code the channel was CREATED with. Session room codes stay fixed
  // across rounds; this separate pin also keeps reconnects on the established
  // channel if client display state is ever corrupted by a stale payload.
  let _customChannelCode = "";
  // Tags feedback opened from the post-race prompt. Declared with the other
  // module state so closeFeedbackOverlay can never hit it in the temporal dead
  // zone if it is ever called during boot.
  let _feedbackSource = "";
  let _heartbeatInterval = null; // Host broadcasts player_list every 10s
  let _playerPingInterval = null; // Guests tell the host they are still connected
  let _watchdogTimeout = null;   // Client: if no player_list in 15s, host disconnected
  let _countdownTimeout = null;  // Host: 3s countdown before game_start / next_round
  let _kickedExitTimeout = null; // Client: delayed exit window to allow host kick undo
  let _leaderboardAutoRetryTimer = null;
  const CUSTOM_RECONNECT_GRACE_MS = 30000;

  // ---------------------------------------------------------------------------
  // BFS Worker — keep heavy searches off the main thread
  // ---------------------------------------------------------------------------
  let _bfsWorker = null;
  let _bfsWorkerSeq = 0;
  const _bfsWorkerPending = new Map(); // id -> { resolve, reject, timer }
  const _bfsCache = new Map(); // key -> { moves, exact }
  const _bfsCacheMax = 2500;
  const _externalScriptLoads = new Map(); // src -> Promise<void>
  let _videoReplayLoadPromise = null;
  const _assetBaseUrl = (() => {
    // Prefer resolving relative to the script URL (works on GitHub Pages project sites
    // where the app lives under a subpath).
    try {
      const cs = document.currentScript;
      if (cs && cs.src) return new URL(".", cs.src).toString();
    } catch {
      // ignore
    }
    try { return new URL(".", location.href).toString(); } catch { return ""; }
  })();

  function _bfsCacheGet(key) {
    return _bfsCache.get(key) || null;
  }

  function resolveAssetUrl(assetPath) {
    try {
      return _assetBaseUrl ? new URL(assetPath, _assetBaseUrl).toString() : assetPath;
    } catch {
      return assetPath;
    }
  }

  function loadExternalScript(src, timeoutMs = 10000) {
    if (_externalScriptLoads.has(src)) return _externalScriptLoads.get(src);
    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts || []).find((s) => s.src === src);
      if (existing && (existing.dataset.pivotLoaded === "1" || existing.readyState === "complete")) {
        resolve();
        return;
      }
      const script = existing || document.createElement("script");
      let settled = false;
      const finishOk = () => {
        if (settled) return;
        settled = true;
        script.dataset.pivotLoaded = "1";
        resolve();
      };
      const finishErr = () => {
        if (settled) return;
        settled = true;
        _externalScriptLoads.delete(src);
        reject(new Error(`script_load_failed:${src}`));
      };
      const timer = setTimeout(finishErr, timeoutMs);
      script.addEventListener("load", () => {
        clearTimeout(timer);
        finishOk();
      }, { once: true });
      script.addEventListener("error", () => {
        clearTimeout(timer);
        finishErr();
      }, { once: true });
      if (!existing) {
        script.src = src;
        script.async = true;
        document.head.appendChild(script);
      }
    });
    _externalScriptLoads.set(src, promise);
    return promise;
  }

  function ensureVideoReplayLoaded() {
    if (window.PivotVideo && typeof window.PivotVideo.generate === "function") {
      return Promise.resolve(true);
    }
    if (_videoReplayLoadPromise) return _videoReplayLoadPromise;
    const mp4MuxerUrl = "https://cdn.jsdelivr.net/npm/mp4-muxer@5/build/mp4-muxer.min.js";
    const replayUrl = resolveAssetUrl(`video-replay.js?v=${VIDEO_REPLAY_VERSION}`);
    const ensureMuxer = typeof Mp4Muxer !== "undefined"
      ? Promise.resolve()
      : loadExternalScript(mp4MuxerUrl, 12000);
    _videoReplayLoadPromise = ensureMuxer
      .then(() => {
        if (window.PivotVideo && typeof window.PivotVideo.generate === "function") {
          return true;
        }
        return loadExternalScript(replayUrl, 12000);
      })
      .then(() => Boolean(window.PivotVideo && typeof window.PivotVideo.generate === "function"))
      .catch(() => false)
      .finally(() => {
        if (!window.PivotVideo || typeof window.PivotVideo.generate !== "function") {
          _videoReplayLoadPromise = null;
        }
      });
    return _videoReplayLoadPromise;
  }

  function _bfsCacheSet(key, value) {
    _bfsCache.set(key, value);
    if (_bfsCache.size > _bfsCacheMax) {
      const it = _bfsCache.keys();
      for (let i = 0; i < 250; i += 1) {
        const k = it.next().value;
        if (!k) break;
        _bfsCache.delete(k);
      }
    }
  }

  function ensureBfsWorker() {
    if (_bfsWorker) return _bfsWorker;
    try {
      const workerUrl = _assetBaseUrl ? new URL("bfs-worker.js", _assetBaseUrl).toString() : "bfs-worker.js";
      _bfsWorker = new Worker(workerUrl);
      _bfsWorker.addEventListener("message", (event) => {
        const msg = event.data || {};
        const pending = msg && msg.id ? _bfsWorkerPending.get(msg.id) : null;
        if (!pending) return;
        _bfsWorkerPending.delete(msg.id);
        if (pending.timer) clearTimeout(pending.timer);
        if (msg.ok) pending.resolve(msg);
        else pending.reject(new Error(msg.error || "bfs_worker_error"));
      });
      _bfsWorker.addEventListener("error", () => {
        // Disable the worker after an error to avoid infinite retries.
        if (_bfsWorker) {
          try { _bfsWorker.terminate(); } catch {}
        }
        _bfsWorker = null;
        for (const [, p] of _bfsWorkerPending) {
          if (p.timer) clearTimeout(p.timer);
          p.reject(new Error("bfs_worker_failed"));
        }
        _bfsWorkerPending.clear();
      });
      return _bfsWorker;
    } catch {
      _bfsWorker = null;
      return null;
    }
  }

  function getBfsFallbackResult(startStr, targetWord) {
    const lb = computeLowerBoundForStart(startStr, targetWord);
    return { moves: Number.isFinite(lb) ? lb : null, exact: false };
  }

  function bfsMinToCenterAsync(startStr, targetWord, maxNodes = 300000, timeBudgetMs = 0, allowMainThreadFallback = false) {
    const cacheKey = `${targetWord}|${maxNodes}|${timeBudgetMs}|${allowMainThreadFallback ? "sync" : "lb"}|${startStr}`;
    const cached = _bfsCacheGet(cacheKey);
    if (cached) return Promise.resolve(cached);

    const worker = ensureBfsWorker();
    if (!worker) {
      const result = allowMainThreadFallback
        ? findMinimumMovesToCenter(startStr, targetWord, maxNodes)
        : getBfsFallbackResult(startStr, targetWord);
      _bfsCacheSet(cacheKey, result);
      return Promise.resolve(result);
    }

    const id = `bfs_${Date.now()}_${++_bfsWorkerSeq}`;
    return new Promise((resolve) => {
      const timeoutMs = Math.max(3500, Math.min(12000, Math.round(maxNodes / 60)));
      const timeoutId = setTimeout(() => {
        const pending = _bfsWorkerPending.get(id);
        if (!pending) return;
        _bfsWorkerPending.delete(id);
        pending.reject(new Error("bfs_worker_timeout"));
        if (_bfsWorker) {
          try { _bfsWorker.terminate(); } catch {}
          _bfsWorker = null;
        }
      }, timeoutMs);
      _bfsWorkerPending.set(id, {
        timer: timeoutId,
        resolve: (msg) => {
          const result = {
            moves: msg.moves ?? null,
            exact: !!msg.exact,
            path: Array.isArray(msg.path) ? msg.path.slice() : [],
          };
          _bfsCacheSet(cacheKey, result);
          resolve(result);
        },
        reject: () => {
          const result = allowMainThreadFallback
            ? findMinimumMovesToCenter(startStr, targetWord, maxNodes)
            : getBfsFallbackResult(startStr, targetWord);
          _bfsCacheSet(cacheKey, result);
          resolve(result);
        },
      });
      try {
        worker.postMessage({ id, type: "min_to_center", startStr, targetWord, maxNodes, timeBudgetMs });
      } catch {
        const pending = _bfsWorkerPending.get(id);
        _bfsWorkerPending.delete(id);
        if (pending && pending.timer) clearTimeout(pending.timer);
        const result = allowMainThreadFallback
          ? findMinimumMovesToCenter(startStr, targetWord, maxNodes)
          : getBfsFallbackResult(startStr, targetWord);
        _bfsCacheSet(cacheKey, result);
        resolve(result);
      }
    });
  }

  function ensureAudioCtx() {
    if (_audioCtx) return _audioCtx;
    // Game effects should mix with music/podcasts, not claim media playback.
    // Feature-detect for older browsers; Web Audio alone respects silent mode.
    try { if (navigator.audioSession) navigator.audioSession.type = "ambient"; } catch {}
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
    _masterGain = _audioCtx.createGain();
    _masterGain.gain.value = 2.5;
    _masterGain.connect(_audioCtx.destination);
    return _audioCtx;
  }

  // ---------------------------------------------------------------------------
  // Haptic feedback
  // ---------------------------------------------------------------------------
  function haptic(style) {
    if (_soundMuted) return;
    if (PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeHaptic) {
      window.webkit.messageHandlers.nativeHaptic.postMessage(style || "medium");
    }
    navigator.vibrate?.(style === "heavy" ? 20 : style === "light" ? 5 : 10);
  }

  // ---------------------------------------------------------------------------
  // Accessibility announcements
  // ---------------------------------------------------------------------------
  const _a11yStatus = document.getElementById("a11y-status");
  const _a11yAlert = document.getElementById("a11y-alert");
  function announceStatus(msg) { if (_a11yStatus) _a11yStatus.textContent = msg; }
  function announceAlert(msg) { if (_a11yAlert) _a11yAlert.textContent = msg; }
  function setCanvasLabel(label) {
    const c = document.getElementById("game");
    if (c) c.setAttribute("aria-label", label);
  }
  function getGridDescription() {
    if (!state.grid) return "";
    const rows = [];
    for (let r = 0; r < GRID_ROWS; r++) {
      const label = r === CENTER_ROW ? "Center row" : `Row ${r + 1}`;
      rows.push(`${label}: ${state.grid[r].join(" ")}`);
    }
    return rows.join(". ");
  }

  // ---------------------------------------------------------------------------
  // Navigation helper — in native app, window.open is blocked by WKWebView
  // ---------------------------------------------------------------------------
  function navigateTo(url, newTab) {
    if (PIVOT_NATIVE || !newTab) {
      window.location.href = url;
    } else {
      window.open(url, "_blank");
    }
  }

  // ---------------------------------------------------------------------------
  // Sound Design — mechanical, analog, restrained
  // Think: brass, felt, wood, watch movements
  // ---------------------------------------------------------------------------

  // Tap: crisp tile click — short, bright, no bass thump
  function playPivotClick() {
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const t = ac.currentTime;
    // Short noise burst band-passed high — ceramic/tile tap
    const bufLen = Math.round(ac.sampleRate * 0.012);
    const buf = ac.createBuffer(1, bufLen, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    const src = ac.createBufferSource();
    src.buffer = buf;
    const hp = ac.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 800;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 3500;
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.22, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.018);
    src.connect(hp).connect(lp).connect(gain).connect(_masterGain);
    src.start(t);
    src.stop(t + 0.015);
  }

  // Rotate: soft air → mechanical glide → confident settle/lock
  function playSolveFanfare() {
    haptic("heavy");
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const t = ac.currentTime + 0.12;
    // Bright resolving chord: C4 + E4 + G4 + B4 (major 7th, mid register)
    const chord = [261.63, 329.63, 392.00, 493.88];
    chord.forEach((freq, i) => {
      const osc = ac.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const osc2 = ac.createOscillator();
      osc2.type = "sine";
      osc2.frequency.value = freq * 2;
      const gain = ac.createGain();
      const vol = i === 3 ? 0.12 : 0.18; // B4 softer for shimmer
      gain.gain.setValueAtTime(vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.3);
      const gain2 = ac.createGain();
      gain2.gain.setValueAtTime(0.04, t);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
      osc.connect(gain).connect(_masterGain);
      osc2.connect(gain2).connect(_masterGain);
      osc.start(t);
      osc.stop(t + 1.3);
      osc2.start(t);
      osc2.stop(t + 0.8);
    });
  }

  // Menu tap: wooden domino touched — short, warm, low
  function playMenuTap() {
    haptic("light");
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const t = ac.currentTime;
    // Short filtered noise + very low tone
    const bufLen = Math.round(ac.sampleRate * 0.02);
    const buf = ac.createBuffer(1, bufLen, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    const src = ac.createBufferSource();
    src.buffer = buf;
    const filter = ac.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 400;
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.28, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    src.connect(filter).connect(gain).connect(_masterGain);
    src.start(t);
    src.stop(t + 0.025);
  }

  // Achievement: muted vibraphone — single warm tone, slow decay
  function playAchievementSound() {
    haptic("medium");
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const t = ac.currentTime;
    // G4 with gentle harmonic — dampened vibraphone
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.value = 392.00; // G4
    const osc2 = ac.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 392.00 * 2.5; // soft partial
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.28, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    const gain2 = ac.createGain();
    gain2.gain.setValueAtTime(0.08, t);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(gain).connect(_masterGain);
    osc2.connect(gain2).connect(_masterGain);
    osc.start(t);
    osc.stop(t + 0.8);
    osc2.start(t);
    osc2.stop(t + 0.4);
  }

  function toggleSound() {
    _soundMuted = !_soundMuted;
    try { localStorage.setItem("pivot_sound_muted", _soundMuted ? "1" : "0"); } catch {}
    if (!_soundMuted) {
      ensureAudioCtx();
      playMenuTap();
    }
  }


  function setShareDetailPreference(enabled) {
    state.shareIncludeDetails = Boolean(enabled);
    safeSetItem(SHARE_DETAILS_KEY, state.shareIncludeDetails ? "1" : "0");
    setShareToast(state.shareIncludeDetails ? "Share details on" : "Share details off", 1400);
  }


  // ---------------------------------------------------------------------------
  // Hint System — BFS distance + highlight optimal pivot
  // ---------------------------------------------------------------------------
  function computeHintDistanceAsync() {
    const startStr = gridToString(state.grid);
    return bfsMinToCenterAsync(startStr, state.targetWord, 300000, 1200, true).then((r) => r.moves);
  }

  function hintScore(stateStr, word) {
    // Sum of minimum token distances for each target letter — lower is better.
    // Much more discriminating than max-based lower bound.
    if (!stateStr || !word) return Infinity;
    let sum = 0;
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      const targetPos = CENTER_POSITIONS[i];
      const letter = word[i];
      let best = Infinity;
      for (let p = 0; p < CELL_COUNT; p += 1) {
        if (stateStr[p] !== letter) continue;
        const d = TOKEN_DIST[p][targetPos];
        if (d < best) best = d;
      }
      if (!Number.isFinite(best)) return Infinity;
      sum += best;
    }
    // Bonus: count letters already in correct position
    const centerStart = CENTER_ROW * GRID_COLS + CENTER_START_COL;
    let correct = 0;
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      if (stateStr[centerStart + i] === word[i]) correct += 1;
    }
    return sum * 10 - correct;
  }

  function computeHeuristicHintPivot(currentStr) {
    if (!currentStr || !state.targetWord) return null;
    const currentScore = hintScore(currentStr, state.targetWord);
    let bestIdx = null;
    let bestScore = Infinity;
    for (let i = 0; i < MOVE_MAPS.length; i += 1) {
      const nextStr = applyMove(currentStr, i, true);
      const s = hintScore(nextStr, state.targetWord);
      if (s < bestScore) {
        bestScore = s;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  function computeHintPivotAsync(currentStr, currentDist) {
    if (!Number.isFinite(currentDist) || currentDist <= 0) {
      return Promise.resolve(null);
    }
    let p = Promise.resolve(null);
    for (let i = 0; i < MOVE_MAPS.length; i += 1) {
      p = p.then((found) => {
        if (found !== null) return found;
        const nextStr = applyMove(currentStr, i, true);
        return bfsMinToCenterAsync(nextStr, state.targetWord, 300000, 700).then((nextResult) => {
          if (Number.isFinite(nextResult.moves) && nextResult.moves === currentDist - 1) return i;
          return null;
        });
      });
    }
    return p.then((found) => found);
  }

  function computeHintSequenceAsync(startStr, maxSteps) {
    maxSteps = Math.min(maxSteps || 3, 5);
    const steps = [];
    function findNext(str, remaining) {
      if (remaining <= 0) return Promise.resolve(steps);
      return bfsMinToCenterAsync(str, state.targetWord, 300000, 700, true).then((result) => {
        const dist = result.moves;
        if (!Number.isFinite(dist) || dist <= 0) return steps;
        // Try each move to find one that reduces distance by 1
        let chain = Promise.resolve(null);
        for (let i = 0; i < MOVE_MAPS.length; i += 1) {
          chain = chain.then((found) => {
            if (found !== null) return found;
            const nextStr = applyMove(str, i, true);
            return bfsMinToCenterAsync(nextStr, state.targetWord, 300000, 700, true).then((nr) => {
              if (Number.isFinite(nr.moves) && nr.moves === dist - 1) return { idx: i, nextStr };
              return null;
            });
          });
        }
        return chain.then((found) => {
          if (!found) {
            return steps;
          }
          steps.push(found.idx);
          return findNext(found.nextStr, remaining - 1);
        });
      });
    }
    return findNext(startStr, maxSteps);
  }

  let _hintConfirmPending = false;
  function useHint() {
    if (state.mode !== "play" || state.solvedToday) return;
    // If already used a hint this game, skip confirmation
    if (!state.hintUsed && !_hintConfirmPending) {
      _hintConfirmPending = true;
      // Show confirmation via a simple overlay approach
      const overlay = document.getElementById("custom-game-overlay");
      const content = document.getElementById("custom-game-content");
      if (overlay && content) {
        // The shared overlay keeps the last opener's title ("Practice Game" by default).
        setCustomOverlayTitle("Hint");
        content.innerHTML = `
          <div style="text-align:center;margin:8px 0">
            <div style="font-size:15px;margin-bottom:10px">Use a hint?</div>
            <div style="font-size:12px;color:#999;margin-bottom:14px">This will show the optimal pivot to tap.<br>You will no longer be eligible for the leaderboard.</div>
            <div style="display:flex;gap:8px;justify-content:center">
              <button id="hint-confirm-yes" class="custom-start-btn" style="width:auto;padding:8px 20px;margin:0">Use hint</button>
              <button id="hint-confirm-no" class="custom-cancel-btn" style="width:auto;padding:8px 20px;margin:0">Cancel</button>
            </div>
          </div>
        `;
        overlay.classList.add("show");
        overlay.setAttribute("aria-hidden", "false");
        document.getElementById("hint-confirm-yes")?.addEventListener("click", () => {
          fadeOutOverlay(overlay);
          _hintConfirmPending = false;
          applyHint();
        });
        document.getElementById("hint-confirm-no")?.addEventListener("click", () => {
          fadeOutOverlay(overlay);
          _hintConfirmPending = false;
        });
      } else {
        // Fallback if overlay not available
        _hintConfirmPending = false;
        applyHint();
      }
      return;
    }
    if (state.hintUsed) applyHint();
  }

  let _hintToken = 0;

  function showHintSequence(sequence, toastText) {
    if (!sequence || !sequence.length) return;
    if (toastText) setShareToast(toastText, 3000);
    const firstPivot = sequence[0];
    const hintR = Math.floor(firstPivot / (GRID_COLS - 1)) + 1;
    const hintC = (firstPivot % (GRID_COLS - 1)) + 1;
    announceStatus(`Hint: rotate pivot at row ${hintR}, column ${hintC}.`);
    const now = Date.now();
    const stepDuration = 1500;
    const stepStagger = 1800;
    state.hintSequence = sequence.map((pivotIdx, i) => ({
      pivotIdx,
      start: now + i * stepStagger,
      end: now + i * stepStagger + stepDuration,
    }));
    state.hintSequenceUntil = now + (sequence.length - 1) * stepStagger + stepDuration;
    state.hintPivotIndex = sequence[0];
    state.hintHighlightUntil = state.hintSequenceUntil;
    scheduleRenderLoop();
  }

  function applyHint() {
    state.hintUsed = true;
    state.leaderboardEligible = false;
    const token = ++_hintToken;
    const currentStr = gridToString(state.grid);
    state.hintDistance = null;
    state.hintDistanceVisible = true;
    state.hintPivotIndex = null;
    state.hintSequence = null;
    state.hintSequenceUntil = 0;

    setShareToast("Computing optimal hint\u2026", 10000);

    // BFS-optimal hint only — no heuristic fallback
    computeHintDistanceAsync()
      .then((dist) => {
        if (token !== _hintToken) return;
        state.hintDistance = dist;
        if (Number.isFinite(dist) && dist > 0) {
          const stepsToShow = Math.min(3, dist);
          return computeHintSequenceAsync(currentStr, stepsToShow).then((sequence) => {
            if (token !== _hintToken) return;
            if (sequence && sequence.length) {
              setShareToast(`${dist} move${dist === 1 ? "" : "s"} away \u2014 follow the dots`, 4000);
              showHintSequence(sequence);
            } else {
              setShareToast("Could not compute hint", 2000);
            }
          });
        } else if (dist === 0) {
          setShareToast("Already solved!", 2000);
        } else {
          setShareToast("Could not compute hint", 2000);
        }
      })
      .catch((err) => {
        console.warn("Hint BFS failed:", err);
        if (token === _hintToken) setShareToast("Could not compute hint", 2000);
      });
    logEvent("hint_used", { moves: state.moves }, `${state.dailyKey}_hint`);
  }

  // ---------------------------------------------------------------------------
  // Achievements — localStorage-based, 12 unlockable
  // ---------------------------------------------------------------------------
  const ACHIEVEMENTS = [
    { id: "first_solve", name: "First Steps", desc: "Solve 1 puzzle", icon: "\ud83c\udfaf" },
    { id: "streak_3", name: "Hat Trick", desc: "3-day streak", icon: "\ud83d\udd25" },
    { id: "streak_7", name: "Week Warrior", desc: "7-day streak", icon: "\ud83d\udcaa" },
    { id: "streak_30", name: "Monthly Master", desc: "30-day streak", icon: "\ud83c\udfc6" },
    { id: "optimal", name: "Perfect Solve", desc: "Solve in BFS-minimum moves", icon: "\u2b50" },
    { id: "under_10", name: "Single Digits", desc: "Solve in <10 moves", icon: "\ud83c\udfaa" },
    { id: "under_15", name: "Quick Thinker", desc: "Solve in <15 moves", icon: "\u26a1" },
    { id: "played_10", name: "Dedicated", desc: "Play 10 puzzles", icon: "\ud83d\udcc5" },
    { id: "played_50", name: "Half Century", desc: "Play 50 puzzles", icon: "\ud83c\udf96\ufe0f" },
    { id: "played_100", name: "Centurion", desc: "Play 100 puzzles", icon: "\ud83d\udc8e" },
    { id: "leaderboard", name: "On the Board", desc: "Submit a leaderboard score", icon: "\ud83d\udccb" },
    { id: "share", name: "Spread the Word", desc: "Share your result", icon: "\ud83d\udce4" },
  ];
  const ACHIEVEMENT_KEY = "pivot_achievements";

  function loadAchievements() {
    try {
      const raw = localStorage.getItem(ACHIEVEMENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  function saveAchievements(ids) {
    try { localStorage.setItem(ACHIEVEMENT_KEY, JSON.stringify(ids)); } catch {}
  }

  function unlockAchievement(id) {
    const unlocked = loadAchievements();
    if (unlocked.includes(id)) return;
    unlocked.push(id);
    saveAchievements(unlocked);
    const def = ACHIEVEMENTS.find((a) => a.id === id);
    if (def) {
      state.achievementToastIcon = def.icon;
      state.achievementToastText = def.name;
      state.achievementToastUntil = Date.now() + 3000;
      playAchievementSound();
    }
  }

  function checkAchievements() {
    const s = computePlayerStats();
    if (s.gamesPlayed >= 1) unlockAchievement("first_solve");
    if (s.currentStreak >= 3 || s.maxStreak >= 3) unlockAchievement("streak_3");
    if (s.currentStreak >= 7 || s.maxStreak >= 7) unlockAchievement("streak_7");
    if (s.currentStreak >= 30 || s.maxStreak >= 30) unlockAchievement("streak_30");
    if (Number.isFinite(state.minMoves) && state.minExact && state.moves === state.minMoves) {
      unlockAchievement("optimal");
    }
    if (state.moves < 10) unlockAchievement("under_10");
    if (state.moves < 15) unlockAchievement("under_15");
    if (s.gamesPlayed >= 10) unlockAchievement("played_10");
    if (s.gamesPlayed >= 50) unlockAchievement("played_50");
    if (s.gamesPlayed >= 100) unlockAchievement("played_100");
  }

  function buildAchievementGridHtml() {
    const unlocked = new Set(loadAchievements());
    let html = `<div class="stats-section-title">${unlocked.size} of ${ACHIEVEMENTS.length} unlocked</div>`;
    html += `<div class="achievement-grid">`;
    for (const a of ACHIEVEMENTS) {
      const isUnlocked = unlocked.has(a.id);
      html += `<div class="achievement-item${isUnlocked ? "" : " locked"}">`;
      html += `<span class="achievement-icon">${isUnlocked ? a.icon : "\ud83d\udd12"}</span>`;
      html += `<span><span class="achievement-name">${a.name}</span><span class="achievement-desc">${a.desc}</span></span>`;
      html += `</div>`;
    }
    html += `</div>`;
    return html;
  }

  // ---------------------------------------------------------------------------
  // Challenge a Friend
  // ---------------------------------------------------------------------------
  function parseChallenge() {
    const params = new URLSearchParams(window.location.search);
    const challengeIdRaw = params.get("cid") || "";
    const challengeId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(challengeIdRaw)
      ? challengeIdRaw
      : "";
    const challengeName = params.get("challenge");
    const challengeDay = params.get("d");
    if (!challengeId && (!challengeName || !challengeDay)) return null;
    const parsedDay = challengeDay && /^\d+$/.test(challengeDay) ? Number(challengeDay) : null;
    const dayNum = Number.isSafeInteger(parsedDay) && parsedDay >= 1 && parsedDay <= getDayNumber() ? parsedDay : null;
    if (!challengeId && dayNum === null) return null;
    // Optional opponent player_id (for rivalry attribution). Drops silently
    // if missing or malformed — challenges from older links still work.
    const opponentPlayerIdRaw = params.get("p") || "";
    const opponentPlayerId =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(opponentPlayerIdRaw)
        ? opponentPlayerIdRaw
        : "";
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("cid");
      url.searchParams.delete("challenge");
      url.searchParams.delete("d");
      url.searchParams.delete("p");
      const nextSearch = url.searchParams.toString();
      history.replaceState(
        null,
        "",
        `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash || ""}`
      );
    } catch {
      history.replaceState(null, "", window.location.pathname);
    }
    return {
      id: challengeId,
      name: challengeName || "A friend",
      dayNum,
      moves: null,
      opponentPlayerId,
    };
  }

  function resolveChallenge(challenge) {
    if (!challenge || (!challenge.id && (!challenge.name || !challenge.dayNum))) return Promise.resolve(null);
    const query = challenge.id
      ? `id=${encodeURIComponent(challenge.id)}`
      : `name=${encodeURIComponent(challenge.name)}&day=${challenge.dayNum}`;
    return fetch(`${SUPABASE_URL}/functions/v1/pivot-get-replay?${query}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data && Number.isFinite(data.moves)) {
          const ghostHistory = Array.isArray(data.move_history)
            ? data.move_history.map(Number).filter((move) => Number.isInteger(move) && move >= 0 && move < MOVE_MAPS.length).slice(0, 300)
            : [];
          const ghostStartState = isValidGridString(data.start_state) ? data.start_state : "";
          const resolved = {
            ...challenge,
            id: typeof data.id === "string" ? data.id : challenge.id,
            name: typeof data.name === "string" && data.name ? data.name : challenge.name,
            dayNum: Number.isFinite(Number(data.day)) ? Number(data.day) : challenge.dayNum,
            moves: data.moves,
            ghostHistory: ghostHistory.length === Number(data.moves) ? ghostHistory : [],
            ghostStartState,
            opponentPlayerId: typeof data.player_id === "string" && data.player_id
              ? data.player_id
              : challenge.opponentPlayerId,
          };
          state.challengeFrom = resolved;
          state.challengeResolved = true;
          if (state.mode === "play" && resolved.ghostHistory.length) scheduleRenderLoop();
          updateChallengeLanding(resolved);
          return resolved;
        }
        updateChallengeLanding(challenge, true);
        return null;
      })
      .catch(() => {
        updateChallengeLanding(challenge, true);
        return null;
      });
  }

  let _challengeLandingEl = null;
  function dismissChallengeLanding(clearChallenge = false) {
    if (_challengeLandingEl) {
      _challengeLandingEl.remove();
      _challengeLandingEl = null;
    }
    if (clearChallenge) {
      state.challengeFrom = null;
      state.challengeResolved = false;
    }
  }

  function dayNumberToKey(dayNum) {
    const start = Date.UTC(2026, 0, 1);
    const date = new Date(start + (Math.max(1, Number(dayNum) || 1) - 1) * 86400000);
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
  }

  function acceptChallengeFromLanding(skipDemo = false) {
    if (!skipDemo) {
      // The invite stacks above the tutorial (z-index 10020 vs 10010), so it must go
      // first or a new player's demo opens hidden behind it.
      dismissChallengeLanding(false);
      if (growth?.maybeOnboard(() => acceptChallengeFromLanding(true))) return;
    }
    const challenge = state.challengeFrom;
    if (!challenge) return;
    // A cid-only invitation has no edition until its replay resolves. Number(null)
    // is zero, which otherwise sends acceptance to the first archive puzzle.
    if (!Number.isSafeInteger(challenge.dayNum) || challenge.dayNum < 1) {
      setShareToast("Still loading this challenge. Please try again.", 2400);
      resolveChallenge(challenge);
      return;
    }
    logUiEvent("challenge_accept", {
      challenge_day: Number(challenge.dayNum) || 0,
      challenge_resolved: state.challengeResolved ? 1 : 0,
      challenge_id: challenge.id ? 1 : 0,
    });
    dismissChallengeLanding(false);
    state.ghostStartedAt = performance.now();
    state.ghostMoveIndex = 0;
    state.ghostGrid = challenge.ghostStartState || "";
    if (Number.isFinite(Number(challenge.dayNum)) && Number(challenge.dayNum) < getDayNumber()) {
      _pendingArchiveChallenge = { ...challenge, archiveKey: dayNumberToKey(challenge.dayNum) };
      startArchive(_pendingArchiveChallenge.archiveKey);
      return;
    }
    if (!getDailyWord()) {
      _pendingMenuStart = true;
      setMenuStartLoadState("loading");
      _fetchDailyWord();
      return;
    }
    resetGame();
    scheduleRenderLoop();
  }

  function updateChallengeLanding(challenge = state.challengeFrom, failed = false) {
    if (!_challengeLandingEl || !challenge) return;
    const title = _challengeLandingEl.querySelector("[data-challenge-title]");
    const score = _challengeLandingEl.querySelector("[data-challenge-score]");
    const edition = _challengeLandingEl.querySelector("[data-challenge-edition]");
    const accept = _challengeLandingEl.querySelector("[data-challenge-accept]");
    const safeName = String(challenge.name || "A friend").trim().slice(0, 32) || "A friend";
    // moves/dayNum are null until resolved; Number(null) is 0, which read "solved it in 0 moves".
    const hasMoves = Number.isFinite(challenge.moves);
    if (title) title.textContent = `${safeName} challenged you`;
    if (score) {
      score.textContent = hasMoves
        ? `${safeName} solved it in ${Number(challenge.moves)} moves. Can you beat that?`
        : (failed ? "Their score couldn’t be loaded, but you can still play the same puzzle." : "Loading their score…");
    }
    if (edition) {
      edition.textContent = Number.isSafeInteger(challenge.dayNum) && challenge.dayNum >= 1
        ? `PIVOT Edition No. ${Number(challenge.dayNum)}`
        : "PIVOT daily challenge";
    }
    if (accept) accept.textContent = hasMoves ? `Accept · Beat ${Number(challenge.moves)}` : "Accept challenge";
  }

  function showChallengeLanding(challenge) {
    growth?.event("landing_view", { new_device: _isNewDevice ? 1 : 0, referral_kind: "challenge" }, "challenge");
    growth?.event("referral_landing", { referral_kind: "challenge" }, "challenge");
    if (!challenge || state.mode !== "menu") return;
    dismissChallengeLanding(false);
    const overlay = document.createElement("div");
    overlay.className = "challenge-invite-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "challenge-invite-title");

    const card = document.createElement("section");
    card.className = "challenge-invite-card";
    const eyebrow = document.createElement("div");
    eyebrow.className = "challenge-invite-eyebrow";
    eyebrow.textContent = "PIVOT CHALLENGE";
    const title = document.createElement("h2");
    title.id = "challenge-invite-title";
    title.dataset.challengeTitle = "";
    const score = document.createElement("p");
    score.className = "challenge-invite-score";
    score.dataset.challengeScore = "";
    const edition = document.createElement("p");
    edition.className = "challenge-invite-edition";
    edition.dataset.challengeEdition = "";
    const actions = document.createElement("div");
    actions.className = "challenge-invite-actions";
    const later = document.createElement("button");
    later.type = "button";
    later.className = "challenge-invite-later";
    later.textContent = "Maybe later";
    later.addEventListener("click", () => {
      logUiEvent("challenge_decline", { challenge_resolved: state.challengeResolved ? 1 : 0 });
      dismissChallengeLanding(true);
      canvas?.focus();
    });
    const accept = document.createElement("button");
    accept.type = "button";
    accept.className = "challenge-invite-accept";
    accept.dataset.challengeAccept = "";
    // Wrapped: passing the handler directly made the click event the truthy skipDemo arg.
    accept.addEventListener("click", () => acceptChallengeFromLanding());
    actions.append(later, accept);
    card.append(eyebrow, title, score, edition, actions);
    overlay.appendChild(card);
    // aria-modal alone doesn't contain focus: keep Tab inside the card and let Escape decline.
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        later.click();
      } else if (e.key === "Tab") {
        if (e.shiftKey && document.activeElement === later) { e.preventDefault(); accept.focus(); }
        else if (!e.shiftKey && document.activeElement === accept) { e.preventDefault(); later.focus(); }
      }
      e.stopPropagation();
    });
    document.body.appendChild(overlay);
    _challengeLandingEl = overlay;
    updateChallengeLanding(challenge);
    setTimeout(() => accept.focus(), 0);
    logUiEvent("challenge_landing_view", {
      challenge_resolved: state.challengeResolved ? 1 : 0,
      challenge_id: challenge.id ? 1 : 0,
    });
  }

  function buildChallengeCompareUrl(challengerName, challengeeName, dayNum, challengerMoves = null, challengeeMoves = null) {
    const safeChallenger = String(challengerName || "Friend").trim().slice(0, 32) || "Friend";
    const safeChallengee = String(challengeeName || "You").trim().slice(0, 32) || "You";
    const safeDay = Number.isFinite(dayNum) && dayNum > 0 ? Math.floor(dayNum) : getDayNumber();
    let url = `${CANONICAL_URL}/challenge/#${encodeURIComponent(safeChallenger)}/${encodeURIComponent(safeChallengee)}/${safeDay}`;
    if (Number.isFinite(challengerMoves) || Number.isFinite(challengeeMoves)) {
      const left = Number.isFinite(challengerMoves) ? String(Math.max(0, Math.floor(challengerMoves))) : "";
      const right = Number.isFinite(challengeeMoves) ? String(Math.max(0, Math.floor(challengeeMoves))) : "";
      url += `/${left}/${right}`;
    }
    return url;
  }

  function getChallengeOutcomeLabel(outcome) {
    if (outcome === "won") return "won";
    if (outcome === "lost") return "lost";
    if (outcome === "tied") return "tied";
    return "got a result";
  }

  function loadSeenChallengeEvents() {
    try {
      const raw = safeGetItem(CHALLENGE_FEED_SEEN_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v) => typeof v === "string" && v).slice(0, 80);
    } catch {
      return [];
    }
  }

  function markChallengeEventSeen(eventId) {
    if (!eventId) return;
    const seen = loadSeenChallengeEvents();
    if (seen.includes(eventId)) return;
    const next = [eventId, ...seen].slice(0, 80);
    safeSetItem(CHALLENGE_FEED_SEEN_KEY, JSON.stringify(next));
  }

  // Challenge context parsed from the URL, preserved across the daily-reset
  // null-out so startArchive (past-edition links) can still form a rivalry.
  let _pendingArchiveChallenge = null;

  let _challengeBannerEl = null;
  let _challengeBannerHideTimer = null;
  function dismissChallengeResultBanner() {
    if (_challengeBannerHideTimer) {
      clearTimeout(_challengeBannerHideTimer);
      _challengeBannerHideTimer = null;
    }
    if (_challengeBannerEl && _challengeBannerEl.parentNode) {
      _challengeBannerEl.parentNode.removeChild(_challengeBannerEl);
    }
    _challengeBannerEl = null;
  }

  function showChallengeResultBanner(eventRow) {
    if (!eventRow || !eventRow.id) return;
    markChallengeEventSeen(eventRow.id);
    dismissChallengeResultBanner();

    const challengerOutcome = getChallengeOutcomeLabel(eventRow.outcome);
    const challengeeName = String(eventRow.challengee_name || "A friend").trim().slice(0, 32) || "A friend";
    const dayNum = Number.isFinite(Number(eventRow.day_num)) ? Number(eventRow.day_num) : getDayNumber();
    const compareUrl = eventRow.compare_url || "";

    const bar = document.createElement("div");
    bar.style.cssText = [
      "position:fixed",
      "left:50%",
      "top:14px",
      "transform:translateX(-50%)",
      "width:min(640px, calc(100vw - 18px))",
      `background:${C.bg}`,
      `border:1px solid ${C.panelStroke}`,
      `box-shadow:0 6px 18px ${_currentTheme === "dark" ? "rgba(0,0,0,0.3)" : "rgba(27,27,27,0.11)"}`,
      "padding:9px 10px",
      "z-index:10020",
      "display:flex",
      "align-items:center",
      "justify-content:space-between",
      "gap:10px",
      "font:13px Georgia,'Times New Roman',serif",
      `color:${C.ink}`,
    ].join(";");
    const msgSpan = document.createElement("span");
    msgSpan.style.lineHeight = "1.35";
    msgSpan.textContent = `${challengeeName} played Edition No. ${dayNum}. You ${challengerOutcome}.`;
    const actionsSpan = document.createElement("span");
    actionsSpan.style.cssText = "display:flex;align-items:center;gap:8px";
    if (compareUrl) {
      const viewBtn = document.createElement("button");
      viewBtn.type = "button";
      viewBtn.id = "pivot-challenge-view";
      viewBtn.style.cssText = `border:1px solid ${C.btnPrimaryBg};background:${C.btnPrimaryBg};color:${C.btnPrimaryText};padding:5px 10px;font:12px Georgia,serif;cursor:pointer`;
      viewBtn.textContent = "View";
      actionsSpan.appendChild(viewBtn);
    }
    const closeEl = document.createElement("button");
    closeEl.type = "button";
    closeEl.id = "pivot-challenge-close";
    closeEl.style.cssText = `border:1px solid ${C.btnStroke};background:${C.btnBg};color:${C.muted};padding:5px 9px;font:12px Georgia,serif;cursor:pointer`;
    closeEl.textContent = "Dismiss";
    actionsSpan.appendChild(closeEl);
    bar.appendChild(msgSpan);
    bar.appendChild(actionsSpan);

    document.body.appendChild(bar);
    _challengeBannerEl = bar;
    const closeBtn = document.getElementById("pivot-challenge-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", dismissChallengeResultBanner);
    }
    const viewBtn = document.getElementById("pivot-challenge-view");
    if (viewBtn) {
      viewBtn.addEventListener("click", () => {
        dismissChallengeResultBanner();
        window.location.href = compareUrl;
      });
    }
    _challengeBannerHideTimer = setTimeout(() => {
      dismissChallengeResultBanner();
    }, 12000);
  }

  let _challengeFeedInFlight = false;
  function maybePollChallengeFeed(force = false) {
    if (!SUPABASE_URL || state.customMode) return;
    const now = Date.now();
    if (!force && now < state.challengeFeedNextPoll) return;
    if (_challengeFeedInFlight) return;
    const deviceId = getDeviceId();
    if (!deviceId) return;

    _challengeFeedInFlight = true;
    state.challengeFeedNextPoll = now + CHALLENGE_FEED_POLL_MS;
    fetch(
      `${SUPABASE_URL}/functions/v1/pivot-challenge-feed?device_id=${encodeURIComponent(deviceId)}&limit=4`,
      { cache: "no-store" }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (!payload || !Array.isArray(payload.events)) return;
        const seen = new Set(loadSeenChallengeEvents());
        const unseen = payload.events.filter((eventRow) => eventRow && eventRow.id && !seen.has(eventRow.id));
        if (!unseen.length) return;
        const newest = unseen[0];
        showChallengeResultBanner(newest);
      })
      .catch(() => {})
      .finally(() => {
        _challengeFeedInFlight = false;
      });
  }

  function getChallengeUrl() {
    const name = state.playerName || loadName() || "Player";
    const day = state.dailyKey ? getDayNumberForKey(state.dailyKey) : getDayNumber();
    const pid = getPlayerId();
    const tail = pid ? `&p=${encodeURIComponent(pid)}` : "";
    return `${CANONICAL_URL}/invite/?challenge=${encodeURIComponent(name)}&d=${day}${tail}`;
  }

  function getChallengeShareUrl() {
    if (state.submissionReceiptId) {
      // Keep player-facing invites on our own domain. Sharing the edge-function
      // URL caused some iOS destinations to turn the invite into a
      // host-derived `supabase.txt` attachment instead of a tappable link.
      return `${CANONICAL_URL}/invite/?cid=${encodeURIComponent(state.submissionReceiptId)}`;
    }
    return getChallengeUrl();
  }

  // Set when shareChallenge needs the user to submit before sharing \u2014
  // checked in the name-overlay submit handlers so the share resumes
  // automatically after the score lands.
  let _pendingActionAfterSubmit = "";

  const _nativeShareRequests = new Map();
  let _nativeShareSequence = 0;
  function postNativeShare({ text, url = "", image = "", title = "PIVOT", eventName = "", eventKey = "", eventData = {} }) {
    if (!(PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeShare)) return false;
    _nativeShareSequence += 1;
    const requestId = `share_${Date.now().toString(36)}_${_nativeShareSequence}`;
    _nativeShareRequests.set(requestId, { eventName, eventKey, eventData, interaction: growth?.shareIntent(eventName.includes("challenge") ? "challenge" : "result") });
    const body = { requestId, title, text };
    if (url) body.url = url;
    if (image) body.image = image;
    window.webkit.messageHandlers.nativeShare.postMessage(body);
    setShareToast("Sharing...", 30000);
    setTimeout(() => {
      if (!_nativeShareRequests.has(requestId)) return;
      growth?.shareOutcome(_nativeShareRequests.get(requestId)?.interaction, "unknown");
      _nativeShareRequests.delete(requestId);
      setShareToast("");
    }, 30000);
    return true;
  }

  window.handlePivotNativeShareResult = (payload) => {
    const requestId = payload && typeof payload.requestId === "string" ? payload.requestId : "";
    const request = requestId ? _nativeShareRequests.get(requestId) : null;
    if (requestId) _nativeShareRequests.delete(requestId);
    if (!request) return;
    growth?.shareOutcome(request.interaction, payload?.completed ? "completed" : "canceled");
    if (payload && payload.completed) {
      if (request.eventName) {
        logEvent(request.eventName, {
          ...request.eventData,
          activity_type: typeof payload.activityType === "string" ? payload.activityType.slice(0, 80) : "",
        }, request.eventKey || undefined);
      }
      setShareToast("Shared!");
      return;
    }
    setShareToast("");
  };

  async function shareChallenge() {
    // The rivalry pipeline requires the challenger's score to exist in
    // pivot_scores so the recipient's eventual submit can find it for
    // comparison. Before 2026-06-01 we shared the URL without submitting,
    // which left rivalry rows uncreated despite challenges being sent.
    const eligibleToSubmit =
      !state.submitted &&
      state.leaderboardEligible &&
      !state.hintUsed &&
      !state.practiceMode &&
      Number.isFinite(state.moves) && state.moves > 0;
    if (eligibleToSubmit) {
      _pendingActionAfterSubmit = "share_challenge";
      openNameOverlay();
      setShareToast("Post your score to send a verified challenge.", 2500);
      return;
    }
    if (!state.submitted || !state.submissionReceiptId) {
      setShareToast("Share your result, or post an eligible score to challenge a friend.", 3500);
      return;
    }
    const day = state.dailyKey ? getDayNumberForKey(state.dailyKey) : getDayNumber();
    const name = state.playerName || loadName() || "Player";
    const text = `I solved PIVOT Edition No. ${day} in ${state.moves} moves \u2014 can you beat that?`;
    const shareUrl = getChallengeShareUrl();
    if (postNativeShare({
      title: `${name} challenged you to PIVOT`,
      text: `${text}\n${shareUrl}`,
      url: shareUrl,
      eventName: "challenge_share_native",
      eventKey: `${state.dailyKey}_challenge_native`,
      eventData: getLandingTelemetryData(),
    })) {
      return;
    }
    const interaction = growth?.shareIntent("challenge");
    if (navigator.share) {
      try {
        setShareToast("Sharing...", 5000);
        await navigator.share({ text, url: shareUrl });
        growth?.shareOutcome(interaction, "completed");
        setShareToast("Shared!");
        logEvent("challenge_share_native", {
          ...getLandingTelemetryData(),
        }, `${state.dailyKey}_challenge_native`);
        return;
      } catch (err) {
        if (err.name === "AbortError") { growth?.shareOutcome(interaction, "canceled"); setShareToast(""); return; }
      }
    }
    setShareToast("Copying...", 5000);
    const clipText = `${text}\n${shareUrl}`;
    const ok = await copyTextToClipboard(clipText);
    growth?.shareOutcome(interaction, ok ? "copied" : "failed");
    if (ok) {
      setShareToast("Challenge link copied!");
      logEvent("challenge_share_copy", {
        ...getLandingTelemetryData(),
      }, `${state.dailyKey}_challenge_copy`);
    } else {
      setShareToast("Copy this link.", 2400);
      openCopyOverlay(clipText, "Challenge link");
    }
  }

  const BRAND_LOCKUP = Object.freeze({
    iconSize: 26,
    gap: 12,
    fontSize: 15,
    letterSpacing: 3,
    get color() { return C.ink; },
  });

  // Preload logo images (light + dark)
  const _logoLight = new Image();
  _logoLight.src = resolveAssetUrl("images/logo-light.png");
  const _logoDark = new Image();
  _logoDark.src = resolveAssetUrl("images/logo-dark.png");

  function drawBrandIcon(targetCtx, x, y, size) {
    targetCtx.save();
    targetCtx.strokeStyle = C.ink;
    targetCtx.lineWidth = Math.max(1, size * 0.06);
    targetCtx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
    targetCtx.fillStyle = C.ink;
    targetCtx.textAlign = "center";
    targetCtx.textBaseline = "alphabetic";
    targetCtx.font = `${Math.round(size * 0.58)}px ${_fontUI}`;
    // Center the P visually: measure cap height via ascent, then position
    const m = targetCtx.measureText("P");
    const capHeight = m.actualBoundingBoxAscent || size * 0.38;
    targetCtx.fillText("P", x + size / 2, y + size / 2 + capHeight / 2);
    targetCtx.restore();
  }

  function drawBrandLockup(targetCtx, x, y, opts = {}) {
    const iconSize = opts.iconSize || BRAND_LOCKUP.iconSize;
    const align = opts.align || "left";
    const logoImg = _currentTheme === "light" ? _logoLight : _logoDark;

    if (logoImg.complete && logoImg.naturalWidth > 0) {
      // Scale logo so its height matches iconSize
      const scale = iconSize / logoImg.naturalHeight;
      const drawW = logoImg.naturalWidth * scale;
      const drawH = iconSize;
      const startX = align === "center" ? x - drawW / 2 : x;
      targetCtx.drawImage(logoImg, startX, y, drawW, drawH);
    } else {
      // Fallback to text while images load
      const gap = opts.gap ?? BRAND_LOCKUP.gap;
      const fontSize = opts.fontSize || BRAND_LOCKUP.fontSize;
      const letterSpacing = opts.letterSpacing ?? BRAND_LOCKUP.letterSpacing;
      const color = opts.color || BRAND_LOCKUP.color;

      targetCtx.save();
      targetCtx.font = `${fontSize}px ${_fontUI}`;
      targetCtx.textAlign = "left";
      targetCtx.textBaseline = "middle";
      if (typeof targetCtx.letterSpacing !== "undefined") targetCtx.letterSpacing = `${letterSpacing}px`;
      const textW = targetCtx.measureText("PIVOT").width;
      const totalW = iconSize + gap + textW;
      const startX = align === "center" ? x - totalW / 2 : x;
      drawBrandIcon(targetCtx, startX, y, iconSize);
      targetCtx.fillStyle = color;
      targetCtx.fillText("PIVOT", startX + iconSize + gap, y + iconSize / 2);
      targetCtx.restore();
    }
  }

  function drawBrandHeader(cx, y) {
    drawBrandLockup(ctx, cx, y, { align: "center" });
    // Decorative rule: ── ○ ──
    const ruleY = y + BRAND_LOCKUP.iconSize + 10;
    const ruleW = 70;
    const dotR = 1.8;
    const dotGap = 6;
    ctx.save();
    ctx.strokeStyle = C.mutedFaint;
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(cx - ruleW / 2, ruleY);
    ctx.lineTo(cx - dotGap - dotR, ruleY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + dotGap + dotR, ruleY);
    ctx.lineTo(cx + ruleW / 2, ruleY);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, ruleY, dotR, 0, Math.PI * 2);
    ctx.fillStyle = C.mutedFaint;
    ctx.fill();
    ctx.restore();
  }

  const supabase =
    window.supabase && SUPABASE_URL && SUPABASE_ANON_KEY
      ? window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
      : null;

  let _overlayOpenedAt = 0; // timestamp to guard against pointerdown→click race
  let _overlayFocusReturnStack = [];

  function captureOverlayFocus() {
    // The release notice is intentionally secondary to anything the player
    // explicitly opens. Remove it before a modal appears so the UI never feels
    // stacked or promotional.
    document.getElementById("updates-banner")?.remove();
    const active = document.activeElement;
    if (active && active.isConnected && typeof active.focus === "function") {
      _overlayFocusReturnStack.push(active);
    } else {
      _overlayFocusReturnStack.push(null);
    }
  }

  function restoreOverlayFocus() {
    const target = _overlayFocusReturnStack.pop();
    if (!target || !target.isConnected || typeof target.focus !== "function") return;
    const focusTarget = () => {
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        try { target.focus(); } catch {}
      }
    };
    focusTarget();
    // Result-screen canvas actions are hidden while an overlay is open and only
    // reappear on the next paint, so focus must wait for that paint or it is
    // silently dropped to <body>.
    if (target.hidden) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        // Only if focus is still nowhere useful: <body>, or the closing sheet.
        const active = document.activeElement;
        const stranded = !active || active === document.body ||
          (active.closest?.(".name-overlay") && !active.closest(".name-overlay.show"));
        if (target.isConnected && !target.hidden && stranded) focusTarget();
      }));
    }
  }

  // Shared keyboard contract for the HTML overlay shells: focus moves into the
  // open sheet, Tab stays inside it, and Escape closes it.
  const OVERLAY_FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getTopOpenOverlay() {
    // The copy fallback opens over other sheets (style.css raises it); the rest
    // stack in DOM order.
    const copy = document.querySelector("#copy-overlay.show");
    if (copy) return copy;
    const open = document.querySelectorAll(".name-overlay.show");
    return open.length ? open[open.length - 1] : null;
  }

  function getOverlaySheet(overlay) {
    return overlay?.querySelector(".name-card, .tutorial-card") || null;
  }

  function focusOverlaySheet(overlay) {
    const sheet = getOverlaySheet(overlay);
    if (!sheet || overlay.contains(document.activeElement)) return;
    if (!sheet.hasAttribute("tabindex")) sheet.setAttribute("tabindex", "-1");
    try { sheet.focus({ preventScroll: true }); } catch { try { sheet.focus(); } catch {} }
  }

  function trapOverlayTab(event) {
    const overlay = getTopOpenOverlay();
    if (!overlay) return false;
    const items = [...overlay.querySelectorAll(OVERLAY_FOCUSABLE)]
      .filter((el) => el.getClientRects().length && !el.closest("[hidden]"));
    const active = document.activeElement;
    if (!items.length) {
      event.preventDefault();
      focusOverlaySheet(overlay);
      return true;
    }
    const first = items[0];
    const last = items[items.length - 1];
    let next = null;
    if (!overlay.contains(active)) next = event.shiftKey ? last : first;
    else if (event.shiftKey && (active === first || active === getOverlaySheet(overlay))) next = last;
    else if (!event.shiftKey && active === last) next = first;
    if (!next) return false;
    event.preventDefault();
    next.focus();
    return true;
  }

  if (typeof MutationObserver === "function") {
    // Every open path adds .show; open handlers that focus a field run first.
    const overlayShowObserver = new MutationObserver((records) => {
      for (const record of records) {
        const overlay = record.target;
        if (overlay.classList.contains("show") && !String(record.oldValue || "").split(/\s+/).includes("show")) {
          setTimeout(() => { if (overlay.classList.contains("show")) focusOverlaySheet(overlay); }, 0);
        }
      }
    });
    document.querySelectorAll(".name-overlay").forEach((overlay) => {
      overlayShowObserver.observe(overlay, { attributes: true, attributeFilter: ["class"], attributeOldValue: true });
    });
  }

  function hasOpenOverlayForKeyboard() {
    if (growth?.blocked() || document.getElementById("growth-reminder")) return true;
    // Include dynamically created social dialogs, not just the original shells.
    // The shells are covered by .show; their aria-hidden lags through the fade-out.
    if (document.querySelector('.name-overlay.show, [role="dialog"][aria-modal="true"]:not([aria-hidden="true"]):not(.name-overlay)')) return true;
    if (nameOverlay && nameOverlay.classList.contains("show")) return true;
    if (archiveOverlay && archiveOverlay.classList.contains("show")) return true;
    if (feedbackOverlay && feedbackOverlay.classList.contains("show")) return true;
    if (a11yOverlay && a11yOverlay.classList.contains("show")) return true;
    if (tutorialOverlay && tutorialOverlay.classList.contains("show")) return true;
    if (copyOverlay && copyOverlay.classList.contains("show")) return true;
    if (statsOverlay && statsOverlay.classList.contains("show")) return true;
    if (moreOverlay && moreOverlay.classList.contains("show")) return true;
    if (updatesOverlay && updatesOverlay.classList.contains("show")) return true;
    if (recoveryOverlay && recoveryOverlay.classList.contains("show")) return true;
    if (customGameOverlayEl && customGameOverlayEl.classList.contains("show")) return true;
    if (_lbOverlay && _lbOverlay.classList.contains("show")) return true;
    if (state.showReadme) return true;
    if (state.showLeaderboardOverlay) return true;
    return false;
  }

  const _keyboardPivotDefault = {
    r: Math.floor(Math.max(0, (GRID_ROWS - 2) / 2)),
    c: Math.floor(Math.max(0, (GRID_COLS - 2) / 2)),
  };
  let _keyboardPivot = { ..._keyboardPivotDefault };

  function _clampKeyboardPivot(pivot = {}) {
    const nextR = Number.isFinite(pivot.r) ? Number(pivot.r) : _keyboardPivotDefault.r;
    const nextC = Number.isFinite(pivot.c) ? Number(pivot.c) : _keyboardPivotDefault.c;
    return {
      r: Math.max(0, Math.min(GRID_ROWS - 2, Math.floor(nextR))),
      c: Math.max(0, Math.min(GRID_COLS - 2, Math.floor(nextC))),
    };
  }

  function _setKeyboardPivot(r, c) {
    _keyboardPivot = _clampKeyboardPivot({ r, c });
    state.hoverPivot = { r: _keyboardPivot.r, c: _keyboardPivot.c };
    announceStatus(`Pivot row ${_keyboardPivot.r + 1}, column ${_keyboardPivot.c + 1}. Enter or Space rotates clockwise.`);
    scheduleRenderLoop();
    return _keyboardPivot;
  }

  function _getKeyboardPivot() {
    const next = _clampKeyboardPivot(_keyboardPivot);
    _keyboardPivot = next;
    state.hoverPivot = { r: next.r, c: next.c };
    return next;
  }

  const nameOverlay = document.getElementById("name-overlay");
  const nameInputEl = document.getElementById("name-input");
  const nameSubmitEl = document.getElementById("name-submit");
  const nameSkipEl = document.getElementById("name-skip");
  const nameErrorEl = document.getElementById("name-error");
  const archiveOverlay = document.getElementById("archive-overlay");
  const archiveDateEl = document.getElementById("archive-date");
  const archiveCalendarEl = document.getElementById("archive-calendar");
  const archivePlayEl = document.getElementById("archive-play");
  const archiveErrorEl = document.getElementById("archive-error");
  const helpButton = document.getElementById("help-button");
  const tutorialOverlay = document.getElementById("tutorial-overlay");
  const tutorialCanvas = document.getElementById("tutorial-canvas");
  const tutorialClose = document.getElementById("tutorial-close");
  const tutorialTextEl = document.getElementById("tutorial-text");
  const tutorialNextBtn = document.getElementById("tutorial-next");
  const tutorialDotsEl = document.getElementById("tutorial-dots");

  const TUTORIAL_STEPS = [
    "Move the word into the gold row.",
    "Tap a dot — it rotates the four tiles around it.",
    "One puzzle a day. Fewest moves wins.",
  ];

  let tutorialStep = 0;

  function updateTutorialStep() {
    if (!tutorialTextEl || !tutorialDotsEl || !tutorialNextBtn) return;
    tutorialTextEl.textContent = TUTORIAL_STEPS[tutorialStep];
    const dots = tutorialDotsEl.querySelectorAll(".tutorial-dot");
    dots.forEach((d, i) => d.classList.toggle("active", i === tutorialStep));
    tutorialNextBtn.textContent = tutorialStep === TUTORIAL_STEPS.length - 1 ? "Got it" : "Next";
  }

  if (tutorialNextBtn) {
    tutorialNextBtn.addEventListener("click", () => {
      if (tutorialStep < TUTORIAL_STEPS.length - 1) {
        tutorialStep++;
        updateTutorialStep();
      } else {
        closeTutorialOverlay();
      }
    });
  }
  const feedbackOverlay = document.getElementById("feedback-overlay");
  const feedbackInputEl = document.getElementById("feedback-input");
  const feedbackDeviceEl = document.getElementById("feedback-device");
  const feedbackSubmitEl = document.getElementById("feedback-submit");
  const feedbackCloseEl = document.getElementById("feedback-close");
  const feedbackStatusEl = document.getElementById("feedback-status");
  const copyOverlay = document.getElementById("copy-overlay");
  const copyTitleEl = document.getElementById("copy-title");
  const copyTextEl = document.getElementById("copy-text");
  const copyNoteEl = document.getElementById("copy-note");
  const copyCloseEl = document.getElementById("copy-close");
  const statsOverlay = document.getElementById("stats-overlay");
  const statsContentEl = document.getElementById("stats-content");
  const statsCloseEl = document.getElementById("stats-close");
  const moreOverlay = document.getElementById("more-overlay");
  const moreContentEl = document.getElementById("more-content");
  const moreCloseEl = document.getElementById("more-close");
  const updatesOverlay = document.getElementById("updates-overlay");
  const updatesListEl = document.getElementById("updates-list");
  const recoveryOverlay = document.getElementById("recovery-restore-overlay");
  const recoveryInputEl = document.getElementById("recovery-input");
  const recoveryStatusEl = document.getElementById("recovery-status");
  const recoverySubmitEl = document.getElementById("recovery-submit");
  const recoveryCancelEl = document.getElementById("recovery-cancel");
  document.documentElement.classList.remove("no-js");
  let _scoreSubmitInFlight = false;
  let _pendingSubmitRetryTimer = null;
  let _activationNudgeTimer = null;
  let _reliabilityTickInFlight = false;

  function setNameSubmitPending(pending) {
    if (nameSubmitEl) {
      nameSubmitEl.disabled = pending;
      nameSubmitEl.textContent = pending ? "Adding to leaderboard..." : "Submit";
      nameSubmitEl.style.opacity = pending ? "0.75" : "";
      nameSubmitEl.style.cursor = pending ? "wait" : "";
    }
    if (nameInputEl) {
      nameInputEl.disabled = pending;
    }
  }

  const tutorialState = {
    grid: null,
    lastStep: 0,
    pivotIndex: 0,
    rafId: 0,
    running: false,
    anim: null,
    activePivot: null,
  };

  // Runs any pending post-submit action queued before the name overlay was
  // opened (e.g. shareChallenge requesting a submit-first flow).
  function runPendingActionAfterSubmit() {
    const action = _pendingActionAfterSubmit;
    _pendingActionAfterSubmit = "";
    if (action === "share_challenge") {
      // Defer slightly so the name overlay close animation can settle and
      // state.submitted is fully reflected.
      setTimeout(() => { shareChallenge().catch(() => {}); }, 60);
    }
  }

  if (nameSubmitEl) {
    nameSubmitEl.addEventListener("click", async () => {
      if (nameInputEl) {
        state.nameInput = nameInputEl.value.trim();
      }
      if (await submitScore()) {
        closeNameOverlay();
        runPendingActionAfterSubmit();
      }
    });
  }

  if (nameSkipEl) {
    nameSkipEl.addEventListener("click", () => {
      const cancelledAction = _pendingActionAfterSubmit;
      _pendingActionAfterSubmit = "";
      closeNameOverlay();
      if (cancelledAction === "share_challenge") {
        setShareToast("Challenge cancelled. Add a name when you’re ready.", 2600);
      }
    });
  }

  const nameCharCount = document.getElementById("name-char-count");
  if (nameInputEl) {
    nameInputEl.addEventListener("keydown", async (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        state.nameInput = nameInputEl.value.trim();
        if (await submitScore()) {
          closeNameOverlay();
          runPendingActionAfterSubmit();
        }
      } else if (nameErrorEl) {
        nameErrorEl.textContent = "";
      }
    });
    nameInputEl.addEventListener("input", () => {
      state.nameInput = nameInputEl.value.trim();
      if (nameCharCount) {
        const len = nameInputEl.value.length;
        nameCharCount.textContent = len >= 12 ? `${len}/16` : "";
        nameCharCount.style.color = len >= 16 ? "#b33" : "";
      }
    });
  }

  if (archivePlayEl) {
    archivePlayEl.addEventListener("click", () => {
      if (archiveDateEl && archiveDateEl.value) {
        haptic("light");
        startArchive(archiveDateEl.value);
      }
    });
  }

  if (archiveOverlay) {
    archiveOverlay.addEventListener("click", (event) => {
      if (event.target === archiveOverlay && Date.now() - _overlayOpenedAt > 400) {
        closeArchiveOverlay();
      }
    });
  }

  if (feedbackSubmitEl) {
    feedbackSubmitEl.addEventListener("click", () => {
      submitFeedback();
    });
  }

  if (feedbackCloseEl) {
    feedbackCloseEl.addEventListener("click", () => {
      closeFeedbackOverlay();
    });
  }

  if (feedbackOverlay) {
    feedbackOverlay.addEventListener("click", (event) => {
      if (event.target === feedbackOverlay) {
        closeFeedbackOverlay();
      }
    });
  }

  if (copyCloseEl) {
    copyCloseEl.addEventListener("click", () => {
      closeCopyOverlay();
    });
  }

  if (copyOverlay) {
    copyOverlay.addEventListener("click", (event) => {
      if (event.target === copyOverlay) {
        closeCopyOverlay();
      }
    });
  }

  if (statsCloseEl) {
    statsCloseEl.addEventListener("click", () => {
      closeStatsOverlay();
    });
  }

  if (statsOverlay) {
    statsOverlay.addEventListener("click", (event) => {
      if (event.target === statsOverlay && Date.now() - _overlayOpenedAt > 400) {
        closeStatsOverlay();
      }
    });
  }

  if (moreCloseEl) {
    moreCloseEl.addEventListener("click", () => { if (Date.now() - _overlayOpenedAt > 400) closeMoreMenu(); });
  }
  if (moreOverlay) {
    moreOverlay.addEventListener("click", (event) => {
      if (event.target === moreOverlay && Date.now() - _overlayOpenedAt > 400) { closeMoreMenu(); }
    });
  }

  const customGameOverlayEl = document.getElementById("custom-game-overlay");
  if (customGameOverlayEl) {
    customGameOverlayEl.addEventListener("click", (event) => {
      if (event.target === customGameOverlayEl) { closeCustomGameOverlay(); }
    });
  }

  function openCopyOverlay(text, title = "Copy") {
    if (!copyOverlay || !copyTextEl) return;
    if (!copyOverlay.classList.contains("show")) captureOverlayFocus();
    if (copyTitleEl) copyTitleEl.textContent = title;
    copyTextEl.value = text || "";
    copyOverlay.classList.add("show");
    copyOverlay.setAttribute("aria-hidden", "false");
    setTimeout(() => {
      try {
        copyTextEl.focus();
        copyTextEl.setSelectionRange(0, copyTextEl.value.length);
      } catch (err) {
        // ignore
      }
    }, 0);
    if (copyNoteEl) {
      copyNoteEl.textContent = isMobileSafari()
        ? "Tip: tap and hold, then choose Copy."
        : "Tip: select all and copy.";
    }
  }

  function closeCopyOverlay() {
    if (!copyOverlay) return;
    const hadFocus = copyOverlay.classList.contains("show");
    fadeOutOverlay(copyOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  if (helpButton) {
    const handleHelp = (event) => {
      event.preventDefault();
      event.stopPropagation();
      logUiEvent("help_open");
      openTutorialOverlay();
    };
    helpButton.addEventListener("click", handleHelp);
  }

  // --- Theme toggle button ---
  const themeToggleBtn = document.getElementById("theme-toggle");
  if (themeToggleBtn) {
    themeToggleBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleTheme();
    });
  }

  const ggBtn = document.getElementById("gg-btn");
  const ggSpotlight = document.getElementById("gg-spotlight");
  if (ggBtn && ggSpotlight) {
    function updateGgBeamOrigin() {
      const rect = ggBtn.getBoundingClientRect();
      const textEl = ggSpotlight.querySelector(".gg-text");
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Button center as % of viewport
      const bx = ((rect.left + rect.width / 2) / vw * 100);
      const by = ((rect.top + rect.height / 2) / vh * 100);
      ggSpotlight.style.setProperty("--gg-x", bx.toFixed(1) + "%");
      ggSpotlight.style.setProperty("--gg-y", by.toFixed(1) + "%");
      // Compute beam target at text position
      const textRect = textEl ? textEl.getBoundingClientRect() : null;
      const tx = textRect ? ((textRect.left + textRect.width / 2) / vw * 100) : 50;
      const ty = textRect ? ((textRect.top + textRect.height / 2) / vh * 100) : 92;
      ggSpotlight.style.setProperty("--gg-tx", tx.toFixed(1) + "%");
      ggSpotlight.style.setProperty("--gg-ty", ty.toFixed(1) + "%");
      // Gradient angle from button toward text
      const dx = (tx - bx);
      const dy = (ty - by);
      const angle = Math.atan2(dy, dx) * 180 / Math.PI - 90;
      ggSpotlight.style.setProperty("--gg-angle", angle.toFixed(1) + "deg");
    }
    let _ggLastMode = null;
    function updateGgVisibility() {
      const anyOverlayOpen = document.querySelector(".name-overlay.show") !== null;
      // Custom games reserve no header above the grid, so the corner button
      // would sit on top of the grid's top-right tile; keep it off there.
      const shouldShow = !anyOverlayOpen && !state.showReadme &&
        !(state.customMode && state.mode === "play") &&
        (state.mode === "menu" || state.mode === "play");
      ggBtn.classList.toggle("visible", shouldShow);
      // Dismiss spotlight on mode change or when hidden
      const modeChanged = _ggLastMode !== null && _ggLastMode !== state.mode;
      _ggLastMode = state.mode;
      if ((!shouldShow || modeChanged) && ggSpotlight.classList.contains("active")) {
        ggSpotlight.classList.remove("active");
        ggSpotlight.style.pointerEvents = "none";
      }
    }
    ggBtn.addEventListener("click", () => {
      updateGgBeamOrigin();
      ggSpotlight.classList.toggle("active");
      if (ggSpotlight.classList.contains("active")) {
        ggSpotlight.style.pointerEvents = "auto";
      }
    });
    ggSpotlight.addEventListener("click", () => {
      ggSpotlight.classList.remove("active");
      ggSpotlight.style.pointerEvents = "none";
    });
    window.addEventListener("resize", () => {
      if (ggSpotlight.classList.contains("active")) updateGgBeamOrigin();
    });
    // Expose for render loop
    window._updateGgVisibility = updateGgVisibility;
  }

  // --- Chat panel wiring ---
  document.getElementById("chat-close")?.addEventListener("click", () => toggleChatPanel());
  document.getElementById("chat-send")?.addEventListener("click", () => {
    const input = document.getElementById("chat-input");
    if (input && input.value.trim()) {
      sendChatMessage(input.value);
      input.value = "";
    }
  });
  document.getElementById("chat-input")?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.target;
      if (input.value.trim()) {
        sendChatMessage(input.value);
        input.value = "";
      }
    }
  });

  // Draggable chat panel on desktop
  (() => {
    const panel = document.getElementById("chat-panel");
    const header = panel?.querySelector(".chat-header");
    if (!panel || !header) return;
    let dragging = false, startX = 0, startY = 0, origX = 0, origY = 0;
    function isMobile() { return window.innerWidth <= 600; }
    header.addEventListener("mousedown", (e) => {
      if (isMobile() || e.target.closest(".chat-close")) return;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const rect = panel.getBoundingClientRect();
      origX = rect.left;
      origY = rect.top;
      e.preventDefault();
    });
    document.addEventListener("mousemove", (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const newX = Math.max(0, Math.min(window.innerWidth - 100, origX + dx));
      const newY = Math.max(0, Math.min(window.innerHeight - 60, origY + dy));
      panel.style.left = newX + "px";
      panel.style.top = newY + "px";
      panel.style.right = "auto";
      panel.style.bottom = "auto";
    });
    document.addEventListener("mouseup", () => { dragging = false; });
  })();

  // --- Text nav wiring (desktop below-canvas navigation) ---
  // Text-nav is now external links (Custom Game, Join, Hall of Fame) — no JS handlers needed

  // Watch overlay show/hide to update button visibility
  {
    const obs = new MutationObserver(() => updateHelpButtonPlacement());
    document.querySelectorAll(".name-overlay").forEach((el) => {
      obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    });
  }

  function updateHelpButtonPlacement() {
    const anyOverlayOpen = document.querySelector(".name-overlay.show") !== null;
    const modalOpen =
      anyOverlayOpen ||
      state.mode === "win" ||
      state.showReadme;
    if (helpButton) {
      if (modalOpen) {
        helpButton.style.display = "none";
        helpButton.classList.remove("help-menu-anchor");
      } else {
        helpButton.style.display = "";
        const useMenuAnchor = state.mode === "menu" && !layout.panelBelow;
        helpButton.classList.toggle("help-menu-anchor", useMenuAnchor);
      }
    }
    const themeBtn = document.getElementById("theme-toggle");
    if (themeBtn) {
      themeBtn.style.display = modalOpen ? "none" : "";
    }
  }

  if (tutorialClose) {
    tutorialClose.addEventListener("click", () => {
      closeTutorialOverlay();
    });
  }

  if (tutorialOverlay) {
    tutorialOverlay.addEventListener("click", (event) => {
      if (Date.now() < state.tutorialIgnoreUntil) {
        return;
      }
      if (event.target === tutorialOverlay) {
        closeTutorialOverlay();
      }
    });
  }

  function getEasternDateParts(date = new Date()) {
    if (window.PivotDaily?.getEasternDateParts) {
      return window.PivotDaily.getEasternDateParts(date);
    }
    // Fallback (should never happen in prod; daily-common.js should load first).
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = formatter.formatToParts(date);
    const map = {};
    parts.forEach((part) => {
      if (part.type !== "literal") map[part.type] = part.value;
    });
    return { year: Number(map.year), month: Number(map.month), day: Number(map.day) };
  }

  function getEasternWeekday() {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
    });
    const value = formatter.format(new Date());
    const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[value] ?? 0;
  }

  function getWeekdayIndexForKey(key) {
    if (window.PivotDaily?.getWeekdayIndexForKey) {
      return window.PivotDaily.getWeekdayIndexForKey(key);
    }
    const [year, month, day] = key.split("-").map((value) => Number(value));
    const dateUTC = new Date(Date.UTC(year, month - 1, day));
    return dateUTC.getUTCDay();
  }

  function getScrambleMovesForKey(key) {
    if (window.PivotDaily?.getScrambleMovesForKey) {
      return window.PivotDaily.getScrambleMovesForKey(key);
    }
    const weekday = key ? getWeekdayIndexForKey(key) : getEasternWeekday();
    const base = [50, 24, 28, 32, 36, 40, 44];
    const bumpStartKey = "2026-02-07";
    const bumped = [62, 30, 34, 38, 42, 46, 50];
    const schedule = key && key >= bumpStartKey ? bumped : base;
    return schedule[weekday] ?? 30;
  }

  function getEasternKey() {
    if (window.PivotDaily?.getEasternKey) {
      return window.PivotDaily.getEasternKey();
    }
    const { year, month, day } = getEasternDateParts();
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  function getMillisUntilNextPuzzle() {
    const { year, month, day } = getEasternDateParts();
    // Build a date string for tomorrow in ET
    const todayUTC = new Date(Date.UTC(year, month - 1, day));
    const tomorrowUTC = new Date(todayUTC.getTime() + 86400000);
    const y = tomorrowUTC.getUTCFullYear();
    const m = tomorrowUTC.getUTCMonth() + 1;
    const d = tomorrowUTC.getUTCDate();
    // Resolve midnight ET tomorrow to a UTC epoch via iterative offset correction
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hourCycle: "h23",
    });
    // Binary-search style: estimate offset by formatting a probe date
    // Start from a rough estimate (midnight ET ≈ 05:00 UTC)
    let guess = Date.UTC(y, m - 1, d, 5); // EST = UTC-5
    for (let i = 0; i < 3; i++) {
      const parts = formatter.formatToParts(new Date(guess));
      const pm = {};
      parts.forEach((p) => { if (p.type !== "literal") pm[p.type] = p.value; });
      const localH = Number(pm.hour);
      const localM = Number(pm.minute);
      const localS = Number(pm.second);
      const diffMs = (localH * 3600 + localM * 60 + localS) * 1000;
      guess -= diffMs;
    }
    return Math.max(0, guess - Date.now());
  }

  function formatCountdown(ms) {
    const totalMin = Math.floor(ms / 60000);
    if (totalMin < 1) return "Next puzzle in: <1m";
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `Next puzzle in: ${h}h ${m}m`;
  }

  function formatCountdownCompact(ms) {
    const totalMin = Math.floor(ms / 60000);
    if (totalMin < 1) return "<1m";
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h <= 0) return `${m}m`;
    return `${h}h ${m}m`;
  }

  function formatRelativeAgeShort(timestampMs) {
    const ts = Number(timestampMs);
    if (!Number.isFinite(ts) || ts <= 0) return "";
    const elapsedMs = Date.now() - ts;
    if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return "just now";
    const totalMin = Math.floor(elapsedMs / 60000);
    if (totalMin < 1) return "just now";
    if (totalMin < 60) return `${totalMin}m ago`;
    const hours = Math.floor(totalMin / 60);
    if (hours < 24) {
      const remMin = totalMin % 60;
      return remMin > 0 ? `${hours}h ${remMin}m ago` : `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  function getArchiveStartKey() {
    return "2026-01-01";
  }

  // Date-only key arithmetic in UTC, so DST never shifts the result.
  function getPreviousDailyKey(key) {
    const parts = String(key || "").split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "";
    const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    dt.setUTCDate(dt.getUTCDate() - 1);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dt.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function hasSeenTutorial() {
    return safeGetItem(TUTORIAL_SEEN_KEY) === "true";
  }

  function markTutorialSeen() {
    safeSetItem(TUTORIAL_SEEN_KEY, "true");
  }

  function safeGetItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSetItem(key, value) {
    try {
      localStorage.setItem(key, value);
      if (key && (key.startsWith(PROGRESS_PREFIX) || key.startsWith(PRACTICE_PROGRESS_PREFIX))) _solvedCountCache = null;
      return true;
    } catch (err) {
      return false;
    }
  }

  function safeRemoveItem(key) {
    try {
      localStorage.removeItem(key);
      if (key && (key.startsWith(PROGRESS_PREFIX) || key.startsWith(PRACTICE_PROGRESS_PREFIX))) _solvedCountCache = null;
      return true;
    } catch (err) {
      return false;
    }
  }

  function normalizeLandingVariant(value) {
    const v = String(value || "").trim().toLowerCase();
    if (v === "social" || v === "b") return "social";
    if (v === "control" || v === "a") return "control";
    return "";
  }

  function pickLandingVariant() {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const forced = params.get("ab") || params.get("variant");
      if (forced) {
        const forcedVariant = normalizeLandingVariant(forced);
        if (forcedVariant) return forcedVariant;
      }
    } catch {}
    const existing = normalizeLandingVariant(safeGetItem(LANDING_VARIANT_KEY));
    if (existing) return existing;
    let pick = Math.random() < 0.5 ? "control" : "social";
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        const bytes = new Uint8Array(1);
        window.crypto.getRandomValues(bytes);
        pick = (bytes[0] & 1) ? "social" : "control";
      }
    } catch {}
    safeSetItem(LANDING_VARIANT_KEY, pick);
    return pick;
  }

  function getLandingVariant() {
    if (state.landingVariant) return state.landingVariant;
    state.landingVariant = pickLandingVariant();
    return state.landingVariant;
  }

  function getLandingTelemetryData() {
    return {
      landing_variant: getLandingVariant(),
      landing_experiment: LANDING_EXPERIMENT_VERSION,
    };
  }


  function hashString(value) {
    if (window.PivotDaily?.hashString) {
      return window.PivotDaily.hashString(value);
    }
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
      hash ^= value.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function mulberry32(seed) {
    if (window.PivotDaily?.mulberry32) {
      return window.PivotDaily.mulberry32(seed);
    }
    let t = seed >>> 0;
    return () => {
      t += 0x6d2b79f5;
      let result = Math.imul(t ^ (t >>> 15), t | 1);
      result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
      return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
    };
  }


  let _serverDailyWord = null; // { word, daily_key } from edge function
  let _pendingMenuStart = false; // true if user tapped start before word arrived
  let _menuStartLoadState = "idle"; // idle | loading | failed
  let _menuStartLoadTimer = null;
  const MENU_START_FAIL_DELAY_MS = 4200;
  let _fetchingDailyWord = false;
  let _nextDailyWordFetchAt = 0;
  // Restore cached server word for today (avoids network round-trip on reload)
  try {
    const cached = JSON.parse(localStorage.getItem("pivot_server_word") || "null");
    if (cached && cached.daily_key === getEasternKey() && /^[A-Z]{4,}$/.test(cached.word)) {
      _serverDailyWord = cached;
    }
  } catch {}

  function getDailyWord() {
    const key = getEasternKey();
    // Server-only: no client word lists to prevent future word spoilers
    if (_serverDailyWord && _serverDailyWord.daily_key === key) {
      return _serverDailyWord.word;
    }
    return null;
  }

  function setMenuStartLoadState(stateValue) {
    if (stateValue === "idle" || stateValue === "loading") {
      if (_menuStartLoadTimer) {
        clearTimeout(_menuStartLoadTimer);
        _menuStartLoadTimer = null;
      }
    }
    _menuStartLoadState = stateValue;
    if (stateValue === "loading" && _pendingMenuStart) {
      _menuStartLoadTimer = setTimeout(() => {
        if (_pendingMenuStart && !getDailyWord()) {
          _menuStartLoadState = "failed";
          setShareToast("Couldn’t load today’s puzzle. Check your connection and retry.", 2600);
        }
      }, MENU_START_FAIL_DELAY_MS);
    }
  }

  function isDailyWordLoadingForMenuStart() {
    return _pendingMenuStart && !getDailyWord();
  }

  function retryMenuStartFetch() {
    _pendingMenuStart = true;
    setMenuStartLoadState("loading");
    _nextDailyWordFetchAt = 0;
    _fetchingDailyWord = false;
    setShareToast("Retrying today’s puzzle…", 1500);
    _fetchDailyWord();
  }

  function loadLeaderboard(key) {
    const storageKey = `${LEADERBOARD_PREFIX}${key}`;
    const raw = safeGetItem(storageKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (err) {
      return [];
    }
    return [];
  }

  function saveLeaderboard(key, entries) {
    const storageKey = `${LEADERBOARD_PREFIX}${key}`;
    safeSetItem(storageKey, JSON.stringify(entries));
  }

  function getProgressStorageKey(key, isPracticeMode = state.practiceMode) {
    if (!key) return "";
    return `${isPracticeMode ? PRACTICE_PROGRESS_PREFIX : PROGRESS_PREFIX}${key}`;
  }

  function clearProgressState(key, isPracticeMode = state.practiceMode) {
    const storageKey = getProgressStorageKey(key, isPracticeMode);
    if (!storageKey) return;
    safeRemoveItem(storageKey);
  }

  function loadProgress(key, isPracticeMode = state.practiceMode) {
    const storageKey = getProgressStorageKey(key, isPracticeMode);
    if (!storageKey) return null;
    const raw = safeGetItem(storageKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.grid)) return null;
      if (!isValidGridString(parsed.grid.join(""))) return null;
      if (!Number.isInteger(parsed.moves) || parsed.moves < 0) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function saveProgress(key, payload, isPracticeMode = state.practiceMode) {
    const storageKey = getProgressStorageKey(key, isPracticeMode);
    if (!storageKey) return;
    const nextPayload = { ...(payload || {}) };
    if (state.submitted && state.submissionReceiptId) {
      nextPayload.submissionId = state.submissionReceiptId;
    }
    if (state.submitted && state.submissionReceiptAt) {
      nextPayload.submittedAt = state.submissionReceiptAt;
    }
    safeSetItem(storageKey, JSON.stringify(nextPayload));
  }

  function getPendingSubmitStorageKey(key) {
    return `${PENDING_SUBMIT_PREFIX}${key}`;
  }

  function loadPendingSubmit(key) {
    if (!key) return null;
    const raw = safeGetItem(getPendingSubmitStorageKey(key));
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const name = sanitizeName(parsed && parsed.name ? parsed.name : "");
      const dailyKey = parsed && parsed.dailyKey ? String(parsed.dailyKey) : "";
      const moves = Number(parsed && parsed.moves);
      const moveHistory = Array.isArray(parsed && parsed.moveHistory) ? parsed.moveHistory : [];
      const startState = parsed && typeof parsed.startState === "string" ? parsed.startState : "";
      if (!name || dailyKey !== key) return null;
      if (!Number.isInteger(moves) || moves < 1 || moves > 300) return null;
      if (moveHistory.length !== moves) return null;
      if (!moveHistory.every((idx) => Number.isInteger(idx) && idx >= 0 && idx < MOVE_MAPS.length)) return null;
      if (!isValidGridString(startState)) return null;
      return {
        name,
        dailyKey,
        moves,
        moveHistory: moveHistory.slice(),
        startState,
        updatedAt: Number(parsed.updatedAt) || 0,
        retries: Number(parsed.retries) || 0,
      };
    } catch {
      return null;
    }
  }

  function savePendingSubmit(payload) {
    if (!payload || !payload.dailyKey) return false;
    return safeSetItem(getPendingSubmitStorageKey(payload.dailyKey), JSON.stringify(payload));
  }

  function clearPendingSubmit(key) {
    if (!key) return;
    safeRemoveItem(getPendingSubmitStorageKey(key));
  }

  function listPendingSubmitEntries(limit = 20) {
    const out = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(PENDING_SUBMIT_PREFIX)) continue;
        const dayKey = key.slice(PENDING_SUBMIT_PREFIX.length);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) continue;
        const pending = loadPendingSubmit(dayKey);
        if (pending) out.push(pending);
      }
    } catch (err) {
      return [];
    }
    out.sort((a, b) => (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0));
    if (limit > 0) return out.slice(0, limit);
    return out;
  }

  function getPendingSubmitSummary() {
    const entries = listPendingSubmitEntries(40);
    const pendingCurrent = loadPendingSubmit(state.dailyKey);
    const current =
      pendingCurrent && pendingSubmitMatchesCurrentRun(pendingCurrent) ? pendingCurrent : null;
    return {
      count: entries.length,
      current,
      latest: entries.length ? entries[0] : null,
    };
  }

  function setReliabilityState(mode, reason = "", ttlMs = RELIABILITY_DEGRADED_TTL_MS) {
    const safeMode = mode === "degraded" ? "degraded" : "healthy";
    const safeReason = String(reason || "").replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, 90);
    const now = Date.now();
    if (safeMode === "healthy") {
      if (state.reliabilityMode !== "healthy") {
        logEvent("reliability_recovered", {
          previous_reason: state.reliabilityReason || "",
          online: navigator.onLine ? 1 : 0,
        });
      }
      state.reliabilityMode = "healthy";
      state.reliabilityReason = "";
      state.reliabilityUntil = 0;
      state.reliabilityChangedAt = now;
      return;
    }
    const wasHealthy = state.reliabilityMode !== "degraded";
    const reasonChanged = state.reliabilityReason !== safeReason;
    state.reliabilityMode = "degraded";
    state.reliabilityReason = safeReason;
    state.reliabilityUntil = Math.max(state.reliabilityUntil || 0, now + Math.max(15000, Number(ttlMs) || 0));
    if (wasHealthy || reasonChanged || (now - (state.reliabilityChangedAt || 0) > 120000)) {
      state.reliabilityChangedAt = now;
      logEvent("reliability_degraded", {
        reason: safeReason || "unknown",
        online: navigator.onLine ? 1 : 0,
      });
    }
  }

  function getReliabilityBadge(pendingCurrent = null) {
    if (state.practiceMode || state.customMode) return null;
    const pending = pendingCurrent || loadPendingSubmit(state.dailyKey);
    const hasPendingCurrent = !!(pending && pendingSubmitMatchesCurrentRun(pending));
    if (!navigator.onLine) {
      if (hasPendingCurrent) {
        return {
          kind: "offline_pending",
          text: "Offline — score queued",
          subtext: "Will sync when connection returns",
        };
      }
      return {
        kind: "offline",
        text: "Offline",
        subtext: "Leaderboard will refresh on reconnect",
      };
    }
    if (hasPendingCurrent) {
      const ageText = formatRelativeAgeShort(pending.updatedAt);
      return {
        kind: "syncing",
        text: "Syncing queued score",
        subtext: ageText ? `Queued ${ageText}` : "Retrying automatically",
      };
    }
    if (state.reliabilityMode === "degraded" && Date.now() < state.reliabilityUntil) {
      return {
        kind: "degraded",
        text: "Live sync delayed",
        subtext: state.reliabilityReason || "Using cached leaderboard data",
      };
    }
    return null;
  }

  function clearSubmissionReceipt() {
    state.submissionReceiptId = "";
    state.submissionReceiptAt = "";
  }

  function normalizeSubmittedAt(value) {
    if (!value) return "";
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return "";
    return date.toISOString();
  }

  function applySubmissionReceiptFromResponse(responseBody) {
    if (!responseBody || typeof responseBody !== "object") return;
    const idRaw = typeof responseBody.submission_id === "string" ? responseBody.submission_id : "";
    const atRaw = typeof responseBody.submitted_at === "string" ? responseBody.submitted_at : "";
    const normalizedAt = normalizeSubmittedAt(atRaw);
    if (idRaw) state.submissionReceiptId = idRaw;
    if (normalizedAt) state.submissionReceiptAt = normalizedAt;
    // Rivalry record (Week 3-4). Server returns this when both sides have
    // played the same daily_key under a challenge attribution. Lives on
    // state.rivalry so the win-screen banner can read it; null otherwise.
    if (responseBody.rivalry && typeof responseBody.rivalry === "object") {
      state.rivalry = responseBody.rivalry;
      try {
        logEvent("rivalry_updated", {
          total: Number(responseBody.rivalry.total) || 0,
          my_wins: Number(responseBody.rivalry.my_wins) || 0,
          opponent_wins: Number(responseBody.rivalry.opponent_wins) || 0,
          i_won_this_one: responseBody.rivalry.i_won_this_one === true ? 1 : 0,
        });
      } catch {}
    }
  }

  function formatSubmissionReceipt() {
    const iso = state.submissionReceiptAt;
    if (!iso) return "";
    const date = new Date(iso);
    if (!Number.isFinite(date.getTime())) return "";
    try {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return iso;
    }
  }

  function recordOnce(eventKey) {
    if (!eventKey) return true;
    const storageKey = `${EVENT_PREFIX}${eventKey}`;
    if (safeGetItem(storageKey)) return false;
    safeSetItem(storageKey, "1");
    return true;
  }

  function detectBrowserFlavor() {
    const ua = navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari\//i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    if (ios && isSafari) return "ios_safari";
    if (ios) return "ios_webview";
    if (/Firefox|FxiOS/i.test(ua)) return "firefox";
    if (/Edg|EdgiOS/i.test(ua)) return "edge";
    if (isSafari) return "safari";
    if (/Chrome|Chromium|CriOS/i.test(ua)) return "chrome";
    return "other";
  }

  const _detectedPlatform = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? "mobile" : "desktop";
  const _detectedClient = PIVOT_NATIVE ? "ios_app" : (navigator.standalone || matchMedia("(display-mode: standalone)").matches ? "pwa" : "web");
  const _isIosBrowser = (function () {
    if (PIVOT_NATIVE) return false;
    const ua = navigator.userAgent || "";
    const isIosUa = /iPhone|iPad|iPod/.test(ua);
    const isIPadOs = /Mac/.test(ua) && navigator.maxTouchPoints > 1;
    if (!isIosUa && !isIPadOs) return false;
    if (navigator.standalone) return false;
    try { if (matchMedia("(display-mode: standalone)").matches) return false; } catch {}
    return true;
  })();
  const IOS_CARD_DISMISS_KEY = "pivot_ios_card_dismissed_until";
  const IOS_CARD_APP_STORE_URL = "https://apps.apple.com/app/id6759224219";
  function shouldShowIosAppCard() {
    if (!_isIosBrowser) return false;
    const until = Number(safeGetItem(IOS_CARD_DISMISS_KEY) || 0);
    return !(until > Date.now());
  }
  function dismissIosAppCard() {
    const until = Date.now() + 30 * 24 * 60 * 60 * 1000;
    safeSetItem(IOS_CARD_DISMISS_KEY, String(until));
  }
  const _detectedBrowser = detectBrowserFlavor();

  // ---------------------------------------------------------------------------
  // Push notifications (opt-in)
  // ---------------------------------------------------------------------------
  const _pushSupported = "serviceWorker" in navigator && "PushManager" in window;
  const _remindersSupported = PIVOT_NATIVE || _pushSupported;
  let _pushSubscribed = false;
  let _reminderPromptEl = null;
  let _promptReminderOnNameOverlayClose = false;
  let _postSolveLeaderboardPromptTimer = null;

  function clearPostSolveLeaderboardPrompt() {
    if (_postSolveLeaderboardPromptTimer === null) return;
    clearTimeout(_postSolveLeaderboardPromptTimer);
    _postSolveLeaderboardPromptTimer = null;
  }

  async function checkPushSubscription() {
    if (!_pushSupported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      _pushSubscribed = !!sub;
    } catch { _pushSubscribed = false; }
  }
  checkPushSubscription();

  function loadNotifyPromptState() {
    const raw = safeGetItem(NOTIFY_PROMPT_KEY);
    if (!raw) return { prompts: 0, lastPromptAt: 0, subscribedAt: 0, dismissedAt: 0 };
    try {
      const parsed = JSON.parse(raw);
      return {
        prompts: Number.isFinite(parsed.prompts) ? Math.max(0, Math.min(20, Math.floor(parsed.prompts))) : 0,
        lastPromptAt: Number.isFinite(parsed.lastPromptAt) ? Math.max(0, Math.floor(parsed.lastPromptAt)) : 0,
        subscribedAt: Number.isFinite(parsed.subscribedAt) ? Math.max(0, Math.floor(parsed.subscribedAt)) : 0,
        dismissedAt: Number.isFinite(parsed.dismissedAt) ? Math.max(0, Math.floor(parsed.dismissedAt)) : 0,
      };
    } catch {
      return { prompts: 0, lastPromptAt: 0, subscribedAt: 0, dismissedAt: 0 };
    }
  }

  function saveNotifyPromptState(next = {}) {
    const prev = loadNotifyPromptState();
    const merged = {
      prompts: Number.isFinite(next.prompts) ? Math.max(0, Math.min(20, Math.floor(next.prompts))) : prev.prompts,
      lastPromptAt: Number.isFinite(next.lastPromptAt) ? Math.max(0, Math.floor(next.lastPromptAt)) : prev.lastPromptAt,
      subscribedAt: Number.isFinite(next.subscribedAt) ? Math.max(0, Math.floor(next.subscribedAt)) : prev.subscribedAt,
      dismissedAt: Number.isFinite(next.dismissedAt) ? Math.max(0, Math.floor(next.dismissedAt)) : prev.dismissedAt,
    };
    safeSetItem(NOTIFY_PROMPT_KEY, JSON.stringify(merged));
    return merged;
  }

  // Scans every localStorage key, so it must not be called per frame.
  // safeSetItem/safeRemoveItem null the cache on any progress write.
  function getSolvedDailyGamesCountCached() {
    if (_solvedCountCache === null) _solvedCountCache = getSolvedDailyGamesCount();
    return _solvedCountCache;
  }

  function getSolvedDailyGamesCount() {
    const solvedKeys = new Set(loadRecoveredHistory().map((row) => row.dailyKey));
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(PROGRESS_PREFIX)) continue;
        const raw = safeGetItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.solvedToday && !parsed.practiceMode && !parsed.redoMode) {
            solvedKeys.add(key.slice(PROGRESS_PREFIX.length));
          }
        } catch {}
      }
    } catch {}
    return solvedKeys.size;
  }

  function isReminderPromptWindow() {
    const hour = new Date().getHours();
    return (hour >= 8 && hour <= 11) || (hour >= 17 && hour <= 21);
  }

  function closeReminderPrompt() {
    if (!_reminderPromptEl) return;
    try { _reminderPromptEl.remove(); } catch {}
    _reminderPromptEl = null;
  }

  function maybePromptReminderOptIn(source = "unknown") {
    if (source !== "post_solve" || hasOpenOverlayForKeyboard() || growth?.busy() || growth?.followupShown()) return;
    if (!_remindersSupported) return;
    if (!PIVOT_NATIVE && _pushSubscribed) return;
    if (!PIVOT_NATIVE && (typeof Notification === "undefined" || Notification.permission !== "default")) return;
    // The time window keeps the ambient landing/heartbeat nag out of odd hours.
    // It must not apply at the win moment: "remind me tomorrow" is a sensible
    // question at any hour, and gating it there silently skipped most solvers.
    if (source !== "post_solve" && !isReminderPromptWindow()) return;
    const solvedCount = getSolvedDailyGamesCount();
    // Protect the first-win reward moment. Ask only after the player has come
    // back and solved a second daily puzzle, when a reminder has earned context.
    if (solvedCount < 2) return;
    const now = Date.now();
    const statePrompt = loadNotifyPromptState();
    if (statePrompt.prompts >= 2) return;
    if (statePrompt.subscribedAt > 0) return;
    if (statePrompt.lastPromptAt > 0 && now - statePrompt.lastPromptAt < 7 * 24 * 60 * 60 * 1000) return;
    if (_reminderPromptEl) return;

    if (growth && !growth.claimFollowup()) return;
    growth?.event("followup_shown", { card: "reminder" });
    const bar = document.createElement("div");
    bar.style.cssText = [
      "position:fixed",
      "left:50%",
      "bottom:18px",
      "transform:translateX(-50%)",
      "width:min(560px, calc(100vw - 24px))",
      `background:${C.bg}`,
      `border:1px solid ${C.panelStroke}`,
      `box-shadow:0 6px 24px ${_currentTheme === "dark" ? "rgba(0,0,0,0.3)" : "rgba(27,27,27,0.12)"}`,
      "padding:10px 12px",
      "z-index:10001",
      "display:flex",
      "align-items:center",
      "justify-content:space-between",
      "gap:10px",
      "font:13px Georgia,'Times New Roman',serif",
      `color:${C.ink}`,
    ].join(";");
    bar.innerHTML =
      '<span style="line-height:1.35">Want a daily reminder?</span>' +
      '<span style="display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end">' +
      `<button type="button" id="pivot-remind-later" style="border:1px solid ${C.btnStroke};background:${C.btnBg};color:${C.muted};padding:6px 10px;font:12px Georgia,serif;cursor:pointer">Not now</button>` +
      `<button type="button" id="pivot-remind-enable" style="border:1px solid ${C.btnPrimaryBg};background:${C.btnPrimaryBg};color:${C.btnPrimaryText};padding:6px 10px;font:12px Georgia,serif;cursor:pointer">Choose a time</button>` +
      "</span>";
    document.body.appendChild(bar);
    _reminderPromptEl = bar;
    saveNotifyPromptState({ prompts: statePrompt.prompts + 1, lastPromptAt: now });
    logEvent("notify_prompt_shown", { source, solved_count: solvedCount });

    // Track whether the user explicitly chose. If they didn't, the timeout
    // fires a separate "timed_out" event — previously timeouts were silent,
    // which made the funnel look like 0 dismissed / 0 enabled.
    let resolved = false;
    const laterBtn = document.getElementById("pivot-remind-later");
    if (laterBtn) {
      laterBtn.addEventListener("click", () => {
        resolved = true;
        saveNotifyPromptState({ dismissedAt: Date.now() });
        logEvent("notify_prompt_dismissed", { source, solved_count: solvedCount });
        closeReminderPrompt();
      });
    }
    const enableBtn = document.getElementById("pivot-remind-enable");
    if (enableBtn) {
      enableBtn.addEventListener("click", async () => {
        resolved = true;
        // Log the tap itself: wanting a reminder and successfully subscribing
        // are different facts, and only this one measures intent.
        logEvent("notify_prompt_accepted", { source, solved_count: solvedCount });
        closeReminderPrompt();
        await openReminderSettings();
      });
    }
    // Doubled the timeout from 12s -> 22s — the bar is meant to be ambient,
    // not a modal, but 12s wasn't long enough for users to read + decide.
    setTimeout(() => {
      if (!resolved) {
        logEvent("notify_prompt_timed_out", { source, solved_count: solvedCount });
      }
      closeReminderPrompt();
    }, 22000);
  }

  window.handlePivotNativeNotificationResult = (payload) => {
    const pending = nativeReminderRequests.get(payload?.requestId);
    if (pending) { nativeReminderRequests.delete(payload.requestId); pending(payload); return; }
    const granted = !!(payload && payload.granted);
    if (granted) {
      saveNotifyPromptState({ subscribedAt: Date.now() });
      logEvent("notify_prompt_enabled", { source: "native_result" });
      setShareToast("Daily reminder enabled.", 2200);
    } else {
      logEvent("notify_prompt_blocked", { source: "native_result" });
      setShareToast("Reminders weren’t enabled. You can change this in Settings.", 3200);
    }
  };

  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  }

  function showDomToast(message, duration = 3000) {
    let el = document.getElementById("pivot-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "pivot-toast";
      const dark = _currentTheme === "dark";
      el.style.cssText = `position:fixed;top:20px;left:50%;transform:translateX(-50%);background:${dark ? "rgba(236,233,225,0.94)" : "rgba(27,27,27,0.92)"};color:${dark ? "#131313" : "#fdf8f1"};padding:10px 22px;border-radius:8px;font:14px Georgia,'Times New Roman',serif;z-index:9999;opacity:0;transition:opacity 0.3s;pointer-events:none;text-align:center;max-width:90vw;`;
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.opacity = "1";
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(() => { el.style.opacity = "0"; }, duration);
  }

  const nativeReminderRequests = new Map();
  function nativeReminder(action, hour = 9) {
    return new Promise((resolve, reject) => {
      const requestId = `schedule_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const timeout = setTimeout(() => { nativeReminderRequests.delete(requestId); reject(new Error("Notification settings timed out. Please try again.")); }, 15000);
      nativeReminderRequests.set(requestId, payload => { clearTimeout(timeout); resolve(payload); });
      window.webkit.messageHandlers.nativeNotification.postMessage({ action, hour, requestId });
    });
  }

  async function openReminderSettings() {
    closeReminderPrompt();
    closeMoreMenu();
    if (document.getElementById("growth-reminder")) return;
    const priorFocus = document.activeElement;
    const overlay = document.createElement("div");
    overlay.id = "growth-reminder"; overlay.className = "growth-overlay";
    overlay.setAttribute("role", "dialog"); overlay.setAttribute("aria-modal", "true"); overlay.setAttribute("aria-labelledby", "reminder-title");
    overlay.innerHTML = '<section class="growth-card"><h2 id="reminder-title">Daily reminder</h2><p id="reminder-status" role="status">Checking notification settings…</p><label id="reminder-time-label" hidden>Time on your device<select id="reminder-time"></select></label><div class="growth-actions"><button id="reminder-save" class="growth-primary" disabled>Enable reminder</button><button id="reminder-off" hidden>Turn off</button><button id="reminder-cancel">Not now</button></div></section>';
    document.body.append(overlay);
    const close = () => { overlay.remove(); priorFocus?.focus?.(); };
    overlay.querySelector('#reminder-cancel').onclick = close;
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {
        const items = [...overlay.querySelectorAll('button:not(:disabled),select')].filter(el => !el.hidden && !el.closest('[hidden]'));
        if (e.shiftKey && document.activeElement === items[0]) { e.preventDefault(); items.at(-1)?.focus(); }
        else if (!e.shiftKey && document.activeElement === items.at(-1)) { e.preventDefault(); items[0]?.focus(); }
      }
      e.stopPropagation();
    });
    overlay.querySelector('#reminder-cancel').focus();
    const status = overlay.querySelector('#reminder-status'), save = overlay.querySelector('#reminder-save'), off = overlay.querySelector('#reminder-off'), select = overlay.querySelector('#reminder-time');
    let supported = false, enabled = false, hour = Number(safeGetItem('pivot_reminder_hour_v2') || 9);
    let reg = null, existing = null;
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    try {
      if (PIVOT_NATIVE) {
        supported = Number(window.PIVOT_REMINDER_VERSION) >= 2;
        if (supported) { const current = await nativeReminder('status'); enabled = current.granted; hour = current.hour; }
      } else {
        if (!_pushSupported) throw new Error('Notifications are not available in this browser.');
        const response = await fetch(SUPABASE_URL + '/functions/v1/pivot-push-subscribe', { signal: AbortSignal.timeout(8000) });
        if (response.ok) supported = (await response.json()).schedule_version >= 2;
        reg = await Promise.race([navigator.serviceWorker.ready, new Promise((_, reject) => setTimeout(() => reject(new Error('Notification service is not ready. Try again.')), 8000))]);
        existing = await reg.pushManager.getSubscription(); enabled = !!existing;
      }
      if (!overlay.isConnected) return;
      for (let h = 0; h < 24; h++) {
        const option = document.createElement('option'); option.value = String(h); option.textContent = `${h % 12 || 12}:00 ${h < 12 ? 'AM' : 'PM'}`; select.append(option);
      }
      select.value = String(Number.isInteger(hour) && hour >= 0 && hour < 24 ? hour : 9);
      overlay.querySelector('#reminder-time-label').hidden = !supported;
      status.textContent = supported ? `Choose an hour in ${zone}. Delivery occurs during that hour. Web reminders use this timezone until you update it.` : PIVOT_NATIVE ? 'This app version supports a daily reminder at 9:00 AM local time.' : 'This version supports the existing daily delivery schedule. Time selection is not available yet.';
      save.textContent = enabled ? 'Save reminder' : 'Enable reminder'; save.disabled = false;
      off.hidden = !enabled || (PIVOT_NATIVE && !supported);
    } catch (err) { status.textContent = err.message || 'Could not load settings. Please try again.'; return; }
    const change = async disable => {
      save.disabled = true; off.disabled = true; status.textContent = 'Saving…';
      try {
        const selectedHour = Number(select.value || 9);
        if (PIVOT_NATIVE) {
          const result = await nativeReminder(disable ? 'disable' : 'setup', supported ? selectedHour : 9);
          if (!disable && !result.granted) throw new Error('Reminders were not enabled. Check notification permissions in Settings.');
        } else if (disable) {
          const response = await fetch(SUPABASE_URL + '/functions/v1/pivot-push-subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: existing.endpoint, unsubscribe: true }), signal: AbortSignal.timeout(10000) });
          if (!response.ok) throw new Error('Could not turn off reminders. Try again.');
          if (!await existing.unsubscribe()) throw new Error('Server reminders are off; browser cleanup failed. Try again.');
          _pushSubscribed = false; logEvent('push_unsubscribe');
        } else {
          if (Notification.permission === 'denied') throw new Error('Notifications are blocked. Enable them in browser settings first.');
          if (await Notification.requestPermission() !== 'granted') throw new Error('Notification permission was not granted.');
          const sub = existing || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
          const keys = sub.toJSON().keys || {};
          const response = await fetch(SUPABASE_URL + '/functions/v1/pivot-push-subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint, ...keys, device_id: getDeviceId(), ...(supported ? { timezone: zone, preferred_hour_local: selectedHour } : {}) }), signal: AbortSignal.timeout(10000) });
          if (!response.ok) throw new Error('Could not save the reminder. Your selection is ready to retry.');
          const confirmed = await response.json();
          if (supported && (confirmed.preferred_hour_local !== selectedHour || confirmed.timezone !== zone)) throw new Error('The schedule was not confirmed. Please try again.');
          existing = sub; _pushSubscribed = true; logEvent('push_subscribe');
        }
        safeSetItem('pivot_reminder_hour_v2', String(selectedHour));
        saveNotifyPromptState({ subscribedAt: disable ? 0 : Date.now() });
        growth?.event('reminder_schedule_result', { success: true, action: disable ? 'disable' : 'setup', hour: selectedHour });
        if (!disable) logEvent('notify_prompt_enabled', { source: 'settings' });
        setShareToast(disable ? 'Reminders turned off.' : supported ? `Reminder saved for ${select.selectedOptions[0].textContent}.` : 'Daily reminder enabled.', 3000);
        close();
      } catch (err) {
        growth?.event('reminder_schedule_result', { success: false });
        status.textContent = err.message || 'Could not save. Please try again.';
      } finally { save.disabled = false; off.disabled = false; }
    };
    save.onclick = () => change(false); off.onclick = () => change(true);
  }

  async function togglePushSubscription() { return openReminderSettings(); }

  const _eventQueue = [];
  let _eventFlushTimer = null;
  let _eventFlushInFlight = false;

  function scheduleTelemetryFlush(delayMs = 1200) {
    if (_eventFlushTimer) return;
    _eventFlushTimer = setTimeout(() => {
      _eventFlushTimer = null;
      flushTelemetryQueue();
    }, Math.max(0, delayMs));
  }

  async function flushTelemetryQueue() {
    if (!supabase || _eventFlushInFlight) return;
    if (!_eventQueue.length) return;
    _eventFlushInFlight = true;
    const batch = _eventQueue.slice(0, 20);
    try {
      // supabase-js RESOLVES with { error } on a PostgREST rejection rather than
      // throwing, so the catch below never saw those and the batch was spliced
      // away anyway — losing up to 20 events per failed flush despite the
      // comment promising a retry. Only drop the batch on confirmed success.
      const { error } = await supabase.from("pivot_events").insert(batch);
      if (!error) {
        // The bounded queue may have evicted part of this batch while awaiting
        // the request. Remove only events that were actually sent.
        for (const payload of batch) {
          const index = _eventQueue.indexOf(payload);
          if (index >= 0) _eventQueue.splice(index, 1);
        }
      }
    } catch {
      // Keep queue for retry; telemetry is best-effort.
    } finally {
      _eventFlushInFlight = false;
      if (_eventQueue.length) {
        scheduleTelemetryFlush(1800);
      }
    }
  }

  const _eventKnownCols = new Set(["event","daily_key","device_id","session_id","mode","moves","archive_key","duration_ms","referrer","platform","browser","client"]);
  function queueTelemetryEvent(raw) {
    const payload = {};
    const extra = {};
    for (const [k, v] of Object.entries(raw)) {
      if (_eventKnownCols.has(k)) { payload[k] = v; }
      else if (k !== "id" && k !== "created_at") { extra[k] = v; }
    }
    if (Object.keys(extra).length) payload.extra = extra;
    _eventQueue.push(payload);
    if (_eventQueue.length > 120) {
      _eventQueue.splice(0, _eventQueue.length - 120);
    }
    if (_eventQueue.length >= 10) {
      scheduleTelemetryFlush(0);
    } else {
      scheduleTelemetryFlush(1200);
    }
  }

  function flushTelemetryNow() {
    if (_eventFlushTimer) {
      clearTimeout(_eventFlushTimer);
      _eventFlushTimer = null;
    }
    flushTelemetryQueue();
  }

  function buildVisitContextReferrer() {
    let tz = "";
    let locale = "";
    let lang = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {}
    try {
      locale = Intl.DateTimeFormat().resolvedOptions().locale || "";
    } catch {}
    lang = (navigator.languages && navigator.languages[0]) || navigator.language || locale || "";
    const regionMatch = String(locale || lang).match(/[-_]([A-Za-z]{2})\b/);
    const region = regionMatch ? regionMatch[1].toUpperCase() : "";
    const offsetMinutes = -new Date().getTimezoneOffset();
    const absMinutes = Math.abs(offsetMinutes);
    const offHours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const offMinutes = String(absMinutes % 60).padStart(2, "0");
    const offset = `${offsetMinutes >= 0 ? "+" : "-"}${offHours}:${offMinutes}`;
    try {
      const url = new URL("https://ctx.pivotthegame.com/");
      if (tz) url.searchParams.set("tz", tz.slice(0, 64));
      if (locale) url.searchParams.set("locale", locale.slice(0, 20));
      if (lang) url.searchParams.set("lang", lang.slice(0, 20));
      if (region) url.searchParams.set("region", region.slice(0, 2));
      url.searchParams.set("tz_offset", offset);
      return url.toString().slice(0, 220);
    } catch {
      return "";
    }
  }

  function buildModerationReferrer(action, details = {}) {
    try {
      const url = new URL("https://mod.pivotthegame.com/");
      const safeAction = String(action || "").replace(/[^a-z0-9_-]/gi, "_").slice(0, 40);
      if (safeAction) url.searchParams.set("action", safeAction);
      const room = String(details.room || state.customRoomCode || "").replace(/[^A-Z0-9]/gi, "").slice(0, 8);
      if (room) url.searchParams.set("room", room.toUpperCase());
      const actor = sanitizeName(details.actor || state.customPlayerName || "").slice(0, 24);
      if (actor) url.searchParams.set("actor", actor);
      const target = sanitizeName(details.target || "").slice(0, 24);
      if (target) url.searchParams.set("target", target);
      const reason = String(details.reason || "").replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, 64);
      if (reason) url.searchParams.set("reason", reason);
      if (Number.isFinite(Number(details.count))) {
        url.searchParams.set("count", String(Math.max(0, Math.min(999, Number(details.count)))));
      }
      return url.toString().slice(0, 220);
    } catch {
      return "";
    }
  }

  function buildSubmitReferrer(outcome, details = {}) {
    try {
      const url = new URL("https://submit.pivotthegame.com/");
      const safeOutcome = String(outcome || "").replace(/[^a-z]/gi, "").slice(0, 16) || "unknown";
      url.searchParams.set("outcome", safeOutcome);
      const safeReason = String(details.reason || "").replace(/[^a-z0-9_-]/gi, "_").slice(0, 40);
      if (safeReason) {
        url.searchParams.set("reason", safeReason);
      }
      if (Number.isFinite(Number(details.status))) {
        url.searchParams.set("status", String(Math.max(0, Math.min(999, Number(details.status)))));
      }
      if (Number.isFinite(Number(details.ms))) {
        url.searchParams.set("ms", String(Math.max(0, Math.min(120000, Math.round(Number(details.ms))))));
      }
      if (Number.isFinite(Number(details.attempts))) {
        url.searchParams.set("attempts", String(Math.max(1, Math.min(9, Math.round(Number(details.attempts))))));
      }
      if (Number.isFinite(Number(details.timedOut))) {
        url.searchParams.set("timed_out", Number(details.timedOut) ? "1" : "0");
      }
      if (Number.isFinite(Number(details.queued))) {
        url.searchParams.set("queued", Number(details.queued) ? "1" : "0");
      }
      return url.toString().slice(0, 220);
    } catch {
      return "";
    }
  }

  function buildPushReferrer(type, token) {
    try {
      const url = new URL("https://push.pivotthegame.com/");
      const safeType = String(type || "").replace(/[^a-z0-9_-]/gi, "_").slice(0, 40) || "unknown";
      const safeToken = String(token || "").replace(/[^a-z0-9_-]/gi, "").slice(0, 80);
      url.searchParams.set("type", safeType);
      if (safeToken) url.searchParams.set("token", safeToken);
      return url.toString().slice(0, 220);
    } catch {
      return "";
    }
  }

  function logEvent(eventName, data = {}, onceKey = "") {
    if (!supabase) return;
    if (onceKey && !recordOnce(onceKey)) return;
    const payload = {
      event: eventName,
      daily_key: state.dailyKey || getEasternKey(),
      device_id: getDeviceId(),
      session_id: state.sessionId,
      mode: state.practiceMode ? "practice" : "daily",
      moves: Number.isFinite(state.moves) ? state.moves : null,
      platform: _detectedPlatform,
      client: _detectedClient,
      browser: _detectedBrowser,
      ...data,
    };
    queueTelemetryEvent(payload);
  }

  // Log an event that is immediately followed by a navigation.
  //
  // The normal path debounces 1200ms and flushes via supabase-js, whose fetch
  // the browser cancels the moment the page unloads — so anything logged right
  // before window.location.assign() is largely lost. `keepalive` fetches are
  // guaranteed to outlive the page, so click-then-navigate events are counted.
  // Sends ONLY the keepalive request — deliberately not also logEvent(), or a
  // cancelled navigation would leave two rows for one click and inflate the
  // very number this exists to measure.
  function logEventBeforeNavigation(eventName, data = {}) {
    if (!supabase || !SUPABASE_URL || !SUPABASE_ANON_KEY) return;
    const base = {
      event: eventName,
      daily_key: state.dailyKey || getEasternKey(),
      device_id: getDeviceId(),
      session_id: state.sessionId,
      mode: state.practiceMode ? "practice" : "daily",
      platform: _detectedPlatform,
      client: _detectedClient,
      browser: _detectedBrowser,
    };
    // Unknown keys must land in `extra`, exactly as queueTelemetryEvent does —
    // posting them as top-level columns would fail the insert outright.
    const payload = { ...base };
    const extra = {};
    for (const [k, v] of Object.entries(data)) {
      if (_eventKnownCols.has(k)) payload[k] = v;
      else if (k !== "id" && k !== "created_at") extra[k] = v;
    }
    if (Object.keys(extra).length) payload.extra = extra;
    try {
      fetch(`${SUPABASE_URL}/rest/v1/pivot_events`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify([payload]),
      }).catch(() => {});
    } catch {}
  }

  function logUiEvent(eventName, data = {}, onceKeySuffix = "") {
    const key = getEasternKey();
    const suffix = onceKeySuffix ? `_${onceKeySuffix}` : "";
    logEvent(eventName, data, `${key}_${state.sessionId}${suffix}_${eventName}`);
  }

  function logSessionEnd(reason) {
    const now = Date.now();
    const durationMs = Math.max(0, now - state.sessionStart);
    logEvent(
      "session_end",
      { duration_ms: durationMs, reason },
      `${state.dailyKey || getEasternKey()}_${state.sessionId}_session_end`
    );
    // Bounce detection — user left from the landing page
    if (state.mode === "menu") {
      logEvent("bounce", { duration_ms: durationMs }, `${state.dailyKey || getEasternKey()}_${state.sessionId}_bounce`);
    }
  }

  const _runtimeAnomalySeenAt = new Map();
  function logRuntimeAnomaly(kind, details = {}, minIntervalMs = 120000) {
    const safeKind = String(kind || "").replace(/[^a-z0-9_-]/gi, "_").slice(0, 48) || "unknown";
    const normalized = {};
    for (const [key, value] of Object.entries(details || {})) {
      const safeKey = String(key || "").replace(/[^a-z0-9_]/gi, "_").slice(0, 32);
      if (!safeKey) continue;
      if (typeof value === "string") {
        normalized[safeKey] = value.replace(/[\u0000-\u001f\u007f]/g, " ").slice(0, 140);
      } else if (typeof value === "boolean") {
        normalized[safeKey] = value ? 1 : 0;
      } else if (Number.isFinite(Number(value))) {
        normalized[safeKey] = Math.round(Number(value) * 1000) / 1000;
      }
    }
    const signature = `${safeKind}|${normalized.message || normalized.reason || normalized.source || ""}`;
    const now = Date.now();
    const prev = _runtimeAnomalySeenAt.get(signature) || 0;
    if (now - prev < Math.max(10000, Number(minIntervalMs) || 0)) return;
    _runtimeAnomalySeenAt.set(signature, now);
    logEvent("runtime_anomaly", {
      kind: safeKind,
      online: navigator.onLine ? 1 : 0,
      ...normalized,
    });
  }

  function logLandingVariantSeen() {
    const day = state.dailyKey || getEasternKey();
    logEvent(
      "landing_variant_seen",
      getLandingTelemetryData(),
      `${day}_${state.sessionId}_landing_variant_seen`
    );
  }

  function handlePushAttributionFromUrl() {
    let url;
    try {
      url = new URL(window.location.href);
    } catch {
      return;
    }
    const pushType = String(url.searchParams.get("push_type") || "").replace(/[^a-z0-9_-]/gi, "_").slice(0, 40);
    const pushToken = String(url.searchParams.get("push_token") || "").replace(/[^a-z0-9_-]/gi, "").slice(0, 80);
    if (!pushType || !pushToken) return;
    logEvent("push_attributed_open", {
      referrer: buildPushReferrer(pushType, pushToken),
      push_type: pushType,
    }, `${pushToken}_push_attributed_open`);
    url.searchParams.delete("push_type");
    url.searchParams.delete("push_token");
    const nextSearch = url.searchParams.toString();
    const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash || ""}`;
    try {
      window.history.replaceState({}, document.title, nextUrl);
    } catch {}
  }

  function loadName() {
    const raw = safeGetItem(NAME_PREFIX);
    return raw ? raw : "";
  }

  function saveName(name) {
    if (!name) return;
    safeSetItem(NAME_PREFIX, name);
  }

  function isDayKey(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
      Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
  }

  function loadStreakState() {
    const raw = safeGetItem(STREAK_PREFIX);
    if (!raw) return { count: 0, lastKey: "", graceUsedOn: "" };
    try {
      const parsed = JSON.parse(raw);
      const count = Number.isFinite(parsed.count) ? Math.max(0, Math.floor(parsed.count)) : 0;
      const lastKey = isDayKey(parsed.lastKey) ? parsed.lastKey : "";
      const graceUsedOn = isDayKey(parsed.graceUsedOn) ? parsed.graceUsedOn : "";
      return { count, lastKey, graceUsedOn };
    } catch (err) {
      return { count: 0, lastKey: "", graceUsedOn: "" };
    }
  }

  function saveStreakState(count, lastKey, meta = {}) {
    const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    const safeLastKey = isDayKey(lastKey) ? lastKey : "";
    const safeGraceUsedOn = isDayKey(meta && meta.graceUsedOn) ? meta.graceUsedOn : "";
    safeSetItem(STREAK_PREFIX, JSON.stringify({
      count: safeCount,
      lastKey: safeLastKey,
      graceUsedOn: safeGraceUsedOn,
    }));
  }

  function daysBetweenKeys(fromKey, toKey) {
    if (!isDayKey(fromKey) || !isDayKey(toKey)) return null;
    const [fromY, fromM, fromD] = fromKey.split("-").map((value) => Number(value));
    const [toY, toM, toD] = toKey.split("-").map((value) => Number(value));
    const fromUtc = Date.UTC(fromY, fromM - 1, fromD);
    const toUtc = Date.UTC(toY, toM - 1, toD);
    if (!Number.isFinite(fromUtc) || !Number.isFinite(toUtc)) return null;
    return Math.floor((toUtc - fromUtc) / (24 * 60 * 60 * 1000));
  }

  function canUseStreakGrace(streak, todayKey) {
    if (!streak || !isDayKey(todayKey)) return false;
    if (!isDayKey(streak.lastKey)) return false;
    const gap = daysBetweenKeys(streak.lastKey, todayKey);
    const allowedGap = streak.count >= LONG_STREAK_GRACE_THRESHOLD ? 3 : 2;
    if (!Number.isFinite(gap) || gap < 2 || gap > allowedGap) return false;
    if (!isDayKey(streak.graceUsedOn)) return true;
    const sinceLastGrace = daysBetweenKeys(streak.graceUsedOn, todayKey);
    return Number.isFinite(sinceLastGrace) && sinceLastGrace >= STREAK_GRACE_DAYS;
  }

  function getStreakGraceStatus(streak = loadStreakState(), todayKey = getEasternKey()) {
    if (!isDayKey(streak && streak.graceUsedOn)) {
      return { available: true, daysUntil: 0 };
    }
    const since = daysBetweenKeys(streak.graceUsedOn, todayKey);
    if (!Number.isFinite(since) || since >= STREAK_GRACE_DAYS) {
      return { available: true, daysUntil: 0 };
    }
    return { available: false, daysUntil: STREAK_GRACE_DAYS - since };
  }

  function addDaysToKey(key, delta) {
    const [year, month, day] = key.split("-").map((value) => Number(value));
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + delta);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function getStreakGraceAllowanceLabel(streak = loadStreakState()) {
    return streak && streak.count >= LONG_STREAK_GRACE_THRESHOLD
      ? "Up to 2 missed days every 7 days"
      : "1 miss every 7 days";
  }

  // --- Streak milestones ---------------------------------------------------

  function loadCelebratedMilestones() {
    try {
      const raw = safeGetItem(STREAK_MILESTONES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((n) => STREAK_MILESTONES.includes(Number(n))).map(Number);
    } catch {
      return [];
    }
  }

  function saveCelebratedMilestone(milestone) {
    const list = loadCelebratedMilestones();
    if (!list.includes(milestone)) {
      list.push(milestone);
      safeSetItem(STREAK_MILESTONES_KEY, JSON.stringify(list));
    }
  }

  function getCurrentMilestoneTier(streakCount) {
    // Highest milestone the player has reached or passed.
    let tier = 0;
    for (const m of STREAK_MILESTONES) {
      if (streakCount >= m) tier = m;
    }
    return tier; // 0 if streak < 7
  }

  function getMilestoneCrossedToday(prevCount, newCount) {
    // Returns the milestone that was JUST crossed with this solve, or 0.
    // We only celebrate once per milestone per device (via localStorage).
    if (newCount <= prevCount) return 0;
    const celebrated = new Set(loadCelebratedMilestones());
    for (const m of STREAK_MILESTONES) {
      if (newCount >= m && prevCount < m && !celebrated.has(m)) {
        return m;
      }
    }
    return 0;
  }

  function getStreakRiskBannerState(todayKey = getEasternKey()) {
    if (!isDayKey(todayKey)) return null;
    const streak = loadStreakState();
    // Threshold lowered from 3 -> 2 on 2026-05-29 so the banner reaches more
    // mid-streak players. CTR was 89% on a tiny denominator; this should grow
    // the denominator without changing the message.
    if (!streak || !Number.isFinite(streak.count) || streak.count < 2) return null;
    if (!isDayKey(streak.lastKey) || streak.lastKey === todayKey) return null;

    const millisLeft = getMillisUntilNextPuzzle();
    if (!Number.isFinite(millisLeft) || millisLeft > STREAK_RISK_BANNER_WINDOW_MS) return null;

    const gap = daysBetweenKeys(streak.lastKey, todayKey);
    if (!Number.isFinite(gap) || gap < 1) return null;

    const graceReady = getStreakGraceStatus(streak, todayKey).available;
    const longGrace = streak.count >= LONG_STREAK_GRACE_THRESHOLD;
    const maxGap = longGrace ? 3 : 2;
    const hoursLeft = Math.max(0, Math.ceil(millisLeft / (60 * 60 * 1000)));

    if (gap === 1) {
      return graceReady
        ? {
            label: `Shield ready. Solve tonight or use it tomorrow to protect your ${streak.count}-day streak.`,
            tone: "shield_ready",
            streakCount: streak.count,
            gap,
            hoursLeft,
          }
        : {
            label: `${streak.count}-day streak ends tonight. Solve before midnight ET.`,
            tone: "urgent",
            streakCount: streak.count,
            gap,
            hoursLeft,
          };
    }

    if (graceReady && gap <= maxGap) {
      return {
        label: gap === maxGap
          ? `Final shield day. Solve today to save your ${streak.count}-day streak.`
          : `Shield active. Solve today to save your ${streak.count}-day streak.`,
        tone: gap === maxGap ? "shield_final" : "shield_active",
        streakCount: streak.count,
        gap,
        hoursLeft,
      };
    }

    return null;
  }

  let _isNewDevice = false;
  function getDeviceId() {
    const existing = safeGetItem(DEVICE_PREFIX);
    if (existing) return existing;
    _isNewDevice = true;
    const id =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `device_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    safeSetItem(DEVICE_PREFIX, id);
    return id;
  }

  // --- Lightweight player identity (Week 2) -------------------------------
  // The server-minted player_id binds across devices via a paste-able
  // recovery code. Optional — players without one keep working device-keyed.
  const PLAYER_REGISTER_URL = `${SUPABASE_URL}/functions/v1/pivot-player-register`;
  const PLAYER_RESTORE_URL = `${SUPABASE_URL}/functions/v1/pivot-player-restore`;

  function getPlayerId() {
    const v = safeGetItem(PLAYER_ID_KEY);
    return typeof v === "string" && v ? v : "";
  }
  function getRecoveryCode() {
    const v = safeGetItem(RECOVERY_CODE_KEY);
    return typeof v === "string" && v ? v : "";
  }
  function setPlayerIdentity(playerId, recoveryCode) {
    if (playerId) safeSetItem(PLAYER_ID_KEY, String(playerId));
    if (recoveryCode) safeSetItem(RECOVERY_CODE_KEY, String(recoveryCode));
  }

  function loadRecoveredHistory() {
    const raw = safeGetItem(RECOVERED_HISTORY_KEY);
    if (!raw) return [];
    try {
      const rows = JSON.parse(raw);
      if (!Array.isArray(rows)) return [];
      const byKey = new Map();
      for (const row of rows) {
        const dailyKey = row && typeof row.dailyKey === "string" ? row.dailyKey : "";
        const moves = Number(row && row.moves);
        if (!isDayKey(dailyKey) || !Number.isInteger(moves) || moves < 1 || moves > 300) continue;
        if (!byKey.has(dailyKey)) byKey.set(dailyKey, { dailyKey, moves });
      }
      return [...byKey.values()].sort((a, b) => a.dailyKey.localeCompare(b.dailyKey)).slice(-730);
    } catch {
      return [];
    }
  }

  function mergeRecoveredHistory(rows) {
    const byKey = new Map(loadRecoveredHistory().map((row) => [row.dailyKey, row]));
    for (const row of Array.isArray(rows) ? rows : []) {
      const dailyKey = row && typeof row.daily_key === "string" ? row.daily_key : "";
      const moves = Number(row && row.moves);
      if (!isDayKey(dailyKey) || !Number.isInteger(moves) || moves < 1 || moves > 300) continue;
      if (!byKey.has(dailyKey)) byKey.set(dailyKey, { dailyKey, moves });
    }
    const merged = [...byKey.values()].sort((a, b) => a.dailyKey.localeCompare(b.dailyKey)).slice(-730);
    safeSetItem(RECOVERED_HISTORY_KEY, JSON.stringify(merged));
    _solvedCountCache = null;
    return merged;
  }

  let _playerRegisterInFlight = null;
  async function ensurePlayerRegistered() {
    // Once we have a player_id we're registered. We no longer re-register just
    // to recover a missing code: pivot-player-register only returns the code at
    // first mint (a device_id is not a credential), so re-calling can't recover it.
    if (getPlayerId()) return getPlayerId();
    if (_playerRegisterInFlight) return _playerRegisterInFlight;
    _playerRegisterInFlight = (async () => {
      try {
        const res = await fetch(PLAYER_REGISTER_URL, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ device_id: getDeviceId() }),
        });
        if (!res.ok) return "";
        const data = await res.json();
        if (data && data.player_id) {
          // recovery_code is present only on a fresh mint (existed:false);
          // setPlayerIdentity stores the id always, the code only when given.
          setPlayerIdentity(data.player_id, data.recovery_code);
          try {
            logEvent("player_registered", {
              existed: data.existed ? 1 : 0,
            });
          } catch {}
          return String(data.player_id);
        }
      } catch (err) {
        // Network failure; non-fatal. Try again on next solve.
      } finally {
        _playerRegisterInFlight = null;
      }
      return "";
    })();
    return _playerRegisterInFlight;
  }

  async function restorePlayerFromRecoveryCode(rawCode) {
    const code = String(rawCode || "").trim().toLowerCase();
    if (!code) return { ok: false, reason: "empty" };
    try {
      const res = await fetch(PLAYER_RESTORE_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ recovery_code: code, device_id: getDeviceId() }),
      });
      if (res.status === 404) return { ok: false, reason: "not_found" };
      if (!res.ok) return { ok: false, reason: `http_${res.status}` };
      const data = await res.json();
      if (data && data.player_id) {
        setPlayerIdentity(data.player_id, code);
        const recoveredHistory = mergeRecoveredHistory(data.history);
        try {
          logEvent("player_restored", {
            had_display_name: data.display_name ? 1 : 0,
            today_completed: data.today_completed ? 1 : 0,
          });
        } catch {}
        return {
          ok: true,
          player_id: data.player_id,
          display_name: data.display_name || null,
          today_completed: Boolean(data.today_completed),
          today_score: data.today_score || null,
          recovered_count: recoveredHistory.length,
        };
      }
      return { ok: false, reason: "no_data" };
    } catch (err) {
      return { ok: false, reason: "network" };
    }
  }

  async function fetchCompletion(key) {
    if (!supabase) return null;
    try {
      const deviceId = getDeviceId();
      const { data, error } = await supabase.rpc("pivot_has_completion", {
        p_daily_key: key,
        p_device_id: deviceId,
      });
      if (error) throw error;
      return Boolean(data);
    } catch (err) {
      return null;
    }
  }

  async function submitCompletion(key, moves, practice) {
    if (!supabase) return;
    const deviceId = getDeviceId();
    await supabase.rpc("pivot_record_completion", {
      p_daily_key: key,
      p_device_id: deviceId,
      p_moves: moves,
      p_mode: practice ? "practice" : "daily",
    });
  }

  function withTimeout(promise, ms, label = "request") {
    let timeoutId = null;
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`${label}_timeout`)), Math.max(1, ms));
      }),
    ]).finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    });
  }

  function looksLikeJwtToken(token) {
    if (typeof token !== "string") return false;
    const trimmed = token.trim();
    if (!trimmed) return false;
    return trimmed.split(".").length === 3;
  }

  function buildPublicSupabaseHeaders(extraHeaders = {}) {
    const headers = { ...extraHeaders };
    if (!SUPABASE_ANON_KEY) return headers;
    headers.apikey = SUPABASE_ANON_KEY;
    // Publishable keys are not JWTs; avoid sending them as Bearer tokens.
    if (looksLikeJwtToken(SUPABASE_ANON_KEY)) {
      headers.Authorization = `Bearer ${SUPABASE_ANON_KEY}`;
    }
    return headers;
  }

  function isSyntheticLeaderboardName(rawName) {
    if (typeof rawName !== "string") return false;
    const name = rawName.trim().toLowerCase();
    if (!name) return false;
    // Monitors post as "__canary__" / "__submit_..."; a real player named
    // Canary must still appear, so match the reserved prefix, not the word.
    return name.startsWith("__");
  }

  function sanitizeLeaderboardRows(rows) {
    if (!Array.isArray(rows)) return [];
    const out = [];
    for (const row of rows) {
      if (!row || typeof row !== "object") continue;
      if (isSyntheticLeaderboardName(row.name)) continue;
      const name = sanitizeName(String(row.name || ""));
      const moves = Number(row.moves);
      if (!name) continue;
      if (!Number.isFinite(moves)) continue;
      out.push({
        name,
        moves: Math.max(0, Math.min(999, Math.floor(moves))),
        created_at: typeof row.created_at === "string" ? row.created_at : null,
      });
      if (out.length >= 300) break;
    }
    return out;
  }

  function getCachedLeaderboardRows(key, limit = 50) {
    const cap = Math.max(1, Math.min(300, Math.floor(Number(limit) || 50)));
    const cached = loadLeaderboard(key);
    if (!Array.isArray(cached) || !cached.length) return [];
    const rows = [];
    for (const row of cached) {
      if (!row || typeof row !== "object") continue;
      const name = sanitizeName(String(row.name || ""));
      const moves = Number(row.moves);
      if (!name || !Number.isFinite(moves)) continue;
      if (isSyntheticLeaderboardName(name)) continue;
      let createdAt = null;
      if (typeof row.created_at === "string") {
        createdAt = row.created_at;
      } else {
        const cachedTime = Number(row.time);
        if (Number.isFinite(cachedTime) && cachedTime > 0) {
          try {
            createdAt = new Date(cachedTime).toISOString();
          } catch {
            createdAt = null;
          }
        }
      }
      rows.push({
        name,
        moves: Math.max(0, Math.min(999, Math.floor(moves))),
        created_at: createdAt,
      });
      if (rows.length >= cap) break;
    }
    return rows;
  }

  function getLeaderboardSnapshotUrls(key) {
    const safeKey = encodeURIComponent(String(key || ""));
    const origin = window.location && window.location.origin ? window.location.origin : CANONICAL_URL;
    return [
      `${origin}/data/leaderboard-${safeKey}.json`,
      `${origin}/data/leaderboard-latest.json`,
    ];
  }

  function parseLeaderboardSnapshotPayload(body, key, options = {}) {
    if (!body) return null;
    if (Array.isArray(body)) return sanitizeLeaderboardRows(body);
    if (typeof body !== "object") return null;
    const payloadKey = String(body.daily_key || body.dailyKey || "").trim();
    if (payloadKey && key && payloadKey !== key) {
      const requestAllowsStaleFutureKey = options.allowStaleForFutureKey === true;
      // If the snapshot is stale for a future request (e.g. tonight before the
      // first score arrives), using it is still better than a hard failure.
      if (requestAllowsStaleFutureKey && payloadKey < key) {
        // Keep entries from the latest known day to avoid a blank overlay.
      } else {
        return null;
      }
    }
    const rows = Array.isArray(body.entries)
      ? body.entries
      : Array.isArray(body.leaderboard)
      ? body.leaderboard
      : null;
    if (!rows) return null;
    return sanitizeLeaderboardRows(rows);
  }

  async function fetchLeaderboardViaSnapshot(key, limit = 50) {
    const cap = Math.max(1, Math.min(300, Math.floor(Number(limit) || 50)));
    const urls = getLeaderboardSnapshotUrls(key);
    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      try {
        const res = await withTimeout(
          fetch(url, { cache: "no-store" }),
          LEADERBOARD_TIMEOUT_MS,
          "leaderboard_snapshot"
        );
        if (!res.ok) continue;
        const body = await res.json();
        const isLatestFallback = i > 0;
        const rows = parseLeaderboardSnapshotPayload(body, key, { allowStaleForFutureKey: isLatestFallback });
        if (!rows) continue;
        return rows.slice(0, cap);
      } catch {
        // Try the next snapshot source.
      }
    }
    return null;
  }

  async function fetchLeaderboardViaRest(key, limit = 50) {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
    const cap = Math.max(1, Math.min(300, Math.floor(Number(limit) || 50)));
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    try {
      const params = new URLSearchParams();
      params.set("select", "name,moves,created_at");
      params.set("daily_key", `eq.${key}`);
      // Exclude leaderboard-hidden rows (rivalry-only archive challenge plays).
      params.set("eligible", "not.is.false");
      params.set("order", "moves.asc,created_at.asc");
      params.set("limit", String(cap));
      const res = await withTimeout(
        fetch(`${SUPABASE_URL}/rest/v1/pivot_scores?${params.toString()}`, {
          headers: buildPublicSupabaseHeaders(),
          signal: controller ? controller.signal : undefined,
        }),
        LEADERBOARD_TIMEOUT_MS,
        "leaderboard_rest"
      );
      if (!res.ok) return null;
      const body = await res.json();
      return sanitizeLeaderboardRows(body);
    } catch {
      if (controller) controller.abort();
      return null;
    }
  }

  async function fetchLeaderboard(key, limit = 50) {
    function markSource(source, details = {}) {
      const safeSource = String(source || "").replace(/[^a-z0-9_]/gi, "_").slice(0, 32) || "unknown";
      logEvent("leaderboard_fetch_source", {
        source: safeSource,
        ...details,
      }, `${key}_${state.sessionId}_leaderboard_source_${safeSource}`);
    }
    state.leaderboardLoading = true;
    state.leaderboardError = "";
    const cap = Math.max(1, Math.min(300, Math.floor(Number(limit) || 50)));
    try {
      // 1) Live query path
      if (!supabase) {
        const rows = await fetchLeaderboardViaRest(key, cap);
        if (rows) {
          markSource("live_rest_no_sdk");
          setReliabilityState("healthy");
          clearLeaderboardAutoRetry();
          return rows;
        }
      } else {
        try {
          const query = supabase
            .from("pivot_scores")
            .select("name,moves,created_at")
            .eq("daily_key", key)
            .not("eligible", "is", false)
            .order("moves", { ascending: true })
            .order("created_at", { ascending: true })
            .limit(cap);
          const { data, error } = await withTimeout(query, LEADERBOARD_TIMEOUT_MS, "leaderboard_query");
          if (!error) {
            markSource("live_realtime");
            setReliabilityState("healthy");
            clearLeaderboardAutoRetry();
            return sanitizeLeaderboardRows(data || []);
          }
        } catch {
          // Continue to live REST fallback.
        }
        const restRows = await fetchLeaderboardViaRest(key, cap);
        if (restRows) {
          markSource("live_rest_fallback");
          setReliabilityState("healthy");
          clearLeaderboardAutoRetry();
          return restRows;
        }
      }

      // 2) Static snapshot fallback
      const cachedRows = getCachedLeaderboardRows(key, cap);
      const snapshotRows = await fetchLeaderboardViaSnapshot(key, cap);
      if (Array.isArray(snapshotRows) && snapshotRows.length > 0) {
        markSource("snapshot", { rows: snapshotRows.length });
        setReliabilityState("degraded", "Using snapshot leaderboard data");
        state.leaderboardError = "Live sync delayed";
        scheduleLeaderboardAutoRetry();
        return snapshotRows;
      }

      // 3) Local cache fallback
      if (cachedRows.length) {
        markSource("local_cache", { rows: cachedRows.length });
        setReliabilityState("degraded", "Using cached leaderboard data");
        state.leaderboardError = "Live sync delayed";
        scheduleLeaderboardAutoRetry();
        return cachedRows;
      }
      if (Array.isArray(snapshotRows)) {
        markSource("snapshot_empty");
        // Legitimate no-entry snapshot (or stale empty snapshot with no local cache).
        setReliabilityState("degraded", "Snapshot available but live query failed");
        state.leaderboardError = "Live sync delayed";
        scheduleLeaderboardAutoRetry();
        return snapshotRows;
      }
      markSource("failed");

      try {
        logEvent("leaderboard_fetch_failed", {
          referrer: buildSubmitReferrer("leaderboard_fetch_failed", {
            reason: "all_sources_failed",
          }),
        });
      } catch {}
      setReliabilityState("degraded", "Leaderboard fetch failed");
      state.leaderboardError = "Live sync delayed";
      scheduleLeaderboardAutoRetry();
      return null;
    } finally {
      state.leaderboardLoading = false;
    }
  }

  async function refreshYesterdayChampion(dayKey) {
    if (!dayKey) {
      state.yesterdayTopFirstName = "";
      state.yesterdayChampionKey = "";
      return;
    }
    const yesterdayKey = addDaysToKey(dayKey, -1);
    if (state.yesterdayChampionKey === yesterdayKey) return;
    state.yesterdayChampionKey = yesterdayKey;
    try {
      const rows = await fetchLeaderboard(yesterdayKey, 1);
      if (rows && rows.length) {
        state.yesterdayTopFirstName = sanitizeName(rows[0].name);
      } else {
        state.yesterdayTopFirstName = "";
      }
    } catch {
      state.yesterdayTopFirstName = "";
    }
  }

  function clearLeaderboardAutoRetry() {
    if (!_leaderboardAutoRetryTimer) return;
    clearTimeout(_leaderboardAutoRetryTimer);
    _leaderboardAutoRetryTimer = null;
  }

  function scheduleLeaderboardAutoRetry(delayMs = LEADERBOARD_AUTO_RETRY_MS) {
    if (_leaderboardAutoRetryTimer) return;
    if (!state.dailyKey) return;
    if (state.customMode || state.practiceMode) return;
    _leaderboardAutoRetryTimer = setTimeout(async () => {
      _leaderboardAutoRetryTimer = null;
      if (state.customMode || state.practiceMode || !state.dailyKey) return;
      const limit = state.showLeaderboardOverlay ? 200 : 50;
      const ok = await retryLeaderboardFetch(limit);
      if (!ok) {
        scheduleLeaderboardAutoRetry(Math.min(60000, Math.max(10000, delayMs * 1.5)));
      } else {
        state.leaderboardError = "";
        render();
      }
    }, Math.max(5000, delayMs));
  }

  function leaderboardChanged(oldList, newList) {
    if (oldList.length !== newList.length) return true;
    for (let i = 0; i < oldList.length; i++) {
      if (oldList[i].name !== newList[i].name || oldList[i].moves !== newList[i].moves) return true;
    }
    return false;
  }

  async function retryLeaderboardFetch(limit = 50) {
    if (!state.dailyKey) return false;
    const entries = await fetchLeaderboard(state.dailyKey, limit);
    if (!entries) return false;
    const normalized = entries.map((row) => ({
      name: row.name,
      moves: row.moves,
      time: row.created_at ? Date.parse(row.created_at) : 0,
    }));
    const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
    if (!leaderboardChanged(state.leaderboard, filtered)) {
      state.leaderboardError = "";
      clearLeaderboardAutoRetry();
      return true;
    }
    state.leaderboard = filtered;
    saveLeaderboard(state.dailyKey, filtered);
    state.leaderboardError = "";
    clearLeaderboardAutoRetry();
    return true;
  }

  async function fetchDailyMetrics(key) {
    if (!supabase) return;
    try {
      if (!state.metrics || state.metrics.dayKey !== key) {
        state.metrics = createEmptyMetrics(key);
      }
      const lowerBound = state.lowerBound;

      const solversQuery = supabase.rpc("pivot_completion_metrics", {
        p_daily_key: key,
      });
      let countQuery = supabase
        .from("pivot_scores")
        .select("id", { count: "exact", head: true })
        .eq("daily_key", key)
        .not("eligible", "is", false);
      if (Number.isFinite(lowerBound)) countQuery = countQuery.gte("moves", lowerBound);
      const [solversResult, scoreCountResult] = await Promise.all([solversQuery, countQuery]);
      if (!solversResult.error) {
        const metricsRow = Array.isArray(solversResult.data) ? solversResult.data[0] : solversResult.data;
        const solvers = Number(metricsRow && metricsRow.solvers);
        state.metrics.solvers = Number.isFinite(solvers) ? solvers : 0;
      }
      const scoreCount = !scoreCountResult.error && Number.isFinite(scoreCountResult.count)
        ? scoreCountResult.count
        : 0;
      if (!scoreCountResult.error) state.metrics.boardEntries = scoreCount;
      if (scoreCount <= 0) {
        state.metrics.median = null;
        state.metrics.best = null;
        return;
      }

      let bestQuery = supabase
        .from("pivot_scores")
        .select("moves")
        .eq("daily_key", key)
        .not("eligible", "is", false)
        .order("moves", { ascending: true })
        .range(0, 0);
      if (Number.isFinite(lowerBound)) bestQuery = bestQuery.gte("moves", lowerBound);
      const bestResult = await bestQuery;
      if (!bestResult.error && Array.isArray(bestResult.data) && bestResult.data.length) {
        const bestMoves = Number(bestResult.data[0].moves);
        state.metrics.best = Number.isFinite(bestMoves) ? bestMoves : null;
      } else {
        state.metrics.best = null;
      }

      const lowerMid = Math.floor((scoreCount - 1) / 2);
      const upperMid = Math.floor(scoreCount / 2);
      let medianQuery = supabase
        .from("pivot_scores")
        .select("moves")
        .eq("daily_key", key)
        .not("eligible", "is", false)
        .order("moves", { ascending: true })
        .range(lowerMid, upperMid);
      if (Number.isFinite(lowerBound)) medianQuery = medianQuery.gte("moves", lowerBound);
      const medianResult = await medianQuery;
      if (!medianResult.error && Array.isArray(medianResult.data) && medianResult.data.length) {
        const mids = medianResult.data.map((row) => Number(row.moves)).filter((n) => Number.isFinite(n));
        if (mids.length === 2) {
          state.metrics.median = Math.round((mids[0] + mids[1]) / 2);
        } else if (mids.length === 1) {
          state.metrics.median = mids[0];
        } else {
          state.metrics.median = null;
        }
      } else {
        state.metrics.median = null;
      }
    } catch (err) {
      // Leave metrics as-is on failure.
    }
  }

  async function fetchPercentileForMoves(key, playerMoves) {
    if (!supabase) return;
    if (!key || !Number.isFinite(playerMoves)) return;
    if (!state.metrics || state.metrics.dayKey !== key) {
      state.metrics = createEmptyMetrics(key);
    }
    const cache = state.metrics.percentileCache || (state.metrics.percentileCache = {});
    if (Object.prototype.hasOwnProperty.call(cache, playerMoves)) return;
    const loading = state.metrics.percentileLoading || (state.metrics.percentileLoading = {});
    if (loading[playerMoves]) return;
    loading[playerMoves] = true;
    try {
      const { data, error } = await supabase.rpc("pivot_completion_metrics", {
        p_daily_key: key,
        p_moves: playerMoves,
      });
      const metricsRow = Array.isArray(data) ? data[0] : data;
      const totalRaw = Number(metricsRow && metricsRow.solvers);
      const countGteRaw = Number(metricsRow && metricsRow.at_or_above);
      const total = !error && Number.isFinite(totalRaw) ? totalRaw : 0;
      const countGte = !error && Number.isFinite(countGteRaw) ? countGteRaw : 0;
      if (total < 3) {
        cache[playerMoves] = null;
      } else {
        cache[playerMoves] = Math.max(1, Math.min(99, Math.round((countGte / total) * 100)));
      }
    } catch {
      // Ignore percentile failures.
    } finally {
      delete loading[playerMoves];
    }
  }

  function computePercentile(playerMoves) {
    if (!Number.isFinite(playerMoves)) return null;
    if (!state.metrics || state.metrics.dayKey !== state.dailyKey) {
      state.metrics = createEmptyMetrics(state.dailyKey || "");
    }
    const cache = state.metrics.percentileCache || (state.metrics.percentileCache = {});
    if (Object.prototype.hasOwnProperty.call(cache, playerMoves)) {
      return cache[playerMoves];
    }
    fetchPercentileForMoves(state.dailyKey, playerMoves);
    return null;
  }

  function aggregateLeaderboard(rows, minDays) {
    const byDevice = {};
    for (const row of rows) {
      const did = row.device_id || row.name;
      if (!byDevice[did]) byDevice[did] = { name: row.name, days: new Set(), totalMoves: 0 };
      byDevice[did].name = row.name;
      const dayKey = row.daily_key || "";
      if (!byDevice[did].days.has(dayKey)) {
        byDevice[did].days.add(dayKey);
        byDevice[did].totalMoves += row.moves;
      }
    }
    const all = Object.values(byDevice)
      .map((e) => ({ name: e.name, games: e.days.size, totalMoves: e.totalMoves, avgMoves: +(e.totalMoves / e.days.size).toFixed(1), qualified: e.days.size >= minDays }));
    const qualified = all.filter((e) => e.qualified).sort((a, b) => b.games - a.games || a.avgMoves - b.avgMoves);
    const unqualified = all.filter((e) => !e.qualified).sort((a, b) => b.games - a.games || a.avgMoves - b.avgMoves);
    return [...qualified, ...unqualified];
  }

  function getWeekStartKey() {
    const { year, month, day } = getEasternDateParts();
    const d = new Date(year, month - 1, day);
    const dow = d.getDay();
    d.setDate(d.getDate() - dow + (dow === 0 ? -6 : 1));
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }

  function getMonthStartKey() {
    const { year, month } = getEasternDateParts();
    return `${year}-${String(month).padStart(2, "0")}-01`;
  }

  const LEADERBOARD_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  function loadCachedLeaderboard(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (Date.now() - cached.ts < LEADERBOARD_CACHE_TTL && Array.isArray(cached.data)) {
        return cached.data;
      }
    } catch {}
    return null;
  }

  function saveCachedLeaderboard(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  }

  async function fetchAggLeaderboard(period) {
    const cacheKey = `pivot_${period}_lb_${period === "week" ? getWeekStartKey() : getMonthStartKey()}`;
    const cached = loadCachedLeaderboard(cacheKey);
    if (cached) return cached;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(`${SUPABASE_URL}/functions/v1/pivot-leaderboard-agg?period=${period}`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          saveCachedLeaderboard(cacheKey, data);
          return data;
        }
      }
    } catch {}
    // Fallback: client-side aggregation via Supabase direct query
    if (!supabase) return null;
    try {
      const from = period === "week" ? getWeekStartKey() : getMonthStartKey();
      const to = getEasternKey();
      const limit = period === "week" ? 2000 : 5000;
      const minDays = period === "week" ? 2 : 4;
      const { data, error } = await supabase
        .from("pivot_scores")
        .select("name, moves, daily_key, device_id")
        .gte("daily_key", from)
        .lte("daily_key", to)
        .not("eligible", "is", false)
        .order("created_at", { ascending: true })
        .limit(limit);
      if (!error && Array.isArray(data)) {
        const result = aggregateLeaderboard(data, minDays);
        saveCachedLeaderboard(cacheKey, result);
        return result;
      }
    } catch {}
    return null;
  }

  async function fetchWeeklyLeaderboard() {
    if (state.weeklyLeaderboardLoading) return;
    state.weeklyLeaderboardLoading = true;
    state._weeklyLeaderboardFailed = false;
    try {
      const result = await fetchAggLeaderboard("week");
      if (result) state.weeklyLeaderboard = result;
      else state._weeklyLeaderboardFailed = true;
    } catch { state._weeklyLeaderboardFailed = true; }
    state.weeklyLeaderboardLoading = false;
    if (state.showLeaderboardOverlay) renderLeaderboardEntries();
  }

  async function fetchMonthlyLeaderboard() {
    if (state.monthlyLeaderboardLoading) return;
    state.monthlyLeaderboardLoading = true;
    state._monthlyLeaderboardFailed = false;
    try {
      const result = await fetchAggLeaderboard("month");
      if (result) state.monthlyLeaderboard = result;
      else state._monthlyLeaderboardFailed = true;
    } catch { state._monthlyLeaderboardFailed = true; }
    state.monthlyLeaderboardLoading = false;
    if (state.showLeaderboardOverlay) renderLeaderboardEntries();
  }

  async function fetchDailyMeta(key, word) {
    if (!supabase) return null;
    try {
      // Only read min_moves/min_exact — anon no longer has SELECT on the `word`
      // column (it would leak future answers). The word is already known from
      // the date-guarded pivot-daily-word fetch, so the old consistency check
      // is unnecessary.
      const { data, error } = await supabase
        .from("pivot_daily_meta")
        .select("min_moves,min_exact")
        .eq("daily_key", key)
        .limit(1)
        .maybeSingle();
      if (error || !data) return null;
      return data;
    } catch (err) {
      return null;
    }
  }

  async function fetchAdminMessage() {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from("pivot_messages")
        .select("message,created_at")
        .eq("active", true)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) return;
      if (data && data.length) {
        state.adminMessage = data[0].message;
        state.adminMessageUpdatedAt = data[0].created_at || "";
      } else {
        state.adminMessage = "";
        state.adminMessageUpdatedAt = "";
      }
    } catch (err) {
      // ignore
    }
  }

  // env(safe-area-inset-*) is only readable from CSS, so measure it via a hidden probe.
  // Non-zero only with viewport-fit=cover, which ensureDuoViewportFit() adds on iPhone Duo.
  let _safeAreaProbe = null;
  function readSafeAreaInsets() {
    if (!document.body) return { top: 0, right: 0, bottom: 0, left: 0 };
    if (!_safeAreaProbe) {
      _safeAreaProbe = document.createElement("div");
      _safeAreaProbe.setAttribute("aria-hidden", "true");
      _safeAreaProbe.style.cssText =
        "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
        "padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) " +
        "env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);";
      document.body.appendChild(_safeAreaProbe);
    }
    const cs = getComputedStyle(_safeAreaProbe);
    return {
      top: parseFloat(cs.paddingTop) || 0,
      right: parseFloat(cs.paddingRight) || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left: parseFloat(cs.paddingLeft) || 0,
    };
  }

  // iPhone Duo: both displays are ~1.4:1. Every other iPhone is >= 1.78 (SE/8) or ~2.17
  // (X and later), and iPads don't report "iPhone", so this is Duo-only. Uses the screen,
  // not the viewport, so it still holds in Split View. A letterboxed (pre-iOS 27 SDK) app
  // sees an ordinary iPhone screen and correctly falls back to the standard layout.
  function isIPhoneDuoDisplay() {
    if (!/iPhone/.test(navigator.userAgent || "")) return false;
    const s = window.screen;
    const longEdge = Math.max(s?.width || 0, s?.height || 0);
    const shortEdge = Math.min(s?.width || 0, s?.height || 0);
    return shortEdge > 0 && longEdge / shortEdge < 1.6;
  }

  // env(safe-area-inset-*) reads as zero without viewport-fit=cover. Opt in only on Duo
  // so every other device keeps its existing viewport and layout untouched.
  let _duoViewportApplied = false;
  function ensureDuoViewportFit() {
    if (_duoViewportApplied) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const content = meta.getAttribute("content") || "";
    if (!/viewport-fit\s*=\s*cover/.test(content)) {
      meta.setAttribute("content", `${content}, viewport-fit=cover`);
    }
    _duoViewportApplied = true;
  }

  const NO_INSETS = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });
  let _gameWrapEl = null;

  function resizeCanvas() {
    const viewport = window.visualViewport;
    const rawWidth = viewport ? viewport.width : window.innerWidth;
    const rawHeight = viewport ? viewport.height : window.innerHeight;
    const duo = isIPhoneDuoDisplay();
    if (duo) ensureDuoViewportFit();
    // Duo Split View panes can be ~310pt wide; the 320 floor would clip the board.
    const minWidth = duo ? 280 : 320;
    const safeWidth = Math.max(minWidth, rawWidth || minWidth);
    const safeHeight = Math.max(260, rawHeight || 480);
    const shortSide = Math.min(safeWidth, safeHeight);
    const longSide = Math.max(safeWidth, safeHeight);
    const coarsePointer =
      typeof window.matchMedia === "function" &&
      !!window.matchMedia("(pointer: coarse)").matches;
    const tabletMode = coarsePointer && shortSide >= 700 && longSide >= 900;
    const tabletPortraitStack = tabletMode && safeHeight > safeWidth;
    const tabletWindowed = tabletMode && !tabletPortraitStack && (safeWidth < 980 || safeHeight < 760);
    // Duo's outer display in landscape (Safari ~594x368, app ~678x340) is the same short,
    // wide shape iPhone landscape is tuned for, but narrower than 700pt.
    const shortLandscape = safeHeight < 460 && (safeWidth >= 700 || (duo && safeWidth > safeHeight));
    // Duo only: keep the canvas clear of the right-edge status bar/camera and the home
    // indicator. Off-Duo the insets are zero, so every clamp below is a no-op and the
    // layout is exactly what it was before Duo support.
    const insets = duo ? readSafeAreaInsets() : NO_INSETS;
    // The status bar/camera sits on one side only. Mirroring it would waste the same
    // width on the far side, so the canvas is offset off the inset (see the .game-wrap
    // margins below) and sized from the remaining width. Vertically it stays centered.
    const insetX = insets.left + insets.right;
    const insetY = 2 * Math.max(insets.top, insets.bottom);
    const maxWidth = Math.min((safeWidth - insetX) * 0.96, 1100);
    const maxHeight = Math.max(300, Math.min(safeHeight * (tabletMode ? 0.9 : 0.86), safeHeight - insetY));
    // Applied after the 320/300 minimums below so they can't push the canvas back under an inset.
    const clampToSafe = (value, limit, inset) => (inset > 0 ? Math.min(value, limit) : value);

    // Desktop layout needs board + sidebar + 3 margins to fit comfortably.
    // Min board ~400 + sidebar 170 + margins ~48 ≈ 618, but we want breathing room.
    // Use 920 threshold so the board never gets squeezed at intermediate widths.
    // A wide, short phone has enough horizontal room for the board and rail.
    // Stacking the rail below consumed a third of the scarce height and made
    // the puzzle thumbnail-sized despite hundreds of unused horizontal pixels.
    // Duo in landscape (inner ~951x669, outer ~678x466) misses both the 920 and the
    // shortLandscape thresholds, which stacked a thumbnail board over a huge empty panel
    // and crowded the header on the outer display. Board + rail fits both comfortably.
    const duoLandscape = duo && safeWidth - insetX > safeHeight;
    const panelBelow = !shortLandscape && !duoLandscape && (maxWidth < 920 || tabletPortraitStack || tabletWindowed);
    layout.panelBelow = panelBelow;
    layout.tabletMode = tabletMode;
    layout.tabletPortraitStack = tabletPortraitStack;
    layout.tabletWindowed = tabletWindowed;
    layout.shortLandscape = shortLandscape;

    _gameWrapEl = _gameWrapEl || document.querySelector(".game-wrap");
    // On Duo the canvas plus its inset margins must also fit inside .stage's padding, or a
    // narrow Split View pane beside the camera edge overflows (e.g. 310pt with a 50pt inset).
    let canvasWidthLimit = Infinity;
    if (duo) {
      const stage = _gameWrapEl && _gameWrapEl.parentElement;
      const stageStyle = stage ? getComputedStyle(stage) : null;
      const stagePadX = stageStyle ? (parseFloat(stageStyle.paddingLeft) || 0) + (parseFloat(stageStyle.paddingRight) || 0) : 0;
      canvasWidthLimit = Math.max(0, safeWidth - insetX - stagePadX);
    } else if (maxWidth < minWidth) {
      // Below ~334px the 320 floor is wider than .stage leaves room for, which pushed
      // the canvas off the right edge (a 320px phone lost its right margin and border).
      const stage = _gameWrapEl && _gameWrapEl.parentElement;
      const stageStyle = stage ? getComputedStyle(stage) : null;
      const stagePadX = stageStyle ? (parseFloat(stageStyle.paddingLeft) || 0) + (parseFloat(stageStyle.paddingRight) || 0) : 0;
      canvasWidthLimit = Math.max(0, safeWidth - stagePadX);
    }
    const targetWidth = Math.min(
      clampToSafe(Math.max(minWidth, maxWidth), safeWidth - insetX, insetX),
      canvasWidthLimit
    );
    const targetHeight = clampToSafe(Math.max(300, maxHeight), safeHeight - insetY, insetY);
    layout.duo = duo;

    // Centering within margins equal to each inset puts the canvas in the middle of the
    // usable area. Cleared off-Duo, so other devices keep the stylesheet's centering.
    if (_gameWrapEl) {
      _gameWrapEl.style.marginLeft = duo && insets.left ? `${insets.left}px` : "";
      _gameWrapEl.style.marginRight = duo && insets.right ? `${insets.right}px` : "";
      // The canvas already clears the insets, so the corner buttons must not add them again.
      _gameWrapEl.classList.toggle("duo-layout", duo);
      // Short Duo landscape: the rail runs to the bottom-right corner, so the help button
      // moves to the empty top-left beside the centered header (see style.css).
      _gameWrapEl.classList.toggle("duo-short", duo && shortLandscape);
      // Phone landscape (off Duo): the board runs to the canvas's bottom-left corner,
      // so the bottom-left theme toggle covered the corner tile. It moves to the empty
      // top-left beside the centered header (see style.css).
      _gameWrapEl.classList.toggle("short-landscape", !duo && shortLandscape);
    }

    const dpr = window.devicePixelRatio || 1;
    const pixelW = Math.round(targetWidth * dpr);
    const pixelH = Math.round(targetHeight * dpr);
    const cssW = `${Math.round(targetWidth)}px`;
    const cssH = `${Math.round(targetHeight)}px`;
    const needsResize =
      canvas.width !== pixelW ||
      canvas.height !== pixelH ||
      canvas.style.width !== cssW ||
      canvas.style.height !== cssH;
    if (needsResize) {
      canvas.width = pixelW;
      canvas.height = pixelH;
      canvas.style.width = cssW;
      canvas.style.height = cssH;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    layout.width = targetWidth;
    layout.height = targetHeight;
    layout.margin = tabletMode
      ? Math.max(16, Math.min(targetWidth, targetHeight) * 0.04)
      : Math.max(8, Math.min(16, targetWidth * 0.025));

    if (panelBelow) {
      // Mobile: give the panel a bit more height so we can show a few leaderboard rows without crowding.
      if (tabletPortraitStack) {
        layout.panelHeight = state.customMode
          ? Math.max(200, Math.min(280, targetHeight * 0.34))
          : Math.max(250, Math.min(360, targetHeight * 0.4));
      } else if (targetHeight < 420) {
        // Phone landscape — minimize panel so grid stays usable
        layout.panelHeight = state.customMode
          ? Math.max(100, Math.min(140, targetHeight * 0.28))
          : Math.max(110, Math.min(150, targetHeight * 0.32));
      } else {
        layout.panelHeight = state.customMode
          ? Math.max(160, Math.min(220, targetHeight * 0.3))
          : Math.max(180, Math.min(280, targetHeight * 0.38));
      }
      layout.panelWidth = targetWidth - layout.margin * 2;

      // Reserve a strip between grid and panel for the developer message on mobile.
      const messageReserve = state.customMode
        ? Math.max(12, Math.min(22, targetHeight * 0.035))
        : (tabletPortraitStack
          ? Math.max(44, Math.min(68, targetHeight * 0.09))
          : Math.max(36, Math.min(64, targetHeight * 0.08)));
      layout.messageReserve = messageReserve;
      const smallPhone = targetHeight < 740;
      const phoneLandscape = targetHeight < 420;
      const headerReserve = state.customMode ? 0 : (phoneLandscape ? 40 : (tabletPortraitStack ? 80 : (smallPhone ? 72 : 78)));
      const availableHeight = targetHeight - layout.panelHeight - layout.margin * 3 - messageReserve - headerReserve;
      const availableWidth = targetWidth - layout.margin * 2;
      const gridHeight = Math.min(availableHeight, availableWidth * (GRID_ROWS / GRID_COLS));
      const gridWidth = gridHeight * (GRID_COLS / GRID_ROWS);

      layout.cellSize = Math.min(gridWidth / GRID_COLS, gridHeight / GRID_ROWS);
      layout.gridWidth = layout.cellSize * GRID_COLS;
      layout.gridHeight = layout.cellSize * GRID_ROWS;
      layout.offsetX = (targetWidth - layout.gridWidth) / 2;
      layout.offsetY = layout.margin + headerReserve;

      layout.panelX = layout.margin;
      layout.panelY = layout.offsetY + layout.gridHeight + layout.margin + messageReserve;
    } else {
      const panelRatio = tabletMode ? 0.28 : 0.24;
      const panelMin = tabletMode ? 205 : 170;
      const panelMax = tabletMode ? 290 : 240;
      layout.panelWidth = Math.max(panelMin, Math.min(panelMax, targetWidth * panelRatio));
      layout.panelHeight = targetHeight - layout.margin * 2;

      const desktopHeaderReserve = state.customMode ? 0 : 60;
      // Phone-landscape custom games use the full height for the grid, so the
      // fixed bottom-left theme toggle would sit on the bottom-left tile.
      const cornerReserve = state.customMode && shortLandscape ? 48 : 0;
      const availableWidth = targetWidth - layout.panelWidth - layout.margin * 3 - cornerReserve;
      const availableHeight = targetHeight - layout.margin * 2 - desktopHeaderReserve;
      const gridWidth = Math.min(availableWidth, availableHeight * (GRID_COLS / GRID_ROWS));
      const gridHeight = gridWidth * (GRID_ROWS / GRID_COLS);

      layout.cellSize = Math.min(gridWidth / GRID_COLS, gridHeight / GRID_ROWS);
      layout.gridWidth = layout.cellSize * GRID_COLS;
      layout.gridHeight = layout.cellSize * GRID_ROWS;
      layout.offsetX = layout.margin + cornerReserve;
      layout.offsetY = desktopHeaderReserve + (targetHeight - desktopHeaderReserve - layout.gridHeight) / 2;

      layout.panelX = layout.offsetX + layout.gridWidth + layout.margin;
      layout.panelY = layout.margin;
    }

    if (!Number.isFinite(layout.cellSize) || layout.cellSize <= 0) {
      layout.cellSize = 60;
      layout.gridWidth = layout.cellSize * GRID_COLS;
      layout.gridHeight = layout.cellSize * GRID_ROWS;
      layout.offsetX = (layout.width - layout.gridWidth) / 2;
      layout.offsetY = (layout.height - layout.gridHeight) / 2;
    }

    layout.pivotRadius = Math.max(10, layout.cellSize * 0.18);
  }

  function buildSolvedGrid(word, rng) {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const grid = Array.from({ length: GRID_ROWS }, () =>
      Array.from({ length: GRID_COLS }, () => {
        const index = Math.floor(rng() * alphabet.length);
        return alphabet[index];
      })
    );
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      grid[CENTER_ROW][CENTER_START_COL + i] = word[i];
    }
    return grid;
  }

  function gridToString(grid) {
    return grid.map((row) => row.join("")).join("");
  }

  function isValidGridString(value) {
    return typeof value === "string" && value.length === GRID_ROWS * GRID_COLS && /^[A-Z]+$/.test(value);
  }

  function stringToGrid(value) {
    const grid = [];
    for (let r = 0; r < GRID_ROWS; r += 1) {
      const row = [];
      for (let c = 0; c < GRID_COLS; c += 1) {
        row.push(value[r * GRID_COLS + c]);
      }
      grid.push(row);
    }
    return grid;
  }

  function buildMoveMaps() {
    const moves = [];
    for (let r = 0; r < GRID_ROWS - 1; r += 1) {
      for (let c = 0; c < GRID_COLS - 1; c += 1) {
        const a = r * GRID_COLS + c;
        const b = r * GRID_COLS + c + 1;
        const cIdx = (r + 1) * GRID_COLS + c;
        const d = (r + 1) * GRID_COLS + c + 1;
        moves.push([a, b, cIdx, d]);
      }
    }
    return moves;
  }

  const MOVE_MAPS = buildMoveMaps();

  function applyMove(stateStr, moveIndex, clockwise) {
    const chars = stateStr.split("");
    const [a, b, cIdx, d] = MOVE_MAPS[moveIndex];
    if (clockwise) {
      // CW rotation (matches rotateBlock(..., true)):
      // new[a]=old[c], new[b]=old[a], new[d]=old[b], new[c]=old[d]
      const temp = chars[cIdx];
      chars[cIdx] = chars[d];
      chars[d] = chars[b];
      chars[b] = chars[a];
      chars[a] = temp;
    } else {
      // CCW rotation (inverse edge of CW):
      // new[a]=old[b], new[b]=old[d], new[d]=old[c], new[c]=old[a]
      const temp = chars[b];
      chars[b] = chars[d];
      chars[d] = chars[cIdx];
      chars[cIdx] = chars[a];
      chars[a] = temp;
    }
    return chars.join("");
  }

  function replayAndVerify(canonicalStartStr, moveHistory, targetWord) {
    if (!canonicalStartStr || !Array.isArray(moveHistory) || !targetWord) {
      return { valid: false, replayedMoves: 0 };
    }
    let gridStr = canonicalStartStr;
    for (let i = 0; i < moveHistory.length; i += 1) {
      const pivotIndex = moveHistory[i];
      if (!Number.isInteger(pivotIndex) || pivotIndex < 0 || pivotIndex >= MOVE_MAPS.length) {
        return { valid: false, replayedMoves: i };
      }
      gridStr = applyMove(gridStr, pivotIndex, true);
    }
    const centerStart = CENTER_ROW * GRID_COLS + CENTER_START_COL;
    const centerWord = gridStr.slice(centerStart, centerStart + CENTER_WORD_LENGTH);
    return { valid: centerWord === targetWord, replayedMoves: moveHistory.length };
  }

  const CELL_COUNT = GRID_ROWS * GRID_COLS;
  const CENTER_POSITIONS = Array.from({ length: CENTER_WORD_LENGTH }, (_, idx) => {
    return CENTER_ROW * GRID_COLS + CENTER_START_COL + idx;
  });

  function buildTokenMoveMaps() {
    // Token movement under CW rotation (matches rotateBlock(..., true)):
    // old[a]->new[b], old[b]->new[d], old[d]->new[c], old[c]->new[a]
    const cw = [];
    for (const [a, b, c, d] of MOVE_MAPS) {
      const mCW = new Uint8Array(CELL_COUNT);
      for (let i = 0; i < CELL_COUNT; i += 1) mCW[i] = i;
      mCW[a] = b;
      mCW[b] = d;
      mCW[d] = c;
      mCW[c] = a;
      cw.push(mCW);
    }
    return { cw };
  }

  const TOKEN_MAPS = buildTokenMoveMaps();

  function buildTokenDistanceMatrix() {
    const dist = Array.from({ length: CELL_COUNT }, () => Array(CELL_COUNT).fill(Infinity));
    for (let start = 0; start < CELL_COUNT; start += 1) {
      dist[start][start] = 0;
      const queue = [start];
      let qi = 0;
      while (qi < queue.length) {
        const pos = queue[qi++];
        const d0 = dist[start][pos];
        for (let mi = 0; mi < TOKEN_MAPS.cw.length; mi += 1) {
          const next = TOKEN_MAPS.cw[mi][pos];
          if (dist[start][next] <= d0 + 1) continue;
          dist[start][next] = d0 + 1;
          queue.push(next);
        }
      }
    }
    return dist;
  }

  const TOKEN_DIST = buildTokenDistanceMatrix();

  function computeLowerBoundForStart(startStr, word) {
    if (!startStr || !word) return null;
    if (startStr.length !== CELL_COUNT) return null;
    if (word.length !== CENTER_WORD_LENGTH) return null;

    let bound = 0;
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      const targetPos = CENTER_POSITIONS[i];
      const letter = word[i];
      let best = Infinity;
      for (let p = 0; p < CELL_COUNT; p += 1) {
        if (startStr[p] !== letter) continue;
        const d = TOKEN_DIST[p][targetPos];
        if (d < best) best = d;
      }
      if (!Number.isFinite(best)) return null;
      bound = Math.max(bound, best);
    }
    return bound;
  }

  function filterLeaderboardByLowerBound(entries, lowerBound) {
    const base = (entries || []).filter((entry) => entry && !isSyntheticLeaderboardName(entry.name));
    if (!Number.isFinite(lowerBound) || lowerBound <= 0) return base;
    return base.filter((entry) => Number.isFinite(entry.moves) && entry.moves >= lowerBound);
  }

  function centerFromStateStr(stateStr) {
    const base = CENTER_ROW * GRID_COLS + CENTER_START_COL;
    return stateStr.slice(base, base + CENTER_WORD_LENGTH);
  }

  // Minimum moves to solve the puzzle (yellow center spells the word) under CW-only gameplay.
  function findMinimumMovesToCenter(startStr, targetWord, maxNodes = 600000) {
    if (!startStr || !targetWord) return { moves: null, exact: false };
    if (centerFromStateStr(startStr) === targetWord) return { moves: 0, exact: true };

    const visited = new Map([[startStr, 0]]);
    const queue = [startStr];
    let head = 0;
    let nodes = 0;

    while (head < queue.length) {
      const stateStr = queue[head++];
      const depth = visited.get(stateStr) ?? 0;
      for (let i = 0; i < MOVE_MAPS.length; i += 1) {
        const next = applyMove(stateStr, i, true);
        if (visited.has(next)) continue;
        const nextDepth = depth + 1;
        if (centerFromStateStr(next) === targetWord) {
          return { moves: nextDepth, exact: true };
        }
        visited.set(next, nextDepth);
        queue.push(next);
        nodes += 1;
        if (nodes > maxNodes) return { moves: null, exact: false };
      }
    }
    return { moves: null, exact: false };
  }

  // CW-only gameplay: shortest path in the directed CW move graph.
  // We run bidirectional BFS: expand start frontier with CW moves, expand goal frontier with CCW moves
  // (which are the inverse edges of CW).
  function findMinimumMoves(startStr, goalStr, maxNodes = 200000) {
    if (startStr === goalStr) return { moves: 0, exact: true };

    const frontA = new Map([[startStr, 0]]);
    const frontB = new Map([[goalStr, 0]]);
    let queueA = [startStr];
    let queueB = [goalStr];
    let nodes = 0;

    while (queueA.length && queueB.length) {
      const expandA = queueA.length <= queueB.length;
      const queue = expandA ? queueA : queueB;
      const visited = expandA ? frontA : frontB;
      const other = expandA ? frontB : frontA;

      const nextQueue = [];
      for (const stateStr of queue) {
        const depth = visited.get(stateStr) ?? 0;
        for (let i = 0; i < MOVE_MAPS.length; i += 1) {
          const next = applyMove(stateStr, i, expandA /* CW from start, CCW from goal */);
          if (visited.has(next)) continue;
          const nextDepth = depth + 1;
          if (other.has(next)) {
            return { moves: nextDepth + (other.get(next) ?? 0), exact: true };
          }
          visited.set(next, nextDepth);
          nextQueue.push(next);
          nodes += 1;
          if (nodes > maxNodes) return { moves: null, exact: false };
        }
      }
      if (expandA) {
        queueA = nextQueue;
      } else {
        queueB = nextQueue;
      }
    }
    return { moves: null, exact: false };
  }

  function rotateBlock(grid, pivotRow, pivotCol, clockwise) {
    const a = grid[pivotRow][pivotCol];
    const b = grid[pivotRow][pivotCol + 1];
    const c = grid[pivotRow + 1][pivotCol];
    const d = grid[pivotRow + 1][pivotCol + 1];
    if (clockwise) {
      grid[pivotRow][pivotCol] = c;
      grid[pivotRow][pivotCol + 1] = a;
      grid[pivotRow + 1][pivotCol + 1] = b;
      grid[pivotRow + 1][pivotCol] = d;
    } else {
      grid[pivotRow][pivotCol] = b;
      grid[pivotRow][pivotCol + 1] = d;
      grid[pivotRow + 1][pivotCol] = a;
      grid[pivotRow + 1][pivotCol + 1] = c;
    }
  }

  function scrambleGrid(grid, rng, moves = 30) {
    for (let i = 0; i < moves; i += 1) {
      const pivotRow = Math.floor(rng() * (GRID_ROWS - 1));
      const pivotCol = Math.floor(rng() * (GRID_COLS - 1));
      const clockwise = rng() > 0.5;
      rotateBlock(grid, pivotRow, pivotCol, clockwise);
    }
  }

  let _minMovesToken = 0;
  function scheduleMinMovesCompute(dailyKey, bfsStr, word) {
    if (!dailyKey || !bfsStr || !word) return;
    const token = ++_minMovesToken;
    bfsMinToCenterAsync(bfsStr, word, 600000, 0).then((result) => {
      if (token !== _minMovesToken) return;
      if (state.dailyKey !== dailyKey) return;
      if (state.targetWord !== word) return;
      state.minMoves = result.moves;
      state.minExact = result.exact;
      state.optimalPath = Array.isArray(result.path) ? result.path.slice() : [];
      scheduleRenderLoop();
    });
  }

function resetGame() {
  dismissChallengeLanding(false);
  state.sessionStats = { games: [] };
  state.shieldCelebrationVisible = false;
  const startedFromMenu = state.mode === "menu";
  if (startedFromMenu) document.getElementById("updates-banner")?.remove();
  clearActivationNudgeTimer();
  clearPostSolveLeaderboardPrompt();
  setMenuStartLoadState("idle");
  const dailyKey = getEasternKey();
  const word = getDailyWord();
  if (!word) {
    _pendingMenuStart = true;
    setMenuStartLoadState("loading");
    _fetchDailyWord();
    return;
  }
  const seed = hashString(`${dailyKey}-${word}`);
  const scrambleMoves = getScrambleMovesForKey(dailyKey);

    const canonicalStartStr = (() => {
      const startState =
        window.PivotDaily?.getStartStateForKey && dailyKey
          ? window.PivotDaily.getStartStateForKey(dailyKey)
          : "";
      if (startState && startState.length === CELL_COUNT) return startState;
      const rngStart = mulberry32(seed);
      const solved = buildSolvedGrid(word, rngStart);
      const grid = solved.map((row) => row.slice());
      scrambleGrid(grid, rngStart, scrambleMoves);
      return gridToString(grid);
    })();

    state.lowerBound = computeLowerBoundForStart(canonicalStartStr, word);

    const rng = mulberry32(seed);

    let saved = loadProgress(dailyKey, false);
    // Legacy cleanup. Redo now writes to the practice namespace, but records
    // written before that change sit in the DAILY key with redoMode:true —
    // holding the replay's state, because the old code had already overwritten
    // the genuine result with moves:0. Repairing is not possible: the real data
    // is not in there to keep. Drop the record and let fetchCompletion restore
    // the solved status from the server, which is authoritative.
    // Safe by construction: every saveProgress site is gated on
    // `practiceMode || redoMode`, so redoMode:true can no longer reach the daily
    // bucket and this can only ever match a legacy record.
    if (saved && saved.redoMode) {
      // Quarantine rather than delete. These records are almost certainly the
      // replay's state - the old code overwrote the genuine result with moves:0
      // the moment redo started - but "almost certainly" is not a good enough
      // reason to destroy someone's data irrecoverably. Move it aside so the day
      // rebuilds clean and the original is still there if it turns out to matter.
      try {
        safeSetItem(`pivot_legacy_redo_backup_${dailyKey}`, JSON.stringify(saved));
      } catch {}
      clearProgressState(dailyKey, false);
      saved = null;
    }
    const savedVersion = safeGetItem(WORDLIST_VERSION_KEY);
    if (savedVersion !== WORDLIST_VERSION) {
      safeSetItem(WORDLIST_VERSION_KEY, WORDLIST_VERSION);
      clearProgressState(dailyKey, false);
    }
    const solvedGrid = buildSolvedGrid(word, rng);
    let newGrid = solvedGrid.map((row) => row.slice());
    let moves = 0;
    let lastMove = null;
    let solvedToday = false;
    let minMoves = null;
    let minExact = false;
    let submitted = false;
    let submissionId = "";
    let submittedAt = "";
    let savedName = loadName();
    let redoMode = false;
    let completed = false;
    let minBfsStr = "";
    if (saved && saved.word && saved.word !== word) {
      clearProgressState(dailyKey, false);
      saved = null;
    }
    if (saved && Array.isArray(saved.grid)) {
      const savedGridStr = saved.grid.join("");
      if (isValidGridString(savedGridStr)) {
        newGrid = stringToGrid(savedGridStr);
      } else {
        // Corrupt local progress can soft-lock input/render; recover to canonical start.
        clearProgressState(dailyKey, false);
      }
      moves = Number.isFinite(saved.moves) && saved.moves >= 0 ? saved.moves : 0;
      lastMove = saved.lastMove || null;
      solvedToday = Boolean(saved.solvedToday);
      minMoves = Number.isFinite(saved.minMoves) && saved.minMoves > 0 ? saved.minMoves : null;
      minExact = Boolean(saved.minExact);
      submitted = Boolean(saved.submitted);
      submissionId = saved && typeof saved.submissionId === "string" ? saved.submissionId : "";
      submittedAt = normalizeSubmittedAt(saved && typeof saved.submittedAt === "string" ? saved.submittedAt : "");
      redoMode = Boolean(saved.redoMode);
    } else {
      const startState = window.PivotDaily?.getStartStateForKey ? window.PivotDaily.getStartStateForKey(dailyKey) : "";
      if (startState && startState.length === GRID_ROWS * GRID_COLS) {
        newGrid = stringToGrid(startState);
      } else {
        scrambleGrid(newGrid, rng, scrambleMoves);
      }
    }

    const savedMinKind = saved && typeof saved.minKind === "string" ? saved.minKind : "";
    if (!saved || minMoves === null || savedMinKind !== MIN_KIND) {
      // Use canonical start state for BFS, not current grid (which may be solved/mid-game)
      const canonicalStart = window.PivotDaily?.getStartStateForKey ? window.PivotDaily.getStartStateForKey(dailyKey) : "";
      minBfsStr = (canonicalStart && canonicalStart.length === GRID_ROWS * GRID_COLS) ? canonicalStart : gridToString(newGrid);
      minMoves = null;
      minExact = false;
    }

    state.grid = newGrid;
    state.targetWord = word;
    state.moves = moves;
    state.mode = "play";
    state.puzzleStartedAt = Date.now();
    state.firstMoveAt = 0;
    state.activationNudgeShown = false;
    state.lastMove = lastMove;
    state.solvedToday = solvedToday;
    state.minMoves = minMoves;
    state.minExact = minExact;
    state.optimalPath = [];
    state.nameInput = "";
    state.submitted = submitted;
    state.dailyKey = dailyKey;
    state.leaderboard = filterLeaderboardByLowerBound(loadLeaderboard(dailyKey), state.lowerBound);
    refreshYesterdayChampion(dailyKey);
    state.showReadme = false;
    state.showLeaderboardOverlay = false;
    state.leaderboardTab = "today";
    state.weeklyLeaderboard = null;
    state.monthlyLeaderboard = null;
    state.showTutorial = false;
    state.playerName = savedName;
    state.practiceMode = false;
    state.completed = completed;
    state.completionLocked = false;
    state.alreadyCompleted = completed;
    state.winAcknowledged = false;
    state.redoMode = redoMode;
    state.leaderboardEligible = !submitted && !redoMode;
    state.submissionReceiptId = submitted ? submissionId : "";
    state.submissionReceiptAt = submitted ? submittedAt : "";
    if (state.submitted) {
      clearPendingSubmit(dailyKey);
      state.pendingSubmitRetryAt = 0;
    } else {
      clearSubmissionReceipt();
    }
    state.showTutorial = false;
    state.metrics = createEmptyMetrics(dailyKey);
    state.shareOnly = false;
    state.canonicalStartStr = canonicalStartStr;
    if (saved && Array.isArray(saved.grid)) {
      state.moveHistory = Array.isArray(saved.moveHistory) ? saved.moveHistory : null;
      state.hintUsed = Boolean(saved.hintUsed);
    } else {
      state.moveHistory = [];
      state.hintUsed = false;
    }
    if (!Array.isArray(state.moveHistory)) {
      state.moveHistory = [];
    }
    const historyValid = state.moveHistory.every((idx) => Number.isInteger(idx) && idx >= 0 && idx < MOVE_MAPS.length);
    if (!historyValid) {
      state.moveHistory = [];
      state.hintUsed = false;
    }
    if (state.moveHistory.length !== state.moves) {
      state.moveHistory = [];
      if (state.moves > 0) state.leaderboardEligible = false;
    }
    state.hintDistanceVisible = false;
    state.hintDistance = null;
    state.hintPivotIndex = null;
    state.hintHighlightUntil = 0;
    state.hintSequence = null;
    state.hintSequenceUntil = 0;
    const activeChallenge =
      state.challengeFrom &&
      Number.isFinite(Number(state.challengeFrom.dayNum)) &&
      Number(state.challengeFrom.dayNum) === getDayNumber()
        ? state.challengeFrom
        : null;
    state.challengeFrom = activeChallenge;
    state.challengeResolved = Boolean(activeChallenge && Number.isFinite(Number(activeChallenge.moves)));
    state._challengeRedirected = false;
    state._rivalryRecorded = false;
    state._archiveRivalrySubmitted = false;
    if (state.hintUsed) state.leaderboardEligible = false;


    const streak = loadStreakState();
    state.streakCount = streak.count;
    state.streakDisplay = streak.count;
    state.streakAnimating = false;

    if (minMoves === null && minBfsStr) {
      scheduleMinMovesCompute(dailyKey, minBfsStr, word);
    }


    fetchCompletion(dailyKey).then((done) => {
      // Same scoping as the daily-meta callback. Key and word alone are not
      // enough: a custom game keeps state.dailyKey and can reuse the same word.
      if (state.dailyKey !== dailyKey || state.targetWord !== word) return;
      if (state.customMode || state.practiceMode || state.redoMode) return;
      if (done) {
        state.completed = true;
        state.alreadyCompleted = true;
        // Already played today and this load came from a challenge link → form
        // the head-to-head now; no fresh score submit will fire to trigger it.
        recordChallengeRivalry();
      }
    });

    fetchLeaderboard(dailyKey, 30).then((entries) => {
      // Same scoping as the daily-meta callback. Key and word alone are not
      // enough: a custom game keeps state.dailyKey and can reuse the same word.
      if (state.dailyKey !== dailyKey || state.targetWord !== word) return;
      if (state.customMode || state.practiceMode || state.redoMode) return;
      if (entries) {
        const normalized = entries.map((entry) => ({
          name: entry.name,
          moves: entry.moves,
          time: entry.created_at ? Date.parse(entry.created_at) : 0,
        }));
        const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
        state.leaderboard = filtered;
        saveLeaderboard(dailyKey, filtered);
      }
    });

    fetchDailyMetrics(dailyKey);
    fetchAdminMessage();
    state.nextMessagePoll = Date.now() + 5 * 60 * 1000;
    state.nextLeaderboardPoll = Date.now() + 2 * 60 * 1000;
    logEvent("visit", { referrer: document.referrer || null, new_device: _isNewDevice ? 1 : 0 }, `${dailyKey}_visit`);
    const visitContextReferrer = buildVisitContextReferrer();
    if (visitContextReferrer) {
      logEvent("visit_context", { referrer: visitContextReferrer }, `${dailyKey}_visit_context`);
    }
    if (startedFromMenu) {
      const msSinceSessionStart = Math.max(0, Date.now() - (state.sessionStart || Date.now()));
      logEvent("start", {
        ms_since_session_start: msSinceSessionStart,
        ...getLandingTelemetryData(),
      }, `${dailyKey}_start`);
      // For attempts-per-player analytics (admin only): one attempt per session load.
      logEvent("attempt_start", {
        ms_since_session_start: msSinceSessionStart,
        ...getLandingTelemetryData(),
      }, `${dailyKey}_${state.sessionId}_attempt_start`);
    }

    closeNameOverlay();
    closeArchiveOverlay();

    fetchDailyMeta(dailyKey, word).then((meta) => {
      if (!meta) return;
      // The board can change while this request is in flight — a custom game, an
      // archive date, a redo, or a midnight rollover. Everything below writes
      // `state.*` under `state.dailyKey` using `state.practiceMode`, so without
      // this guard a late reply files the NEW board's grid and word under the
      // OLD key. Reopening that day then sees a word mismatch and clears the
      // record, so the player's grid, history and receipt vanish with no error.
      if (state.dailyKey !== dailyKey || state.targetWord !== word) return;
      // Key and word alone are NOT sufficient. startCustomPuzzle leaves
      // state.dailyKey untouched and clears the namespace flags, and the custom
      // and daily word pools overlap — so a same-word custom game satisfies the
      // check above and would write the custom board straight into the daily
      // key. This request is only issued from the daily reset path, so any other
      // mode means the board moved on.
      if (state.customMode || state.practiceMode || state.redoMode) return;
      if (Number.isFinite(meta.min_moves) && meta.min_moves > 0) {
        state.minMoves = meta.min_moves;
        state.minExact = Boolean(meta.min_exact);
        saveProgress(state.dailyKey, {
          grid: state.grid.map((row) => row.join("")),
          moves: state.moves,
          lastMove: state.lastMove,
          solvedToday: state.solvedToday,
          minMoves: state.minMoves,
          minExact: state.minExact,
          submitted: state.submitted,
          redoMode: state.redoMode,
          word: state.targetWord,
          moveHistory: state.moveHistory,
        }, state.practiceMode || state.redoMode);
      }
    });

    const pendingForDay = loadPendingSubmit(dailyKey);
    if (!navigator.onLine) {
      setReliabilityState("degraded", "Offline");
    } else if (pendingForDay && pendingSubmitMatchesCurrentRun(pendingForDay)) {
      setReliabilityState("degraded", "Queued score awaiting sync");
    } else {
      setReliabilityState("healthy");
    }

    maybeShowResubmitToast();
    maybeRetryPendingSubmit("restore", 900);
    if (startedFromMenu) {
      scheduleActivationNudge();
      state._gameEntryAnimStart = performance.now();
    }
    announceStatus(`Spell ${state.targetWord} by rotating tile blocks. Grid is ${GRID_ROWS} by ${GRID_COLS}.`);
    setCanvasLabel(`PIVOT puzzle. Target word: ${state.targetWord}. Moves: ${state.moves}. Use pivot dots to rotate 2x2 tile blocks.`);
  }

  function isSolved() {
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      if (state.grid[CENTER_ROW][CENTER_START_COL + i] !== state.targetWord[i]) {
        return false;
      }
    }
    return true;
  }

  // --- Textured background ---
  function buildBgTexture(w, h, theme) {
    const off = document.createElement("canvas");
    off.width = w; off.height = h;
    const oc = off.getContext("2d");
    if (theme === "dark") {
      // Subtle dark grain texture (inspired by logodarkmode.png)
      oc.fillStyle = "#131313";
      oc.fillRect(0, 0, w, h);
      // Overlay very subtle noise
      const id = oc.createImageData(w, h);
      const d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        const base = 19;
        const n = (Math.random() - 0.5) * 6.5; // subtle grain (reduced 15%)
        d[i] = base + n; d[i + 1] = base + n; d[i + 2] = base + n; d[i + 3] = 255;
      }
      oc.putImageData(id, 0, 0);
    } else {
      // Subtle parchment texture for light mode
      oc.fillStyle = "#F5F2EA";
      oc.fillRect(0, 0, w, h);
      const id = oc.createImageData(w, h);
      const d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        const n = (Math.random() - 0.5) * 6; // very subtle
        const px = (i / 4) % w;
        const py = Math.floor(i / 4 / w);
        const wave = Math.sin(px * 0.012) * 1.5 + Math.cos(py * 0.01) * 1.5;
        d[i] = Math.max(0, Math.min(255, 245 + n + wave));
        d[i + 1] = Math.max(0, Math.min(255, 242 + n + wave));
        d[i + 2] = Math.max(0, Math.min(255, 234 + n + wave));
        d[i + 3] = 255;
      }
      oc.putImageData(id, 0, 0);
    }
    return off;
  }

  function getBgTexture(w, h) {
    if (_currentTheme === "highContrast") return null;
    if (_bgTextureTheme === _currentTheme && _bgTextureW === w && _bgTextureH === h) {
      return _currentTheme === "dark" ? _bgTextureDark : _bgTextureLight;
    }
    const tex = buildBgTexture(w, h, _currentTheme);
    if (_currentTheme === "dark") _bgTextureDark = tex; else _bgTextureLight = tex;
    _bgTextureTheme = _currentTheme;
    _bgTextureW = w;
    _bgTextureH = h;
    return tex;
  }

  function fillRoundRect(c, x, y, w, h, r) {
    c.beginPath();
    c.roundRect(x, y, w, h, r);
    c.fill();
  }
  function strokeRoundRect(c, x, y, w, h, r) {
    c.beginPath();
    c.roundRect(x, y, w, h, r);
    c.stroke();
  }

  function drawBackground() {
    const w = layout.width;
    const h = layout.height;
    // Fill 1px extra to cover any subpixel gap from DPR rounding
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, w + 1, h + 1);
    const tex = getBgTexture(w, h);
    if (tex) {
      ctx.drawImage(tex, 0, 0);
    }
  }

  function drawBackgroundWithLayout(targetCtx, targetLayout) {
    targetCtx.fillStyle = C.bg;
    targetCtx.fillRect(0, 0, targetLayout.width, targetLayout.height);
  }

  function buildShareImageCanvas(portrait = false) {
    if (!state.shareIncludeDetails && window.PivotGrowth) return window.PivotGrowth.drawShareCard(document.createElement("canvas"), { edition: _currentDayNumber(), moves: state.moves, portrait });
    const width = 1200;
    const height = 630;
    const canvasEl = document.createElement("canvas");
    canvasEl.width = width;
    canvasEl.height = height;
    const t = canvasEl.getContext("2d");
    t.textAlign = "center";

    // Background
    t.fillStyle = C.bg;
    t.fillRect(0, 0, width, height);

    // Subtle border
    t.strokeStyle = C.gridShadow;
    t.lineWidth = 2;
    t.strokeRect(1, 1, width - 2, height - 2);

    const cx = width / 2;
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    const day = isArchive ? getDayNumberForKey(state.dailyKey) : (state.practiceMode ? null : getDayNumber());

    // Brand lockup (same geometry as hall, scaled for OG image readability)
    drawBrandLockup(t, cx, 34, {
      align: "center",
      iconSize: 44,
      gap: 20,
      fontSize: 26,
      letterSpacing: 4,
      color: C.ink,
    });

    // Edition label
    t.fillStyle = C.mutedSoft;
    t.font = `13px ${_fontUI}`;
    if (typeof t.letterSpacing !== "undefined") t.letterSpacing = "1.5px";
    const isSundayShare = day && getWeekdayIndexForKey(state.dailyKey || getEasternKey()) === 0;
    t.fillText(day ? `Edition No. ${day}${isSundayShare ? " \u00b7 Sunday Challenge" : ""}` : "Bespoke Edition", cx, 100);
    if (typeof t.letterSpacing !== "undefined") t.letterSpacing = "0px";

    // Moves — large, dominant
    t.fillStyle = C.ink;
    t.font = `italic 56px ${_fontUI}`;
    t.fillText(`${state.moves} moves`, cx, 160);

    // Percentile + near-miss callout — the most shareable line
    const sharePct = computePercentile(state.moves);
    const shareDelta = Number.isFinite(state.minMoves) ? Math.max(0, state.moves - state.minMoves) : null;
    let calloutText = "";
    if (state.isPersonalBest) {
      calloutText = "New personal best!";
    } else if (shareDelta === 0) {
      calloutText = "Perfect solve!";
    } else if (shareDelta === 1) {
      calloutText = "One move from perfect!";
    } else if (sharePct !== null && sharePct <= 10) {
      calloutText = `Top ${sharePct}%`;
    }
    if (calloutText) {
      t.font = `italic 24px ${_fontUI}`;
      t.fillStyle = C.gold;
      t.fillText(calloutText, cx, 205);
    }

    // Emoji grid as colored squares
    const moveColors = buildShareGridColors();
    const sqSize = Math.min(42, Math.floor((width - 200) / Math.max(moveColors.length, 1)));
    const sqGap = 5;

    // Wrap into rows if > 20 moves
    const maxPerRow = Math.min(moveColors.length, 20);
    const rows = [];
    for (let i = 0; i < moveColors.length; i += maxPerRow) {
      rows.push(moveColors.slice(i, i + maxPerRow));
    }

    const rowHeight = sqSize + sqGap;
    let gridStartY = calloutText ? 240 : 230;
    for (const row of rows) {
      const rowW = row.length * sqSize + (row.length - 1) * sqGap;
      const rowStartX = cx - rowW / 2;
      for (let i = 0; i < row.length; i++) {
        const x = rowStartX + i * (sqSize + sqGap);
        t.fillStyle = row[i];
        t.beginPath();
        t.roundRect(x, gridStartY, sqSize, sqSize, 5);
        t.fill();
      }
      gridStartY += rowHeight;
    }

    // Stats line
    const statsY = gridStartY + 28;
    t.font = `20px ${_fontUI}`;
    t.fillStyle = C.muted;
    const statParts = [];
    if (Number.isFinite(state.minMoves)) statParts.push(`${state.minExact ? "Min" : "Par"}: ${state.minMoves}`);
    if (state.metrics.best !== null) statParts.push(`Best today: ${state.metrics.best}`);
    if (statParts.length) t.fillText(statParts.join("  \u00b7  "), cx, statsY);

    // Personalized recap line. This is deliberately derived from existing
    // stats so sharing never creates or mutates player data.
    const recapParts = [];
    if (state.streakCount > 1) recapParts.push(`${state.streakCount}-day streak`);
    if (sharePct !== null) recapParts.push(`top ${sharePct}% today`);
    if (state.challengeResolved && state.challengeFrom && Number.isFinite(Number(state.challengeFrom.moves))) {
      const rivalName = String(state.challengeFrom.name || "a friend").slice(0, 16);
      if (state.moves < Number(state.challengeFrom.moves)) recapParts.push(`beat ${rivalName}`);
      else if (state.moves === Number(state.challengeFrom.moves)) recapParts.push(`tied ${rivalName}`);
    }
    if (recapParts.length) {
      t.font = `600 20px ${_fontUI}`;
      t.fillStyle = C.goldEdge;
      t.fillText(recapParts.join("  ·  "), cx, height - 142);
    }

    // CTA — percentile-powered when available
    t.font = `italic 22px ${_fontUI}`;
    t.fillStyle = C.mutedSoft;
    const ctaText = sharePct !== null && sharePct <= 25
      ? `Only ${sharePct}% solved it this fast`
      : "Can you beat this?";
    t.fillText(ctaText, cx, height - 100);

    // URL
    t.font = `18px ${_fontUI}`;
    t.fillStyle = C.mutedFaint;
    t.fillText("pivotthegame.com", cx, height - 55);

    return canvasEl;
  }

  function classifyMoves() {
    // Walk move history tracking lower-bound distance changes.
    // Returns { green, yellow, red, total }.
    let green = 0, yellow = 0, red = 0;
    if (state.moveHistory && state.moveHistory.length > 0 && state.canonicalStartStr && state.targetWord) {
      try {
        let gridStr = state.canonicalStartStr;
        let prevDist = computeLowerBoundForStart(gridStr, state.targetWord);
        for (const moveIdx of state.moveHistory) {
          gridStr = applyMove(gridStr, moveIdx, true);
          const dist = computeLowerBoundForStart(gridStr, state.targetWord);
          if (dist === null || prevDist === null) {
            yellow++;
          } else if (dist < prevDist) {
            green++;
          } else if (dist === prevDist) {
            yellow++;
          } else {
            red++;
          }
          if (dist !== null) prevDist = dist;
        }
      } catch (_) { /* fall through */ }
    }
    const total = green + yellow + red;
    return { green, yellow, red, total: total || state.moves };
  }

  function buildShareGridColors() {
    // Returns array of CSS colors for each move (lower-bound heuristic, fast on mobile).
    const mc = classifyMoves();
    if (mc.total > 0 && state.moveHistory && state.moveHistory.length > 0 && state.canonicalStartStr && state.targetWord) {
      try {
        let gridStr = state.canonicalStartStr;
        let prevDist = computeLowerBoundForStart(gridStr, state.targetWord);
        const cols = [];
        for (const moveIdx of state.moveHistory) {
          gridStr = applyMove(gridStr, moveIdx, true);
          const dist = computeLowerBoundForStart(gridStr, state.targetWord);
          if (dist === null || prevDist === null) {
            cols.push("#ddd");
          } else if (dist < prevDist) {
            cols.push("#5ba85b"); // green — closer
          } else if (dist === prevDist) {
            cols.push("#f4d35e"); // yellow — neutral
          } else {
            cols.push("#d95f5f"); // red — farther
          }
          if (dist !== null) prevDist = dist;
        }
        return cols;
      } catch (_) { /* fall through */ }
    }
    return Array(Math.min(state.moves, 30)).fill("#5ba85b");
  }

  function drawTargetHighlight() {
    const { cellSize, offsetX, offsetY } = layout;

    // Solve ritual: contract band then pulse glow outward
    let contractInset = 0;
    let solveGlow = 0;
    if (state.winAnimStart && !_prefersReducedMotion) {
      const el = performance.now() - state.winAnimStart;
      if (el < 400) {
        // Phase 1 (0-150ms): contract inward
        // Phase 2 (150-400ms): snap back + glow pulse
        if (el < 150) {
          const t = el / 150;
          contractInset = easeInOutCubic(t) * 3;
        } else {
          const t = (el - 150) / 250;
          contractInset = 3 * (1 - easeInOutCubic(t));
          solveGlow = (1 - t) * (_currentTheme === "dark" ? 18 : 12);
        }
      }
    }

    ctx.save();
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      const col = CENTER_START_COL + i;
      const x = offsetX + col * cellSize + contractInset;
      const y = offsetY + CENTER_ROW * cellSize + contractInset;
      const s = cellSize - contractInset * 2;
      // Vertical gradient fill
      const grad = ctx.createLinearGradient(x, y, x, y + s);
      grad.addColorStop(0, C.gold);
      grad.addColorStop(1, C.goldEdge);
      ctx.fillStyle = grad;
      if (!_prefersReducedMotion) {
        ctx.shadowColor = C.gold;
        ctx.shadowBlur = (_currentTheme === "dark" ? 8 : 6) + solveGlow;
      }
      ctx.fillRect(x, y, s, s);
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.strokeStyle = C.goldEdge;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, s, s);
    }
    ctx.restore();
  }

  function drawGrid() {
    const { cellSize, offsetX, offsetY } = layout;
    const anim = state.anim;
    const skipCells = anim ? new Set(anim.cells.map((cell) => `${cell.r}-${cell.c}`)) : null;

    ctx.strokeStyle = C.gridLine;
    ctx.lineWidth = _currentTheme === "dark" ? 2 : 1.5;

    for (let r = 0; r <= GRID_ROWS; r += 1) {
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY + r * cellSize);
      ctx.lineTo(offsetX + GRID_COLS * cellSize, offsetY + r * cellSize);
      ctx.stroke();
    }

    for (let c = 0; c <= GRID_COLS; c += 1) {
      ctx.beginPath();
      ctx.moveTo(offsetX + c * cellSize, offsetY);
      ctx.lineTo(offsetX + c * cellSize, offsetY + GRID_ROWS * cellSize);
      ctx.stroke();
    }

    ctx.fillStyle = C.tileText;
    ctx.font = `600 ${Math.floor(cellSize * 0.52)}px ${_fontUI}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const settleMap = new Map();
    if (state._settleCells) state._settleCells = null;

    // Game entry fade-in
    let entryAlpha = 1;
    if (state._gameEntryAnimStart && !_prefersReducedMotion) {
      const el = performance.now() - state._gameEntryAnimStart;
      if (el < 250) entryAlpha = el / 250;
      else state._gameEntryAnimStart = 0;
    }

    for (let r = 0; r < GRID_ROWS; r += 1) {
      const isGoldRow = r === CENTER_ROW;
      for (let c = 0; c < GRID_COLS; c += 1) {
        if (skipCells && skipCells.has(`${r}-${c}`)) continue;
        const inGold = isGoldRow && c >= CENTER_START_COL && c < CENTER_START_COL + CENTER_WORD_LENGTH;
        ctx.fillStyle = inGold ? C.tileTextGold : C.tileText;
        const x = offsetX + c * cellSize + cellSize / 2;
        const y = offsetY + r * cellSize + cellSize / 2;
        const key = `${r}-${c}`;
        const scale = settleMap.get(key);
        if (scale && scale < 1) {
          ctx.save();
          ctx.translate(x, y);
          ctx.scale(scale, scale);
          ctx.globalAlpha = entryAlpha;
          ctx.fillText(state.grid[r][c], 0, 0);
          ctx.restore();
        } else {
          if (entryAlpha < 1) { ctx.save(); ctx.globalAlpha = entryAlpha; }
          ctx.fillText(state.grid[r][c], x, y);
          if (entryAlpha < 1) ctx.restore();
        }
      }
    }
  }

  function drawAnimation() {
    const anim = state.anim;
    if (!anim) return;
    const now = performance.now();
    const elapsed = now - anim.start;
    const t = Math.min(1, elapsed / anim.duration);
    const eased = easePivotRotation(t);
    const angle = (Math.PI / 2) * eased * (anim.clockwise ? 1 : -1);

    const { cellSize, offsetX, offsetY } = layout;
    const pivotX = offsetX + (anim.pivot.c + 1) * cellSize;
    const pivotY = offsetY + (anim.pivot.r + 1) * cellSize;

    // --- Visual treatment during rotation ---
    if (!_prefersReducedMotion && t > 0 && t < 1) {
      // Intensity peaks at midpoint, fades at start/end
      const intensity = Math.sin(t * Math.PI);

      // Clip all rotation effects to the 2x2 block to avoid bleed onto neighbors
      const blockX = offsetX + anim.pivot.c * cellSize;
      const blockY = offsetY + anim.pivot.r * cellSize;
      const blockW = cellSize * 2;
      const blockH = cellSize * 2;

      // Dark mode: faint gold glow radiating from pivot during rotation
      if (_currentTheme === "dark") {
        ctx.save();
        ctx.beginPath();
        ctx.rect(blockX, blockY, blockW, blockH);
        ctx.clip();
        const glowAlpha = 0.4 + 0.2 * intensity;
        const glowGrad = ctx.createRadialGradient(pivotX, pivotY, 0, pivotX, pivotY, cellSize * 1.5);
        glowGrad.addColorStop(0, `rgba(212,175,55,${glowAlpha})`);
        glowGrad.addColorStop(1, "rgba(212,175,55,0)");
        ctx.fillStyle = glowGrad;
        ctx.fillRect(blockX, blockY, blockW, blockH);
        ctx.restore();
      }
    }

    // --- Rotating letters (stay upright, positions orbit the pivot) ---
    ctx.font = `600 ${Math.floor(cellSize * 0.52)}px ${_fontUI}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = C.tileText;

    // During rotation, slightly intensify shadow on letters
    if (!_prefersReducedMotion && t > 0 && t < 1) {
      const intensity = Math.sin(t * Math.PI);
      ctx.save();
      ctx.shadowColor = _currentTheme === "dark"
        ? `rgba(212,175,55,${0.3 * intensity})`
        : `rgba(27,27,27,${0.12 * intensity})`;
      ctx.shadowBlur = 6 * intensity;
    }

    anim.cells.forEach((cell) => {
      const fromX = offsetX + cell.c * cellSize + cellSize / 2;
      const fromY = offsetY + cell.r * cellSize + cellSize / 2;
      const dx = fromX - pivotX;
      const dy = fromY - pivotY;
      const rx = dx * Math.cos(angle) - dy * Math.sin(angle);
      const ry = dx * Math.sin(angle) + dy * Math.cos(angle);
      ctx.fillText(cell.letter, pivotX + rx, pivotY + ry);
    });

    if (!_prefersReducedMotion && t > 0 && t < 1) {
      ctx.restore();
    }

    if (t >= 1) {
      // Trigger post-rotation animations
      const pivotIdx = anim.pivot.r * (GRID_COLS - 1) + anim.pivot.c;
      state._lastPivotPulse = { pivotIdx, start: now };
      state._settleCells = anim.cells.map((cell) => {
        const fromX = cell.c - anim.pivot.c;
        const fromY = cell.r - anim.pivot.r;
        const dir = anim.clockwise ? 1 : -1;
        const destC = anim.pivot.c + (dir === 1 ? (1 - fromY) : fromY);
        const destR = anim.pivot.r + (dir === 1 ? fromX : (1 - fromX));
        return { r: destR, c: destC, start: now };
      });
      state.anim = null;
      playPivotSettle();
    }
  }

  // Settle: chess piece placed with intention — short warm thunk
  function playPivotSettle() {
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const t = ac.currentTime;
    // Low thump + filtered noise — felt-on-wood lock
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.06);
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.28, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
    osc.connect(gain).connect(_masterGain);
    osc.start(t);
    osc.stop(t + 0.07);
    // Noise layer — felt impact
    const bufLen = Math.round(ac.sampleRate * 0.03);
    const buf = ac.createBuffer(1, bufLen, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
    const src = ac.createBufferSource();
    src.buffer = buf;
    const filter = ac.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 500;
    const ng = ac.createGain();
    ng.gain.setValueAtTime(0.14, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.035);
    src.connect(filter).connect(ng).connect(_masterGain);
    src.start(t);
    src.stop(t + 0.035);
  }

  function drawPivots() {
    const { cellSize, offsetX, offsetY, pivotRadius } = layout;
    // Show the exact footprint before committing a move; no solution is revealed.
    if (state.mode === "play" && state.hoverPivot && !state.anim && !hasOpenOverlayForKeyboard()) {
      const { r, c } = state.hoverPivot;
      const inset = Math.max(3, cellSize * 0.055);
      ctx.save();
      ctx.strokeStyle = C.ink;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(offsetX + c * cellSize + inset, offsetY + r * cellSize + inset,
        2 * cellSize - 2 * inset, 2 * cellSize - 2 * inset);
      ctx.restore();
    }
    const now = Date.now();
    const hintSeqActive = state.hintSequence && now < state.hintSequenceUntil;
    const hintActive = !hintSeqActive && state.hintPivotIndex !== null && now < state.hintHighlightUntil;
    for (let r = 0; r < GRID_ROWS - 1; r += 1) {
      for (let c = 0; c < GRID_COLS - 1; c += 1) {
        const x = offsetX + (c + 1) * cellSize;
        const y = offsetY + (r + 1) * cellSize;
        const isHover = state.hoverPivot && state.hoverPivot.r === r && state.hoverPivot.c === c;
        const pivotIdx = r * (GRID_COLS - 1) + c;

        // Multi-step hint sequence — gold pulse per step
        let isHint = false;
        let hintStepNum = 0;
        if (hintSeqActive && !_prefersReducedMotion) {
          for (let si = 0; si < state.hintSequence.length; si++) {
            const step = state.hintSequence[si];
            if (step.pivotIdx === pivotIdx && now >= step.start && now < step.end) {
              isHint = true;
              hintStepNum = si + 1;
              const elapsed = (now - step.start) / (step.end - step.start);
              // Smooth pulse: fade in, hold, fade out
              const pulse = elapsed < 0.15 ? elapsed / 0.15
                : elapsed > 0.85 ? (1 - elapsed) / 0.15
                : 1.0;
              ctx.save();
              ctx.globalAlpha = pulse * 0.8;
              ctx.beginPath();
              ctx.arc(x, y, pivotRadius * 1.4, 0, Math.PI * 2);
              ctx.fillStyle = C.goldFill || C.gold;
              ctx.fill();
              ctx.beginPath();
              ctx.arc(x, y, pivotRadius * 1.4, 0, Math.PI * 2);
              ctx.strokeStyle = C.highlight;
              ctx.lineWidth = 2.5;
              ctx.stroke();
              // Step number
              ctx.globalAlpha = pulse;
              ctx.fillStyle = C.ink;
              ctx.font = `600 ${Math.round(pivotRadius * 0.9)}px ${_fontUI}`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(String(hintStepNum), x, y);
              ctx.restore();
              break;
            }
          }
        }

        // Legacy single-pivot hint fallback
        if (!isHint && hintActive && pivotIdx === state.hintPivotIndex && !_prefersReducedMotion) {
          isHint = true;
          const pulse = 0.4 + 0.6 * Math.abs(Math.sin(now / 300));
          ctx.save();
          ctx.globalAlpha = pulse;
          ctx.beginPath();
          ctx.arc(x, y, pivotRadius * 1.3, 0, Math.PI * 2);
          ctx.strokeStyle = C.highlight;
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.restore();
        }
        // During rotation, compress the active pivot dot inward (mechanical hinge)
        const isActivePivot = state.anim && state.anim.pivot && state.anim.pivot.r === r && state.anim.pivot.c === c;
        let dotRadius = isHover ? pivotRadius * 0.72 : pivotRadius * 0.50;
        if (isActivePivot) {
          const animT = Math.min(1, (performance.now() - state.anim.start) / state.anim.duration);
          const compress = Math.sin(animT * Math.PI) * 0.12; // compress then release
          dotRadius = dotRadius * (1 - compress);
        }

        // Opaque base to cover grid line intersection
        const baseR = dotRadius + 2;
        const touchesGold = (r === CENTER_ROW - 1 || r === CENTER_ROW) &&
          (c >= CENTER_START_COL - 1 && c < CENTER_START_COL + CENTER_WORD_LENGTH);
        ctx.beginPath();
        ctx.arc(x, y, baseR, 0, Math.PI * 2);
        ctx.fillStyle = touchesGold ? C.goldEdge : (_currentTheme === "dark" ? "#131313" : C.bg);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
        if (_currentTheme === "dark") {
          // Dark mode: warm gold dots with glow
          const grad = ctx.createRadialGradient(x - dotRadius * 0.3, y - dotRadius * 0.3, 0, x, y, dotRadius);
          if (isHover || isHint) {
            grad.addColorStop(0, "#D4B650");
            grad.addColorStop(1, "#A8872A");
          } else {
            grad.addColorStop(0, "#C9A83E");
            grad.addColorStop(1, "#8B6F28");
          }
          ctx.fillStyle = grad;
          if (!_prefersReducedMotion) {
            ctx.save();
            ctx.shadowColor = isHover ? "rgba(180,145,40,0.8)" : "rgba(160,130,40,0.45)";
            ctx.shadowBlur = isHover ? 14 : 8;
            ctx.fill();
            ctx.shadowColor = isHover ? "rgba(160,130,40,0.3)" : "rgba(140,110,30,0.15)";
            ctx.shadowBlur = isHover ? 22 : 14;
            ctx.fill();
            ctx.restore();
          } else {
            ctx.fill();
          }
          ctx.strokeStyle = "rgba(180,160,100,0.35)";
          ctx.lineWidth = 0.75;
          ctx.stroke();
          // Specular highlight — machined brass pin upper-left gleam
          ctx.save();
          ctx.beginPath();
          ctx.arc(x - dotRadius * 0.2, y - dotRadius * 0.2, dotRadius * 0.28, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,240,180,0.3)";
          ctx.fill();
          ctx.restore();
        } else {
          // Light mode: sculpted brass rivet
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
          const bodyGrad = ctx.createRadialGradient(
            x - dotRadius * 0.3, y - dotRadius * 0.3, 0,
            x, y, dotRadius
          );
          if (isHover || isHint) {
            bodyGrad.addColorStop(0, "#5A554C");
            bodyGrad.addColorStop(0.7, "#403C35");
            bodyGrad.addColorStop(1, "#2E2B26");
          } else {
            bodyGrad.addColorStop(0, "#504C46");
            bodyGrad.addColorStop(0.7, "#3A3730");
            bodyGrad.addColorStop(1, "#2A2824");
          }
          ctx.fillStyle = bodyGrad;
          ctx.fill();
          // Inner shadow — bottom-right crescent for depth
          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
          ctx.clip();
          const innerShadow = ctx.createRadialGradient(
            x + dotRadius * 0.35, y + dotRadius * 0.35, 0,
            x + dotRadius * 0.2, y + dotRadius * 0.2, dotRadius * 0.9
          );
          innerShadow.addColorStop(0, "rgba(0,0,0,0.25)");
          innerShadow.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = innerShadow;
          ctx.fill();
          ctx.restore();
          // Specular highlight — upper-left gleam
          ctx.save();
          ctx.beginPath();
          ctx.arc(x - dotRadius * 0.22, y - dotRadius * 0.22, dotRadius * 0.32, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.14)";
          ctx.fill();
          ctx.restore();
          // Thin metallic edge
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(0,0,0,0.12)";
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }

        // Post-rotation dot pulse
        if (state._lastPivotPulse && state._lastPivotPulse.pivotIdx === pivotIdx && !_prefersReducedMotion) {
          const pulseEl = performance.now() - state._lastPivotPulse.start;
          if (pulseEl < 200) {
            const pt = pulseEl / 200;
            const pulseScale = easeOutBack(pt);
            const pRadius = dotRadius * (1 + 0.2 * (1 - Math.abs(pulseScale - 1)));
            const pulseGrad = ctx.createRadialGradient(x - pRadius * 0.3, y - pRadius * 0.3, 0, x, y, pRadius);
            if (_currentTheme === "dark") {
              pulseGrad.addColorStop(0, "#F0D060");
              pulseGrad.addColorStop(1, C.gold);
            } else {
              pulseGrad.addColorStop(0, "#555048");
              pulseGrad.addColorStop(0.5, "#403C35");
              pulseGrad.addColorStop(1, "#302E2A");
            }
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, pRadius, 0, Math.PI * 2);
            ctx.fillStyle = pulseGrad;
            if (_currentTheme === "dark") {
              ctx.shadowColor = "rgba(212,175,55,0.7)";
              ctx.shadowBlur = 20;
            }
            ctx.fill();
            ctx.restore();
          } else {
            state._lastPivotPulse = null;
          }
        }


      }
    }
  }

  function drawAdminMessage() {
    if (state.mode === "play" && state.challengeResolved && state.challengeFrom) return;
    if (!state.adminMessage) return;

    const maxWidth = layout.gridWidth;
    const messageX = layout.offsetX;
    const minGap = Math.max(8, layout.margin * 0.2);
    const messageY = layout.offsetY + layout.gridHeight + minGap;
    const maxY = layout.panelBelow ? layout.panelY - 6 : layout.height - layout.margin;
    if (maxY - messageY < 28) return;
    if (messageY + 14 >= maxY) return;

    const messagePrefix = "Andrew (developer) ";
    const messageSuffix = "says:";
    ctx.save();
    ctx.globalAlpha = 0.75;
    const messageLineHeight = 16;
    const messageFont = `12px ${_fontSerif}`;
    const messageItalic = `italic 12px ${_fontSerif}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.font = messageItalic;
    ctx.fillStyle = colors.highlight;
    ctx.fillText(messagePrefix, messageX, messageY);
    const prefixWidth = ctx.measureText(messagePrefix).width;
    ctx.font = messageFont;
    ctx.fillStyle = colors.ink;
    const suffixText = `${messageSuffix} ${state.adminMessage}`;
    const startX = messageX + prefixWidth;
    wrapText(suffixText, startX, messageY, maxWidth - prefixWidth, messageLineHeight);
    ctx.restore();
  }

  function drawChallengeBenchmark() {
    const challenge = state.challengeFrom;
    if (state.mode !== "play" || !state.challengeResolved || !challenge || !Number.isFinite(Number(challenge.moves))) return;
    const maxY = layout.panelBelow ? layout.panelY - 5 : layout.height - layout.margin;
    const y = layout.offsetY + layout.gridHeight + Math.max(8, layout.margin * 0.22);
    if (y + 24 > maxY) return;
    const name = String(challenge.name || "Friend").trim().slice(0, 18) || "Friend";
    const ghostHistory = Array.isArray(challenge.ghostHistory) ? challenge.ghostHistory : [];
    const ghostStart = isValidGridString(challenge.ghostStartState) ? challenge.ghostStartState : "";
    let ghostSuffix = "";
    if (ghostHistory.length && ghostStart && ghostStart === state.canonicalStartStr) {
      if (!state.ghostStartedAt) state.ghostStartedAt = performance.now();
      const nextIndex = Math.min(
        ghostHistory.length,
        Math.max(0, Math.floor((performance.now() - state.ghostStartedAt) / 1150))
      );
      if (nextIndex !== state.ghostMoveIndex || !state.ghostGrid) {
        let ghostGrid = ghostStart;
        for (let i = 0; i < nextIndex; i += 1) ghostGrid = applyMove(ghostGrid, ghostHistory[i], true);
        state.ghostMoveIndex = nextIndex;
        state.ghostGrid = ghostGrid;
      }
      const ghostCenter = state.ghostGrid ? state.ghostGrid.slice(7, 11) : "";
      ghostSuffix = ` · Ghost ${state.ghostMoveIndex}/${ghostHistory.length}${ghostCenter ? ` ${ghostCenter}` : ""}`;
    }
    const label = `Beat ${name} · ${Number(challenge.moves)} moves${ghostSuffix}`;
    ctx.save();
    ctx.font = `600 12px ${_fontSerif}`;
    const width = Math.min(layout.gridWidth, ctx.measureText(label).width + 28);
    const x = layout.offsetX + (layout.gridWidth - width) / 2;
    ctx.fillStyle = C.goldFill;
    fillRoundRect(ctx, x, y, width, 24, 12);
    ctx.strokeStyle = C.gold;
    ctx.lineWidth = 1;
    strokeRoundRect(ctx, x, y, width, 24, 12);
    ctx.fillStyle = C.goldEdge;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + width / 2, y + 12);
    ctx.restore();
  }

  function drawLeaderboardList(x, startY, maxItems) {
    layout.leaderboardRetry = { x: 0, y: 0, width: 0, height: 0 };
    if (maxItems <= 0) return;
    const minExact = Boolean(state.minExact);
    const minMoves = Number.isFinite(state.minMoves) ? state.minMoves : null;
    const entries = [...state.leaderboard]
      .filter((entry) => {
        if (!minExact || !minMoves) return true;
        if (!Number.isFinite(entry.moves)) return true;
        // Hide impossible scores (e.g. injected) once we have an exact minimum for the day.
        return entry.moves >= minMoves;
      })
      .sort((a, b) => {
      if (a.moves !== b.moves) return a.moves - b.moves;
      return (a.time || 0) - (b.time || 0);
    });

    const mobileList = layout.panelBelow;
    const listFont = mobileList ? `12px ${_fontSerif}` : `14px ${_fontSerif}`;
    const listBoldFont = mobileList ? `bold 12px ${_fontSerif}` : `bold 14px ${_fontSerif}`;
    const listLineH = mobileList ? 16 : 18;

    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.font = listFont;
    ctx.fillStyle = colors.ink;

    if (state.leaderboardLoading && entries.length === 0) {
      ctx.fillText("Loading", x, startY);
      return;
    }

    if (entries.length === 0 && state.leaderboardError) {
      ctx.fillStyle = C.error;
      ctx.fillText(state.leaderboardError, x, startY);
      if (maxItems < 3) return;
      const btnW = 62;
      const btnH = 20;
      const btnY = startY + 18;
      layout.leaderboardRetry = { x, y: btnY, width: btnW, height: btnH };
      ctx.fillStyle = C.btnBg;
      ctx.strokeStyle = C.btnStroke;
      ctx.lineWidth = 1;
      ctx.fillRect(x, btnY, btnW, btnH);
      ctx.strokeRect(x, btnY, btnW, btnH);
      ctx.fillStyle = C.ink;
      ctx.font = `12px ${_fontSerif}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Retry", x + btnW / 2, btnY + btnH / 2 + 1);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      return;
    }

    if (entries.length === 0) {
      ctx.fillText("No entries yet", x, startY);
      return;
    }

    const bestMoves = entries.length ? entries[0].moves : null;
    const nameEmojis = { "Geenybeans": "💚 ", "Abby": "🐰 ", "Jake": "⚖️ " };
    const highlightActive = Date.now() < state.highlightUntil;
    let medalRect = null;
    entries.slice(0, maxItems).forEach((entry, index) => {
      const crown = (bestMoves !== null && entry.moves === bestMoves) ? "👑 " : "";
      const flair = nameEmojis[entry.name] || "";
      const isYesterdayChampion =
        state.yesterdayTopFirstName &&
        entry.name === state.yesterdayTopFirstName &&
        Number.isFinite(entry.moves) &&
        bestMoves !== null &&
        entry.moves === bestMoves;
      const medal = isYesterdayChampion ? "🥇 " : "";
      const line = `${index + 1}. ${crown}${medal}${entry.name}${flair} — ${entry.moves}`;
      const y = startY + index * listLineH;
      if (isYesterdayChampion) {
        const prefix = `${index + 1}. ${crown}`;
        const prefixW = ctx.measureText(prefix).width;
        const medalW = ctx.measureText("🥇 ").width;
        medalRect = { x: x + prefixW, y: y - 2, width: medalW, height: listLineH + 2 };
      }
      const isHighlight =
        highlightActive &&
        state.playerName &&
        entry.name === state.playerName &&
        entry.moves === state.moves;
      // Fade long tail: full color for top 10, gradual fade 11-15, fully muted 15+
      const fadeAlpha = index < 10 ? 1 : Math.max(0.35, 1 - (index - 10) * 0.13);
      if (isHighlight) {
        const metrics = ctx.measureText(line);
        ctx.fillStyle = C.leaderHighlight;
        ctx.fillRect(x - 4, y - 1, metrics.width + 8, Math.max(14, listLineH - 2));
        ctx.fillStyle = colors.ink;
        ctx.font = listBoldFont;
        ctx.fillText(line, x, y);
        ctx.font = listFont;
      } else {
        ctx.fillStyle = _currentTheme === "light" ? `rgba(27,27,27,${fadeAlpha})` : `rgba(255,255,255,${fadeAlpha})`;
        ctx.fillText(line, x, y);
      }
    });
    layout.yesterdayMedalRect = medalRect;
    // Medal tooltip on hover or tap
    const showMedalTip = medalRect && (
      (_canvasLastMouse &&
        _canvasLastMouse.x >= medalRect.x && _canvasLastMouse.x <= medalRect.x + medalRect.width &&
        _canvasLastMouse.y >= medalRect.y && _canvasLastMouse.y <= medalRect.y + medalRect.height) ||
      performance.now() < _medalTooltipUntil
    );
    if (showMedalTip) {
        const tip = "First place yesterday";
        ctx.font = `11px ${_fontSerif}`;
        const tipW = ctx.measureText(tip).width + 10;
        const tipH = 18;
        const tipX = medalRect.x + medalRect.width / 2 - tipW / 2;
        const tipY = medalRect.y - tipH - 3;
        ctx.fillStyle = _currentTheme === "light" ? "rgba(27,27,27,0.88)" : "rgba(253,248,241,0.92)";
        fillRoundRect(ctx, tipX, tipY, tipW, tipH, 3);
        ctx.fillStyle = _currentTheme === "light" ? "#fdf8f1" : "#1d1d1f";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(tip, tipX + tipW / 2, tipY + tipH / 2);
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
    }
    ctx.fillStyle = colors.ink;

    if (state.leaderboardError) {
      const warningY = startY + Math.min(maxItems, entries.length) * listLineH + 4;
      const btnW = 56;
      const btnH = 18;
      const btnX = x + 120;
      const btnY = warningY - 1;
      ctx.fillStyle = "#8a3a30";
      ctx.font = `12px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(state.leaderboardError, x, warningY);
      layout.leaderboardRetry = { x: btnX, y: btnY, width: btnW, height: btnH };
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#ccc";
      ctx.lineWidth = 1;
      ctx.fillRect(btnX, btnY, btnW, btnH);
      ctx.strokeRect(btnX, btnY, btnW, btnH);
      ctx.fillStyle = colors.ink;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Retry", btnX + btnW / 2, btnY + btnH / 2 + 1);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
    }
  }

  function getPlayerRank(entries, name, moves) {
    if (!name || !entries.length) return null;
    const uniqueMoves = [...new Set(entries.map((entry) => entry.moves).filter(Number.isFinite))].sort(
      (a, b) => a - b
    );
    const rank = uniqueMoves.indexOf(moves);
    return rank >= 0 ? rank + 1 : null;
  }

  function canOfferResubmit() {
    if (!canSubmitCurrentRun()) return false;
    if (hasPendingSubmitForCurrentRun()) return false;
    return true;
  }

  // A queued score is exactly what the background retry exists to send, so
  // the retry path checks run eligibility without the pending-queue veto.
  function canSubmitCurrentRun() {
    if (state.practiceMode || state.customMode) return false;
    if (state.submitted || state.redoMode) return false;
    if (!state.leaderboardEligible || state.hintUsed) return false;
    if (!(state.completed || state.alreadyCompleted || state.solvedToday)) return false;
    if (!Number.isFinite(state.moves) || state.moves < 1) return false;
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length !== state.moves) return false;
    return true;
  }

  function hasCurrentRunLeaderboardEntry() {
    if (!state.playerName || !Array.isArray(state.leaderboard) || !state.leaderboard.length) return false;
    const normalizedName = sanitizeName(state.playerName);
    if (!normalizedName) return false;
    return state.leaderboard.some((entry) => {
      const entryName = sanitizeName(entry && entry.name ? entry.name : "");
      return entryName && entryName === normalizedName && Number(entry.moves) === Number(state.moves);
    });
  }

  function hasPendingSubmitForCurrentRun() {
    if (state.practiceMode || state.customMode || state.redoMode) return false;
    if (!state.dailyKey) return false;
    if (!Number.isFinite(state.moves) || state.moves < 1) return false;
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length !== state.moves) return false;
    const pending = loadPendingSubmit(state.dailyKey);
    if (!pending) return false;
    return pendingSubmitMatchesCurrentRun(pending);
  }

  function getSubmitButtonLabel() {
    return hasPendingSubmitForCurrentRun() ? "Sync now" : "Submit";
  }

  function getResubmitPromptText() {
    if (!canOfferResubmit()) return "";
    if (hasCurrentRunLeaderboardEntry()) return "";
    // During the initial celebration, the player has not skipped or failed a
    // submission yet. Invite them onto the board without implying data loss;
    // retain the stronger recovery copy once they return to the solved board.
    if (state.mode === "win" && !state.winAcknowledged) {
      return "Add to leaderboard · Tap Submit.";
    }
    return "Score missing. Tap Submit.";
  }

  function getMobilePrimarySidebarAction(showShare, showRedo) {
    if (canOfferResubmit()) return { key: "submit", label: getSubmitButtonLabel() };
    if (showShare) return { key: "share", label: "Share" };
    if (state.mode === "play" && !state.solvedToday) return { key: "hint", label: "Hint" };
    if (showRedo) return { key: "redo", label: "Redo" };
    return null;
  }

  let _resubmitPromptToastSeen = "";
  function maybeShowResubmitToast() {
    const promptText = getResubmitPromptText();
    if (!promptText) return;
    const toastKey = `${state.dailyKey}|${state.moves}|${state.playerName || ""}|${promptText}`;
    if (_resubmitPromptToastSeen === toastKey) return;
    _resubmitPromptToastSeen = toastKey;
    setShareToast(promptText, 3200);
  }

  function clearActivationNudgeTimer() {
    if (_activationNudgeTimer) {
      clearTimeout(_activationNudgeTimer);
      _activationNudgeTimer = null;
    }
  }

  function scheduleActivationNudge() {
    clearActivationNudgeTimer();
    if (state.customMode || state.practiceMode) return;
    if (state.mode !== "play") return;
    if (state.moves > 0) return;
    if (!state.dailyKey) return;
    _activationNudgeTimer = setTimeout(() => {
      _activationNudgeTimer = null;
      if (state.mode !== "play" || state.moves > 0 || state.customMode || state.practiceMode) return;
      if (state.activationNudgeShown) return;
      state.activationNudgeShown = true;
      const msFromStart = state.puzzleStartedAt > 0 ? Math.max(0, Date.now() - state.puzzleStartedAt) : null;
      setShareToast("Tip: tap any pivot dot to rotate.", 2600);
      logEvent("activation_nudge_shown", {
        ms_from_start: msFromStart,
        ...getLandingTelemetryData(),
      }, `${state.dailyKey}_${state.sessionId}_activation_nudge`);
    }, ACTIVATION_NUDGE_DELAY_MS);
  }

  function resetSidebarHitboxes() {
    const zeroRect = { x: 0, y: 0, width: 0, height: 0 };
    layout.archiveButton = { ...zeroRect };
    layout.hudPracticeLink = { ...zeroRect };
    layout.fullLeaderboardLink = { ...zeroRect };
    layout.leaderboardRetry = { ...zeroRect };
    layout.shareButton = { ...zeroRect };
    layout.submitButton = { ...zeroRect };
    layout.todayButton = { ...zeroRect };
    layout.redoButton = { ...zeroRect };
    layout.statsButton = { ...zeroRect };
    layout.soundButton = { ...zeroRect };
    layout.notifyButton = { ...zeroRect };
    layout.hintButton = { ...zeroRect };
    layout.undoButton = { ...zeroRect };
    layout.moreButton = { ...zeroRect };
    layout.exitCustomButton = { ...zeroRect };
    layout.customNextRoundButton = { ...zeroRect };
    layout.customEndRoundButton = { ...zeroRect };
    layout.customObserveModeButton = { ...zeroRect };
    layout.chatButton = { ...zeroRect };
    layout.observePlayerRects = [];
    layout.observeKickButtons = [];
    layout.observeRosterKickButtons = [];
    layout.observeRosterApproveButtons = [];
    layout.observeRosterDenyButtons = [];
    layout.observeHostActionButtons = [];
    layout.observeUndoKickButton = { ...zeroRect };
    layout.observeAutoButton = { ...zeroRect };
    layout.observeFilterButtons = [];
    layout.observeApproveAllButton = { ...zeroRect };
    layout.observeDenyAllButton = { ...zeroRect };
    layout.observeApproveButtons = [];
    layout.observeDenyButtons = [];
  }

  function drawSidebar() {
    const x = layout.panelX;
    const y = layout.panelY;
    const width = layout.panelWidth;
    const height = layout.panelHeight;

    // Reset shadow state to prevent artifact bleed from prior draw calls
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.fillStyle = C.panel;
    fillRoundRect(ctx, x, y, width, height, 8);
    ctx.strokeStyle = C.panelBorder;
    ctx.lineWidth = 1;
    strokeRoundRect(ctx, x, y, width, height, 8);

    // Decorative rule with centered dot break between grid and panel
    if (layout.panelBelow) {
      ctx.save();
      const ruleInset = 40;
      const ruleX0 = x + ruleInset;
      const ruleX1 = x + width - ruleInset;
      // Center the rule vertically between game board bottom and panel top
      const gridBottom = layout.offsetY + layout.gridHeight;
      const ruleY = Math.round((gridBottom + y) / 2);
      const cx = x + width / 2;
      const dotR = 2;
      const dotGap = 6;
      ctx.strokeStyle = C.mutedFaint;
      ctx.lineWidth = 0.75;
      // Left segment
      ctx.beginPath();
      ctx.moveTo(ruleX0, ruleY);
      ctx.lineTo(cx - dotGap - dotR, ruleY);
      ctx.stroke();
      // Right segment
      ctx.beginPath();
      ctx.moveTo(cx + dotGap + dotR, ruleY);
      ctx.lineTo(ruleX1, ruleY);
      ctx.stroke();
      // Center dot
      ctx.beginPath();
      ctx.arc(cx, ruleY, dotR, 0, Math.PI * 2);
      ctx.fillStyle = C.mutedFaint;
      ctx.fill();
      ctx.restore();
    }

    ctx.fillStyle = C.ink;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    let cursorY = y + 12;
    const paddingX = x + 12;
    // Reset all sidebar button rects — prevents stale hitboxes from intercepting clicks
    resetSidebarHitboxes();

    if (layout.panelBelow) {
      if (height < 160) {
        // Short landscape has room for one compact status strip, not the full
        // portrait card/list stack. The portrait geometry previously drew its
        // card, metrics and nav into the same 110px panel.
        const innerX = x + 18;
        const innerW = width - 36;
        const cardY = y + 8;
        const cardH = 52;
        ctx.fillStyle = C.card;
        fillRoundRect(ctx, x + 10, cardY, width - 20, cardH, 6);
        ctx.strokeStyle = C.cardStroke;
        ctx.lineWidth = 1;
        strokeRoundRect(ctx, x + 10, cardY, width - 20, cardH, 6);

        const columns = [0, 0.27, 0.56, 0.8].map((fraction) => innerX + innerW * fraction);
        const labels = state.customMode
          ? ["Moves", "Word", "Mode", "Room"]
          : ["Moves", "Word", "Streak", "Next"];
        const nextText = formatCountdownCompact(getMillisUntilNextPuzzle());
        const values = state.customMode
          ? [String(state.moves), state.targetWord, state.customDifficulty, state.customRoomCode || "Solo"]
          : [String(state.moves), state.targetWord, String(state.streakCount), nextText];
        for (let i = 0; i < labels.length; i += 1) {
          ctx.fillStyle = C.muted;
          ctx.font = `10px ${_fontSerif}`;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.fillText(labels[i], columns[i], cardY + 7);
          ctx.fillStyle = i === 1 ? C.gold : C.ink;
          ctx.font = i === 1 ? `600 18px ${_fontUI}` : `15px ${_fontSerif}`;
          ctx.fillText(values[i], columns[i], cardY + 24);
        }

        const compactItems = state.customMode
          ? [
              { key: "exitCustom", label: "Exit" },
              { key: "stats", label: "Stats" },
              ...(state.customMultiplayer ? [{ key: "observe", label: "Observe" }, { key: "chat", label: "Chat" }] : []),
              { key: "more", label: "More" },
            ]
          : [
              { key: "archive", label: "Archive" },
              { key: "leader", label: "Leader" },
              ...(state.mode === "play" && !state.solvedToday ? [{ key: "hint", label: "Hint" }] : []),
              ...(state.completed || state.submitted || state.alreadyCompleted ? [{ key: "share", label: "Share" }] : []),
              ...(!state.practiceMode && (state.alreadyCompleted || state.submitted || state.redoMode) ? [{ key: "redo", label: "Redo" }] : []),
              { key: "stats", label: "Stats" },
              { key: "more", label: "More" },
            ];
        const keyMap = { archive: "archiveButton", leader: "fullLeaderboardLink", hint: "hintButton", share: "shareButton", redo: "redoButton", stats: "statsButton", more: "moreButton", exitCustom: "exitCustomButton", observe: "customObserveModeButton", chat: "chatButton" };
        ctx.font = `11px ${_fontSerif}`;
        const separator = " · ";
        const separatorW = ctx.measureText(separator).width;
        const itemWidths = compactItems.map((item) => ctx.measureText(item.label).width);
        const totalW = itemWidths.reduce((sum, value) => sum + value, 0) + separatorW * Math.max(0, compactItems.length - 1);
        let navX = x + (width - totalW) / 2;
        const navY = y + height - 20;
        for (let i = 0; i < compactItems.length; i += 1) {
          const item = compactItems[i];
          const itemW = itemWidths[i];
          layout[keyMap[item.key]] = { x: navX - 5, y: navY - 11, width: itemW + 10, height: 22 };
          ctx.fillStyle = item.key === "hint" ? C.gold : C.muted;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(item.label, navX, navY);
          navX += itemW;
          if (i < compactItems.length - 1) {
            ctx.fillStyle = C.mutedFaint;
            ctx.fillText(separator, navX, navY);
            navX += separatorW;
          }
        }
        return;
      }
      const roomyPanel = layout.tabletPortraitStack || width >= 680;
      const showRedo = !state.practiceMode && (state.alreadyCompleted || state.submitted || state.redoMode);
      const showShare = !state.practiceMode && (state.completed || state.submitted || state.alreadyCompleted);
      const primaryAction = state.customMode ? null : getMobilePrimarySidebarAction(showShare, showRedo);
      const pendingCurrent = (() => {
        const pending = loadPendingSubmit(state.dailyKey);
        return pending && pendingSubmitMatchesCurrentRun(pending) ? pending : null;
      })();
      const reliabilityBadge = getReliabilityBadge(pendingCurrent);
      const hasMinLine = !state.customMode && (state.practiceMode || state.redoMode) && Number.isFinite(state.minMoves);

      const cardX = x + 10;
      const cardY = y + 10;
      const cardW = width - 20;

      let statusText = "";
      let statusSubtext = "";
      if (!state.customMode) {
        if (reliabilityBadge) {
          statusText = reliabilityBadge.text;
          statusSubtext = reliabilityBadge.subtext || "";
        } else if (state.submitted && !state.redoMode) {
          statusText = "Submitted";
          statusSubtext = "On today's leaderboard";
        } else if (state.alreadyCompleted && !state.practiceMode) {
          statusText = getResubmitPromptText() || "Solved today";
        } else if (!state.leaderboardEligible && state.hintUsed) {
          statusText = "Hint used";
        } else if (state.practiceMode) {
          statusText = "Practice mode";
        }
      } else if (state.customMultiplayer && state.customPhase !== "playing") {
        statusText = state.customPhase === "lobby" ? "Lobby open" : "Round complete";
      }
      const innerPadX = roomyPanel ? 14 : 12;
      const leftX = cardX + innerPadX;
      const innerW = cardW - innerPadX * 2;
      const colGap = roomyPanel ? 20 : 16;
      const colW = Math.max(88, (innerW - colGap) / 2);
      const rightX = leftX + colW + colGap;
      const rightEdge = rightX + colW;
      const rowLabelH = roomyPanel ? 14 : 13;
      const rowValueH = roomyPanel ? 36 : 32;
      const rowMetaH = roomyPanel ? 17 : 15;
      const rowGapTight = roomyPanel ? 6 : 5;
      const rowGap = roomyPanel ? 8 : 7;
      const cardTopPad = roomyPanel ? 12 : 11;
      const cardBottomPad = roomyPanel ? 12 : 10;
      const statusTopGap = roomyPanel ? 10 : 9;
      const statusH = statusText
        ? (statusSubtext ? (roomyPanel ? 31 : 28) : (roomyPanel ? 18 : 16))
        : 0;
      const labelY = cardY + cardTopPad;
      const valueY = labelY + rowLabelH + rowGapTight;
      const metaY = valueY + rowValueH + rowGap;
      const minLineY = metaY + rowMetaH;

      let detailBottomY = metaY + rowMetaH;
      if (hasMinLine) detailBottomY = minLineY + rowMetaH;

      const statusTextY = statusText ? detailBottomY + statusTopGap : 0;
      const cardContentBottomY = statusText ? statusTextY + statusH : detailBottomY;
      let cardH = Math.ceil(cardContentBottomY - cardY + cardBottomPad);
      if (state.customMode && state.customMultiplayer) cardH += 4;

      const cardR = 6;
      ctx.fillStyle = C.card;
      fillRoundRect(ctx, cardX, cardY, cardW, cardH, cardR);
      ctx.strokeStyle = C.cardStroke;
      ctx.lineWidth = 1;
      strokeRoundRect(ctx, cardX, cardY, cardW, cardH, cardR);
      ctx.fillStyle = C.muted;
      const labelFS = roomyPanel ? 13 : 12;
      ctx.font = `${labelFS}px ${_fontSerif}`;
      ctx.fillText("Moves", leftX, labelY);
      ctx.fillText("Word", rightX, labelY);
      drawGoldAccentLine(leftX, labelY + labelFS + 3, ctx.measureText("Moves").width);
      drawGoldAccentLine(rightX, labelY + labelFS + 3, ctx.measureText("Word").width);
      ctx.fillStyle = C.muted;
      ctx.font = `${roomyPanel ? 24 : 21}px ${_fontSerif}`;
      ctx.fillText(String(state.moves), leftX, valueY + 2);
      ctx.fillStyle = _currentTheme === "dark" ? C.highlight : C.yellowEdge;
      const wordFS = roomyPanel ? 30 : 27;
      ctx.font = `600 ${wordFS}px ${_fontUI}`;
      ctx.fillText(state.targetWord, rightX, valueY);
      ctx.fillStyle = C.muted;
      ctx.font = `${roomyPanel ? 12 : 11}px ${_fontSerif}`;
      if (state.customMode) {
        const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
        const modeLabel = state.customCommunityCode
          ? `Made by ${state.customCreatorName || "a player"}`
          : `${state.customMultiplayer ? "Custom" : "Practice"} · ${diffLabel}`;
        ctx.fillText(modeLabel, leftX, metaY);
        if (state.customMultiplayer) {
          const activePlayers = Object.values(state.customPlayers).filter((p) => !p.isObserver).length;
          const observers = Object.values(state.customPlayers).filter((p) => p.isObserver).length;
          ctx.textAlign = "right";
          ctx.fillText(`${activePlayers} playing · ${observers} watching`, rightEdge, metaY);
          ctx.textAlign = "left";
        }
      } else {
        const nextText = formatCountdownCompact(getMillisUntilNextPuzzle());
        ctx.fillText(`Streak ${state.streakCount}`, leftX, metaY);
        ctx.textAlign = "right";
        ctx.fillText(`Next ${nextText}`, rightEdge, metaY);
        ctx.textAlign = "left";
        if (hasMinLine) {
          const label = state.minExact ? "Min" : "Par";
          const delta = Math.max(0, state.moves - state.minMoves);
          ctx.fillStyle = C.gold;
          ctx.fillText(`${label} ${state.minMoves} · +${delta}`, leftX, minLineY);
        }
      }

      if (statusText) {
        const statusPadX = roomyPanel ? 9 : 8;
        ctx.font = `${roomyPanel ? 11 : 10}px ${_fontSerif}`;
        const statusW = innerW;
        const statusX = leftX;
        const tone = statusText.includes("Tap")
          ? C.statusWarn
          : statusText.toLowerCase().includes("offline")
          ? C.statusError
          : statusText.toLowerCase().includes("sync delayed")
          ? C.statusWarn
          : C.statusDefault;
        ctx.fillStyle = tone.bg;
        ctx.strokeStyle = tone.stroke;
        ctx.lineWidth = 1;
        fillRoundRect(ctx, statusX, statusTextY, statusW, statusH, 4);
        strokeRoundRect(ctx, statusX, statusTextY, statusW, statusH, 4);
        ctx.fillStyle = tone.text;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(statusText, statusX + statusPadX, statusTextY + 2);
        if (statusSubtext) {
          ctx.fillStyle = C.muted;
          ctx.font = `${roomyPanel ? 10 : 9}px ${_fontSerif}`;
          ctx.fillText(statusSubtext, statusX + statusPadX, statusTextY + (roomyPanel ? 15 : 13));
        }
        ctx.textBaseline = "top";
      }
      let infoY = cardY + cardH + (roomyPanel ? 12 : 10);

      if (!state.customMode && (state.metrics.solvers !== null || state.metrics.median !== null)) {
        const metricsParts = [];
        if (state.metrics.solvers !== null) metricsParts.push(`${state.metrics.solvers} solved`);
        if (state.metrics.boardEntries !== null) metricsParts.push(`${state.metrics.boardEntries} on board`);
        if (state.metrics.median !== null) metricsParts.push(`Median ${state.metrics.median} moves`);
        ctx.fillStyle = C.muted;
        ctx.font = `${roomyPanel ? 12 : 11}px ${_fontSerif}`;
        ctx.fillText(metricsParts.join(" · "), leftX, infoY);
        infoY += roomyPanel ? 17 : 15;
      }

      if (state.customMode && state.customMultiplayer && state.customPhase === "playing") {
        const effectivelyObserver = state.customIsObserver || (!state.customIsHost && isCurrentCustomPlayerObserver());
        ctx.font = `${roomyPanel ? 12 : 11}px ${_fontSerif}`;
        ctx.fillStyle = C.mutedSoft;
        if (state.customObserveMode || effectivelyObserver || state.customClassroomMode) {
          ctx.fillText("Live boards above", leftX, infoY);
          infoY += 14;
        }
      } else if (!state.customMode) {
        ctx.fillStyle = C.muted;
        ctx.font = `${roomyPanel ? 12 : 11}px ${_fontSerif}`;
        ctx.fillText("Today", leftX, infoY);
        infoY += roomyPanel ? 16 : 14;
      }
      const listStartY = infoY;

      const buttonHeight = roomyPanel ? 24 : 22;
      const buttonItems = [];
      if (state.customMode) {
        buttonItems.push({ key: "exitCustom", label: "Exit" });
        buttonItems.push({ key: "stats", label: "Stats" });
        if (state.customMultiplayer) {
          const effectivelyObserver = state.customIsObserver || (!state.customIsHost && isCurrentCustomPlayerObserver());
          const observeLabel = effectivelyObserver ? "Lobby" : (state.customObserveMode ? "Play" : "Observe");
          buttonItems.push({ key: "observe", label: observeLabel });
          buttonItems.push({ key: "chat", label: state.customChatUnread > 0 ? `Chat (${Math.min(state.customChatUnread, 99)})` : "Chat" });
        }
        buttonItems.push({ key: "more", label: "More" });
      } else {
        buttonItems.push({ key: "archive", label: "Archive" });
        buttonItems.push({ key: "leader", label: "Leader" });
        if (primaryAction) buttonItems.push(primaryAction);
        if (state.practiceMode) {
          buttonItems.push({ key: "today", label: "Today" });
        } else if (showRedo && (!primaryAction || primaryAction.key !== "redo")) {
          buttonItems.push({ key: "redo", label: "Redo" });
        }
        buttonItems.push({ key: "stats", label: "Stats" });
        if (state.mode === "play" && !state.solvedToday && (!primaryAction || primaryAction.key !== "hint")) {
          buttonItems.push({ key: "hint", label: "Hint" });
        }
        if (canUndoMove()) {
          buttonItems.push({ key: "undo", label: "Undo" });
        }
        buttonItems.push({ key: "more", label: "More" });
      }
      // --- Balanced grid layout for mobile buttons (prevents "tacked on" last-row look) ---
      const availBtnW = innerW;
      const buttonGapX = roomyPanel ? 8 : 7;
      const buttonGapY = roomyPanel ? 8 : 7;
      const buttonCols = buttonItems.length <= 2
        ? buttonItems.length
        : (buttonItems.length === 4 ? 2 : 3);
      const buttonCellW = Math.floor((availBtnW - (buttonCols - 1) * buttonGapX) / Math.max(1, buttonCols));
      const btnRows = [];
      for (let i = 0; i < buttonItems.length; i += Math.max(1, buttonCols)) {
        btnRows.push(buttonItems.slice(i, i + Math.max(1, buttonCols)));
      }
      const buttonsBlockH = btnRows.length * buttonHeight + Math.max(0, btnRows.length - 1) * buttonGapY;
      const clusterInset = cardX - x;
      const clusterPad = roomyPanel ? 8 : 6;
      const clusterX = cardX;
      const clusterW = cardW;
      const clusterH = buttonsBlockH + clusterPad * 2;
      const clusterY = y + height - clusterInset - clusterH;
      const buttonY = clusterY + clusterPad;

      const warningText = state.practiceMode
        ? "Practice mode — no leaderboard entry"
        : state.redoMode
        ? "Redo mode — no leaderboard entry"
        : !state.leaderboardEligible && state.hintUsed
        ? "Hint used — no leaderboard entry"
        : "";
      const resubmitPromptText = getResubmitPromptText();
      const noteCount = (resubmitPromptText ? 1 : 0) + (warningText ? 1 : 0);
      const notesHeight = noteCount ? noteCount * 18 : 0;
      const notesStartY = buttonY - notesHeight - 4;
      const warningY = notesStartY + (resubmitPromptText ? 18 : 0);
      const listEndY = noteCount ? notesStartY - 6 : buttonY - 6;
      const listRowHeight = roomyPanel ? 19 : 18;
      const listMaxHeight = listEndY - listStartY;
      const listMax = Math.max(0, Math.floor(listMaxHeight / listRowHeight));
      const desiredLeaders = 3;
      const listLimit = state.customMode
        ? 0
        : (listMax >= desiredLeaders ? Math.min(4, listMax) : Math.max(0, listMax));
      drawLeaderboardList(leftX, listStartY, listLimit);

      if (resubmitPromptText && notesStartY >= listStartY) {
        ctx.fillStyle = C.warning;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(resubmitPromptText, leftX, notesStartY);
      }

      if (warningText && warningY >= listStartY) {
        ctx.fillStyle = C.error;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(warningText, leftX, warningY);
      }

      // Draw hint distance in sidebar if active
      if (state.hintDistanceVisible && Number.isFinite(state.hintDistance)) {
        const hintInfoY = (noteCount ? notesStartY : warningY) - 16;
        ctx.fillStyle = colors.highlight;
        ctx.font = `12px ${_fontSerif}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`${state.hintDistance} moves away`, leftX, hintInfoY);
      }

      const layoutKeyMap = { archive: "archiveButton", leader: "fullLeaderboardLink", submit: "submitButton", share: "shareButton", today: "todayButton", readme: "readmeButton", redo: "redoButton", stats: "statsButton", notify: "notifyButton", sound: "soundButton", hint: "hintButton", undo: "undoButton", hard: "hardButton", more: "moreButton", exitCustom: "exitCustomButton", observe: "customObserveModeButton", chat: "chatButton" };

      // Text nav with dot separators and flanking rules
      const navFontSize = roomyPanel ? 13 : 12;
      ctx.font = `${navFontSize}px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const sep = " \u00b7 ";
      const allItems = buttonItems.map(b => b.label);
      // Measure each item and separator to lay out as a single line (or wrap into rows)
      const itemWidths = allItems.map(label => ctx.measureText(label).width);
      const sepWidth = ctx.measureText(sep).width;
      // Split into rows that fit available width
      const navRows = [];
      let curRow = [];
      let curRowW = 0;
      for (let i = 0; i < allItems.length; i++) {
        const addW = curRow.length > 0 ? sepWidth + itemWidths[i] : itemWidths[i];
        if (curRow.length > 0 && curRowW + addW > innerW) {
          navRows.push(curRow);
          curRow = [i];
          curRowW = itemWidths[i];
        } else {
          curRow.push(i);
          curRowW += addW;
        }
      }
      if (curRow.length) navRows.push(curRow);

      const navRowHeight = roomyPanel ? 22 : 20;
      const navBlockH = navRows.length * navRowHeight;
      const navStartY = clusterY + (clusterH - navBlockH) / 2;

      for (let ri = 0; ri < navRows.length; ri++) {
        const row = navRows[ri];
        // Measure total row width
        let totalW = 0;
        for (let j = 0; j < row.length; j++) {
          totalW += itemWidths[row[j]];
          if (j < row.length - 1) totalW += sepWidth;
        }
        const rowCenterY = navStartY + ri * navRowHeight + navRowHeight / 2;
        // Flanking rules
        const ruleGap = 8;
        const ruleLen = Math.min(30, (innerW - totalW - ruleGap * 2) / 2);
        const rowStartX = x + (width - totalW) / 2;
        if (ruleLen > 6) {
          ctx.strokeStyle = C.mutedFaint;
          ctx.lineWidth = 0.75;
          ctx.beginPath();
          ctx.moveTo(rowStartX - ruleGap - ruleLen, rowCenterY);
          ctx.lineTo(rowStartX - ruleGap, rowCenterY);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(rowStartX + totalW + ruleGap, rowCenterY);
          ctx.lineTo(rowStartX + totalW + ruleGap + ruleLen, rowCenterY);
          ctx.stroke();
        }
        let cx = rowStartX;
        for (let j = 0; j < row.length; j++) {
          const idx = row[j];
          const btn = buttonItems[idx];
          const w = itemWidths[idx];
          // Store hit rect for tap handling
          const rect = { x: cx - 4, y: rowCenterY - navRowHeight / 2, width: w + 8, height: navRowHeight };
          if (layoutKeyMap[btn.key]) layout[layoutKeyMap[btn.key]] = rect;
          const isPrimary = !!primaryAction && btn.key === primaryAction.key;
          const chatHighlight = btn.key === "chat" && state.customChatUnread > 0;
          ctx.fillStyle = isPrimary || chatHighlight ? C.gold : C.muted;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(btn.label, cx, rowCenterY);
          cx += w;
          if (j < row.length - 1) {
            ctx.fillStyle = C.mutedFaint;
            ctx.fillText(sep, cx, rowCenterY);
            cx += sepWidth;
          }
        }
      }
    } else {
      const compact = height < 400; // iPhone landscape or very short viewport

      if (!compact) {
        drawBrandLockup(ctx, x + width / 2, cursorY, {
          align: "center",
          iconSize: BRAND_LOCKUP.iconSize,
          gap: BRAND_LOCKUP.gap,
          fontSize: BRAND_LOCKUP.fontSize,
          letterSpacing: BRAND_LOCKUP.letterSpacing,
          color: BRAND_LOCKUP.color,
        });
        cursorY += BRAND_LOCKUP.iconSize + 14;
      }

      ctx.fillStyle = colors.ink;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";

      if (compact) {
        // Compact: Moves + Word on one line
        ctx.font = `14px ${_fontSerif}`;
        ctx.fillText("Moves", paddingX, cursorY);
        ctx.fillText("Word", paddingX + 72, cursorY);
        drawGoldAccentLine(paddingX, cursorY + 14 + 2, ctx.measureText("Moves").width);
        drawGoldAccentLine(paddingX + 72, cursorY + 14 + 2, ctx.measureText("Word").width);
        cursorY += 16;
        ctx.fillStyle = C.muted;
        ctx.font = `18px ${_fontSerif}`;
        ctx.fillText(String(state.moves), paddingX, cursorY);
        ctx.fillStyle = _currentTheme === "dark" ? colors.highlight : C.yellowEdge;
        ctx.font = `600 20px ${_fontUI}`;
        ctx.fillText(state.targetWord, paddingX + 72, cursorY);
        cursorY += 26;
      } else {
        ctx.font = `15px ${_fontSerif}`;
        ctx.fillText("Moves", paddingX, cursorY);
        drawGoldAccentLine(paddingX, cursorY + 15 + 3, ctx.measureText("Moves").width);
        cursorY += 20;
        ctx.fillStyle = C.muted;
        ctx.font = `22px ${_fontSerif}`;
        ctx.fillText(String(state.moves), paddingX, cursorY);
        cursorY += 36;
        ctx.font = `14px ${_fontSerif}`;
        ctx.fillStyle = colors.ink;
        ctx.fillText("Word", paddingX, cursorY);
        drawGoldAccentLine(paddingX, cursorY + 14 + 3, ctx.measureText("Word").width);
        cursorY += 20;
        ctx.fillStyle = _currentTheme === "dark" ? colors.highlight : C.yellowEdge;
        ctx.font = `600 26px ${_fontUI}`;
        ctx.fillText(state.targetWord, paddingX, cursorY);
        cursorY += 38;
      }

      if (state.customMode) {
        ctx.save();
        ctx.globalAlpha = 0.75;
        ctx.font = `12px ${_fontSerif}`;
        ctx.fillStyle = colors.ink;
        const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
        ctx.fillText(`Custom \u00b7 ${diffLabel}`, paddingX, cursorY);
        ctx.restore();
        cursorY += 16;
        if (state.customMultiplayer) {
          const pCount = Object.keys(state.customPlayers).length;
          ctx.fillStyle = colors.ink;
          ctx.font = `12px ${_fontSerif}`;
          ctx.fillText(`${pCount} player${pCount !== 1 ? "s" : ""} \u00b7 Round ${state.customRoundNumber}`, paddingX, cursorY);
          cursorY += 16;
        }
      } else if (!compact) {
        ctx.save();
        ctx.globalAlpha = 0.75;
        ctx.font = `12px ${_fontSerif}`;
        ctx.fillStyle = colors.ink;
        ctx.fillText("Same puzzle for everyone, every day.", paddingX, cursorY);
        ctx.restore();
        cursorY += 28;
      }

      // Dot-break separator: ── o ──
      const sepLeft = paddingX;
      const sepRight = x + width - 12;
      const sepMid = (sepLeft + sepRight) / 2;
      const dotR = 2;
      const dotGap = 6;
      ctx.strokeStyle = C.panelStroke;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sepLeft, cursorY);
      ctx.lineTo(sepMid - dotGap - dotR, cursorY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sepMid + dotGap + dotR, cursorY);
      ctx.lineTo(sepRight, cursorY);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(sepMid, cursorY, dotR, 0, Math.PI * 2);
      ctx.fillStyle = C.panelStroke;
      ctx.fill();
      cursorY += compact ? 8 : 16;

    ctx.fillStyle = C.ink;
    ctx.font = compact ? `12px ${_fontSerif}` : `14px ${_fontSerif}`;
    if (Number.isFinite(state.minMoves)) {
      const label = state.minExact ? "Min" : "Par";
      const delta = Math.max(0, state.moves - state.minMoves);
      if (compact) {
        ctx.fillStyle = colors.highlight;
        ctx.fillText(`${label}: ${state.minMoves}`, paddingX, cursorY);
        ctx.fillStyle = colors.ink;
        ctx.fillText(`Over: ${delta}`, paddingX + 72, cursorY);
        cursorY += 16;
      } else {
        ctx.fillStyle = colors.highlight;
        ctx.fillText(`${label}: ${state.minMoves}`, paddingX, cursorY);
        cursorY += 18;
        ctx.fillStyle = colors.ink;
        ctx.fillText(`Over: ${delta}`, paddingX, cursorY);
        cursorY += 22;
      }
    }

    if (!state.practiceMode && !state.customMode) {
      ctx.fillText(`Streak: ${state.streakCount} days`, paddingX, cursorY);
      cursorY += compact ? 14 : 20;
    }

    if (!state.practiceMode && !state.customMode && state.submitted && !state.redoMode) {
      const receiptText = formatSubmissionReceipt();
      if (receiptText) {
        ctx.fillStyle = C.muted;
        ctx.font = compact ? `11px ${_fontSerif}` : `12px ${_fontSerif}`;
        ctx.fillText(`Submitted ${receiptText}`, paddingX, cursorY);
        ctx.fillStyle = C.ink;
        ctx.font = compact ? `12px ${_fontSerif}` : `14px ${_fontSerif}`;
        cursorY += compact ? 14 : 18;
      }
    }

    if (!state.practiceMode && !state.customMode && !compact) {
      ctx.fillText(formatCountdown(getMillisUntilNextPuzzle()), paddingX, cursorY);
      cursorY += 20;
    }

    if (!state.customMode && (state.metrics.solvers !== null || state.metrics.median !== null)) {
      const metricsParts = [];
      if (state.metrics.solvers !== null) {
        metricsParts.push(`Solvers: ${state.metrics.solvers}`);
      }
      if (state.metrics.boardEntries !== null) {
        metricsParts.push(`On board: ${state.metrics.boardEntries}`);
      }
      if (state.metrics.median !== null) {
        metricsParts.push(`Median: ${state.metrics.median} moves`);
      }
      if (Number.isFinite(state.minMoves) && !compact && (state.practiceMode || state.redoMode)) {
        metricsParts.push(`${state.minExact ? "Min" : "Par"}: ${state.minMoves}`);
      }
      ctx.fillStyle = C.ink;
      ctx.font = `12px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      const metricsLine = metricsParts.join("  ");
      if (layout.duo && ctx.measureText(metricsLine).width > x + width - 12 - paddingX) {
        // iPhone Duo's outer-landscape rail is ~170pt wide; stack rather than clip.
        for (const part of metricsParts) {
          ctx.fillText(part, paddingX, cursorY);
          cursorY += 14;
        }
        cursorY += compact ? 0 : 6;
      } else {
        ctx.fillText(metricsLine, paddingX, cursorY);
        cursorY += compact ? 14 : 20;
      }
    }

    if (!state.customMode) {
      ctx.fillText("Today", paddingX, cursorY);
      cursorY += compact ? 14 : 18;
      const desktopReliability = getReliabilityBadge();
      if (desktopReliability) {
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = desktopReliability.kind === "degraded"
          ? C.warning
          : desktopReliability.kind === "offline" || desktopReliability.kind === "offline_pending"
          ? C.error
          : C.muted;
        ctx.fillText(desktopReliability.text, paddingX, cursorY);
        cursorY += 14;
      } else if (state.submitted && !state.redoMode) {
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = C.muted;
        ctx.fillText("Submitted", paddingX, cursorY);
        cursorY += 14;
      }
    }

      const showRedo = !state.practiceMode && (state.alreadyCompleted || state.submitted || state.redoMode);
      const showShare = !state.practiceMode && (state.completed || state.submitted || state.alreadyCompleted);
      const primaryAction = state.customMode ? null : getMobilePrimarySidebarAction(showShare, showRedo);
      const buttonHeight = 22;
      const buttonGap = 6;
      const buttonItems = [];
      if (state.customMode) {
        buttonItems.push({ key: "exitCustom", label: "Exit" });
        buttonItems.push({ key: "stats", label: "Stats" });
        if (state.customMultiplayer) {
          const effectivelyObserver = state.customIsObserver || (!state.customIsHost && isCurrentCustomPlayerObserver());
          const observeLabel = effectivelyObserver ? "Lobby" : (state.customObserveMode ? "Play" : "Observe");
          buttonItems.push({ key: "observe", label: observeLabel });
          buttonItems.push({ key: "chat", label: state.customChatUnread > 0 ? `Chat (${Math.min(state.customChatUnread, 99)})` : "Chat" });
        }
        buttonItems.push({ key: "more", label: "More" });
      } else {
      buttonItems.push({ key: "archive", label: "Archive" });
      buttonItems.push({ key: "leader", label: "Leader" });
      if (canOfferResubmit()) {
        buttonItems.push({ key: "submit", label: getSubmitButtonLabel() });
      }
      if (showShare) {
        buttonItems.push({ key: "share", label: "Share" });
      }
      if (state.practiceMode) {
        buttonItems.push({ key: "today", label: "Today" });
      } else if (showRedo) {
        buttonItems.push({ key: "redo", label: "Redo" });
      }
      buttonItems.push({ key: "stats", label: "Stats" });
      if (state.mode === "play" && !state.solvedToday) buttonItems.push({ key: "hint", label: "Hint" });
      if (canUndoMove()) buttonItems.push({ key: "undo", label: "Undo" });
      buttonItems.push({ key: "more", label: "More" });
      }

      // --- Multiplayer sidebar leaderboard (desktop, during play) ---
      if (state.customMode && state.customMultiplayer && state.customPhase === "playing") {
        const effectivelyObserver = state.customIsObserver || (!state.customIsHost && isCurrentCustomPlayerObserver());
        const players = Object.values(state.customPlayers).filter((p) => !p.isObserver).length;
        const observers = Object.values(state.customPlayers).filter((p) => p.isObserver).length;
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = C.mutedSoft;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`Players: ${players}`, paddingX, cursorY);
        cursorY += 14;
        ctx.fillText(`Watching: ${observers}`, paddingX, cursorY);
        cursorY += 14;
        if (state.customObserveMode || effectivelyObserver || state.customClassroomMode) {
          ctx.fillStyle = C.mutedSoft;
          ctx.fillText("Live boards in main view", paddingX, cursorY);
          cursorY += 14;
        }
        cursorY += 2;
      }

      // --- Fixed-grid layout for desktop buttons (clean alignment across rows) ---
      const dAvailBtnW = width - 24;
      const dButtonGapX = 6;
      const dButtonGapY = 6;
      const dButtonCols = buttonItems.length <= 2
        ? buttonItems.length
        : (buttonItems.length === 4 ? 2 : 3);
      const dCellW = Math.floor((dAvailBtnW - (Math.max(1, dButtonCols) - 1) * dButtonGapX) / Math.max(1, dButtonCols));
      const dBtnRows = [];
      for (let i = 0; i < buttonItems.length; i += Math.max(1, dButtonCols)) {
        dBtnRows.push(buttonItems.slice(i, i + Math.max(1, dButtonCols)));
      }

      const dButtonsBlockH = dBtnRows.length * buttonHeight + Math.max(0, dBtnRows.length - 1) * dButtonGapY;
      const buttonY = y + height - dButtonsBlockH - 10;

      const warningText = state.practiceMode
        ? "Practice mode — no leaderboard entry"
        : state.redoMode
        ? "Redo mode — no leaderboard entry"
        : !state.leaderboardEligible && state.hintUsed
        ? "Hint used — no leaderboard entry"
        : "";
      const resubmitPromptText = getResubmitPromptText();
      let hintY = buttonY - 20;

      // Show rank above buttons
      if (!state.practiceMode && state.submitted && state.playerName && state.leaderboardEligible) {
        const rank = getPlayerRank(state.leaderboard, state.playerName, state.moves);
        if (rank) {
          ctx.font = `italic 11px ${_fontSerif}`;
          ctx.fillStyle = C.mutedSoft;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          const pctSb = computePercentile(state.moves);
          ctx.fillText(pctSb !== null ? `#${rank} \u00b7 Top ${pctSb}%` : `Your rank: #${rank}`, paddingX, hintY);
          hintY -= 16;
        }
      }

      const warningY = warningText ? hintY - 18 : hintY;
      const resubmitPromptY = resubmitPromptText ? warningY - 18 : warningY;
      const listEndY = (resubmitPromptText ? resubmitPromptY : (warningText ? warningY : hintY)) - 6;
      if (resubmitPromptText && resubmitPromptY >= cursorY) {
        ctx.fillStyle = C.warning;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(resubmitPromptText, paddingX, resubmitPromptY);
      }
      if (warningText && warningY >= cursorY) {
        ctx.fillStyle = C.error;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(warningText, paddingX, warningY);
      }

      // Draw hint distance in sidebar if active
      if (state.hintDistanceVisible && Number.isFinite(state.hintDistance)) {
        const hintInfoDesktopY = warningText ? warningY - 16 : hintY - 16;
        ctx.fillStyle = colors.highlight;
        ctx.font = `12px ${_fontSerif}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`${state.hintDistance} moves away`, paddingX, hintInfoDesktopY);
      }

      const availableHeight = listEndY - cursorY;
      const maxItems = state.customMode ? 0 : Math.max(0, Math.floor(availableHeight / 18));
      drawLeaderboardList(paddingX, cursorY, maxItems);

      const dLayoutKeyMap = { archive: "archiveButton", leader: "fullLeaderboardLink", submit: "submitButton", share: "shareButton", today: "todayButton", readme: "readmeButton", redo: "redoButton", stats: "statsButton", notify: "notifyButton", sound: "soundButton", hint: "hintButton", undo: "undoButton", hard: "hardButton", more: "moreButton", exitCustom: "exitCustomButton", observe: "customObserveModeButton", chat: "chatButton" };

      // Text nav with dot separators for desktop sidebar
      ctx.font = `12px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const dSep = " \u00b7 ";
      const dSepW = ctx.measureText(dSep).width;
      const dItemWidths = buttonItems.map(b => ctx.measureText(b.label).width);
      // Wrap into rows that fit sidebar
      const dNavRows = [];
      let dCurRow = [];
      let dCurW = 0;
      for (let i = 0; i < buttonItems.length; i++) {
        const addW = dCurRow.length > 0 ? dSepW + dItemWidths[i] : dItemWidths[i];
        if (dCurRow.length > 0 && dCurW + addW > dAvailBtnW) {
          dNavRows.push(dCurRow);
          dCurRow = [i];
          dCurW = dItemWidths[i];
        } else {
          dCurRow.push(i);
          dCurW += addW;
        }
      }
      if (dCurRow.length) dNavRows.push(dCurRow);

      const dNavRowH = 20;
      const dNavBlockH = dNavRows.length * dNavRowH;
      const dNavStartY = buttonY;

      for (let ri = 0; ri < dNavRows.length; ri++) {
        const row = dNavRows[ri];
        let totalW = 0;
        for (let j = 0; j < row.length; j++) {
          totalW += dItemWidths[row[j]];
          if (j < row.length - 1) totalW += dSepW;
        }
        const rowCY = dNavStartY + ri * dNavRowH + dNavRowH / 2;
        let dx = x + (width - totalW) / 2;
        for (let j = 0; j < row.length; j++) {
          const idx = row[j];
          const btn = buttonItems[idx];
          const w = dItemWidths[idx];
          const rect = { x: dx - 4, y: rowCY - dNavRowH / 2, width: w + 8, height: dNavRowH };
          if (dLayoutKeyMap[btn.key]) layout[dLayoutKeyMap[btn.key]] = rect;
          const isPrimary = !!primaryAction && btn.key === primaryAction.key;
          const chatHighlight = btn.key === "chat" && state.customChatUnread > 0;
          ctx.fillStyle = isPrimary || chatHighlight ? C.gold : C.muted;
          ctx.fillText(btn.label, dx, rowCY);
          dx += w;
          if (j < row.length - 1) {
            ctx.fillStyle = C.mutedFaint;
            ctx.fillText(dSep, dx, rowCY);
            dx += dSepW;
          }
        }
      }
    }
  }

  function drawEditionHeader() {
    if (state.customMode && state.customMultiplayer) return;
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    const day = isArchive ? getDayNumberForKey(state.dailyKey)
      : (state.practiceMode || (state.customMode && !state.customMultiplayer)) ? null
      : getDayNumber();
    const cx = layout.offsetX + layout.gridWidth / 2;
    const mobile = layout.panelBelow || layout.shortLandscape;

    // Scale font sizes when header space is tight
    const headerSpace = layout.offsetY - layout.margin;
    const nominalWordSize = mobile ? 36 : 48;
    const nominalEditionSize = mobile ? 12 : 15;
    // If header space < 70px on mobile (or 90px desktop), scale down proportionally
    const minSpace = mobile ? 50 : 70;
    const comfortSpace = mobile ? 78 : 100;
    const scaleFactor = headerSpace < comfortSpace
      ? Math.max(minSpace / comfortSpace, headerSpace / comfortSpace)
      : 1;
    const wordFontSize = Math.round(nominalWordSize * scaleFactor);
    const editionFontSize = Math.max(9, Math.round(nominalEditionSize * scaleFactor));

    const wordGap = Math.round((mobile ? 8 : 14) * scaleFactor);
    const lineGap = Math.round((mobile ? 6 : 10) * scaleFactor);
    const editionGap = Math.round((mobile ? 5 : 10) * scaleFactor);

    // On desktop, pin edition header to align with sidebar brand lockup
    // On mobile, position relative to grid top
    let editionBottom, lineY, wordBottom;
    if (!mobile) {
      const brandCenterY = layout.margin + 12 + BRAND_LOCKUP.iconSize / 2;
      editionBottom = brandCenterY + editionFontSize / 2;
      lineY = editionBottom + editionGap;
      wordBottom = lineY + lineGap + wordFontSize;
    } else {
      wordBottom = layout.offsetY - wordGap;
      const wordTop = wordBottom - wordFontSize;
      // Place edition halfway between word top and top of canvas margin
      editionBottom = Math.round((layout.margin + wordTop) / 2 + editionFontSize / 2);
      // Rule sits midway between edition text and word text
      lineY = Math.round((editionBottom + wordTop) / 2);
    }

    // Custom layouts reserve no header strip, so on stacked layouts there can
    // be no room above the grid; skip the label rather than clip it at the
    // canvas top or draw it across the first row of tiles.
    if (editionBottom - editionFontSize < 0 || lineY >= layout.offsetY) return;

    // Edition label
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.font = `${editionFontSize}px ${_fontSerif}`;
    ctx.letterSpacing = "1.5px";
    ctx.fillStyle = C.muted;
    ctx.fillText(day ? `Edition No. ${day}` : "Bespoke Edition", cx, editionBottom);

    // Dot-break rule between edition and word: ── ○ ──
    // In a tight custom header the midpoint lands inside the label's glyphs;
    // drop the rule there instead of striking through the text.
    if (lineY <= editionBottom + 2) {
      ctx.restore();
      return;
    }
    const ruleW = mobile ? 60 : 80;
    const dotR = 1.5;
    const dotGap = 5;
    ctx.strokeStyle = C.mutedFaint;
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(cx - ruleW / 2, lineY);
    ctx.lineTo(cx - dotGap - dotR, lineY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + dotGap + dotR, lineY);
    ctx.lineTo(cx + ruleW / 2, lineY);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, lineY, dotR, 0, Math.PI * 2);
    ctx.fillStyle = C.mutedFaint;
    ctx.fill();

    ctx.restore();
  }

  function drawWordHeader() {
    if (state.customMode) return;
    const cx = layout.offsetX + layout.gridWidth / 2;
    const mobile = layout.panelBelow || layout.shortLandscape;

    // Scale to match drawEditionHeader
    const headerSpace = layout.offsetY - layout.margin;
    const nominalSize = mobile ? 36 : 48;
    const comfortSpace = mobile ? 78 : 100;
    const minSpace = mobile ? 50 : 70;
    const scaleFactor = headerSpace < comfortSpace
      ? Math.max(minSpace / comfortSpace, headerSpace / comfortSpace)
      : 1;
    const fontSize = Math.round(nominalSize * scaleFactor);
    const wordGap = Math.round((mobile ? 8 : 14) * scaleFactor);

    let wordY;
    if (!mobile) {
      // Place word halfway between edition rule and grid top
      const editionFontSize = 13;
      const editionGap = 10;
      const brandCenterY = layout.margin + 12 + BRAND_LOCKUP.iconSize / 2;
      const editionBottom = brandCenterY + editionFontSize / 2;
      const lineY = editionBottom + editionGap;
      const midpoint = lineY + (layout.offsetY - lineY) / 2;
      wordY = midpoint + fontSize / 2;
    } else {
      wordY = layout.offsetY - wordGap;
    }

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.font = `400 ${fontSize}px ${_fontUI}`;
    if (typeof ctx.letterSpacing !== "undefined") ctx.letterSpacing = mobile ? "2px" : "3px";
    ctx.fillStyle = C.ink;
    ctx.fillText(state.targetWord, cx, wordY);

    // Gold decorative rule under word: ── ○ ──
    const wordMetrics = ctx.measureText(state.targetWord);
    const ruleUlY = wordY + (mobile ? 4 : 6);
    drawGoldRule(cx, ruleUlY, wordMetrics.width * 0.75);

    ctx.restore();
  }

  function drawHUD() {
    if (state.alreadyCompleted && !state.practiceMode) {
      if (layout.panelBelow) return;
      const hudY = layout.margin * 0.3;
      const hudFontSize = 13;
      ctx.font = `italic ${hudFontSize}px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      const part1 = "Solved today. Replay or ";
      const part3 = " to improve.";
      const practiceWord = "practice";
      ctx.fillStyle = C.mutedSoft;
      ctx.fillText(part1, layout.offsetX, hudY);
      const part1W = ctx.measureText(part1).width;
      ctx.fillStyle = C.gold;
      ctx.fillText(practiceWord, layout.offsetX + part1W, hudY);
      const practiceW = ctx.measureText(practiceWord).width;
      layout.hudPracticeLink = { x: layout.offsetX + part1W, y: hudY, width: practiceW, height: hudFontSize + 4 };
      ctx.fillStyle = C.mutedSoft;
      ctx.fillText(part3, layout.offsetX + part1W + practiceW, hudY);
    }
  }

  function drawMenu() {
    drawBackground();
    layout.menuPlayToday = { x: 0, y: 0, width: 0, height: 0 };
    const landingVariant = getLandingVariant();
    logLandingVariantSeen();

    drawBrandHeader(layout.width / 2, layout.height * 0.04);

    const isMobileMenu = layout.panelBelow || _detectedPlatform === "mobile";
    const compactMenu = layout.height < 760 || layout.width < 980;
    const extraCompactMenu = compactMenu && (layout.height < 700 || layout.tabletWindowed);
    const shortMenu = layout.height < 440;
    const menuStreakState = loadStreakState();
    const menuStreak = menuStreakState.count || 0;
    const streakBanner = shortMenu ? null : getStreakRiskBannerState();
    const headlineGap = shortMenu ? 0 : (extraCompactMenu ? 24 : 36);
    const labelGap = shortMenu ? 20 : 26;
    const badgeGap = shortMenu ? 30 : 36;
    // Reserve the complete stack before sizing the decorative board. The footer
    // must never be clamped upward into instructions, status, or streak copy.
    const menuBottomReserve = headlineGap + labelGap + badgeGap + 23 + 40 + 43
      + 28 + (streakBanner ? 76 : 0) + 80;
    const logoY = shortMenu ? 62 : Math.max(64, layout.height * 0.1);
    const logoHeight = shortMenu ? 0 : Math.max(0, Math.min(
      layout.width * 0.34, 300, layout.height - logoY - menuBottomReserve
    ));
    const logoWidth = logoHeight * (GRID_COLS / GRID_ROWS);
    const logoX = (layout.width - logoWidth) / 2;
    if (logoHeight >= 48) drawLogoGrid(logoX, logoY, logoWidth, logoHeight);

    const taglineY = logoY + logoHeight + headlineGap;
    const todayLabelY = taglineY + labelGap;
    const badgeY = todayLabelY + badgeGap;

    // Edition header — matches game drawEditionHeader style
    const day = getDayNumber();
    const edFS = compactMenu ? 12 : 15;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${edFS}px ${_fontSerif}`;
    const edSpacing = _currentTheme === "dark" ? 1.5 * DARK_SPACING_BOOST : 1.5;
    if (typeof ctx.letterSpacing !== "undefined") ctx.letterSpacing = `${edSpacing}px`;
    ctx.fillStyle = C.muted;
    const isSunday = getWeekdayIndexForKey(state.dailyKey || getEasternKey()) === 0;
    ctx.fillText(`Edition No. ${day}${isSunday ? " \u00b7 Sunday Challenge" : ""}`, layout.width / 2, taglineY);
    // Dot-break rule underneath: ── ○ ──
    const edRuleW = compactMenu ? 60 : 80;
    const edRuleY = taglineY + edFS / 2 + 6;
    const edDotR = 1.5;
    const edDotGap = 5;
    const edCx = layout.width / 2;
    ctx.strokeStyle = C.mutedFaint;
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(edCx - edRuleW / 2, edRuleY);
    ctx.lineTo(edCx - edDotGap - edDotR, edRuleY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(edCx + edDotGap + edDotR, edRuleY);
    ctx.lineTo(edCx + edRuleW / 2, edRuleY);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(edCx, edRuleY, edDotR, 0, Math.PI * 2);
    ctx.fillStyle = C.muted;
    ctx.fill();
    ctx.restore();

    const word = getDailyWord();
    const menuStartLoading = _menuStartLoadState === "loading" && !word;
    const menuStartFailed = _menuStartLoadState === "failed" && !word;
    const badgePadX = compactMenu ? 16 : 18;
    const badgePadY = compactMenu ? 9 : 10;
    const badgeH = (compactMenu ? 24 : 26) + badgePadY * 2;
    if (word) {
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${compactMenu ? 11 : 12}px ${_fontSerif}`;
    ctx.fillStyle = C.muted;
    ctx.fillText("Today\u2019s word", layout.width / 2, todayLabelY);

    const badgeFont = `${compactMenu ? 24 : 26}px ${_fontSerif}`;
    ctx.font = badgeFont;
    const wordWidth = ctx.measureText(word).width;
    const badgeW = wordWidth + badgePadX * 2;
    const badgeX = layout.width / 2 - badgeW / 2;
    const badgeBoxY = badgeY - badgeH / 2;
    ctx.fillStyle = C.goldFill;
    ctx.fillRect(badgeX, badgeBoxY, badgeW, badgeH);
    ctx.strokeStyle = C.gold;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(badgeX, badgeBoxY, badgeW, badgeH);
    ctx.fillStyle = C.goldEdge;
    ctx.fillText(word, layout.width / 2, badgeY);
    }

    const ctaFontSize = 15;
    const ctaY = badgeY + badgeH / 2 + 40;
    layout.menuPlayToday = { x: layout.width / 2 - Math.min(145, layout.width * 0.43), y: ctaY - 22, width: Math.min(290, layout.width * 0.86), height: 44 };
    if (growth) growth.positionEntry(layout.menuPlayToday);
    else { ctx.fillStyle = C.ink; ctx.font = `600 15px ${_fontSerif}`; ctx.fillText("Play today’s puzzle", layout.width / 2, ctaY); }
    const tapY = ctaY + 43;
    ctx.font = `${extraCompactMenu ? 12 : 14}px ${_fontSerif}`;
    ctx.fillStyle = C.ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(menuStartFailed ? "Today's puzzle couldn't load. Please try again." : "Rotate four tiles. Spell the word.", layout.width / 2, tapY);

    let statusY = tapY;
    if (menuStartLoading || menuStartFailed) {
      ctx.font = `${extraCompactMenu ? 11 : (compactMenu ? 12 : 13)}px ${_fontSerif}`;
      ctx.fillStyle = menuStartFailed ? C.muted : C.mutedSoft;
      statusY = tapY + 18;
      ctx.fillText(menuStartFailed ? "Couldn\u2019t load today\u2019s puzzle." : "Loading today\u2019s puzzle\u2026", layout.width / 2, statusY);
    }

    let contentBottomY = statusY;
    // Duo app outer landscape leaves a ~272pt canvas where this optional line lands on the
    // Practice/Hall footer. Drop it there rather than overlap; elsewhere unchanged.
    const optionalLineY = statusY + (extraCompactMenu ? 20 : (compactMenu ? 24 : 28));
    const optionalLineFits = !layout.duo
      || optionalLineY + (extraCompactMenu ? 18 : (compactMenu ? 22 : 24)) + 20 <= layout.height - (isMobileMenu ? 36 : 34);
    if (!optionalLineFits) {
      // Skip both the streak and the first-visit cadence line.
    } else if (menuStreak >= 2) {
      const streakY = statusY + (extraCompactMenu ? 20 : (compactMenu ? 24 : 28));
      ctx.font = `${extraCompactMenu ? 11 : (compactMenu ? 12 : 13)}px ${_fontSerif}`;
      ctx.fillStyle = C.gold;
      ctx.fillText(`\uD83D\uDD25 ${menuStreak}-day streak`, layout.width / 2, streakY);
      contentBottomY = streakY;
    } else if (getSolvedDailyGamesCountCached() === 0) {
      // Only <title> and the OG card say this game recurs; the canvas never
      // did. A stranger cannot intend to return to something they don't know
      // comes back. Shares the streak slot, which a first-timer cannot have.
      const cadenceY = statusY + (extraCompactMenu ? 20 : (compactMenu ? 24 : 28));
      ctx.font = `${extraCompactMenu ? 11 : (compactMenu ? 12 : 13)}px ${_fontSerif}`;
      ctx.fillStyle = C.muted;
      ctx.fillText("A new puzzle every day at midnight.", layout.width / 2, cadenceY);
      contentBottomY = cadenceY;
    }

    if (streakBanner) {
      logEvent("streak_banner_shown", {
        streak_count: streakBanner.streakCount,
        grace_available: streakBanner.tone.indexOf("shield") === 0 ? 1 : 0,
        hours_left: streakBanner.hoursLeft,
        gap_days: streakBanner.gap,
      }, `${state.dailyKey || getEasternKey()}_${state.sessionId}_streak_banner_shown`);
      const boxTop = contentBottomY + (extraCompactMenu ? 14 : 18);
      const boxWidth = Math.min(layout.width * 0.84, isMobileMenu ? 330 : 420);
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${extraCompactMenu ? 10 : 12}px ${_fontSerif}`;
      const lineHeight = extraCompactMenu ? 13 : 15;
      const padY = extraCompactMenu ? 9 : 11;
      // Size the box to the wrapped line count and vertically center the text.
      // (Previously the box height + text y were both fixed, so single-line
      // labels sat too high and long labels overflowed the box.)
      const bannerText = `${streakBanner.tone === "urgent" ? "🔥" : "🛡️"} ${streakBanner.label}`;
      const bannerLines = wrapTextLines(bannerText, boxWidth - 24);
      const boxHeight = bannerLines.length * lineHeight + padY * 2;
      const boxX = layout.width / 2 - boxWidth / 2;
      const boxY = boxTop;
      ctx.fillStyle = streakBanner.tone === "urgent"
        ? (_currentTheme === "dark" ? "rgba(128, 55, 35, 0.82)" : "rgba(212, 126, 44, 0.14)")
        : (_currentTheme === "dark" ? "rgba(112, 95, 44, 0.82)" : "rgba(244, 211, 94, 0.18)");
      ctx.strokeStyle = streakBanner.tone === "urgent" ? C.goldEdge : C.gold;
      ctx.lineWidth = 1;
      fillRoundRect(ctx, boxX, boxY, boxWidth, boxHeight, 6);
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
      ctx.fillStyle = _currentTheme === "dark" ? "#fdf8f1" : C.ink;
      let bannerLineY = boxY + padY + lineHeight / 2;
      for (const bannerLine of bannerLines) {
        ctx.fillText(bannerLine, layout.width / 2, bannerLineY);
        bannerLineY += lineHeight;
      }
      ctx.restore();
      contentBottomY = boxY + boxHeight;
    }

    // Custom game links — compute Y first so we can center the stats line
    const minFooterGap = extraCompactMenu ? 18 : (compactMenu ? 22 : 24);
    const defaultFooterY = layout.height - (isMobileMenu ? 60 : 46);
    const customY = Math.min(
      layout.height - (isMobileMenu ? 36 : 34),
      Math.max(contentBottomY + minFooterGap + 20, defaultFooterY)
    );

    // Edition/streak stats — centered between "tap anywhere"/streak and custom game links
    {
      const solversY = Math.round((contentBottomY + customY) / 2);
      const statsSize = isMobileMenu ? (extraCompactMenu ? 10 : (compactMenu ? 11 : 12)) : 18;
      ctx.font = `${statsSize}px ${_fontSerif}`;
      ctx.fillStyle = C.muted;
      const statsParts = [];
      if (state.metrics.median !== null) {
        statsParts.push(`Median solve: ${state.metrics.median} moves`);
      }
      if (customY - contentBottomY >= 54) ctx.fillText(statsParts.join("  \u00b7  "), layout.width / 2, solversY);
    }
    ctx.font = `${isMobileMenu ? (compactMenu ? 11 : 12) : 11}px ${_fontSerif}`;
    ctx.fillStyle = C.muted;
    const practiceText = "Practice";
    const sep = "  \u00b7  ";
    const hallText = "Hall of Fame";
    const showAppStoreLink = !PIVOT_NATIVE;
    const appStoreText = "iOS app";
    const practiceW = ctx.measureText(practiceText).width;
    const sepW = ctx.measureText(sep).width;
    const hallW = ctx.measureText(hallText).width;
    const appStoreW = showAppStoreLink ? ctx.measureText(appStoreText).width : 0;
    const totalW = practiceW + sepW + hallW + (showAppStoreLink ? sepW + appStoreW : 0);
    const hitPadX = isMobileMenu ? 8 : 4;
    const hitTop = isMobileMenu ? 12 : 8;
    const hitHeight = isMobileMenu ? 28 : 20;
    ctx.textAlign = "left";
    const startX = layout.width / 2 - totalW / 2;
    ctx.fillStyle = C.muted;
    ctx.fillText(practiceText, startX, customY);
    layout.menuCreateCustom = { x: startX - hitPadX, y: customY - hitTop, width: practiceW + hitPadX * 2, height: hitHeight };
    ctx.fillStyle = C.mutedFaintest;
    ctx.fillText(sep, startX + practiceW, customY);
    const hallX = startX + practiceW + sepW;
    ctx.fillStyle = C.muted;
    ctx.fillText(hallText, hallX, customY);
    layout.menuHall = { x: hallX - hitPadX, y: customY - hitTop, width: hallW + hitPadX * 2, height: hitHeight };
    if (showAppStoreLink) {
      ctx.fillStyle = C.mutedFaintest;
      ctx.fillText(sep, hallX + hallW, customY);
      const appStoreX = hallX + hallW + sepW;
      ctx.fillStyle = C.gold;
      ctx.fillText(appStoreText, appStoreX, customY);
      layout.menuAppStore = { x: appStoreX - hitPadX, y: customY - hitTop, width: appStoreW + hitPadX * 2, height: hitHeight };
    } else {
      layout.menuAppStore = { x: 0, y: 0, width: 0, height: 0 };
    }

    const siteY = Math.min(layout.height - 18, customY + 20);
    ctx.textAlign = "center";
    ctx.font = `${compactMenu ? 11 : 12}px ${_fontSerif}`;
    ctx.fillStyle = C.mutedFaintest;
    ctx.fillText("pivotthegame.com", layout.width / 2, siteY);
  }

  // Hero illustration grid — static, matches game visuals exactly
  function drawLogoGrid(x, y, width, height) {
    const cellSize = Math.min(width / GRID_COLS, height / GRID_ROWS);
    const gridWidth = cellSize * GRID_COLS;
    const gridHeight = cellSize * GRID_ROWS;
    const offsetX = x + (width - gridWidth) / 2;
    const offsetY = y + (height - gridHeight) / 2;

    // Gold highlight row first (same as game: drawTargetHighlight before drawGrid)
    ctx.save();
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      const col = CENTER_START_COL + i;
      const xCell = offsetX + col * cellSize;
      const yCell = offsetY + CENTER_ROW * cellSize;
      const grad = ctx.createLinearGradient(xCell, yCell, xCell, yCell + cellSize);
      grad.addColorStop(0, C.gold);
      grad.addColorStop(1, C.goldEdge);
      ctx.fillStyle = grad;
      if (!_prefersReducedMotion) {
        ctx.shadowColor = C.gold;
        ctx.shadowBlur = _currentTheme === "dark" ? 8 : 6;
      }
      ctx.fillRect(xCell, yCell, cellSize, cellSize);
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.strokeStyle = C.goldEdge;
      ctx.lineWidth = 2;
      ctx.strokeRect(xCell, yCell, cellSize, cellSize);
    }
    ctx.restore();

    // Grid lines on top — slightly softer than game (specimen feel)
    ctx.save();
    ctx.globalAlpha = 0.75;
    ctx.strokeStyle = C.gridLine;
    ctx.lineWidth = _currentTheme === "dark" ? 1.5 : 1.2;
    for (let r = 0; r <= GRID_ROWS; r += 1) {
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY + r * cellSize);
      ctx.lineTo(offsetX + gridWidth, offsetY + r * cellSize);
      ctx.stroke();
    }
    for (let c = 0; c <= GRID_COLS; c += 1) {
      ctx.beginPath();
      ctx.moveTo(offsetX + c * cellSize, offsetY);
      ctx.lineTo(offsetX + c * cellSize, offsetY + gridHeight);
      ctx.stroke();
    }
    ctx.restore();

    // Letters — gold row crisp, outer rows faded for mystique
    const logoGrid = [
      ["S", "P", "I", "V", "O", "T"],
      ["P", "I", "V", "O", "T", "S"],
      ["G", "R", "I", "D", "S", "X"],
    ];
    ctx.font = `600 ${Math.floor(cellSize * 0.52)}px ${_fontUI}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let r = 0; r < GRID_ROWS; r += 1) {
      for (let c = 0; c < GRID_COLS; c += 1) {
        const isHighlightRow = r === CENTER_ROW && c >= CENTER_START_COL && c < CENTER_START_COL + CENTER_WORD_LENGTH;
        const distFromGold = Math.abs(r - CENTER_ROW);
        if (isHighlightRow) {
          ctx.globalAlpha = 1;
          ctx.fillStyle = C.ink;
        } else {
          // Fade based on distance from gold row
          ctx.globalAlpha = distFromGold >= 2 ? 0.18 : 0.4;
          ctx.fillStyle = C.ink;
        }
        const lx = offsetX + c * cellSize + cellSize / 2;
        const ly = offsetY + r * cellSize + cellSize / 2;
        ctx.fillText(logoGrid[r][c], lx, ly);
        ctx.globalAlpha = 1;
      }
    }

    // Fade top and bottom edges of grid into background
    ctx.save();
    const bgTransparent = _currentTheme === "dark"
      ? "rgba(19,19,19,0)" : "rgba(245,242,234,0)";
    const edgeFade = ctx.createLinearGradient(offsetX, offsetY, offsetX, offsetY + gridHeight);
    edgeFade.addColorStop(0, C.bg);
    edgeFade.addColorStop(0.22, bgTransparent);
    edgeFade.addColorStop(0.78, bgTransparent);
    edgeFade.addColorStop(1, C.bg);
    ctx.fillStyle = edgeFade;
    ctx.fillRect(offsetX - 2, offsetY - 2, gridWidth + 4, gridHeight + 4);
    ctx.restore();

    // Pivot dots — matching real game style
    const pivotRadius = Math.max(5, cellSize * 0.14);
    const dotRadius = pivotRadius * 0.50;
    for (let r = 0; r < GRID_ROWS - 1; r += 1) {
      for (let c = 0; c < GRID_COLS - 1; c += 1) {
        const px = offsetX + (c + 1) * cellSize;
        const py = offsetY + (r + 1) * cellSize;

        // Opaque base to cover grid line intersection
        const baseR = dotRadius + 2;
        const touchesGold = (r === CENTER_ROW - 1 || r === CENTER_ROW) &&
          (c >= CENTER_START_COL - 1 && c < CENTER_START_COL + CENTER_WORD_LENGTH);
        ctx.beginPath();
        ctx.arc(px, py, baseR, 0, Math.PI * 2);
        ctx.fillStyle = touchesGold ? C.goldEdge : (_currentTheme === "dark" ? "#131313" : C.bg);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
        if (_currentTheme === "dark") {
          const grad = ctx.createRadialGradient(px - dotRadius * 0.3, py - dotRadius * 0.3, 0, px, py, dotRadius);
          grad.addColorStop(0, "#C9A83E");
          grad.addColorStop(1, "#8B6F28");
          ctx.fillStyle = grad;
          if (!_prefersReducedMotion) {
            ctx.save();
            ctx.shadowColor = "rgba(160,130,40,0.45)";
            ctx.shadowBlur = 8;
            ctx.fill();
            ctx.shadowColor = "rgba(140,110,30,0.15)";
            ctx.shadowBlur = 14;
            ctx.fill();
            ctx.restore();
          } else {
            ctx.fill();
          }
          ctx.strokeStyle = "rgba(180,160,100,0.35)";
          ctx.lineWidth = 0.75;
          ctx.stroke();
          // Specular highlight
          ctx.save();
          ctx.beginPath();
          ctx.arc(px - dotRadius * 0.2, py - dotRadius * 0.2, dotRadius * 0.28, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,240,180,0.3)";
          ctx.fill();
          ctx.restore();
        } else {
          // Sculpted brass rivet (light mode)
          const bodyGrad = ctx.createRadialGradient(
            px - dotRadius * 0.3, py - dotRadius * 0.3, 0, px, py, dotRadius
          );
          bodyGrad.addColorStop(0, "#504C46");
          bodyGrad.addColorStop(0.7, "#3A3730");
          bodyGrad.addColorStop(1, "#2A2824");
          ctx.fillStyle = bodyGrad;
          ctx.fill();
          // Inner shadow
          ctx.save();
          ctx.beginPath();
          ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
          ctx.clip();
          const innerShadow = ctx.createRadialGradient(
            px + dotRadius * 0.35, py + dotRadius * 0.35, 0,
            px + dotRadius * 0.2, py + dotRadius * 0.2, dotRadius * 0.9
          );
          innerShadow.addColorStop(0, "rgba(0,0,0,0.25)");
          innerShadow.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = innerShadow;
          ctx.fill();
          ctx.restore();
          // Specular highlight
          ctx.save();
          ctx.beginPath();
          ctx.arc(px - dotRadius * 0.22, py - dotRadius * 0.22, dotRadius * 0.32, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.14)";
          ctx.fill();
          ctx.restore();
          // Thin metallic edge
          ctx.beginPath();
          ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(0,0,0,0.12)";
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function easeOutBack(t) {
    return 1 + 2.7 * Math.pow(t - 1, 3) + 1.7 * Math.pow(t - 1, 2);
  }

  function easeOutQuart(t) {
    return 1 - Math.pow(1 - t, 4);
  }

  // Mechanical hinge easing — cubic-bezier(0.22, 1, 0.36, 1)
  // Quick acceleration, slight overshoot (~3°), then settle
  function easePivotRotation(t) {
    const x1 = 0.22, y1 = 1.0, x2 = 0.36, y2 = 1.0;
    let lo = 0, hi = 1;
    for (let i = 0; i < 16; i++) {
      const u = (lo + hi) / 2;
      const x = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u;
      if (x < t) lo = u; else hi = u;
    }
    const u = (lo + hi) / 2;
    let y = 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u;
    // Micro-overshoot: peaks at ~1.035, then settles to 1.0
    if (t > 0.6) {
      const settle = (t - 0.6) / 0.4; // 0→1 over last 40%
      const overshoot = Math.sin(settle * Math.PI) * 0.035;
      y += overshoot * (1 - settle);
    }
    return y;
  }


  function drawCustomWin() {
    drawBackground();
    drawTargetHighlight();
    drawGrid();
    drawPivots();
    drawSidebar();

    ctx.fillStyle = C.overlay;
    ctx.fillRect(0, 0, layout.width, layout.height);

    const isMobile = layout.panelBelow;
    const titleSize = isMobile ? 20 : 24;
    const summarySize = isMobile ? 13 : 15;
    const buttonFontSize = isMobile ? 12 : 13;
    const cx = layout.width / 2;
    const btnW = isMobile ? 110 : 130;
    const btnH = 28;
    const btnGap = 8;

    ctx.fillStyle = C.overlayText;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (!state.customMultiplayer) {
      // Solo custom win — stats + trend view
      const infoSize = isMobile ? 13 : 14;
      const winElapsed = state.winAnimStart ? performance.now() - state.winAnimStart : 9999;
      const countDelay = 200;
      const countDuration = 800;
      const countRaw = Math.max(0, winElapsed - countDelay);
      const countProgress = Math.min(1, countRaw / countDuration);
      const eased = easeOutQuart(countProgress);
      const displayMoves = Math.round(eased * state.moves);
      const hasPar = Number.isFinite(state.minMoves);
      const delta = hasPar ? Math.max(0, state.moves - state.minMoves) : null;
      const sg = state.sessionStats.games;
      const priorGames = sg.slice(0, -1);
      const showTrend = priorGames.length >= 1 && hasPar && priorGames.some(g => Number.isFinite(g.minMoves));
      const showSession = sg.length >= 2;

      // Measure content height
      let contentH = titleSize + 12; // title
      contentH += summarySize + 8; // summary
      contentH += infoSize + 12; // rating
      if (showTrend) contentH += infoSize + 8;
      if (showSession) contentH += infoSize + 12;
      contentH += btnH + 8; // next puzzle
      contentH += infoSize + 6; // change difficulty
      contentH += infoSize + 4; // back to today

      if (growth && !state.practiceMode && !state.redoMode && layout.height >= 560) contentH += growthCircleSummary() ? 93 : 49;
    let curY = Math.max(20, (layout.height - contentH) / 2);

      // Title
      ctx.font = `italic ${titleSize}px ${_fontSerif}`;
      ctx.fillText("Solved!", cx, curY + titleSize / 2);
      curY += titleSize + 12;

      // Summary line
      ctx.font = `${summarySize}px ${_fontSerif}`;
      let summary = `You solved ${state.targetWord} in ${displayMoves} moves`;
      if (hasPar && countProgress >= 1) {
        const label = state.minExact ? "minimum" : "par";
        summary = `You solved ${state.targetWord} in ${state.moves} moves (${delta} over ${label} of ${state.minMoves})`;
      }
      const summaryMaxW = layout.width * 0.88;
      const nextY = wrapText(summary, cx, curY + summarySize / 2, summaryMaxW, summarySize + 6);
      curY = nextY + 8;

      // Rating
      ctx.font = `italic ${infoSize}px ${_fontSerif}`;
      ctx.fillStyle = C.overlayTextSoft;
      const rating = hasPar ? getRating(delta) : "Nice work!";
      ctx.fillText(rating, cx, curY + infoSize / 2);
      curY += infoSize + 12;

      // Trend arrow
      if (showTrend) {
        let priorTotal = 0, priorCount = 0;
        for (const g of priorGames) {
          if (Number.isFinite(g.minMoves)) { priorTotal += g.moves - g.minMoves; priorCount++; }
        }
        if (priorCount > 0) {
          const priorAvg = priorTotal / priorCount;
          const diff = delta - priorAvg;
          let trendSymbol, trendLabel, trendColor;
          if (diff < -0.5) { trendSymbol = "\u25b2"; trendLabel = "Improving"; trendColor = C.tileGreenText || "#2d8a4e"; }
          else if (diff > 0.5) { trendSymbol = "\u25bc"; trendLabel = "Keep at it"; trendColor = C.tileRedText || "#c0392b"; }
          else { trendSymbol = "\u25cf"; trendLabel = "Steady"; trendColor = C.tileYellowText || "#b8860b"; }
          ctx.font = `${infoSize}px ${_fontSerif}`;
          ctx.fillStyle = trendColor;
          ctx.fillText(`${trendSymbol} ${trendLabel}`, cx, curY + infoSize / 2);
        }
        curY += infoSize + 8;
      }

      // Session stats
      if (showSession) {
        let totalOverPar = 0, overParCount = 0, totalGreen = 0, totalMoves = 0;
        for (const g of sg) {
          if (Number.isFinite(g.minMoves)) { totalOverPar += g.moves - g.minMoves; overParCount++; }
          totalGreen += g.green;
          totalMoves += g.moves;
        }
        const avgOver = overParCount > 0 ? (totalOverPar / overParCount).toFixed(1) : null;
        const perfectPct = totalMoves > 0 ? Math.round(100 * totalGreen / totalMoves) : 0;
        let statsText = `Session: ${sg.length} games`;
        if (avgOver !== null) statsText += ` \u00b7 avg ${avgOver} over par`;
        statsText += ` \u00b7 ${perfectPct}% perfect moves`;
        ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
        ctx.fillStyle = C.overlayTextSoft;
        ctx.fillText(statsText, cx, curY + infoSize / 2);
        curY += infoSize + 12;
      }

      // "Next puzzle" button (primary gold)
      ctx.font = `${buttonFontSize}px ${_fontSerif}`;
      const nextLabel = state.customCommunityCode ? "Share this PIVOT" : "Next puzzle";
      const nextW = Math.min(layout.width * 0.7, Math.max(150, ctx.measureText(nextLabel).width + 28));
      const nextX = cx - nextW / 2;
      layout.customNextPuzzle = { x: nextX, y: curY, width: nextW, height: btnH };
      ctx.fillStyle = C.overlayBtnPrimaryBg;
      ctx.strokeStyle = C.overlayBtnPrimaryStroke;
      ctx.lineWidth = 1;
      fillRoundRect(ctx, nextX, curY, nextW, btnH, 4);
      ctx.strokeRect(nextX, curY, nextW, btnH);
      ctx.fillStyle = C.overlayBtnPrimaryText;
      ctx.fillText(nextLabel, cx, curY + btnH / 2);
      curY += btnH + 8;

      // "Change difficulty" link
      ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
      ctx.fillStyle = C.overlayTextFaint;
      const changeDiffLabel = state.customCommunityCode ? "Make your own" : "Change difficulty";
      const changeDiffW = ctx.measureText(changeDiffLabel).width + 12;
      layout.customChangeDifficulty = { x: cx - changeDiffW / 2, y: curY - 2, width: changeDiffW, height: infoSize + 6 };
      ctx.fillText(changeDiffLabel, cx, curY + infoSize / 2);
      curY += infoSize + 6;

      // "Back to today" link
      ctx.fillStyle = C.overlayTextFaint;
      const backLabel = "Back to today\u2019s puzzle";
      const backW = ctx.measureText(backLabel).width + 12;
      layout.customBackToToday = { x: cx - backW / 2, y: curY - 2, width: backW, height: infoSize + 6 };
      ctx.fillText(backLabel, cx, curY + infoSize / 2);
    } else {
      // N-player results leaderboard
      drawResultsLeaderboard(cx, isMobile, titleSize, summarySize, buttonFontSize, btnW, btnH, btnGap);
    }

    // Close X
    const closeSz = 20;
    const closeX = layout.width - closeSz - 16;
    const closeY = 16;
    layout.winClose = { x: closeX, y: closeY, width: closeSz, height: closeSz };
    ctx.font = `16px ${_fontSerif}`;
    ctx.fillStyle = "rgba(253, 248, 241, 0.5)";
    ctx.fillText("\u00d7", closeX + closeSz / 2, closeY + closeSz / 2);
  }

  function drawResultsLeaderboard(cx, isMobile, titleSize, summarySize, buttonFontSize, btnW, btnH, btnGap) {
    // Sort players by finish order (solved first, then unsolved)
    const allPlayers = Object.values(state.customPlayers);
    const active = allPlayers.filter((p) => !p.isObserver);
    const observers = allPlayers.filter((p) => p.isObserver);
    active.sort((a, b) => {
      if (a.solved && !b.solved) return -1;
      if (!a.solved && b.solved) return 1;
      if (a.solved && b.solved) return a.finishOrder - b.finishOrder;
      return a.moves - b.moves;
    });
    const allSolved = active.every((p) => p.solved);

    // Title
    let curY = Math.max(32, isMobile ? 30 : 36);
    ctx.font = `italic ${titleSize}px ${_fontSerif}`;
    ctx.fillStyle = "#fdf8f1";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const seriesWinner = state.customSeriesWinnerId === "tie"
      ? null
      : state.customPlayers[state.customSeriesWinnerId];
    const roundLabel = state.customSeriesComplete
      ? (state.customSeriesWinnerId === "tie" ? "Series tied" : `${sanitizeName(seriesWinner?.name || "Player")} wins the series`)
      : `Round ${state.customRoundNumber} Results`;
    ctx.fillText(roundLabel, cx, curY + titleSize / 2);
    curY += titleSize + 10;

    // Self summary
    const winElapsed = state.winAnimStart ? performance.now() - state.winAnimStart : 9999;
    const countProgress = Math.min(1, winElapsed / 800);
    const eased = 1 - Math.pow(1 - countProgress, 3);
    const displayMoves = Math.round(eased * state.moves);
    ctx.font = `${summarySize}px ${_fontSerif}`;
    ctx.fillStyle = "rgba(253, 248, 241, 0.7)";
    const selfPlayer = state.customPlayers[state.customPlayerId];
    const selfSummary = selfPlayer?.solved ? `You solved in ${displayMoves} moves` : "Round complete";
    ctx.fillText(selfSummary, cx, curY + summarySize / 2);
    curY += summarySize + 12;

    if (state.customSeriesBestOf > 1) {
      const seriesLeaders = [...active].sort((a, b) => normalizeWinLoss(b.wins) - normalizeWinLoss(a.wins));
      const scoreText = seriesLeaders.length <= 3
        ? seriesLeaders.map((player) => `${(sanitizeName(player.name || "Player") || "Player").slice(0, 8)} ${normalizeWinLoss(player.wins)}`).join(" · ")
        : `Leader ${(sanitizeName(seriesLeaders[0]?.name || "Player") || "Player").slice(0, 10)} ${normalizeWinLoss(seriesLeaders[0]?.wins)} · ${seriesLeaders.length - 1} others`;
      ctx.font = `italic ${summarySize - 2}px ${_fontSerif}`;
      ctx.fillStyle = "rgba(253, 248, 241, 0.58)";
      ctx.fillText(`Best of ${state.customSeriesBestOf} · ${scoreText}`, cx, curY + 6);
      curY += 17;
    }

    // Leaderboard rows
    const rowH = isMobile ? 20 : 22;
    const maxVisible = Math.min(active.length, isMobile ? 8 : 10);
    const leaderW = isMobile ? 240 : 300;
    const lx = cx - leaderW / 2;

    for (let i = 0; i < maxVisible; i++) {
      const p = active[i];
      const isSelf = p.id === state.customPlayerId;
      const ry = curY + i * rowH;
      // Highlight self row
      if (isSelf) {
        ctx.fillStyle = "rgba(244, 211, 94, 0.12)";
        ctx.fillRect(lx, ry - 1, leaderW, rowH);
      }
      // Rank
      ctx.font = `${isSelf ? "bold " : ""}${summarySize - 1}px ${_fontSerif}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const rank = i + 1;
      const medal = rank === 1 && p.solved ? "\uD83C\uDFC6 " : `${rank}.  `;
      ctx.fillStyle = p.solved ? "#fdf8f1" : "rgba(253, 248, 241, 0.45)";
      ctx.fillText(medal, lx + 4, ry + rowH / 2);
      // Name
      const nameX = lx + (rank === 1 && p.solved ? 28 : 28);
      const pDisplayName = p.isBot ? `${p.name} (Robot)` : p.name;
      const pName = pDisplayName.length > 12 ? pDisplayName.slice(0, 11) + "\u2026" : pDisplayName;
      const selfTag = isSelf ? " (you)" : "";
      ctx.fillText(pName + selfTag, nameX, ry + rowH / 2);
      // Moves
      ctx.textAlign = "right";
      if (p.solved) {
        ctx.fillStyle = "#fdf8f1";
        ctx.fillText(`${p.moves} moves`, lx + leaderW - 4, ry + rowH / 2);
      } else {
        ctx.fillStyle = "rgba(253, 248, 241, 0.4)";
        ctx.fillText(`${p.moves} (playing)`, lx + leaderW - 4, ry + rowH / 2);
      }
    }
    curY += maxVisible * rowH + 6;

    // Separator
    ctx.strokeStyle = "rgba(253, 248, 241, 0.15)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(lx, curY);
    ctx.lineTo(lx + leaderW, curY);
    ctx.stroke();
    curY += 6;

    // Observers
    if (observers.length > 0) {
      ctx.font = `italic ${summarySize - 2}px ${_fontSerif}`;
      ctx.fillStyle = "rgba(253, 248, 241, 0.35)";
      ctx.textAlign = "center";
      const obsNames = observers.map((p) => p.name).join(", ");
      ctx.fillText(`Watching: ${obsNames}`, cx, curY + 8);
      curY += 18;
    }

    // Pending join requests (host only, classroom mode)
    if (state.customIsHost && state.customClassroomMode && state.customPendingJoins.length > 0) {
      const unapproved = state.customPendingJoins.filter((j) => !j.approved);
      if (unapproved.length > 0) {
        ctx.font = `${summarySize - 2}px ${_fontSerif}`;
        ctx.fillStyle = "rgba(199, 167, 66, 0.8)";
        ctx.textAlign = "center";
        const names = unapproved.map((j) => j.name).join(", ");
        ctx.fillText(`Join requests: ${names}`, cx, curY + 8);
        curY += 18;
      }
    }

    // Next difficulty indicator
    if (state.customNextDifficulty && state.customNextDifficulty !== state.customDifficulty) {
      ctx.font = `italic ${summarySize - 2}px ${_fontSerif}`;
      ctx.fillStyle = "rgba(199, 167, 66, 0.8)";
      ctx.textAlign = "center";
      const nextDl = state.customNextDifficulty.charAt(0).toUpperCase() + state.customNextDifficulty.slice(1);
      ctx.fillText(`Next round: ${nextDl}`, cx, curY + 8);
      curY += 20;
    }

    curY += 4;

    // Buttons
    if (state.customIsHost) {
      // Next Round + Exit
      const totalBtnW = btnW * 2 + btnGap;
      const btnX = cx - totalBtnW / 2;
      layout.customNextRoundButton = { x: btnX, y: curY, width: btnW, height: btnH };
      layout.customExitWinButton = { x: btnX + btnW + btnGap, y: curY, width: btnW, height: btnH };
      layout.customPlayAgainButton = { x: 0, y: 0, width: 0, height: 0 }; // disable solo play again
      ctx.fillStyle = "rgba(253, 248, 241, 0.1)";
      ctx.strokeStyle = "rgba(253, 248, 241, 0.25)";
      ctx.lineWidth = 1;
      for (const b of [layout.customNextRoundButton, layout.customExitWinButton]) {
        ctx.fillRect(b.x, b.y, b.width, b.height);
        ctx.strokeRect(b.x, b.y, b.width, b.height);
      }
      ctx.fillStyle = "rgba(253, 248, 241, 0.85)";
      ctx.font = `${buttonFontSize}px ${_fontSerif}`;
      ctx.textAlign = "center";
      const nextRoundLabel = state.customSeriesComplete ? "New series" : (allSolved ? "Next round" : "End round");
      ctx.fillText(nextRoundLabel, layout.customNextRoundButton.x + btnW / 2, layout.customNextRoundButton.y + btnH / 2 + 1);
      ctx.fillText("Exit", layout.customExitWinButton.x + btnW / 2, layout.customExitWinButton.y + btnH / 2 + 1);
      curY += btnH + 6;
    } else {
      // Only the host can start another round. Never move an unfinished player
      // to a new puzzle on a timer.
      layout.customPlayAgainButton = { x: 0, y: 0, width: 0, height: 0 };
      layout.customNextRoundButton = { x: 0, y: 0, width: 0, height: 0 };
      ctx.font = `italic ${summarySize - 1}px ${_fontSerif}`;
      ctx.fillStyle = "rgba(253, 248, 241, 0.45)";
      ctx.textAlign = "center";
      ctx.fillText("Waiting for host", cx, curY + 10);
      curY += 24;
      layout.customExitWinButton = { x: cx - btnW / 2, y: curY, width: btnW, height: btnH };
      ctx.fillStyle = "rgba(253, 248, 241, 0.1)";
      ctx.strokeStyle = "rgba(253, 248, 241, 0.25)";
      ctx.lineWidth = 1;
      ctx.fillRect(layout.customExitWinButton.x, layout.customExitWinButton.y, btnW, btnH);
      ctx.strokeRect(layout.customExitWinButton.x, layout.customExitWinButton.y, btnW, btnH);
      ctx.fillStyle = "rgba(253, 248, 241, 0.85)";
      ctx.font = `${buttonFontSize}px ${_fontSerif}`;
      ctx.fillText("Exit", layout.customExitWinButton.x + btnW / 2, layout.customExitWinButton.y + btnH / 2 + 1);
    }
  }

  // Dark mode needs slightly more letter spacing — dark absorbs, letters need air
  const DARK_SPACING_BOOST = 1.03;

  // Gold underline accent for dark mode section labels
  function drawGoldAccentLine(x, y, width) {
    if (_currentTheme !== "dark") return;
    ctx.save();
    ctx.strokeStyle = "rgba(212,175,55,0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + width, y);
    ctx.stroke();
    ctx.restore();
  }

  // Decorative gold rule: ── ○ ── centered at (cx, y)
  function drawGoldRule(cx, y, width) {
    const dotR = 1.8;
    const dotGap = 5;
    const halfW = width / 2;
    ctx.save();
    ctx.strokeStyle = C.gold;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(cx - halfW, y);
    ctx.lineTo(cx - dotGap - dotR, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + dotGap + dotR, y);
    ctx.lineTo(cx + halfW, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, y, dotR, 0, Math.PI * 2);
    ctx.fillStyle = C.gold;
    ctx.fill();
    ctx.restore();
  }

  function drawGoldShimmer(elapsed, duration) {
    const { cellSize, offsetX, offsetY } = layout;
    const goldX = offsetX + CENTER_START_COL * cellSize;
    const goldY = offsetY + CENTER_ROW * cellSize;
    const goldW = CENTER_WORD_LENGTH * cellSize;
    const goldH = cellSize;

    // Eased progress — fast start, gentle end
    const raw = elapsed / duration;
    const t = raw < 0.5 ? 2 * raw * raw : 1 - Math.pow(-2 * raw + 2, 2) / 2;

    // Shimmer beam position spans beyond the band for smooth entry/exit
    const beamW = cellSize * 1.2;
    const skew = cellSize * 0.3; // parallelogram lean
    const beamCenter = goldX - beamW + t * (goldW + beamW * 2);

    // Fade envelope — soft edges at entry and exit
    const fade = raw < 0.12 ? raw / 0.12 : raw > 0.8 ? (1 - raw) / 0.2 : 1;
    const peakAlpha = _currentTheme === "dark" ? 0.55 : 0.45;

    ctx.save();
    // Clip to gold row bounds
    ctx.beginPath();
    ctx.rect(goldX, goldY, goldW, goldH);
    ctx.clip();

    // Draw angled shimmer beam as a parallelogram
    ctx.beginPath();
    ctx.moveTo(beamCenter - beamW / 2 + skew, goldY);
    ctx.lineTo(beamCenter + beamW / 2 + skew, goldY);
    ctx.lineTo(beamCenter + beamW / 2 - skew, goldY + goldH);
    ctx.lineTo(beamCenter - beamW / 2 - skew, goldY + goldH);
    ctx.closePath();

    // Gradient across the beam width
    const gx1 = beamCenter - beamW / 2;
    const gx2 = beamCenter + beamW / 2;
    const shimGrad = ctx.createLinearGradient(gx1, goldY, gx2, goldY);
    shimGrad.addColorStop(0, "rgba(255,252,240,0)");
    shimGrad.addColorStop(0.3, `rgba(255,252,240,${peakAlpha * fade * 0.4})`);
    shimGrad.addColorStop(0.5, `rgba(255,255,255,${peakAlpha * fade})`);
    shimGrad.addColorStop(0.7, `rgba(255,252,240,${peakAlpha * fade * 0.4})`);
    shimGrad.addColorStop(1, "rgba(255,252,240,0)");
    ctx.fillStyle = shimGrad;
    ctx.fill();

    // Secondary softer glow trailing the beam
    const trailCenter = beamCenter - beamW * 0.4;
    ctx.beginPath();
    ctx.moveTo(trailCenter - beamW * 0.6 + skew, goldY);
    ctx.lineTo(trailCenter + beamW * 0.6 + skew, goldY);
    ctx.lineTo(trailCenter + beamW * 0.6 - skew, goldY + goldH);
    ctx.lineTo(trailCenter - beamW * 0.6 - skew, goldY + goldH);
    ctx.closePath();
    const trailGrad = ctx.createLinearGradient(trailCenter - beamW * 0.6, goldY, trailCenter + beamW * 0.6, goldY);
    trailGrad.addColorStop(0, "rgba(255,248,220,0)");
    trailGrad.addColorStop(0.5, `rgba(255,248,220,${peakAlpha * fade * 0.2})`);
    trailGrad.addColorStop(1, "rgba(255,248,220,0)");
    ctx.fillStyle = trailGrad;
    ctx.fill();

    ctx.restore();
  }

  function growthCircleSummary() {
    if (!growth || state.friendCirclesError || Date.now() - state.friendCirclesFetchedAt > 60000) return '';
    const circle = selectedFriendCircle();
    if (!circle) return '';
    const rows = circle.standings || [];
    const index = rows.findIndex(row => row.is_viewer);
    const place = index >= 0 && rows[index].days_played > 0 ? `#${index + 1} of ${rows.length} this week` : 'Post a score to join the standings';
    return `${String(circle.name).slice(0, 18)} · ${place}`;
  }

  function drawGrowthResult(cx, y) {
    layout.growthStats = { x: 0, y: 0, width: 0, height: 0 };
    layout.growthCircle = { x: 0, y: 0, width: 0, height: 0 };
    if (!growth || state.practiceMode || state.redoMode || layout.height < 560) { growth?.hideResultAction("stats"); growth?.hideResultAction("circle"); return y; }
    const weekly = growth.week();
    ctx.font = `12px ${_fontSerif}`; ctx.fillStyle = C.overlayText; ctx.textAlign = 'center';
    layout.growthStats = { x: cx - 145, y: y + 5, width: 290, height: 44 };
    ctx.fillText(`Played ${weekly.played} ${weekly.played === 1 ? "day" : "days"} this week · View stats`, cx, y + 19);
    ctx.fillStyle = C.gold;
    ctx.fillText(weekly.days.map(day => day.status === 'solved' ? '●' : day.status === 'played' ? '○' : '·').join('   '), cx, y + 37);
    growth.positionResultAction("stats", `Played ${weekly.played} ${weekly.played === 1 ? "day" : "days"} this week. View stats`, layout.growthStats);
    growth.event('weekly_progress_view', {}, 'result');
    y += 49;
    const summary = growthCircleSummary();
    if (!summary) growth.hideResultAction("circle");
    if (summary) {
      layout.growthCircle = { x: cx - 155, y, width: 310, height: 44 };
      ctx.fillStyle = C.overlayText;
      ctx.fillText(summary, cx, y + 15, Math.min(310, layout.width - 24));
      const circle = selectedFriendCircle();
      ctx.fillStyle = C.gold;
      const count = circle.today_posted_count;
      ctx.fillText(Number.isInteger(count) ? `${count} of ${circle.standings.length} posted today · View circle` : 'View circle standings', cx, y + 33);
      growth.positionResultAction("circle", summary + ". View circle", layout.growthCircle);
      y += 44;
    }
    return y;
  }

  // The result overlay waits for the gold sweep. Until it is drawn, the win hit
  // rects still hold the previous result screen's positions, so taps must not use them.
  function isWinOverlayPending() {
    if (!state.winAnimStart || _prefersReducedMotion) return false;
    return performance.now() - state.winAnimStart < 2000;
  }

  function drawWin() {
    if (state.customMode) { drawCustomWin(); return; }
    drawBackground();
    drawTargetHighlight();
    drawGrid();
    drawPivots();
    drawAdminMessage();
    drawSidebar();

    // Gold shimmer on solve — specular light sweep across gold row
    const sweepElapsed = state.winAnimStart ? performance.now() - state.winAnimStart : 9999;
    if (state.winAnimStart && !_prefersReducedMotion && sweepElapsed < 820) {
      drawGoldShimmer(sweepElapsed, 820);
    }

    // Delay overlay so the gold sweep animation is visible
    const overlayDelay = _prefersReducedMotion ? 0 : 2000;
    if (sweepElapsed < overlayDelay) return;

    const overlayFadeT = Math.min(1, (sweepElapsed - overlayDelay) / 300);
    ctx.save();
    ctx.globalAlpha = overlayFadeT;
    // The result content sits over both the letter grid and the sidebar. A
    // translucent veil left those two dense layers legible underneath the
    // result copy, which looked like overlapping controls on phones. Keep a
    // slight sense of depth without sacrificing result-screen readability.
    ctx.fillStyle = _currentTheme === "highContrast"
      ? "#000000"
      : (_currentTheme === "dark" ? "#0d0d0d" : "#1b1b1b");
    ctx.fillRect(0, 0, layout.width, layout.height);

    const isMobile = layout.panelBelow;
    const titleSize = isMobile ? 22 : 26;
    const summarySize = isMobile ? 14 : 16;
    const buttonFontSize = isMobile ? 12 : 13;
    const infoSize = isMobile ? 13 : 14;

    // --- Measure total content height, then center vertically ---
    const cx = layout.width / 2;
    const btnH = 44;
    const submitBtnH = 44;
    const btnGap = 10;
    layout.winResubmit = { x: 0, y: 0, width: 0, height: 0 };
    layout.winShare = { x: 0, y: 0, width: 0, height: 0 };
    layout.winShareImage = { x: 0, y: 0, width: 0, height: 0 };
    layout.winShareVideo = { x: 0, y: 0, width: 0, height: 0 };
    layout.challengeButton = { x: 0, y: 0, width: 0, height: 0 };
    layout.challengeReplayLink = { x: 0, y: 0, width: 0, height: 0 };
    layout.winWatchBest = { x: 0, y: 0, width: 0, height: 0 };
    layout.winWatchOptimal = { x: 0, y: 0, width: 0, height: 0 };
    layout.winPlayYesterday = { x: 0, y: 0, width: 0, height: 0 };
    layout.winPlayAnother = { x: 0, y: 0, width: 0, height: 0 };
    layout.winBackToToday = { x: 0, y: 0, width: 0, height: 0 };
    layout.winSkipLeaderboard = { x: 0, y: 0, width: 0, height: 0 };
    layout.growthStats = { x: 0, y: 0, width: 0, height: 0 };
    layout.growthCircle = { x: 0, y: 0, width: 0, height: 0 };
    const hasSubmitPrompt = !state.shareOnly && !state.submitted && state.leaderboardEligible && !state.hintUsed;
    const oneTapResubmitName = hasSubmitPrompt ? sanitizeName(state.playerName || state.nameInput || "") : "";
    const showOneTapResubmit = Boolean(oneTapResubmitName);
    const rank = !state.shareOnly && state.submitted && state.playerName && state.leaderboardEligible
      ? getPlayerRank(state.leaderboard, state.playerName, state.moves)
      : null;

    // Pre-compute total content height
    let contentH = titleSize + 12; // title + gap
    contentH += summarySize + 6 + 8; // summary line + gap (may wrap — add extra later)
    if (state.practiceMode) {
      contentH += infoSize + 16 + btnH + 8 + btnH + 12 + infoSize + 8;
      if (state.sessionStats.games.length >= 2) contentH += infoSize + 8;
    } else if (!state.shareOnly && state.hintUsed && !state.submitted) {
      contentH += infoSize + 8;
    } else {
      contentH += infoSize + 8; // rating
      if (hasSubmitPrompt) contentH += submitBtnH + 8; // submit to leaderboard button
      if (rank) contentH += 18;
      contentH += btnH; // single row: Share | Replay | Challenge
      contentH += 12; // gap before stats
      if (state.milestoneJustReached) contentH += 26; // one-time milestone celebration line
      contentH += (infoSize - 2) + 14 + (infoSize - 2); // streak + median line
      if (state.redoMode && state.sessionStats.games.length >= 2) contentH += 24;
      if (state.optimalPath.length > 0 || state.leaderboard.length > 0) contentH += 18; // replay link
    }

    let curY = Math.max(40, (layout.height - contentH) / 2);

    const winElapsed = state.winAnimStart ? performance.now() - state.winAnimStart : 9999;

    ctx.fillStyle = C.overlayText;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Title — fade in over 400ms
    const titleAlpha = _prefersReducedMotion ? 1 : Math.min(1, winElapsed / 400);
    ctx.save();
    ctx.globalAlpha = titleAlpha;
    ctx.font = `italic ${titleSize}px ${_fontSerif}`;
    ctx.font = `italic ${isMobile ? 20 : titleSize}px ${_fontSerif}`;
    ctx.fillText(state.shareOnly ? "Share" : state.practiceMode ? "Puzzle complete!" : "You solved today’s PIVOT!", cx, curY + titleSize / 2);
    growth?.resultView();
    ctx.restore();
    curY += titleSize + 12;

    // Summary — animated counter with 200ms delay, easeOutQuart over 800ms
    ctx.font = `${summarySize}px ${_fontSerif}`;
    const countDelay = 200;
    const countDuration = 800;
    const countRaw = Math.max(0, winElapsed - countDelay);
    const countProgress = _prefersReducedMotion ? 1 : Math.min(1, countRaw / countDuration);
    const eased = easeOutQuart(countProgress);
    const displayMoves = Math.round(eased * state.moves);
    let summary = `You solved ${state.targetWord} in ${displayMoves} moves`;
    if (Number.isFinite(state.minMoves) && countProgress >= 1 && (state.practiceMode || state.redoMode || state.customMode)) {
      const delta = Math.max(0, state.moves - state.minMoves);
      const label = state.minExact ? "minimum" : "par";
      summary = `You solved ${state.targetWord} in ${state.moves} moves (${delta} over ${label} of ${state.minMoves})`;
    }
    const summaryMaxWidth = layout.width * 0.88;
    const nextY = wrapText(summary, cx, curY + summarySize / 2, summaryMaxWidth, summarySize + 6);
    curY = nextY + 8;

    // Rating / name prompt / practice
    ctx.font = `italic ${infoSize}px ${_fontSerif}`;
    if (state.practiceMode) {
      ctx.fillStyle = C.overlayTextSoft;
      const practiceRating = Number.isFinite(state.minMoves)
        ? getRating(Math.max(0, state.moves - state.minMoves))
        : "Archive puzzle complete";
      ctx.fillText(practiceRating, cx, curY);
      curY += infoSize + 16;
      // "Play another" button
      ctx.font = `${buttonFontSize}px ${_fontSerif}`;
      const anotherLabel = "Play another day";
      const anotherW = Math.min(layout.width * 0.7, Math.max(150, ctx.measureText(anotherLabel).width + 28));
      const anotherX = cx - anotherW / 2;
      const anotherY = curY;
      layout.winPlayAnother = { x: anotherX, y: anotherY, width: anotherW, height: btnH };
      ctx.fillStyle = C.overlayBtnPrimaryBg;
      ctx.strokeStyle = C.overlayBtnPrimaryStroke;
      ctx.lineWidth = 1;
      fillRoundRect(ctx, anotherX, anotherY, anotherW, btnH, 4);
      ctx.strokeRect(anotherX, anotherY, anotherW, btnH);
      ctx.fillStyle = C.overlayBtnPrimaryText;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(anotherLabel, cx, anotherY + btnH / 2);
      curY += btnH + 8;
      // "Challenge a friend" button (secondary)
      ctx.font = `${Math.max(11, buttonFontSize - 1)}px ${_fontSerif}`;
      const chalLabel = "Challenge a friend";
      const chalW = Math.min(layout.width * 0.7, Math.max(150, ctx.measureText(chalLabel).width + 28));
      const chalX = cx - chalW / 2;
      const chalY = curY;
      layout.challengeButton = { x: chalX, y: chalY, width: chalW, height: btnH };
      ctx.fillStyle = C.overlayBtnBg;
      ctx.strokeStyle = C.overlayBtnStroke;
      ctx.lineWidth = 1;
      fillRoundRect(ctx, chalX, chalY, chalW, btnH, 4);
      ctx.strokeRect(chalX, chalY, chalW, btnH);
      ctx.fillStyle = C.overlayBtnText;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(chalLabel, cx, chalY + btnH / 2);
      curY += btnH + 12;
      // "Back to today" link
      ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
      ctx.fillStyle = C.overlayTextFaint;
      layout.winBackToToday = { x: cx - 60, y: curY - 4, width: 120, height: infoSize + 8 };
      ctx.fillText("Back to today\u2019s puzzle", cx, curY + infoSize / 2);
      curY += infoSize + 8;
      // Session stats (shown after 2+ practice games)
      if (state.sessionStats.games.length >= 2) {
        const sg = state.sessionStats.games;
        let totalOverPar = 0, overParCount = 0, totalGreen = 0, totalMoves = 0;
        for (const g of sg) {
          if (Number.isFinite(g.minMoves)) { totalOverPar += g.moves - g.minMoves; overParCount++; }
          totalGreen += g.green;
          totalMoves += g.moves;
        }
        const avgOver = overParCount > 0 ? (totalOverPar / overParCount).toFixed(1) : null;
        const perfectPct = totalMoves > 0 ? Math.round(100 * totalGreen / totalMoves) : 0;
        let statsText = `Session: ${sg.length} games`;
        if (avgOver !== null) statsText += ` \u00b7 avg ${avgOver} over par`;
        statsText += ` \u00b7 ${perfectPct}% perfect moves`;
        ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
        ctx.fillStyle = C.overlayTextSoft;
        ctx.fillText(statsText, cx, curY + infoSize / 2);
        curY += infoSize + 8;
      }
    } else if (!state.shareOnly && state.hintUsed && !state.submitted) {
      ctx.fillStyle = C.overlayTextFaint;
      ctx.fillText("Hint used \u2014 not eligible for leaderboard", cx, curY);
      curY += infoSize + 4;
    } else {
      ctx.fillStyle = C.overlayText;
      ctx.fillText(growth?.personalLine() || "Daily puzzle complete", cx, curY);
      curY += infoSize + 8;


      if (rank) {
        ctx.fillStyle = "rgba(253, 248, 241, 0.55)";
        ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
        const pct = computePercentile(state.moves);
        const rankText = pct !== null ? `#${rank} \u00b7 Top ${pct}%` : `Rank today: #${rank}`;
        ctx.fillText(rankText, cx, curY);
        curY += 18;
      }

      // One low-friction invitation; replay and verified challenges remain available.
      const actionRailW = Math.min(layout.width * 0.92, isMobile ? 340 : 400);
      const actionRailX = cx - actionRailW / 2;
      const inviteW = Math.floor((actionRailW - btnGap * 2) * 0.5);
      const otherW = (actionRailW - inviteW - btnGap * 2) / 2;
      layout.winShareResults = { x: actionRailX, y: curY, width: inviteW, height: btnH };
      layout.winShareVideo = { x: actionRailX + inviteW + btnGap, y: curY, width: otherW, height: btnH };
      layout.challengeButton = { x: actionRailX + inviteW + otherW + btnGap * 2, y: curY, width: otherW, height: btnH };
      layout.winShareImage = { x: 0, y: 0, width: 0, height: 0 };

      ctx.font = `${Math.max(11, buttonFontSize)}px ${_fontSerif}`;
      ctx.textBaseline = "middle";
      const btnLabels = [
        { rect: layout.winShareResults, label: "Send to a friend", isPrimary: true, focusIndex: 0 },
        { rect: layout.winShareVideo, label: "Replay", isPrimary: false, focusIndex: 1 },
        { rect: layout.challengeButton, label: "Challenge", isPrimary: false, focusIndex: 2 },
      ];
      for (const { rect, label, isPrimary, focusIndex } of btnLabels) {
        ctx.fillStyle = C.overlayBtnBg;
        fillRoundRect(ctx, rect.x, rect.y, rect.width, rect.height, 4);
        // Emphasize the invitation while retaining the secondary actions.
        if (isPrimary) {
          ctx.strokeStyle = C.gold;
          ctx.lineWidth = 1.5;
        } else {
          ctx.strokeStyle = C.overlayBtnStroke;
          ctx.lineWidth = 1;
        }
        ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
        ctx.fillStyle = isPrimary ? C.gold : C.overlayBtnText;
        ctx.fillText(label, rect.x + rect.width / 2, rect.y + btnH / 2 + 1);
        if (_winFocusIndex === focusIndex) {
          ctx.save();
          ctx.strokeStyle = C.gold;
          ctx.lineWidth = 2;
          ctx.strokeRect(rect.x - 1, rect.y - 1, rect.width + 2, rect.height + 2);
          ctx.restore();
        }
      }
      growth?.positionResultAction("friend", "Send today’s puzzle to a friend", layout.winShareResults);
      curY += btnH + 8;

      // Optional leaderboard entry stays below the friend invitation.
      if (hasSubmitPrompt) {
        const submitLabel = oneTapResubmitName ? `Submit as ${oneTapResubmitName}` : "Submit to leaderboard";
        ctx.font = `${buttonFontSize}px ${_fontSerif}`;
        const submitLabelW = ctx.measureText(submitLabel).width;
        const submitW = Math.min(layout.width * 0.7, Math.max(150, submitLabelW + 28));
        const submitX = cx - submitW / 2;
        layout.winResubmit = { x: submitX, y: curY, width: submitW, height: submitBtnH };
        ctx.fillStyle = C.overlayBtnBg;
        ctx.strokeStyle = C.overlayBtnStroke;
        ctx.lineWidth = 1;
        fillRoundRect(ctx, submitX, curY, submitW, submitBtnH, 4);
        ctx.strokeRect(submitX, curY, submitW, submitBtnH);
        ctx.fillStyle = C.overlayBtnText;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(submitLabel, cx, curY + submitBtnH / 2 + 1);
        curY += submitBtnH + 8;
      }


      if (state.challengeResolved && state.challengeFrom && Number.isFinite(state.challengeFrom.moves)) {
        curY += 10;
        const cf = state.challengeFrom;
        ctx.font = `italic ${infoSize - 2}px ${_fontSerif}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        if (state.moves < cf.moves) {
          ctx.fillStyle = C.success;
          ctx.fillText(`You beat ${cf.name}: ${state.moves} to ${cf.moves}`, cx, curY);
        } else if (state.moves > cf.moves) {
          ctx.fillStyle = C.goldEdge;
          ctx.fillText(`${cf.name} leads: ${cf.moves} to ${state.moves}`, cx, curY);
        } else {
          ctx.fillStyle = C.overlayTextSoft;
          ctx.fillText(`Tied at ${state.moves}`, cx, curY);
        }
        curY += 14;

        // Rivalry banner — cumulative head-to-head record vs this opponent.
        // Only renders when the server returned rivalry data on this submit,
        // i.e. when both players have a player_id and the challenge URL
        // included the opponent's id.
        if (state.rivalry && Number.isFinite(Number(state.rivalry.total)) && Number(state.rivalry.total) > 0) {
          const r = state.rivalry;
          const mine = Number(r.my_wins) || 0;
          const theirs = Number(r.opponent_wins) || 0;
          const ties = Number(r.ties) || 0;
          const total = Number(r.total) || 0;
          const streak = Number(r.current_streak) || 0;
          const streakIsMine = r.streak_is_mine === true;

          // Pick a tagline by standing. Special-case the all-draws case so we
          // don't print "Tied with Sam 0-0 · 1 draw" which reads oddly.
          let leadText;
          let leadColor;
          if (mine === 0 && theirs === 0 && ties === total) {
            leadText = total === 1
              ? `Drew with ${cf.name}`
              : `${ties} draws with ${cf.name}`;
            leadColor = C.gold;
          } else if (mine > theirs) {
            leadText = `You lead ${cf.name} ${mine}-${theirs}`;
            leadColor = C.success;
          } else if (mine < theirs) {
            leadText = `${cf.name} leads ${theirs}-${mine}`;
            leadColor = C.goldEdge;
          } else {
            leadText = `Tied ${mine}-${theirs} vs ${cf.name}`;
            leadColor = C.gold;
          }
          if (ties > 0 && !(mine === 0 && theirs === 0)) {
            leadText += ` · ${ties} draw${ties === 1 ? "" : "s"}`;
          }

          ctx.fillStyle = leadColor;
          ctx.font = `600 ${infoSize - 1}px ${_fontSerif}`;
          ctx.fillText(leadText, cx, curY);
          curY += 14;

          if (streak >= 2) {
            const streakText = streakIsMine
              ? `${streak} in a row 🔥`
              : `${cf.name} on a ${streak}-game streak`;
            ctx.fillStyle = streakIsMine ? C.gold : C.overlayTextFaint;
            ctx.font = `italic ${infoSize - 2}px ${_fontSerif}`;
            ctx.fillText(streakText, cx, curY);
            curY += 14;
          }
        }
      }

      if (state.shieldCelebrationVisible) {
        logEvent("shield_celebration_shown", {
          streak_count: state.streakCount,
        }, `${state.dailyKey || getEasternKey()}_${state.sessionId}_shield_celebration_shown`);
        curY += 8;
        ctx.font = `italic ${infoSize - 1}px ${_fontSerif}`;
        ctx.fillStyle = C.gold;
        ctx.fillText("\uD83D\uDEE1\uFE0F Your shield saved your streak", cx, curY);
        curY += 16;
      }

      // Milestone celebration \u2014 one-time, fades after ~6 seconds. Sits just
      // above the compact-stats block so it reads as a personal milestone
      // attached to "your streak".
      if (state.milestoneJustReached) {
        const m = state.milestoneJustReached;
        const meta = STREAK_MILESTONE_LABELS[m];
        if (meta) {
          curY += 8;
          ctx.font = `600 ${infoSize}px ${_fontSerif}`;
          ctx.fillStyle = C.gold;
          ctx.fillText(`${meta.emoji} ${meta.label} streak unlocked!`, cx, curY);
          curY += 18;
        }
      }

      // Compact stats
      curY += 12;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `italic ${infoSize - 2}px ${_fontSerif}`;
      ctx.fillStyle = C.overlayTextFaint;
      // Append a tier badge to the streak line if the player is in a
      // milestone tier \u2014 quiet, persistent acknowledgement on every win.
      const tier = getCurrentMilestoneTier(state.streakDisplay);
      const tierMeta = tier ? STREAK_MILESTONE_LABELS[tier] : null;
      const streakLine = tierMeta
        ? `Streak ${state.streakDisplay} ${state.streakDisplay === 1 ? "day" : "days"} ${tierMeta.emoji}`
        : `Streak ${state.streakDisplay} ${state.streakDisplay === 1 ? "day" : "days"}`;
      ctx.fillText(streakLine, cx, curY);
      curY += 14;

      if (state.metrics.median !== null || Number.isFinite(state.metrics.best)) {
        const medianText = state.metrics.median !== null ? `Median ${state.metrics.median} moves` : "";
        const bestText = Number.isFinite(state.metrics.best) ? `Best ${state.metrics.best}` : "";
        const line = [medianText, bestText].filter(Boolean).join(" \u00b7 ");
        ctx.fillText(line, cx, curY);
      }

      // States the cadence instead of asking for anything, so it cannot nag.
      if (!state.shareOnly && !state.practiceMode && !state.redoMode) {
        curY += 14;
        ctx.font = `italic ${infoSize - 2}px ${_fontSerif}`;
        ctx.fillStyle = C.overlayTextFaint;
        if (growth && layout.height < 560) {
          const days = growth.week().played;
          ctx.fillText(`Played ${days} ${days === 1 ? "day" : "days"} this week`, cx, curY);
        } else if (!growth) ctx.fillText(formatCountdown(getMillisUntilNextPuzzle()), cx, curY);
      }

      // Session stats for redo mode
      if (state.redoMode && state.sessionStats.games.length >= 2) {
        curY += 10;
        const sg = state.sessionStats.games;
        let totalOverPar = 0, overParCount = 0, totalGreen = 0, totalMoves = 0;
        for (const g of sg) {
          if (Number.isFinite(g.minMoves)) { totalOverPar += g.moves - g.minMoves; overParCount++; }
          totalGreen += g.green;
          totalMoves += g.moves;
        }
        const avgOver = overParCount > 0 ? (totalOverPar / overParCount).toFixed(1) : null;
        const perfectPct = totalMoves > 0 ? Math.round(100 * totalGreen / totalMoves) : 0;
        let statsText = `Session: ${sg.length} games`;
        if (avgOver !== null) statsText += ` \u00b7 avg ${avgOver} over par`;
        statsText += ` \u00b7 ${perfectPct}% perfect moves`;
        ctx.fillStyle = C.overlayTextSoft;
        ctx.fillText(statsText, cx, curY);
        curY += 14;
      }

      curY = drawGrowthResult(cx, curY);

      // Prefer the locally verified perfect path. It is available only after
      // the exact token-state search completes and never requires another
      // player's score or replay to stay online.
      if (state.optimalPath.length > 0 && Number.isFinite(state.minMoves) && state.minExact) {
        curY += 18;
        ctx.font = `italic ${infoSize - 3}px ${_fontSerif}`;
        ctx.fillStyle = C.gold;
        const optimalLabel = `Watch the perfect ${state.minMoves}-move path`;
        const optimalW = ctx.measureText(optimalLabel).width + 12;
        layout.winWatchOptimal = { x: cx - optimalW / 2, y: curY - 8, width: optimalW, height: 16 };
        ctx.fillText(optimalLabel, cx, curY);
      } else if (state.leaderboard.length > 0) {
        curY += 18;
        const bestEntry = [...state.leaderboard].sort((a, b) => {
          if (a.moves !== b.moves) return a.moves - b.moves;
          return (a.time || 0) - (b.time || 0);
        })[0];
        ctx.font = `italic ${infoSize - 3}px ${_fontSerif}`;
        ctx.fillStyle = C.overlayTextFaint;
        const watchLabel = `Watch today\u2019s best: ${bestEntry.name}, ${bestEntry.moves} moves`;
        const watchW = ctx.measureText(watchLabel).width + 12;
        layout.winWatchBest = { x: cx - watchW / 2, y: curY - 8, width: watchW, height: 16 };
        ctx.fillText(watchLabel, cx, curY);
      }

      // A first-timer wins and then has nothing to do for 14 hours, on faith.
      // 60+ puzzles sit in an archive they never find. solvedCount is 1 here
      // (today's write already landed), so this is only ever their first win \u2014
      // a returning player cannot see it.
      if (!state.shareOnly && !state.practiceMode && !state.redoMode &&
          getSolvedDailyGamesCountCached() <= 1) {
        const prevKey = getPreviousDailyKey(state.dailyKey || getEasternKey());
        if (prevKey && prevKey >= getArchiveStartKey()) {
          curY += 18;
          ctx.font = `italic ${infoSize - 3}px ${_fontSerif}`;
          ctx.fillStyle = C.gold;
          const playLabel = "Play yesterday\u2019s puzzle \u2192";
          const playW = ctx.measureText(playLabel).width + 12;
          layout.winPlayYesterday = { x: cx - playW / 2, y: curY - 8, width: playW, height: 16 };
          ctx.fillText(playLabel, cx, curY);
        }
      }

      // iOS app suggestion — only on iOS Safari, not in app/PWA, dismissible 30d.
      if (!growth?.followupShown() && !state.shareOnly && !state.practiceMode && shouldShowIosAppCard()) {
        curY += 16;
        const cardW = Math.min(layout.width * 0.88, isMobile ? 320 : 360);
        const cardX = cx - cardW / 2;
        const cardH = 36;
        const dismissSize = 22;
        layout.winIosAppCard = { x: cardX, y: curY, width: cardW, height: cardH };
        layout.winIosAppDismiss = {
          x: cardX + cardW - dismissSize - 6,
          y: curY + (cardH - dismissSize) / 2,
          width: dismissSize,
          height: dismissSize,
        };
        ctx.fillStyle = C.overlayBtnBg;
        fillRoundRect(ctx, cardX, curY, cardW, cardH, 4);
        ctx.strokeStyle = C.gold;
        ctx.lineWidth = 1;
        ctx.strokeRect(cardX, curY, cardW, cardH);
        ctx.fillStyle = C.gold;
        ctx.font = `${buttonFontSize - 1}px ${_fontSerif}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText("Get the iOS app for streak reminders", cardX + 14, curY + cardH / 2 + 1);
        ctx.fillStyle = C.overlayTextFaint;
        ctx.font = `16px ${_fontSerif}`;
        ctx.textAlign = "center";
        ctx.fillText("\u00d7", layout.winIosAppDismiss.x + dismissSize / 2, layout.winIosAppDismiss.y + dismissSize / 2 + 1);
        curY += cardH + 6;
      } else {
        layout.winIosAppCard = { x: 0, y: 0, width: 0, height: 0 };
        layout.winIosAppDismiss = { x: 0, y: 0, width: 0, height: 0 };
      }
    }

    if (state.shareToastUntil > Date.now()) {
      const toastText = state.shareToastMessage || "Copied!";
      const toastY = Math.max(56, layout.height * 0.28);
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `13px ${_fontSerif}`;
      const metrics = ctx.measureText(toastText);
      const padX = 12;
      const boxW = Math.min(layout.width * 0.86, metrics.width + padX * 2);
      const boxH = 24;
      const boxX = layout.width / 2 - boxW / 2;
      const boxY = toastY - boxH / 2;
      ctx.fillStyle = C.toastBg;
      ctx.strokeStyle = C.toastStroke;
      ctx.lineWidth = 1;
      ctx.fillRect(boxX, boxY, boxW, boxH);
      ctx.strokeRect(boxX, boxY, boxW, boxH);
      ctx.fillStyle = C.ink;
      ctx.fillText(toastText, layout.width / 2, toastY + 1);
      ctx.restore();
    }

    const closeSize = 26;
    const closeX = layout.width - closeSize - 16;
    const closeY = 16;
    layout.winClose = { x: closeX, y: closeY, width: closeSize, height: closeSize };
    ctx.fillStyle = C.overlayBtnBg;
    ctx.strokeStyle = C.overlayBtnStroke;
    ctx.lineWidth = 1;
    ctx.fillRect(closeX, closeY, closeSize, closeSize);
    ctx.strokeRect(closeX, closeY, closeSize, closeSize);
    ctx.fillStyle = C.overlayTextFaint;
    ctx.font = `18px ${_fontSerif}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("\u00d7", closeX + closeSize / 2, closeY + closeSize / 2 + 1);
    ctx.restore(); // matches globalAlpha save for overlay fade-in
  }

  function wrapText(text, x, y, maxWidth, lineHeight) {
    const words = text.split(" ");
    let line = "";
    let cursorY = y;
    for (let i = 0; i < words.length; i += 1) {
      const testLine = line ? `${line} ${words[i]}` : words[i];
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        ctx.fillText(line, x, cursorY);
        line = words[i];
        cursorY += lineHeight;
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, x, cursorY);
      cursorY += lineHeight;
    }
    return cursorY;
  }

  // Wrap text into an array of lines (no drawing) using the current ctx.font.
  // Lets callers size a container to the actual line count and vertically
  // center the block — wrapText alone can't, since it draws from a fixed y.
  function wrapTextLines(text, maxWidth) {
    const words = String(text).split(" ");
    const lines = [];
    let line = "";
    for (let i = 0; i < words.length; i += 1) {
      const testLine = line ? `${line} ${words[i]}` : words[i];
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = words[i];
      } else {
        line = testLine;
      }
    }
    if (line) lines.push(line);
    return lines.length ? lines : [""];
  }

  function drawReadmeOverlay() {
    const isMobile = layout.panelBelow;
    const panelWidth = Math.min(layout.width * (isMobile ? 0.92 : 0.78), isMobile ? 520 : 520);
    const panelHeight = Math.min(layout.height * (isMobile ? 0.78 : 0.7), isMobile ? 520 : 420);
    const panelX = (layout.width - panelWidth) / 2;
    const panelY = (layout.height - panelHeight) / 2;

    ctx.fillStyle = C.overlay;
    ctx.fillRect(0, 0, layout.width, layout.height);

    ctx.save();
    ctx.shadowColor = _currentTheme === "dark" ? "rgba(0,0,0,0.3)" : "rgba(27,27,27,0.06)";
    ctx.shadowBlur = 16;
    ctx.shadowOffsetY = 4;
    ctx.fillStyle = C.bg;
    fillRoundRect(ctx, panelX, panelY, panelWidth, panelHeight, 8);
    ctx.restore();
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = C.panelBorder;
    ctx.lineWidth = 1;
    strokeRoundRect(ctx, panelX, panelY, panelWidth, panelHeight, 8);

    const bodySize = isMobile ? 12 : 14;
    const bodyLineHeight = isMobile ? 17 : 20;
    const pad = isMobile ? 18 : 24;

    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    ctx.fillStyle = "#999";
    ctx.font = `${isMobile ? 11 : 12}px ${_fontSerif}`;
    ctx.letterSpacing = "1.5px";
    const labelText = "HOW TO PLAY";
    ctx.fillText(labelText, panelX + pad, panelY + pad);
    ctx.letterSpacing = "0px";

    ctx.fillStyle = colors.ink;
    ctx.font = `italic ${isMobile ? 13 : 15}px ${_fontSerif}`;
    ctx.fillStyle = "#777";
    const taglineWidth = panelWidth - pad * 2;
    const inCustomMode = state.customMode;
    const inClassroomMode = inCustomMode && state.customClassroomMode;
    const taglineText = inClassroomMode
      ? "A live classroom puzzle room built around planning, not speed."
      : inCustomMode
      ? "A custom puzzle room built around planning, not speed."
      : "A daily tile puzzle built around planning, not speed.";
    const taglineEnd = wrapText(taglineText, panelX + pad, panelY + pad + (isMobile ? 18 : 20), taglineWidth, isMobile ? 16 : 18);

    ctx.font = `${bodySize}px ${_fontSerif}`;
    ctx.fillStyle = "#444";
    const lines = inClassroomMode
      ? [
        "Rotate tiles so the yellow center spells the target word.",
        "Tap a pivot dot to rotate the 2\u00d72 block clockwise.",
        "Every rotation counts as one move \u2014 lower is better.",
        "",
        "Players join as observers by default.",
        "The host approves who plays each round.",
        "Wins and losses are tracked for this room session.",
        "",
        "Made by Andrew Leahey.",
        "Email | Mastodon | Bluesky",
      ]
      : inCustomMode
      ? [
        "Rotate tiles so the yellow center spells the target word.",
        "Tap a pivot dot to rotate the 2\u00d72 block clockwise.",
        "Every rotation counts as one move \u2014 lower is better.",
        "",
        "Custom room results do not affect the daily leaderboard.",
        "",
        "Made by Andrew Leahey.",
        "Email | Mastodon | Bluesky",
      ]
      : [
        "Rotate tiles so the yellow center spells the daily word.",
        "Tap a pivot dot to rotate the 2\u00d72 block clockwise.",
        "Every rotation counts as one move \u2014 lower is better.",
        "",
        "The scramble gets harder as the week goes on.",
        "Only your first solve each day counts for the leaderboard.",
        "",
        "Made by Andrew Leahey.",
        "Email | Mastodon | Bluesky",
      ];
    const startY = Math.max(taglineEnd + 6, panelY + (isMobile ? 64 : 68));
    const textWidth = panelWidth - pad * 2;
    let cursorY = startY;
    let mailRect = null;
    let mastoRect = null;
    let blueRect = null;
    lines.forEach((line) => {
      if (!line) {
        cursorY += Math.round(bodyLineHeight * 0.45);
        return;
      }
      if (line === "Email | Mastodon | Bluesky") {
        const startX = panelX + pad;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.font = `${bodySize}px ${_fontSerif}`;

        const emailLabel = "Email";
        const mastoLabel = "Mastodon";
        const blueLabel = "Bluesky";
        const divider = "  \u00b7  ";

        let x = startX;
        ctx.fillStyle = "#999";
        ctx.fillText(emailLabel, x, cursorY);
        const emailWidth = ctx.measureText(emailLabel).width;
        mailRect = { x, y: cursorY, width: emailWidth, height: bodyLineHeight - 4 };
        x += emailWidth;

        ctx.fillStyle = "#ccc";
        ctx.fillText(divider, x, cursorY);
        x += ctx.measureText(divider).width;

        ctx.fillStyle = "#999";
        ctx.fillText(mastoLabel, x, cursorY);
        const mastoWidth = ctx.measureText(mastoLabel).width;
        mastoRect = { x, y: cursorY, width: mastoWidth, height: bodyLineHeight - 4 };
        x += mastoWidth;

        ctx.fillStyle = "#ccc";
        ctx.fillText(divider, x, cursorY);
        x += ctx.measureText(divider).width;

        ctx.fillStyle = "#999";
        ctx.fillText(blueLabel, x, cursorY);
        const blueWidth = ctx.measureText(blueLabel).width;
        blueRect = { x, y: cursorY, width: blueWidth, height: bodyLineHeight - 4 };

        cursorY += bodyLineHeight;
        ctx.fillStyle = "#444";
        return;
      }
      cursorY = wrapText(line, panelX + pad, cursorY, textWidth, bodyLineHeight);
    });
    if (mailRect) layout.readmeMailRect = mailRect;
    if (mastoRect) layout.readmeMastoRect = mastoRect;
    if (blueRect) layout.readmeBlueRect = blueRect;

    const buttonWidth = 100;
    const buttonHeight = 26;
    const buttonY = panelY + panelHeight - 58;
    layout.readmeFeedbackButton = {
      x: panelX + panelWidth - buttonWidth - pad,
      y: buttonY,
      width: buttonWidth,
      height: buttonHeight,
    };
    ctx.fillStyle = C.btnBg;
    ctx.strokeStyle = C.cardStroke;
    ctx.lineWidth = 1;
    ctx.fillRect(
      layout.readmeFeedbackButton.x,
      layout.readmeFeedbackButton.y,
      buttonWidth,
      buttonHeight
    );
    ctx.strokeRect(
      layout.readmeFeedbackButton.x,
      layout.readmeFeedbackButton.y,
      buttonWidth,
      buttonHeight
    );
    ctx.fillStyle = "#999";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${isMobile ? 11 : 12}px ${_fontSerif}`;
    ctx.fillText(
      "Send Feedback",
      layout.readmeFeedbackButton.x + buttonWidth / 2,
      layout.readmeFeedbackButton.y + buttonHeight / 2 + 1
    );

    ctx.textAlign = "center";
    ctx.fillStyle = "#bbb";
    ctx.font = `italic ${isMobile ? 11 : 12}px ${_fontSerif}`;
    ctx.fillText("Tap anywhere to close", panelX + panelWidth / 2, panelY + panelHeight - 16);
  }

  function buildTutorialGrid() {
    // Scripted demo: rotating pivot (0,2) clockwise solves the word PLAY in the gold row.
    // Clockwise rotation of 2x2 at (0,2): (0,2)→(0,3), (0,3)→(1,3), (1,3)→(1,2), (1,2)→(0,2)
    // Before: row1 = [_, P, A, L, Y, _] → After rotation: row1 = [_, P, L, A, Y, _]
    // The L at (0,3) moves down to (1,3)=A's spot, A at (1,3) moves to (1,2), etc.
    // Before: (0,2)=K, (0,3)=L, (1,2)=A, (1,3)=Y → CW → (0,2)=A? No...
    // CW: top-left→top-right, top-right→bottom-right, bottom-right→bottom-left, bottom-left→top-left
    // (0,2)→(0,3), (0,3)→(1,3), (1,3)→(1,2), (1,2)→(0,2)
    // Want after: (1,2)=L, (1,3)=A  → before: (1,2) comes from (1,3), so before (1,3)=L
    //                                          (1,3) comes from (0,3), so before (0,3)=A
    // Want after: (0,2)=? (0,3)=?   → (0,2) comes from (1,2), so before (1,2)=whatever→(0,2)
    //                                  (0,3) comes from (0,2), so before (0,2)=whatever→(0,3)
    // So before: (0,2)=X, (0,3)=A, (1,2)=Y, (1,3)=L → after CW: (0,2)=Y, (0,3)=X, (1,2)=L, (1,3)=A
    // Center row after: P, L, A, Y ✓ (col1=P stays, col2=L, col3=A, col4=Y stays)
    // Wait col4=Y: (1,4) is not in the rotation block, so it stays. Good.
    // Before center row: [_, P, Y-goes-to-0,2, L-from-1,3... ] Hmm let me just be explicit.
    //
    // Before grid:
    //   row0: G  R  X  A  K  W       (0,2)=X, (0,3)=A
    //   row1: M  P  Y  L  Y  T       (1,2)=Y(temp), (1,3)=L  → center shows P,Y,L,Y (scrambled)
    //   row2: D  S  N  E  B  H
    // After CW rotation at pivot(0,2):
    //   (0,2)←(1,2)=Y(temp)  (0,3)←(0,2)=X  (1,2)←(1,3)=L  (1,3)←(0,3)=A
    //   row1: M  P  L  A  Y  T       → center shows P,L,A,Y ✓
    return [
      ["G", "R", "E", "A", "K", "W"],
      ["M", "P", "N", "L", "Y", "T"],
      ["D", "S", "H", "R", "B", "F"],
    ];
  }

  function resizeTutorialCanvas() {
    if (!tutorialCanvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = tutorialCanvas.clientWidth || 320;
    const height = Math.round(width * (GRID_ROWS / GRID_COLS));
    tutorialCanvas.width = Math.round(width * dpr);
    tutorialCanvas.height = Math.round(height * dpr);
    tutorialCanvas.style.height = `${height}px`;
    const tctx = tutorialCanvas.getContext("2d");
    if (tctx) {
      tctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  function drawTutorialFrame(timestamp) {
    if (!tutorialCanvas || !tutorialState.running) return;
    const tctx = tutorialCanvas.getContext("2d");
    if (!tctx) return;

    // Scripted loop: show scrambled → pulse target dot → rotate → show solved → reset
    // Phase timing (ms): 0-1800 pulse dot, 1800-2300 rotate, 2300-4000 solved, 4000+ reset
    const PHASE_PULSE = 1800;
    const PHASE_ROTATE_START = PHASE_PULSE;
    const PHASE_ROTATE_DUR = 500;
    const PHASE_SOLVED_START = PHASE_ROTATE_START + PHASE_ROTATE_DUR;
    const PHASE_SOLVED_DUR = 1700;
    const CYCLE = PHASE_SOLVED_START + PHASE_SOLVED_DUR;
    const TARGET_PIVOT = { r: 0, c: 2 }; // rotating this solves PLAY

    if (!tutorialState.grid) {
      tutorialState.grid = buildTutorialGrid();
      tutorialState.cycleStart = timestamp;
    }

    const elapsed = timestamp - tutorialState.cycleStart;
    const phase = elapsed % CYCLE;

    // Reset grid at start of each cycle
    if (phase < 16 && elapsed > CYCLE / 2) {
      tutorialState.grid = buildTutorialGrid();
      tutorialState.cycleStart = timestamp;
    }

    // Trigger rotation at phase boundary
    if (!tutorialState.anim && phase >= PHASE_ROTATE_START && phase < PHASE_ROTATE_START + 50) {
      const pivot = TARGET_PIVOT;
      const animCells = [
        { r: pivot.r, c: pivot.c },
        { r: pivot.r, c: pivot.c + 1 },
        { r: pivot.r + 1, c: pivot.c },
        { r: pivot.r + 1, c: pivot.c + 1 },
      ];
      const fromLetters = animCells.map((cell) => ({
        ...cell,
        letter: tutorialState.grid[cell.r][cell.c],
      }));
      rotateBlock(tutorialState.grid, pivot.r, pivot.c, true);
      tutorialState.anim = {
        start: timestamp,
        duration: PHASE_ROTATE_DUR,
        pivot,
        clockwise: true,
        cells: fromLetters,
      };
    }

    const width = tutorialCanvas.clientWidth || 320;
    const height = tutorialCanvas.clientHeight || 160;
    const cellSize = Math.min(width / GRID_COLS, height / GRID_ROWS);
    const offsetX = (width - cellSize * GRID_COLS) / 2;
    const offsetY = (height - cellSize * GRID_ROWS) / 2;
    const isSolved = phase >= PHASE_SOLVED_START;

    tctx.fillStyle = C.bg;
    tctx.fillRect(0, 0, width, height);

    // Gold gradient highlight row
    tctx.save();
    for (let i = 0; i < CENTER_WORD_LENGTH; i += 1) {
      const col = CENTER_START_COL + i;
      const x = offsetX + col * cellSize;
      const y = offsetY + CENTER_ROW * cellSize;
      const grad = tctx.createLinearGradient(x, y, x, y + cellSize);
      grad.addColorStop(0, C.gold);
      grad.addColorStop(1, C.goldEdge);
      tctx.fillStyle = grad;
      tctx.fillRect(x, y, cellSize, cellSize);
      tctx.strokeStyle = C.goldEdge;
      tctx.lineWidth = 1.5;
      tctx.strokeRect(x, y, cellSize, cellSize);
    }
    tctx.restore();

    // Grid lines
    tctx.strokeStyle = C.gridLine;
    tctx.lineWidth = 1;
    for (let r = 0; r <= GRID_ROWS; r += 1) {
      tctx.beginPath();
      tctx.moveTo(offsetX, offsetY + r * cellSize);
      tctx.lineTo(offsetX + GRID_COLS * cellSize, offsetY + r * cellSize);
      tctx.stroke();
    }
    for (let c = 0; c <= GRID_COLS; c += 1) {
      tctx.beginPath();
      tctx.moveTo(offsetX + c * cellSize, offsetY);
      tctx.lineTo(offsetX + c * cellSize, offsetY + GRID_ROWS * cellSize);
      tctx.stroke();
    }

    // Letters
    const anim = tutorialState.anim;
    const skipCells = anim ? new Set(anim.cells.map((cell) => `${cell.r}-${cell.c}`)) : null;
    tctx.fillStyle = C.ink;
    tctx.font = `600 ${Math.floor(cellSize * 0.52)}px ${_fontUI}`;
    tctx.textAlign = "center";
    tctx.textBaseline = "middle";
    for (let r = 0; r < GRID_ROWS; r += 1) {
      for (let c = 0; c < GRID_COLS; c += 1) {
        if (skipCells && skipCells.has(`${r}-${c}`)) continue;
        const x = offsetX + c * cellSize + cellSize / 2;
        const y = offsetY + r * cellSize + cellSize / 2;
        tctx.fillText(tutorialState.grid[r][c], x, y);
      }
    }

    // Animate rotating letters
    if (anim) {
      const ael = timestamp - anim.start;
      const t = Math.min(1, ael / anim.duration);
      const eased = easeInOutCubic(t);
      const angle = (Math.PI / 2) * eased;
      const pivotX = offsetX + (anim.pivot.c + 1) * cellSize;
      const pivotY = offsetY + (anim.pivot.r + 1) * cellSize;
      anim.cells.forEach((cell) => {
        const fromX = offsetX + cell.c * cellSize + cellSize / 2;
        const fromY = offsetY + cell.r * cellSize + cellSize / 2;
        const dx = fromX - pivotX;
        const dy = fromY - pivotY;
        const rx = dx * Math.cos(angle) - dy * Math.sin(angle);
        const ry = dx * Math.sin(angle) + dy * Math.cos(angle);
        tctx.fillText(cell.letter, pivotX + rx, pivotY + ry);
      });
      if (t >= 1) {
        tutorialState.anim = null;
      }
    }

    // Pivot dots (matching game style)
    const pivotRadius = Math.max(3, cellSize * 0.08);
    const isRotating = phase >= PHASE_ROTATE_START && phase < PHASE_SOLVED_START;
    for (let r = 0; r < GRID_ROWS - 1; r += 1) {
      for (let c = 0; c < GRID_COLS - 1; c += 1) {
        const x = offsetX + (c + 1) * cellSize;
        const y = offsetY + (r + 1) * cellSize;
        const isTarget = r === TARGET_PIVOT.r && c === TARGET_PIVOT.c;
        const isPulsing = isTarget && phase < PHASE_ROTATE_START;
        const dr = (isPulsing || isRotating && isTarget) ? pivotRadius * 0.72 : pivotRadius * 0.50;

        // Opaque base
        const touchesGold = (r === CENTER_ROW - 1 || r === CENTER_ROW) &&
          (c >= CENTER_START_COL - 1 && c < CENTER_START_COL + CENTER_WORD_LENGTH);
        tctx.beginPath();
        tctx.arc(x, y, dr + 2, 0, Math.PI * 2);
        tctx.fillStyle = touchesGold ? C.goldEdge : (_currentTheme === "dark" ? "#131313" : C.bg);
        tctx.fill();

        tctx.beginPath();
        tctx.arc(x, y, dr, 0, Math.PI * 2);
        if (_currentTheme === "dark") {
          const tGrad = tctx.createRadialGradient(x - dr * 0.3, y - dr * 0.3, 0, x, y, dr);
          if (isPulsing) {
            tGrad.addColorStop(0, "#F0D060");
            tGrad.addColorStop(1, C.gold);
          } else {
            tGrad.addColorStop(0, "#E0D0A0");
            tGrad.addColorStop(1, "#C4B480");
          }
          tctx.fillStyle = tGrad;
          tctx.save();
          tctx.shadowColor = isPulsing ? "rgba(212,175,55,0.9)" : "rgba(212,175,55,0.55)";
          tctx.shadowBlur = isPulsing ? 16 : 10;
          tctx.fill();
          tctx.restore();
          tctx.strokeStyle = "rgba(236,233,225,0.3)";
          tctx.lineWidth = 1;
          tctx.stroke();
        } else {
          const bodyGrad = tctx.createRadialGradient(x - dr * 0.25, y - dr * 0.25, 0, x, y, dr);
          if (isPulsing) {
            bodyGrad.addColorStop(0, "#504B44");
            bodyGrad.addColorStop(1, "#363330");
          } else {
            bodyGrad.addColorStop(0, "#484440");
            bodyGrad.addColorStop(1, "#333028");
          }
          tctx.fillStyle = bodyGrad;
          tctx.fill();
        }

        // Pulsing ring on target dot before rotation
        if (isPulsing) {
          const pulse = 0.5 + 0.5 * Math.sin(timestamp / 200);
          tctx.save();
          tctx.beginPath();
          tctx.arc(x, y, pivotRadius * (1.8 + pulse * 0.5), 0, Math.PI * 2);
          tctx.strokeStyle = _currentTheme === "dark" ? `rgba(212,175,55,${0.3 + pulse * 0.3})` : `rgba(80,75,68,${0.2 + pulse * 0.25})`;
          tctx.lineWidth = 2;
          tctx.stroke();
          tctx.restore();

          // "tap" label above the pulsing dot
          tctx.save();
          tctx.globalAlpha = 0.5 + pulse * 0.3;
          tctx.fillStyle = C.muted;
          tctx.font = `${Math.floor(cellSize * 0.22)}px ${_fontSerif}`;
          tctx.textAlign = "center";
          tctx.textBaseline = "bottom";
          tctx.fillText("tap", x, y - pivotRadius * 2.8);
          // Small arrow pointing down to dot
          tctx.beginPath();
          const arrowY = y - pivotRadius * 2.5;
          tctx.moveTo(x, arrowY + cellSize * 0.08);
          tctx.lineTo(x - cellSize * 0.04, arrowY);
          tctx.lineTo(x + cellSize * 0.04, arrowY);
          tctx.closePath();
          tctx.fillStyle = C.muted;
          tctx.fill();
          tctx.restore();
        }
      }
    }

    // "Solved!" flash after rotation completes
    if (isSolved) {
      const solvedT = Math.min(1, (phase - PHASE_SOLVED_START) / 400);
      tctx.save();
      tctx.globalAlpha = solvedT * 0.85;
      tctx.fillStyle = _currentTheme === "dark" ? C.gold : C.goldEdge;
      tctx.font = `600 ${Math.floor(cellSize * 0.38)}px ${_fontUI}`;
      tctx.textAlign = "center";
      tctx.textBaseline = "middle";
      const checkX = offsetX + GRID_COLS * cellSize / 2;
      const checkY = offsetY - cellSize * 0.25;
      tctx.fillText("✓  PLAY", checkX, checkY);
      tctx.restore();
    }

    tutorialState.rafId = requestAnimationFrame(drawTutorialFrame);
  }

  function startTutorialAnimation() {
    if (!tutorialCanvas) return;
    tutorialState.running = true;
    tutorialState.grid = null;
    tutorialState.cycleStart = 0;
    tutorialState.anim = null;
    resizeTutorialCanvas();
    tutorialState.rafId = requestAnimationFrame(drawTutorialFrame);
  }

  function stopTutorialAnimation() {
    tutorialState.running = false;
    if (tutorialState.rafId) {
      cancelAnimationFrame(tutorialState.rafId);
      tutorialState.rafId = 0;
    }
  }

  function openTutorialOverlay() {
    if (growth) { growth.showDemo(); return; }
    if (!tutorialOverlay) return;
    if (!tutorialOverlay.classList.contains("show")) captureOverlayFocus();
    tutorialStep = 0;
    updateTutorialStep();
    tutorialOverlay.classList.add("show");
    tutorialOverlay.setAttribute("aria-hidden", "false");
    state.showTutorial = true;
    state.tutorialIgnoreUntil = Date.now() + 250;
    startTutorialAnimation();
  }

  function closeTutorialOverlay() {
    if (!tutorialOverlay) return;
    const hadFocus = tutorialOverlay.classList.contains("show");
    fadeOutOverlay(tutorialOverlay);
    if (hadFocus) restoreOverlayFocus();
    state.showTutorial = false;
    stopTutorialAnimation();
    markTutorialSeen();
  }

  function fadeOutOverlay(el) {
    if (!el || !el.classList.contains("show")) return;
    el.classList.remove("show");
    el.classList.add("hiding");
    updateHelpButtonPlacement();
    // Canvas accessible buttons stay hidden while any overlay is open and are
    // only re-shown when the canvas repaints.
    scheduleRenderLoop();
    el.addEventListener("animationend", function handler() {
      el.classList.remove("hiding");
      el.setAttribute("aria-hidden", "true");
      el.removeEventListener("animationend", handler);
    }, { once: true });
  }

  // --- Leaderboard HTML overlay ---
  const _lbNameEmojis = { "Geenybeans": "\u{1F49A} ", "Abby": "\u{1F430} ", "Jake": "\u2696\uFE0F " };
  const _lbOverlay = document.getElementById("leaderboard-overlay");
  const _lbCard = document.getElementById("leaderboard-card");
  const _lbEntries = document.getElementById("leaderboard-entries");
  const _lbNav = document.getElementById("leaderboard-nav");
  const _lbFooter = document.getElementById("leaderboard-footer");
  const _lbScopeTabs = document.getElementById("leaderboard-scope-tabs");
  const _lbTabs = document.getElementById("leaderboard-tabs");
  const _lbPerPage = 15;

  function setLeaderboardScope(scope) {
    state.leaderboardScope = scope === "circles" ? "circles" : "global";
    state.leaderboardPage = 0;
    _lbScopeTabs?.querySelectorAll(".lb-scope-tab").forEach((button) => {
      const active = button.dataset.scope === state.leaderboardScope;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    if (_lbTabs) _lbTabs.hidden = state.leaderboardScope !== "global";
    if (state.leaderboardScope === "circles") loadCircleLeaderboards();
    renderLeaderboardEntries();
  }

  function openLeaderboardOverlay() {
    if (_lbOverlay && !_lbOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    state.showLeaderboardOverlay = true;
    state.leaderboardPage = 0;
    state.leaderboardScope = "global";
    state.leaderboardTab = "today";
    _lbOverlay.classList.add("show");
    _lbOverlay.setAttribute("aria-hidden", "false");
    if (window.PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeScrollControl)
      window.webkit.messageHandlers.nativeScrollControl.postMessage(true);
    // Reset to the familiar public table. My Circles remains one obvious tap
    // away and fetches private data only when selected.
    setLeaderboardScope("global");
    syncLeaderboardPeriodTabs();
  }

  function syncLeaderboardPeriodTabs() {
    _lbTabs?.querySelectorAll(".lb-tab").forEach((b) => {
      const active = b.dataset.tab === state.leaderboardTab;
      b.classList.toggle("active", active);
      b.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function closeLeaderboardOverlay() {
    const hadFocus = _lbOverlay && _lbOverlay.classList.contains("show");
    state.showLeaderboardOverlay = false;
    state.leaderboardScope = "global";
    state.leaderboardTab = "today";
    fadeOutOverlay(_lbOverlay);
    if (hadFocus) restoreOverlayFocus();
    if (window.PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeScrollControl)
      window.webkit.messageHandlers.nativeScrollControl.postMessage(false);
  }

  function renderLeaderboardEntries() {
    if (!_lbEntries || !_lbNav || !_lbFooter) return;
    if (state.leaderboardScope === "circles") {
      renderCircleLeaderboardEntries();
      return;
    }
    const tab = state.leaderboardTab;
    let html = "";
    let navHtml = "";
    let footerHtml = "";

    if (tab === "today") {
      const entries = [...state.leaderboard].sort((a, b) => {
        if (a.moves !== b.moves) return a.moves - b.moves;
        return (a.time || 0) - (b.time || 0);
      });
      const hasEntries = entries.length > 0;
      if (state.leaderboardLoading && !hasEntries) {
        html = '<div class="lb-loading">Loading\u2026</div>';
      } else if (!hasEntries && state.leaderboardError) {
        html = `<div class="lb-error">${escapeHtml(state.leaderboardError)}</div><button class="lb-retry" id="lb-retry-btn">Retry</button>`;
      } else {
        if (state.leaderboardError) {
          html += `<div class="lb-error">${escapeHtml(state.leaderboardError)}</div><button class="lb-retry" id="lb-retry-btn">Retry</button>`;
        }
        const totalPages = Math.max(1, Math.ceil(entries.length / _lbPerPage));
        if (state.leaderboardPage >= totalPages) state.leaderboardPage = totalPages - 1;
        const pageStart = state.leaderboardPage * _lbPerPage;
        const show = entries.slice(pageStart, pageStart + _lbPerPage);
        const bestMoves = entries.length ? entries[0].moves : null;
        if (!show.length && !state.leaderboardError) {
          html += '<div class="lb-empty">No entries yet</div>';
        } else {
          const dayNum = getDayNumber();
          show.forEach((entry, index) => {
            const rank = pageStart + index;
            const crown = (bestMoves !== null && entry.moves === bestMoves) ? "\u{1F451} " : "";
            const flair = _lbNameEmojis[entry.name] || "";
            const replayBtn = state.won ? ` <button type="button" class="lb-replay" data-replay-name="${escapeHtml(entry.name)}" data-replay-day="${dayNum}" title="Watch replay" aria-label="Watch ${escapeHtml(entry.name)}’s replay">\u{1F3AC}</button>` : "";
            html += `<div class="lb-entry">${rank + 1}. ${crown}${escapeHtml(entry.name)}${flair} \u2014 ${entry.moves}${replayBtn}</div>`;
          });
        }
        if (totalPages > 1) {
          navHtml = `<button class="lb-nav-btn" id="lb-prev" ${state.leaderboardPage === 0 ? "disabled" : ""}>Prev.</button>` +
            `<span class="lb-page-info">Page ${state.leaderboardPage + 1} / ${totalPages}</span>` +
            `<button class="lb-nav-btn" id="lb-next" ${state.leaderboardPage >= totalPages - 1 ? "disabled" : ""}>Next.</button>`;
        }
      }
    } else {
      const isWeek = tab === "week";
      const data = isWeek ? state.weeklyLeaderboard : state.monthlyLeaderboard;
      const loading = isWeek ? state.weeklyLeaderboardLoading : state.monthlyLeaderboardLoading;
      const failed = isWeek ? state._weeklyLeaderboardFailed : state._monthlyLeaderboardFailed;
      if (loading) {
        html = '<div class="lb-loading">Loading\u2026</div>';
      } else if (failed && (!data || !data.length)) {
        html = '<div class="lb-error">Could not load leaderboard</div><button class="lb-retry" id="lb-agg-retry-btn">Retry</button>';
      } else if (!data || !data.length) {
        html = '<div class="lb-empty">No entries yet</div>';
      } else {
        const combined = [...data];
        const totalPages = Math.max(1, Math.ceil(combined.length / _lbPerPage));
        if (state.leaderboardPage >= totalPages) state.leaderboardPage = totalPages - 1;
        const pageStart = state.leaderboardPage * _lbPerPage;
        const show = combined.slice(pageStart, pageStart + _lbPerPage);
        let rank = combined.slice(0, pageStart).filter(e => e.qualified).length;
        show.forEach(entry => {
          const flair = _lbNameEmojis[entry.name] || "";
          const days = `${entry.games} day${entry.games !== 1 ? "s" : ""}`;
          if (entry.qualified) {
            rank++;
            html += `<div class="lb-entry">${rank}. ${escapeHtml(entry.name)}${flair} <span class="lb-stat">\u2014 ${days}, avg ${entry.avgMoves}</span></div>`;
          } else {
            html += `<div class="lb-entry unqualified">${escapeHtml(entry.name)}${flair} <span class="lb-stat">\u2014 ${days}, avg ${entry.avgMoves}</span></div>`;
          }
        });
        if (totalPages > 1) {
          navHtml = `<button class="lb-nav-btn" id="lb-prev" ${state.leaderboardPage === 0 ? "disabled" : ""}>Prev.</button>` +
            `<span class="lb-page-info">Page ${state.leaderboardPage + 1} / ${totalPages}</span>` +
            `<button class="lb-nav-btn" id="lb-next" ${state.leaderboardPage >= totalPages - 1 ? "disabled" : ""}>Next.</button>`;
        }
        const minDays = isWeek ? 2 : 4;
        footerHtml = `Ranked by days played, then avg moves \u00b7 Min ${minDays} days`;
      }
    }

    _lbEntries.innerHTML = html;
    _lbNav.innerHTML = navHtml;
    _lbFooter.textContent = footerHtml;

    // Wire retry buttons
    const retryBtn = document.getElementById("lb-retry-btn");
    if (retryBtn) retryBtn.onclick = () => { retryLeaderboardFetch(200); renderLeaderboardEntries(); };
    const aggRetryBtn = document.getElementById("lb-agg-retry-btn");
    if (aggRetryBtn) aggRetryBtn.onclick = () => {
      if (state.leaderboardTab === "week") fetchWeeklyLeaderboard();
      else if (state.leaderboardTab === "month") fetchMonthlyLeaderboard();
    };
    // Wire nav buttons
    const prevBtn = document.getElementById("lb-prev");
    const nextBtn = document.getElementById("lb-next");
    if (prevBtn) prevBtn.onclick = () => { state.leaderboardPage = Math.max(0, state.leaderboardPage - 1); renderLeaderboardEntries(); };
    if (nextBtn) nextBtn.onclick = () => { state.leaderboardPage += 1; renderLeaderboardEntries(); };
    // Wire replay buttons
    _lbEntries.querySelectorAll(".lb-replay").forEach((el) => {
      el.onclick = () => {
        const name = el.dataset.replayName;
        const day = el.dataset.replayDay;
        if (name && day) {
          navigateTo(`${CANONICAL_URL}/replay/#${encodeURIComponent(name)}/${day}`, true);
        }
      };
    });
  }

  function formatCircleWeek(circle) {
    const start = new Date(`${circle?.week_start || ""}T12:00:00Z`);
    const end = new Date(`${circle?.week_end || ""}T12:00:00Z`);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return "This week";
    const startLabel = start.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    const endLabel = end.toLocaleDateString(undefined, {
      month: start.getUTCMonth() === end.getUTCMonth() ? undefined : "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return `${startLabel}–${endLabel}`;
  }

  function circleScoreLabel(row) {
    const days = Number(row?.days_played) || 0;
    const moves = Number(row?.total_moves) || 0;
    if (!days) return "No scores yet";
    return `${days} day${days === 1 ? "" : "s"} · ${moves} moves`;
  }

  function selectedFriendCircle() {
    const circles = Array.isArray(state.friendCircles) ? state.friendCircles : [];
    const selected = circles.find((circle) => circle.id === state.selectedFriendCircleId) || circles[0] || null;
    if (selected && selected.id !== state.selectedFriendCircleId) state.selectedFriendCircleId = selected.id;
    safeSetItem("pivot_selected_circle_v1", selected?.id || "");
    return selected;
  }

  async function loadCircleLeaderboards(force = false) {
    const cacheFresh = Array.isArray(state.friendCircles) &&
      Date.now() - state.friendCirclesFetchedAt < 30000 && !state.friendCirclesError;
    if (state.friendCirclesLoading || (!force && cacheFresh)) return;
    state.friendCirclesLoading = true;
    state.friendCirclesError = "";
    if (state.showLeaderboardOverlay && state.leaderboardScope === "circles") renderLeaderboardEntries();
    try {
      const data = await circleRequest("list");
      state.friendCircles = Array.isArray(data.circles) ? data.circles : [];
      state.friendCirclesFetchedAt = Date.now();
      const selected = selectedFriendCircle();
      state.selectedFriendCircleId = selected?.id || "";
    } catch (err) {
      state.friendCirclesError = err?.message || "Could not load your circles";
      if (!Array.isArray(state.friendCircles)) state.friendCircles = [];
    } finally {
      state.friendCirclesLoading = false;
      if (state.showLeaderboardOverlay && state.leaderboardScope === "circles") renderLeaderboardEntries();
    }
  }

  function openCircleManagerFromLeaderboard() {
    closeLeaderboardOverlay();
    setTimeout(() => openFriendCircles(), 90);
  }

  function renderCircleLeaderboardEntries() {
    let html = "";
    let footerHtml = "";
    const circles = Array.isArray(state.friendCircles) ? state.friendCircles : [];
    if (state.friendCirclesLoading && !circles.length) {
      html = '<div class="lb-loading">Loading your circles…</div>';
    } else if (state.friendCirclesError && !circles.length) {
      html = `<div class="lb-empty"><strong>We couldn’t load your circles.</strong><span>${escapeHtml(state.friendCirclesError)}</span></div>` +
        '<button class="lb-retry" id="lb-circle-retry-btn" type="button">Try again</button>';
    } else if (!circles.length) {
      html = '<div class="lb-circle-empty"><strong>No circles yet</strong><span>Create one or join with a friend’s invite code.</span>' +
        '<button class="lb-primary-action" id="lb-manage-circles" type="button">Create or join a circle</button></div>';
    } else {
      const circle = selectedFriendCircle();
      const standings = Array.isArray(circle?.standings) ? circle.standings : [];
      const viewerIndex = standings.findIndex((row) => row?.is_viewer);
      const viewer = viewerIndex >= 0 ? standings[viewerIndex] : null;
      if (circles.length > 1) {
        html += '<div class="lb-circle-picker" role="tablist" aria-label="Choose a circle">';
        for (const item of circles) {
          const active = item.id === circle.id;
          html += `<button class="lb-circle-chip${active ? " active" : ""}" type="button" role="tab" aria-selected="${active ? "true" : "false"}" data-circle-id="${escapeHtmlAttr(item.id)}">${escapeHtml(String(item.name || "Circle"))}</button>`;
        }
        html += '</div>';
      }
      html += `<div class="lb-circle-heading"><div><strong>${escapeHtml(String(circle?.name || "Friend Circle"))}</strong><span>${escapeHtml(formatCircleWeek(circle))}</span></div>` +
        '<button class="lb-text-action" id="lb-manage-circles" type="button">Manage</button></div>';
      if (viewer) {
        html += `<div class="lb-you-summary"><span>Your place</span><strong>#${viewerIndex + 1} of ${standings.length}</strong><small>${escapeHtml(circleScoreLabel(viewer))}</small></div>`;
      } else {
        html += '<div class="lb-you-summary"><span>Your place</span><strong>Not ranked yet</strong><small>Play and submit a daily score to appear.</small></div>';
      }
      html += '<div class="lb-circle-table" role="list">';
      if (!standings.length) {
        html += '<div class="lb-empty">Invite friends to start the table.</div>';
      } else {
        standings.forEach((row, index) => {
          html += `<div class="lb-circle-row${row?.is_viewer ? " is-viewer" : ""}" role="listitem"><span class="lb-circle-rank">${index + 1}</span>` +
            `<span class="lb-circle-name">${escapeHtml(String(row?.display_name || "Player"))}${row?.is_viewer ? " <em>(you)</em>" : ""}</span>` +
            `<span class="lb-circle-score">${escapeHtml(circleScoreLabel(row))}</span></div>`;
        });
      }
      html += '</div>';
      const todayStatus = state.submitted
        ? "Today’s submitted score counts here automatically."
        : hasPlayedTodayForCircle()
        ? "Your solve is not in circles yet. Submit it from today’s result."
        : "Play today and submit once to score in every circle.";
      html += `<div class="lb-circle-today"><span>${escapeHtml(todayStatus)}</span><button class="lb-primary-action" id="lb-circle-play" type="button">${hasPlayedTodayForCircle() ? "View today’s result" : "Play today’s PIVOT"}</button></div>`;
      footerHtml = "Ranked by days played, then fewest total moves · Private to circle members";
      if (state.friendCirclesError) html = `<div class="lb-error">${escapeHtml(state.friendCirclesError)}</div>` + html;
    }

    _lbEntries.innerHTML = html;
    _lbNav.innerHTML = "";
    _lbFooter.textContent = footerHtml;
    document.getElementById("lb-circle-retry-btn")?.addEventListener("click", () => loadCircleLeaderboards(true));
    document.getElementById("lb-manage-circles")?.addEventListener("click", openCircleManagerFromLeaderboard);
    document.getElementById("lb-circle-play")?.addEventListener("click", () => {
      closeLeaderboardOverlay();
      if (_isCustomPage || state.customMode || state.practiceMode) {
        window.location.href = CANONICAL_URL;
      } else if (!hasPlayedTodayForCircle() && state.mode === "menu") {
        resetGame();
      }
    });
    _lbEntries.querySelectorAll(".lb-circle-chip").forEach((button) => {
      button.addEventListener("click", () => {
        state.selectedFriendCircleId = button.dataset.circleId || "";
        renderLeaderboardEntries();
      });
    });
  }

  if (_lbScopeTabs) _lbScopeTabs.addEventListener("click", (event) => {
    const button = event.target.closest?.(".lb-scope-tab");
    if (!button?.dataset.scope) return;
    setLeaderboardScope(button.dataset.scope);
  });

  // Tab click handlers
  // Guarded: the leaderboard markup only exists on the main page, not /custom/.
  if (_lbTabs) _lbTabs.addEventListener("click", (e) => {
    const tab = e.target.dataset?.tab;
    if (!tab) return;
    state.leaderboardTab = tab;
    state.leaderboardPage = 0;
    syncLeaderboardPeriodTabs();
    if (tab === "week" && (!state.weeklyLeaderboard || state._weeklyLeaderboardFailed) && !state.weeklyLeaderboardLoading) {
      fetchWeeklyLeaderboard();
    }
    if (tab === "month" && (!state.monthlyLeaderboard || state._monthlyLeaderboardFailed) && !state.monthlyLeaderboardLoading) {
      fetchMonthlyLeaderboard();
    }
    renderLeaderboardEntries();
  });

  // Click outside card to close
  if (_lbOverlay) _lbOverlay.addEventListener("click", (e) => {
    if (e.target === _lbOverlay && Date.now() - _overlayOpenedAt > 400) closeLeaderboardOverlay();
  });

  // Helper — escapeHtml
  function escapeHtml(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // Countdown overlay: 3-2-1-GO! before game start
  let _lastCountdownN = 0;
  function drawCountdownOverlay() {
    const now = performance.now();
    const remaining = state.customCountdownUntil - now;
    if (remaining <= 0) return;
    const secondsLeft = Math.ceil(remaining / 1000);
    const fraction = (remaining % 1000) / 1000; // 1→0 within each second

    // Full-screen background
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, layout.width, layout.height);

    const cx = layout.width / 2;
    const cy = layout.height / 2;

    // Difficulty label above
    const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
    ctx.font = `italic 14px ${_fontSerif}`;
    ctx.fillStyle = C.mutedSoft;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const roundText = state.customRoundNumber > 1 ? `Round ${state.customRoundNumber} \u00b7 ${diffLabel}` : diffLabel;
    ctx.fillText(roundText, cx, cy - 60);

    // Number with scale/fade animation
    const scale = 0.6 + 0.4 * fraction;
    const alpha = 0.3 + 0.7 * fraction;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.globalAlpha = alpha;
    ctx.font = `bold 72px ${_fontSerif}`;
    ctx.fillStyle = C.ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(secondsLeft), 0, 0);
    ctx.restore();

    // "Get ready" text below
    ctx.font = `13px ${_fontSerif}`;
    ctx.fillStyle = C.mutedSoft;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Get ready\u2026", cx, cy + 55);

    // Play tick sound on each new second
    if (secondsLeft !== _lastCountdownN) {
      _lastCountdownN = secondsLeft;
      playCountdownTick(secondsLeft <= 0);
    }
  }

  function playCountdownTick(isGo) {
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "sine";
    osc.frequency.value = isGo ? 880 : 440;
    gain.gain.setValueAtTime(0.30, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.15);
    osc.connect(gain).connect(_masterGain);
    osc.start();
    osc.stop(ac.currentTime + 0.15);
  }

  // Animation loop for countdown and reconnect UI. Outside those transient
  // states, callers get one repaint only so custom observation does not become
  // a permanent battery-draining loop.
  let _renderLoopActive = false;
  function scheduleRenderLoop() {
    if (_renderLoopActive) return;
    _renderLoopActive = true;
    requestAnimationFrame(renderLoop);
  }

  function scheduleObserverRepaint() {
    if (state.customObserveMode && isCurrentCustomPlayerObserver()) {
      scheduleRenderLoop();
    }
  }

  function renderLoop() {
    // The guard flag MUST be cleared even if render() throws. Leaving it set
    // would suppress every future scheduleRenderLoop() call and freeze the UI
    // permanently, turning a single bad frame into a dead page.
    const keepAnimating =
      (state.customCountdownUntil > 0 && performance.now() < state.customCountdownUntil) ||
      state.customReconnecting ||
      (state.mode === "play" && state.challengeResolved && Array.isArray(state.challengeFrom?.ghostHistory) &&
        state.ghostMoveIndex < state.challengeFrom.ghostHistory.length);
    try {
      render();
    } finally {
      if (keepAnimating) {
        requestAnimationFrame(renderLoop);
      } else {
        _renderLoopActive = false;
      }
    }
  }

  function render() {
    updateHelpButtonPlacement();
    if (window._updateGgVisibility) window._updateGgVisibility();
    updateClassroomQrPanel();
    if (!state.customReconnecting) hideReconnectChip();

    // Countdown overlay takes over the entire screen
    if (state.customCountdownUntil > 0 && performance.now() < state.customCountdownUntil) {
      drawCountdownOverlay();
      return;
    }

    // Reconnecting overlay
    if (state.customReconnecting) {
      const reconnectRemainingMs = Math.max(0, state.customReconnectUntil - performance.now());
      layout.customExitWinButton = { x: 0, y: 0, width: 0, height: 0 };
      updateReconnectChip();
      if (reconnectRemainingMs <= 0) {
        const session = getReturnSession();
        const canRejoin = !!(session && !session.isHost && session.roomCode);
        state.customReconnecting = false;
        state.customReconnectUntil = 0;
        if (canRejoin) {
          exitCustomMode("Host disconnected. Tap Rejoin room.", { keepReturnSession: true, skipAutoOverlay: true });
          showReturnBanner(session, {
            id: "pivot-reconnect-banner",
            message: "Host disconnected.",
            actionLabel: "Rejoin room",
            timeoutMs: 30000,
          });
        } else {
          exitCustomMode("Host disconnected");
        }
        return;
      }
      drawBackground();
      ctx.fillStyle = C.overlay;
      ctx.fillRect(0, 0, layout.width, layout.height);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `italic 18px ${_fontSerif}`;
      ctx.fillStyle = C.overlayText;
      const dots = ".".repeat(1 + Math.floor(performance.now() / 500) % 3);
      const secs = Math.ceil(reconnectRemainingMs / 1000);
      ctx.fillText(`Reconnecting${dots} ${secs}s`, layout.width / 2, layout.height / 2 - 10);
      return;
    }

    if (state.mode === "menu") {
      drawMenu();
      if (state.showReadme) {
        drawReadmeOverlay();
      }
      return;
    }

    // Full observation mode — takes over the entire canvas (multi-TV sports view)
    if (state.customMode && state.customMultiplayer && (state.customObserveMode || state.customIsObserver || isCurrentCustomPlayerObserver()) && state.mode === "play") {
      const classroomHostView = state.customIsHost && state.customClassroomMode;
      drawBackground(); // cream background for sidebar area
      if (classroomHostView) resetSidebarHitboxes();
      drawObservationGrid(); // dark multi-TV view over game area
      if (!classroomHostView) drawSidebar();
      drawHUD();
      // Achievement toast still shows
      if (state.achievementToastUntil > Date.now()) {
        ctx.save();
        const toastText = `${state.achievementToastIcon} ${state.achievementToastText}`;
        ctx.font = `13px ${_fontSerif}`;
        const tm = ctx.measureText(toastText);
        const padX = 14;
        const boxW = Math.min(layout.width * 0.86, tm.width + padX * 2);
        const boxH = 28;
        const boxX = layout.width / 2 - boxW / 2;
        const boxY = layout.height - 52;
        ctx.fillStyle = C.btnPrimaryBg;
        ctx.fillRect(boxX, boxY, boxW, boxH);
        ctx.fillStyle = C.btnPrimaryText;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(toastText, layout.width / 2, boxY + boxH / 2);
        ctx.restore();
      }
      return;
    }

    drawBackground();
    drawEditionHeader();
    drawWordHeader();
    // Subtle board float shadow
    {
      ctx.save();
      const { offsetX, offsetY, gridWidth, gridHeight } = layout;
      ctx.shadowColor = _currentTheme === "dark" ? "rgba(0,0,0,0.45)" : "rgba(27,27,27,0.08)";
      ctx.shadowBlur = _currentTheme === "dark" ? 22 : 10;
      ctx.shadowOffsetY = _currentTheme === "dark" ? 5 : 3;
      ctx.fillStyle = C.bg;
      ctx.fillRect(offsetX, offsetY, gridWidth, gridHeight);
      ctx.restore();
    }
    drawTargetHighlight();
    drawGrid();
    drawPivots();
    drawAnimation();
    // Gold shimmer on archive solve (no win overlay for practice mode)
    if (state.practiceMode && state.winAnimStart && !_prefersReducedMotion) {
      const pSweep = performance.now() - state.winAnimStart;
      if (pSweep < 820) {
        drawGoldShimmer(pSweep, 820);
      }
    }
    drawAdminMessage();
    drawChallengeBenchmark();
    drawSidebar();
    drawHUD();

    // Custom mode indicator (desktop only; mobile status card already carries this context)
    if (state.customMode && state.mode !== "win" && !layout.panelBelow) {
      ctx.save();
      ctx.font = `11px ${_fontSerif}`;
      // The fixed light-mode brown is near-invisible on the dark background.
      ctx.fillStyle = _currentTheme === "light" ? "rgba(106, 91, 42, 0.85)" : C.goldEdge;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
      // Phone landscape solo: the edition header shares this top strip and the
      // two labels collide; the side panel already says "Custom \u00b7 <diff>".
      if (!(layout.shortLandscape && !state.customMultiplayer)) {
        ctx.fillText(`CUSTOM \u00b7 ${diffLabel.toUpperCase()}`, layout.width / 2, 6);
      }
      // LIVE indicator for multiplayer
      if (state.customMultiplayer) {
        ctx.font = `bold 9px ${_fontSerif}`;
        ctx.fillStyle = "#b33a2f";
        ctx.textAlign = "left";
        ctx.fillText("LIVE", 8, 8);
        // Blinking dot — on 1s, off 0.5s
        const blinkOn = (performance.now() % 1500) < 1000;
        if (blinkOn) {
          ctx.fillStyle = "rgba(179, 58, 47, 1)";
          ctx.beginPath();
          ctx.arc(35, 12, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // N-player status strip (multiplayer custom game)
    if (state.customMode && state.customMultiplayer && state.mode === "play") {
      drawPlayerStatusStrip();
    }

    if (state.mode === "win") {
      drawWin();
    }

    if (state.showReadme) {
      drawReadmeOverlay();
    }

    // Achievement toast
    if (state.achievementToastUntil > Date.now()) {
      ctx.save();
      const toastText = `${state.achievementToastIcon} ${state.achievementToastText}`;
      ctx.font = `13px ${_fontSerif}`;
      const tm = ctx.measureText(toastText);
      const padX = 14;
      const boxW = Math.min(layout.width * 0.86, tm.width + padX * 2);
      const boxH = 28;
      const boxX = layout.width / 2 - boxW / 2;
      const boxY = 12;
      ctx.fillStyle = C.toastBg;
      ctx.strokeStyle = C.goldEdge;
      ctx.lineWidth = 1.5;
      ctx.fillRect(boxX, boxY, boxW, boxH);
      ctx.strokeRect(boxX, boxY, boxW, boxH);
      ctx.fillStyle = C.ink;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(toastText, layout.width / 2, boxY + boxH / 2 + 1);
      ctx.restore();
    }
  }

  function drawPlayerStatusStrip() {
    ctx.save();
    if (layout.panelBelow) {
      ctx.restore();
      return;
    }
    if (state.customMode && state.customMultiplayer) {
      // Multiplayer already surfaces roster/state in dedicated UI; avoid top-strip overlap on dense layouts.
      ctx.restore();
      return;
    }
    const others = Object.values(state.customPlayers).filter((p) => p.id !== state.customPlayerId);
    if (others.length === 0) { ctx.restore(); return; }
    // Sort: solved first (by finishOrder), then active by moves
    others.sort((a, b) => {
      if (a.solved && !b.solved) return -1;
      if (!a.solved && b.solved) return 1;
      if (a.solved && b.solved) return a.finishOrder - b.finishOrder;
      return 0;
    });
    const maxVisible = 5;
    let visible = others.slice(0, maxVisible);
    let overflow = others.length - visible.length;
    const chipH = 16;
    const chipGap = 6;
    const chipFont = `9px ${_fontSerif}`;
    ctx.font = chipFont;
    const stripY = 22;
    const availableW = layout.panelBelow ? layout.width : (layout.width - layout.panelWidth);
    let chipWidths = visible.map((p) => {
      const label = p.name.length > 6 ? p.name.slice(0, 5) + "\u2026" : p.name;
      const status = p.solved ? `\u2713${p.moves}` : `${p.moves}`;
      return 14 + ctx.measureText(label).width + 4 + ctx.measureText(status).width + 8;
    });
    let totalW = chipWidths.reduce((s, w) => s + w, 0) + Math.max(0, visible.length - 1) * chipGap;
    if (overflow > 0) totalW += chipGap + ctx.measureText(`+${overflow}`).width + 8;
    while (visible.length > 1 && totalW > (availableW - 8)) {
      visible = visible.slice(0, visible.length - 1);
      overflow += 1;
      chipWidths = visible.map((p) => {
        const label = p.name.length > 6 ? p.name.slice(0, 5) + "\u2026" : p.name;
        const status = p.solved ? `\u2713${p.moves}` : `${p.moves}`;
        return 14 + ctx.measureText(label).width + 4 + ctx.measureText(status).width + 8;
      });
      totalW = chipWidths.reduce((s, w) => s + w, 0) + Math.max(0, visible.length - 1) * chipGap;
      if (overflow > 0) totalW += chipGap + ctx.measureText(`+${overflow}`).width + 8;
    }
    let cx = Math.max(4, availableW / 2 - totalW / 2);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, stripY - 2, availableW, chipH + 6);
    ctx.clip();
    for (let i = 0; i < visible.length; i++) {
      const p = visible[i];
      const w = chipWidths[i];
      // Background
      if (p.solved) {
        ctx.fillStyle = "rgba(91, 168, 91, 0.12)";
      } else if (p.isObserver) {
        ctx.fillStyle = C.panel;
      } else {
        ctx.fillStyle = C.panel;
      }
      ctx.fillRect(cx, stripY, w, chipH);
      ctx.strokeStyle = C.panelStroke;
      ctx.lineWidth = 0.5;
      ctx.strokeRect(cx, stripY, w, chipH);
      // Initial circle
      const circR = 5;
      const circX = cx + 7;
      const circY = stripY + chipH / 2;
      const hue = (hashString(p.name) % 360);
      ctx.fillStyle = `hsl(${hue}, 45%, 55%)`;
      ctx.beginPath();
      ctx.arc(circX, circY, circR, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = `bold 7px ${_fontSerif}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(p.name.charAt(0).toUpperCase(), circX, circY);
      // Name
      ctx.font = chipFont;
      ctx.fillStyle = p.isObserver ? "#999" : colors.ink;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const label = p.name.length > 6 ? p.name.slice(0, 5) + "\u2026" : p.name;
      ctx.fillText(label, cx + 14, stripY + chipH / 2);
      // Status
      const status = p.solved ? `\u2713${p.moves}` : `${p.moves}`;
      ctx.fillStyle = p.solved ? "#5ba85b" : "#999";
      ctx.textAlign = "right";
      ctx.fillText(status, cx + w - 4, stripY + chipH / 2);
      cx += w + chipGap;
    }
    if (overflow > 0) {
      ctx.font = chipFont;
      ctx.fillStyle = "#999";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(`+${overflow} more`, cx, stripY + chipH / 2);
    }
    ctx.restore();
    ctx.restore();
  }

  function drawObservationGrid() {
    ctx.save();
    const allPlayers = Object.values(state.customPlayers);
    const activePlayers = allPlayers.filter((p) => !p.isObserver);
    const observers = allPlayers.filter((p) => p.isObserver);
    activePlayers.sort((a, b) => {
      if (!a.solved && b.solved) return -1;
      if (a.solved && !b.solved) return 1;
      if (a.solved && b.solved) return (a.finishOrder || 999) - (b.finishOrder || 999);
      return (b.moves || 0) - (a.moves || 0);
    });

    const classroomHostView = state.customIsHost && state.customClassroomMode;
    const sidebarW = (!layout.panelBelow && !classroomHostView) ? layout.panelWidth : 0;
    const availW = layout.width - sidebarW;
    const availH = layout.panelBelow ? (layout.height - layout.panelHeight) : layout.height;
    const hostObserveStatusRow = state.customIsHost && !classroomHostView;
    const headerH = classroomHostView ? (layout.panelBelow ? 96 : 124) : (layout.panelBelow ? 52 : (hostObserveStatusRow ? 42 : 30));
    const footerH = classroomHostView ? (layout.panelBelow ? 58 : 50) : (layout.panelBelow ? 36 : 46);
    const padding = layout.panelBelow ? 8 : (classroomHostView ? 11 : 10);

    const rosterRailW = classroomHostView && !layout.panelBelow
      ? Math.min(270, Math.max(210, Math.floor(availW * 0.27)))
      : 0;
    const showQrRail = !layout.panelBelow && !!state.customRoomCode && !classroomHostView;
    const qrRailW = showQrRail ? Math.min(186, Math.max(138, Math.floor(availW * 0.22))) : 0;
    const boardAreaX = padding;
    const boardAreaY = headerH + padding;
    const boardAreaW = Math.max(
      120,
      availW - padding * 2 - (showQrRail ? qrRailW + padding : 0) - (rosterRailW ? rosterRailW + padding : 0)
    );
    const boardAreaH = Math.max(100, availH - headerH - footerH - padding * 2);

    const zeroRect = { x: 0, y: 0, width: 0, height: 0 };
    layout.observeRequestJoinButton = { ...zeroRect };
    layout.observeApproveAllButton = { ...zeroRect };
    layout.observeDenyAllButton = { ...zeroRect };
    layout.observeApproveButtons = [];
    layout.observeDenyButtons = [];
    layout.observeFilterButtons = [];
    layout.observePlayerRects = [];
    layout.observeKickButtons = [];
    layout.observeRosterKickButtons = [];
    layout.observeRosterApproveButtons = [];
    layout.observeRosterDenyButtons = [];
    layout.observeHostActionButtons = [];
    layout.observeUndoKickButton = { ...zeroRect };
    layout.observeAutoButton = { ...zeroRect };

    const filteredPlayers = activePlayers.filter((p) => {
      if (state.customObserveFilter === "active") return !p.solved;
      if (state.customObserveFilter === "solved") return !!p.solved;
      return true;
    });

    const focusedName = state.customObserveFocusPlayer;
    const focusExists = focusedName && filteredPlayers.some((p) => p.name === focusedName);
    if (state.customObserveFocusLocked && !focusExists) {
      state.customObserveFocusLocked = false;
      state.customObserveFocusPlayer = null;
    }
    if (!state.customObserveFocusLocked) {
      const lead = filteredPlayers[0];
      state.customObserveFocusPlayer = lead ? lead.name : null;
    }

    ctx.fillStyle = C.panel;
    ctx.fillRect(0, 0, availW, headerH);
    const blinkOn = (performance.now() % 1500) < 1000;
    if (blinkOn) {
      ctx.fillStyle = C.error;
      ctx.beginPath();
      ctx.arc(14, classroomHostView ? 14 : (headerH / 2), 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.font = classroomHostView ? `bold 12px ${_fontSerif}` : `bold 10px ${_fontSerif}`;
    ctx.fillStyle = C.error;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("LIVE", 22, classroomHostView ? 14 : (headerH / 2));

    const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
    const solvedN = activePlayers.filter((p) => p.solved).length;
    const pendingN = state.customPendingJoins.filter((j) => !j.approved).length;
    if (classroomHostView) {
      const roomLabel = `Room ${state.customRoomCode || "-----"}`;
      ctx.font = `11px ${_fontSerif}`;
      const roomW = Math.ceil(ctx.measureText(roomLabel).width) + 14;
      const roomX = 62;
      const roomY = 8;
      const roomH = 20;
      ctx.fillStyle = C.panel;
      ctx.fillRect(roomX, roomY, roomW, roomH);
      ctx.strokeStyle = C.mutedFaint;
      ctx.lineWidth = 1;
      ctx.strokeRect(roomX, roomY, roomW, roomH);
      ctx.fillStyle = C.muted;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(roomLabel, roomX + roomW / 2, roomY + roomH / 2 + 0.5);

      ctx.font = `12px ${_fontSerif}`;
      ctx.fillStyle = C.muted;
      ctx.textAlign = "left";
      ctx.fillText(
        `${diffLabel} · Round ${state.customRoundNumber} · ${solvedN}/${activePlayers.length} finished`,
        10,
        44
      );
      ctx.textAlign = "right";
      ctx.fillText(
        `Playing ${activePlayers.length} · Watching ${observers.length}${pendingN ? ` · Waiting ${pendingN}` : ""}${state.customRoomLocked ? " · Locked" : ""}`,
        availW - 10,
        44
      );
    } else {
      ctx.font = `11px ${_fontSerif}`;
      ctx.fillStyle = C.muted;
      const statusY = layout.panelBelow ? 36 : (headerH / 2);
      ctx.textAlign = "left";
      ctx.fillText(
        `${diffLabel} · Round ${state.customRoundNumber} · ${solvedN}/${activePlayers.length} finished`,
        10,
        statusY
      );
      ctx.textAlign = "right";
      ctx.fillText(
        `Watching ${observers.length}${pendingN ? ` · ${pendingN} waiting` : ""}${state.customRoomLocked ? " · Locked" : ""}`,
        availW - 10,
        statusY
      );
      // Keep this row visually quiet; filters/actions already signal host state.
    }

    let actionLeftBoundary = boardAreaX + boardAreaW - 8;
    if (classroomHostView) {
      const actions = [];
      if (state.customPhase === "lobby") actions.push({ action: "start_round", label: "Start round", tone: "primary" });
      if (state.customPhase === "results") actions.push({ action: "next_round", label: "Next round", tone: "primary" });
      if (pendingN > 0) actions.push({ action: "approve_all", label: "Approve all", tone: "neutral" });
      actions.push({ action: "copy_link", label: "Copy link", tone: "neutral" });
      actions.push({
        action: "toggle_lock",
        label: state.customRoomLocked ? "Unlock room" : "Lock room",
        tone: state.customRoomLocked ? "primary" : "neutral",
      });
      actions.push({ action: "exit_room", label: "Exit room", tone: "danger" });
      const compactActions = availW < 1160 || boardAreaW < 700;
      let actionX = availW - 12;
      let actionY = 10;
      ctx.font = `12px ${_fontSerif}`;
      const actionH = 20;
      const actionGap = 6;
      const actionRowGap = 4;
      const actionMinX = Math.max(boardAreaX + 8, boardAreaX + Math.min(220, Math.floor(boardAreaW * 0.42)));
      let firstRowLeft = availW - 12;
      for (let i = actions.length - 1; i >= 0; i -= 1) {
        const item = actions[i];
        const label = compactActions
          ? (item.action === "start_round" ? "Start"
            : item.action === "next_round" ? "Next"
            : item.action === "approve_all" ? "Approve"
            : item.action === "copy_link" ? "Copy"
            : item.action === "toggle_lock" ? (state.customRoomLocked ? "Unlock" : "Lock")
            : item.action === "exit_room" ? "Exit"
            : item.label)
          : item.label;
        const actionW = Math.max(compactActions ? 56 : 66, Math.ceil(ctx.measureText(label).width) + (compactActions ? 14 : 18));
        actionX -= actionW;
        if (actionX < actionMinX) {
          actionX = availW - 12 - actionW;
          actionY += actionH + actionRowGap;
          if (actionY + actionH > headerH - 30) continue;
        }
        if (item.tone === "danger") {
          ctx.fillStyle = C.bg;
          ctx.strokeStyle = C.error;
        } else if (item.tone === "primary") {
          ctx.fillStyle = C.ink;
          ctx.strokeStyle = C.ink;
        } else {
          ctx.fillStyle = C.bg;
          ctx.strokeStyle = C.border;
        }
        ctx.fillRect(actionX, actionY, actionW, actionH);
        ctx.lineWidth = 1;
        ctx.strokeRect(actionX, actionY, actionW, actionH);
        if (item.tone === "danger") {
          ctx.fillStyle = C.error;
        } else if (item.tone === "primary") {
          ctx.fillStyle = C.bg;
        } else {
          ctx.fillStyle = colors.ink;
        }
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, actionX + actionW / 2, actionY + actionH / 2 + 0.5);
        layout.observeHostActionButtons.push({ x: actionX, y: actionY, width: actionW, height: actionH, action: item.action });
        if (actionY === 10) firstRowLeft = Math.min(firstRowLeft, actionX);
        actionX -= actionGap;
      }
      actionLeftBoundary = Math.max(boardAreaX + 150, firstRowLeft - actionGap);
      const kick = state.customLastKick;
      if (kick && kick.player && Date.now() <= kick.expiresAt) {
        const labelName = (kick.player.name || "Player").length > 9 ? `${kick.player.name.slice(0, 8)}…` : kick.player.name;
        const label = `Undo kick: ${labelName || "Player"}`;
        const undoY = headerH - 26;
        ctx.font = `12px ${_fontSerif}`;
        const undoW = Math.min(180, Math.max(94, Math.ceil(ctx.measureText(label).width) + 16));
        const undoX = 12;
        const undoH = 22;
        ctx.fillStyle = "rgba(199, 167, 66, 0.16)";
        ctx.fillRect(undoX, undoY, undoW, undoH);
        ctx.strokeStyle = "rgba(199, 167, 66, 0.48)";
        ctx.lineWidth = 1;
        ctx.strokeRect(undoX, undoY, undoW, undoH);
        ctx.fillStyle = colors.yellowEdge;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, undoX + undoW / 2, undoY + undoH / 2 + 0.5);
        layout.observeUndoKickButton = { x: undoX, y: undoY, width: undoW, height: undoH };
      } else if (kick && Date.now() > kick.expiresAt) {
        state.customLastKick = null;
      }
    }

    const observeFilterItems = [
      { key: "all", label: "All" },
      { key: "active", label: "Playing" },
      { key: "solved", label: "Solved" },
    ];
    let filterX = classroomHostView ? 12 : 62;
    if (classroomHostView && layout.observeUndoKickButton && layout.observeUndoKickButton.width > 0) {
      filterX = layout.observeUndoKickButton.x + layout.observeUndoKickButton.width + 8;
    }
    const filterY = classroomHostView ? (headerH - 24) : 6;
    const filterH = classroomHostView ? 20 : 20;
    const filterRightLimit = classroomHostView
      ? Math.min(boardAreaX + boardAreaW - 8, actionLeftBoundary - 10)
      : (availW - 170);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = classroomHostView ? `12px ${_fontSerif}` : `11px ${_fontSerif}`;
    for (const item of observeFilterItems) {
      const filterW = Math.ceil(ctx.measureText(item.label).width) + 16;
      if (filterX + filterW > filterRightLimit) break;
      const selected = state.customObserveFilter === item.key;
      ctx.fillStyle = selected ? C.panelStroke : C.panel;
      ctx.fillRect(filterX, filterY, filterW, filterH);
      ctx.strokeStyle = selected ? C.border : C.panelStroke;
      ctx.lineWidth = 1;
      ctx.strokeRect(filterX, filterY, filterW, filterH);
      ctx.fillStyle = selected ? C.ink : C.muted;
      ctx.fillText(item.label, filterX + 8, filterY + filterH / 2 + 0.5);
      layout.observeFilterButtons.push({ x: filterX, y: filterY, width: filterW, height: filterH, filter: item.key });
      filterX += filterW + 6;
    }

    if (activePlayers.length === 0) {
      ctx.fillStyle = C.mutedSoft;
      ctx.font = `italic 16px ${_fontSerif}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Waiting for players…", availW / 2, boardAreaY + boardAreaH / 2);
    } else if (filteredPlayers.length === 0) {
      ctx.fillStyle = C.mutedSoft;
      ctx.font = `italic 16px ${_fontSerif}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("No players match this filter", availW / 2, boardAreaY + boardAreaH / 2);
    } else {
      const tileGap = layout.panelBelow ? 6 : 8;
      const captionH = layout.panelBelow ? 14 : 16;
      function pickLayout(count) {
        let best = { cols: 1, rows: count, tileW: boardAreaW, tileH: boardAreaH, cell: 1 };
        const maxCols = Math.max(1, Math.min(layout.panelBelow ? 5 : 7, count));
        for (let cols = 1; cols <= maxCols; cols += 1) {
          const rows = Math.ceil(count / cols);
          const tileW = (boardAreaW - (cols - 1) * tileGap) / cols;
          const tileH = (boardAreaH - (rows - 1) * tileGap) / rows;
          const cell = Math.floor(Math.min((tileW - 10) / GRID_COLS, (tileH - captionH - 10) / GRID_ROWS));
          if (cell > best.cell) best = { cols, rows, tileW, tileH, cell };
        }
        return best;
      }

      let visiblePlayers = filteredPlayers.slice(0, Math.min(36, filteredPlayers.length));
      let gridPlan = pickLayout(Math.max(1, visiblePlayers.length));
      const minCell = layout.panelBelow ? 7 : 10;
      while (visiblePlayers.length > 1 && gridPlan.cell < minCell) {
        visiblePlayers = visiblePlayers.slice(0, visiblePlayers.length - 1);
        gridPlan = pickLayout(visiblePlayers.length);
      }
      const overflowPlayers = Math.max(0, filteredPlayers.length - visiblePlayers.length);

      if (state.customObserveFocusLocked) {
        const stillVisible = visiblePlayers.some((p) => p.name === state.customObserveFocusPlayer);
        if (!stillVisible) {
          state.customObserveFocusLocked = false;
          state.customObserveFocusPlayer = visiblePlayers[0] ? visiblePlayers[0].name : null;
        }
      }

      if (state.customObserveFocusLocked) {
        const autoBtnW = 44;
        const autoBtnH = 20;
        const autoBtnX = boardAreaX + boardAreaW - autoBtnW - 8;
        const autoBtnY = classroomHostView ? (headerH - 28) : 5;
        ctx.fillStyle = C.panel;
        ctx.fillRect(autoBtnX, autoBtnY, autoBtnW, autoBtnH);
        ctx.strokeStyle = C.border;
        ctx.lineWidth = 1;
        ctx.strokeRect(autoBtnX, autoBtnY, autoBtnW, autoBtnH);
        ctx.fillStyle = C.muted;
        ctx.font = `11px ${_fontSerif}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Auto", autoBtnX + autoBtnW / 2, autoBtnY + autoBtnH / 2 + 0.5);
        layout.observeAutoButton = { x: autoBtnX, y: autoBtnY, width: autoBtnW, height: autoBtnH };
      }

      const centerRow = Math.floor(GRID_ROWS / 2);
      for (let i = 0; i < visiblePlayers.length; i += 1) {
        const p = visiblePlayers[i];
        const col = i % gridPlan.cols;
        const row = Math.floor(i / gridPlan.cols);
        const tileX = boardAreaX + col * (gridPlan.tileW + tileGap);
        const tileY = boardAreaY + row * (gridPlan.tileH + tileGap);
        const tileW = gridPlan.tileW;
        const tileH = gridPlan.tileH;
        const focused = !!state.customObserveFocusPlayer && state.customObserveFocusPlayer === p.name;

        ctx.fillStyle = p.solved ? C.solvedTileBg : C.bg;
        ctx.fillRect(tileX, tileY, tileW, tileH);
        ctx.strokeStyle = focused ? C.ink : C.mutedFaint;
        ctx.lineWidth = focused ? 1.6 : 1;
        ctx.strokeRect(tileX, tileY, tileW, tileH);

        const cell = Math.max(6, gridPlan.cell);
        const boardW = cell * GRID_COLS;
        const boardH = cell * GRID_ROWS;
        const boardX = tileX + (tileW - boardW) / 2;
        const boardY = tileY + 4;
        const gridStr = p.grid || state.customStartState || state.canonicalStartStr || gridToString(state.grid);

        for (let r = 0; r < GRID_ROWS; r += 1) {
          for (let c = 0; c < GRID_COLS; c += 1) {
            const cx = boardX + c * cell;
            const cy = boardY + r * cell;
            const isCenterWord = r === centerRow && c >= CENTER_START_COL && c < CENTER_START_COL + CENTER_WORD_LENGTH;
            if (isCenterWord) ctx.fillStyle = p.solved ? C.solvedTileBg : colors.yellow;
            else ctx.fillStyle = C.bg;
            ctx.fillRect(cx, cy, cell, cell);
            ctx.strokeStyle = isCenterWord ? (p.solved ? C.solvedStroke : colors.yellowEdge) : C.gridLine;
            ctx.lineWidth = Math.max(0.8, Math.min(1.5, cell * 0.05));
            ctx.strokeRect(cx, cy, cell, cell);
            const letter = gridStr.charAt(r * GRID_COLS + c);
            if (!letter) continue;
            ctx.fillStyle = colors.ink;
            ctx.font = `${Math.max(8, Math.floor(cell * 0.52))}px ${_fontSerif}`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(letter, cx + cell / 2, cy + cell / 2 + 0.5);
          }
        }

        const nameY = tileY + tileH - captionH + 2;
        const status = p.solved ? `✓${p.moves}` : `${p.moves}`;
        const displayName = p.isBot ? `${p.name} (Robot)` : p.name;
        const label = displayName.length > 14 ? `${displayName.slice(0, 13)}…` : displayName;
        ctx.font = `${layout.panelBelow ? 10 : 11}px ${_fontSerif}`;
        ctx.fillStyle = focused ? C.ink : C.muted;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(label, tileX + 6, nameY);
        ctx.fillStyle = p.solved ? C.success : C.mutedSoft;
        ctx.textAlign = "right";
        ctx.fillText(status, tileX + tileW - 6, nameY);

        if (state.customIsHost && !p.isHost && !classroomHostView) {
          const kickSize = Math.max(12, Math.min(18, Math.floor(cell * 0.8)));
          const kickX = tileX + tileW - kickSize - 4;
          const kickY = tileY + 4;
          ctx.fillStyle = _currentTheme === "dark" ? "rgba(220,80,60,0.16)" : "rgba(179,58,47,0.16)";
          ctx.fillRect(kickX, kickY, kickSize, kickSize);
          ctx.strokeStyle = _currentTheme === "dark" ? "rgba(220,80,60,0.55)" : "rgba(179,58,47,0.55)";
          ctx.lineWidth = 1;
          ctx.strokeRect(kickX, kickY, kickSize, kickSize);
          ctx.fillStyle = C.error;
          ctx.font = `${Math.max(9, Math.floor(kickSize * 0.72))}px ${_fontSerif}`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("×", kickX + kickSize / 2, kickY + kickSize / 2 + 0.5);
          layout.observeKickButtons.push({ x: kickX, y: kickY, width: kickSize, height: kickSize, playerId: p.id });
        }

        layout.observePlayerRects.push({ x: tileX, y: tileY, width: tileW, height: tileH, playerName: p.name });
      }

      if (overflowPlayers > 0) {
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = "#888";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText(`+${overflowPlayers} more players`, boardAreaX, boardAreaY + boardAreaH - 2);
      }
    }

    if (classroomHostView && rosterRailW > 0) {
      const railX = boardAreaX + boardAreaW + padding;
      const railY = boardAreaY;
      const railH = boardAreaH;
      ctx.fillStyle = C.panel;
      ctx.fillRect(railX, railY, rosterRailW, railH);
      ctx.strokeStyle = C.panelStroke;
      ctx.lineWidth = 1;
      ctx.strokeRect(railX, railY, rosterRailW, railH);

      let railCursorY = railY + 10;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = `10px ${_fontSerif}`;
      ctx.fillStyle = "#777";
      ctx.fillText("CLASSROOM", railX + 10, railCursorY);
      railCursorY += 13;
      ctx.font = `bold 20px ${_fontSerif}`;
      ctx.fillStyle = colors.ink;
      ctx.fillText(state.customRoomCode || "-----", railX + 10, railCursorY);
      railCursorY += 24;
      ctx.font = `10px ${_fontSerif}`;
      ctx.fillStyle = "#777";
      ctx.fillText("QR pinned on screen", railX + 10, railCursorY);
      railCursorY += 20;

      const unapproved = state.customPendingJoins.filter((j) => !j.approved);
      const rosterSections = [
        {
          title: `Playing (${activePlayers.length})`,
          entries: activePlayers.filter((p) => !p.isHost),
          mode: "playing",
        },
        {
          title: `Watching (${observers.length})`,
          entries: observers.filter((p) => !p.isHost),
          mode: "watching",
        },
        {
          title: `Waiting (${unapproved.length})`,
          entries: unapproved,
          mode: "waiting",
        },
      ];

      const rowH = 40;
      const sectionGap = 9;
      const bottomLimit = railY + railH - 12;
      ctx.save();
      ctx.beginPath();
      ctx.rect(railX + 1, railY + 1, rosterRailW - 2, railH - 2);
      ctx.clip();
      for (const section of rosterSections) {
        if (railCursorY + rowH > bottomLimit) break;
        ctx.font = `bold 11px ${_fontSerif}`;
        ctx.fillStyle = "#666";
        ctx.fillText(section.title, railX + 10, railCursorY);
        railCursorY += 15;

        if (!section.entries.length) {
          ctx.font = `10px ${_fontSerif}`;
          ctx.fillStyle = "#999";
          ctx.fillText("None", railX + 12, railCursorY);
          railCursorY += 16 + sectionGap;
          continue;
        }
        let shown = 0;
        for (const entry of section.entries) {
          if (railCursorY + rowH > bottomLimit) break;
          const rowX = railX + 8;
          const rowW = rosterRailW - 16;
          ctx.fillStyle = C.card;
          ctx.fillRect(rowX, railCursorY, rowW, rowH);
          ctx.strokeStyle = C.cardStroke;
          ctx.lineWidth = 1;
          ctx.strokeRect(rowX, railCursorY, rowW, rowH);
          ctx.font = `12px ${_fontSerif}`;
          ctx.textAlign = "left";
          ctx.textBaseline = "alphabetic";
          const name = (entry.name || "Player");
          const label = name.length > 13 ? `${name.slice(0, 12)}…` : name;
          ctx.fillStyle = colors.ink;
          ctx.fillText(label, rowX + 6, railCursorY + 14);
          const recordSource = section.mode === "waiting" ? state.customPlayers[entry.id] || entry : entry;
          const wins = normalizeWinLoss(recordSource && recordSource.wins);
          const losses = normalizeWinLoss(recordSource && recordSource.losses);
          ctx.font = `10px ${_fontSerif}`;
          ctx.fillStyle = "#777";
          ctx.fillText(`W-L ${wins}-${losses}`, rowX + 6, railCursorY + rowH - 8);
          ctx.textBaseline = "middle";
          if (section.mode === "playing") {
            const kickRect = { x: rowX + rowW - 42, y: railCursorY + 9, width: 36, height: 22, playerId: entry.id };
            ctx.textAlign = "right";
            ctx.fillStyle = entry.solved ? "#5ba85b" : "#777";
            ctx.fillText(entry.solved ? `✓${entry.moves}` : `${entry.moves}`, kickRect.x - 5, railCursorY + rowH / 2 + 0.5);
            ctx.fillStyle = "#fff";
            ctx.fillRect(kickRect.x, kickRect.y, kickRect.width, kickRect.height);
            ctx.strokeStyle = "rgba(179, 58, 47, 0.4)";
            ctx.lineWidth = 1;
            ctx.strokeRect(kickRect.x, kickRect.y, kickRect.width, kickRect.height);
            ctx.fillStyle = "#9b2d24";
            ctx.font = `10px ${_fontSerif}`;
            ctx.textAlign = "center";
            ctx.fillText("Kick", kickRect.x + kickRect.width / 2, kickRect.y + kickRect.height / 2 + 0.5);
            layout.observeRosterKickButtons.push(kickRect);
          } else if (section.mode === "watching") {
            const kickRect = { x: rowX + rowW - 42, y: railCursorY + 9, width: 36, height: 22, playerId: entry.id };
            ctx.fillStyle = "#fff";
            ctx.fillRect(kickRect.x, kickRect.y, kickRect.width, kickRect.height);
            ctx.strokeStyle = "rgba(179, 58, 47, 0.36)";
            ctx.lineWidth = 1;
            ctx.strokeRect(kickRect.x, kickRect.y, kickRect.width, kickRect.height);
            ctx.fillStyle = "#9b2d24";
            ctx.font = `10px ${_fontSerif}`;
            ctx.textAlign = "center";
            ctx.fillText("Kick", kickRect.x + kickRect.width / 2, kickRect.y + kickRect.height / 2 + 0.5);
            layout.observeRosterKickButtons.push(kickRect);
          } else {
            const denyRect = { x: rowX + rowW - 86, y: railCursorY + 9, width: 38, height: 22, playerId: entry.id };
            const approveRect = { x: rowX + rowW - 44, y: railCursorY + 9, width: 38, height: 22, playerId: entry.id };
            ctx.fillStyle = "#fff";
            ctx.fillRect(denyRect.x, denyRect.y, denyRect.width, denyRect.height);
            ctx.strokeStyle = "rgba(179, 58, 47, 0.35)";
            ctx.lineWidth = 1;
            ctx.strokeRect(denyRect.x, denyRect.y, denyRect.width, denyRect.height);
            ctx.fillStyle = "#9b2d24";
            ctx.textAlign = "center";
            ctx.font = `10px ${_fontSerif}`;
            ctx.fillText("Deny", denyRect.x + denyRect.width / 2, denyRect.y + denyRect.height / 2 + 0.5);
            ctx.fillStyle = "#fff";
            ctx.fillRect(approveRect.x, approveRect.y, approveRect.width, approveRect.height);
            ctx.strokeStyle = C.ink;
            ctx.lineWidth = 1;
            ctx.strokeRect(approveRect.x, approveRect.y, approveRect.width, approveRect.height);
            ctx.fillStyle = C.ink;
            ctx.fillText("Allow", approveRect.x + approveRect.width / 2, approveRect.y + approveRect.height / 2 + 0.5);
            layout.observeRosterDenyButtons.push(denyRect);
            layout.observeRosterApproveButtons.push(approveRect);
          }
          railCursorY += rowH + 4;
          shown += 1;
        }
        if (shown < section.entries.length && railCursorY + 12 <= bottomLimit) {
          ctx.font = `10px ${_fontSerif}`;
          ctx.fillStyle = "#999";
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.fillText(`+${section.entries.length - shown} more`, railX + 12, railCursorY);
          railCursorY += 14;
        }
        railCursorY += sectionGap;
      }
      ctx.restore();
    } else if (showQrRail) {
      const qrX = boardAreaX + boardAreaW + padding;
      const qrY = boardAreaY;
      const qrH = boardAreaH;
      ctx.fillStyle = C.card;
      ctx.fillRect(qrX, qrY, qrRailW, qrH);
      ctx.strokeStyle = C.panelStroke;
      ctx.lineWidth = 1;
      ctx.strokeRect(qrX, qrY, qrRailW, qrH);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.font = `10px ${_fontSerif}`;
      ctx.fillStyle = "#666";
      ctx.fillText("Classroom link", qrX + qrRailW / 2, qrY + 12);
      ctx.font = `bold 20px ${_fontSerif}`;
      ctx.fillStyle = colors.ink;
      ctx.fillText(state.customRoomCode, qrX + qrRailW / 2, qrY + 30);
      ctx.font = `11px ${_fontSerif}`;
      ctx.fillStyle = "#777";
      ctx.fillText("QR is shown on-screen", qrX + qrRailW / 2, qrY + 64);
      ctx.fillText("for quick joining", qrX + qrRailW / 2, qrY + 80);
      if (state.customClassroomMode) {
        ctx.fillStyle = colors.yellowEdge;
        ctx.fillText("Classroom mode", qrX + qrRailW / 2, qrY + 106);
      }
    }

    const footerY = availH - footerH;
    ctx.fillStyle = C.panel;
    ctx.fillRect(0, footerY, availW, footerH);

    if ((state.customIsObserver || isCurrentCustomPlayerObserver()) && !state.customIsHost) {
      ctx.font = `12px ${_fontSerif}`;
      const btnW = Math.min(250, availW - 24);
      const btnH = 28;
      const btnX = (availW - btnW) / 2;
      const btnY = footerY + (footerH - btnH) / 2;
      if (!state.customClassroomMode) {
        ctx.fillStyle = "rgba(91, 168, 91, 0.16)";
        ctx.fillRect(btnX, btnY, btnW, btnH);
        ctx.fillStyle = "#5ba85b";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Queued for next round", btnX + btnW / 2, btnY + btnH / 2 + 0.5);
      } else if (state.customJoinApproved) {
        ctx.fillStyle = "rgba(91, 168, 91, 0.2)";
        ctx.fillRect(btnX, btnY, btnW, btnH);
        ctx.strokeStyle = "rgba(91, 168, 91, 0.5)";
        ctx.lineWidth = 1;
        ctx.strokeRect(btnX, btnY, btnW, btnH);
        ctx.fillStyle = "#5ba85b";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Approved for next round ✓", btnX + btnW / 2, btnY + btnH / 2 + 0.5);
      } else if (state.customJoinRequested) {
        ctx.fillStyle = "rgba(199, 167, 66, 0.15)";
        ctx.fillRect(btnX, btnY, btnW, btnH);
        ctx.strokeStyle = "rgba(199, 167, 66, 0.45)";
        ctx.lineWidth = 1;
        ctx.strokeRect(btnX, btnY, btnW, btnH);
        ctx.fillStyle = colors.yellowEdge;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Request sent. Waiting for host", btnX + btnW / 2, btnY + btnH / 2 + 0.5);
      } else {
        ctx.fillStyle = C.panel;
        ctx.fillRect(btnX, btnY, btnW, btnH);
        ctx.strokeStyle = C.border;
        ctx.lineWidth = 1;
        ctx.strokeRect(btnX, btnY, btnW, btnH);
        ctx.fillStyle = colors.ink;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Request next round", btnX + btnW / 2, btnY + btnH / 2 + 0.5);
        layout.observeRequestJoinButton = { x: btnX, y: btnY, width: btnW, height: btnH };
      }
    } else if (state.customIsHost) {
      if (classroomHostView) {
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = "#777";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const pendingCount = state.customPendingJoins.filter((j) => !j.approved).length;
        const statusText = pendingCount > 0
          ? `${pendingCount} join request${pendingCount === 1 ? "" : "s"} pending`
          : "Classroom lobby open";
        ctx.fillText(statusText, availW / 2, footerY + footerH / 2);
        ctx.restore();
        return;
      }
      const unapproved = state.customPendingJoins.filter((j) => !j.approved);
      if (unapproved.length > 0) {
        const approveAllW = 86;
        const denyAllW = 70;
        const approveAllH = 24;
        const denyAllX = availW - denyAllW - 10;
        const approveAllX = denyAllX - approveAllW - 8;
        const approveAllY = footerY + (footerH - approveAllH) / 2;

        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = colors.ink;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const pendingText = `${unapproved.length} waiting in lobby`;
        ctx.fillText(pendingText, 10, footerY + footerH / 2);

        ctx.fillStyle = "rgba(91, 168, 91, 0.2)";
        ctx.fillRect(approveAllX, approveAllY, approveAllW, approveAllH);
        ctx.strokeStyle = "#5ba85b";
        ctx.lineWidth = 1;
        ctx.strokeRect(approveAllX, approveAllY, approveAllW, approveAllH);
        ctx.fillStyle = "#3a8f3a";
        ctx.textAlign = "center";
        ctx.fillText("Approve all", approveAllX + approveAllW / 2, approveAllY + approveAllH / 2 + 0.5);
        layout.observeApproveAllButton = { x: approveAllX, y: approveAllY, width: approveAllW, height: approveAllH };

        ctx.fillStyle = "rgba(179, 58, 47, 0.18)";
        ctx.fillRect(denyAllX, approveAllY, denyAllW, approveAllH);
        ctx.strokeStyle = "#b33a2f";
        ctx.lineWidth = 1;
        ctx.strokeRect(denyAllX, approveAllY, denyAllW, approveAllH);
        ctx.fillStyle = "#9b2d24";
        ctx.textAlign = "center";
        ctx.fillText("Deny all", denyAllX + denyAllW / 2, approveAllY + approveAllH / 2 + 0.5);
        layout.observeDenyAllButton = { x: denyAllX, y: approveAllY, width: denyAllW, height: approveAllH };

        let chipX = 14 + ctx.measureText(pendingText).width + 12;
        let shown = 0;
        for (const pending of unapproved) {
          const nameLabel = pending.name.length > 9 ? `${pending.name.slice(0, 8)}…` : pending.name;
          const chipW = Math.min(140, Math.max(84, Math.ceil(ctx.measureText(nameLabel).width) + 44));
          const chipH = 22;
          const chipY = footerY + (footerH - chipH) / 2;
          if (chipX + chipW > approveAllX - 8) break;

          ctx.fillStyle = C.panel;
          ctx.fillRect(chipX, chipY, chipW, chipH);
          ctx.strokeStyle = C.panelStroke;
          ctx.lineWidth = 1;
          ctx.strokeRect(chipX, chipY, chipW, chipH);
          ctx.fillStyle = C.ink;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(nameLabel, chipX + 5, chipY + chipH / 2 + 0.5);

          const denyRect = { x: chipX + chipW - 32, y: chipY + 3, width: 12, height: 16, playerId: pending.id };
          const approveRect = { x: chipX + chipW - 17, y: chipY + 3, width: 12, height: 16, playerId: pending.id };
          ctx.fillStyle = "rgba(179, 58, 47, 0.2)";
          ctx.fillRect(denyRect.x, denyRect.y, denyRect.width, denyRect.height);
          ctx.fillStyle = "#b33a2f";
          ctx.textAlign = "center";
          ctx.fillText("×", denyRect.x + denyRect.width / 2, denyRect.y + denyRect.height / 2 + 0.5);

          ctx.fillStyle = "rgba(91, 168, 91, 0.2)";
          ctx.fillRect(approveRect.x, approveRect.y, approveRect.width, approveRect.height);
          ctx.fillStyle = "#3a8f3a";
          ctx.fillText("✓", approveRect.x + approveRect.width / 2, approveRect.y + approveRect.height / 2 + 0.5);

          layout.observeDenyButtons.push(denyRect);
          layout.observeApproveButtons.push(approveRect);
          chipX += chipW + 6;
          shown += 1;
        }
        if (shown < unapproved.length) {
          ctx.fillStyle = "#888";
          ctx.textAlign = "left";
          ctx.fillText(`+${unapproved.length - shown}`, chipX, footerY + footerH / 2);
        }
      } else {
        ctx.font = `11px ${_fontSerif}`;
        ctx.fillStyle = "#777";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          state.customClassroomMode
            ? "Lobby open — approve observers to play next round"
            : "Observers will auto-join next round",
          availW / 2,
          footerY + footerH / 2
        );
      }
    }

    ctx.restore();
  }

  function update() {
    const currentKey = getEasternKey();
    if (!state.practiceMode && !state.customMode && state.dailyKey && currentKey !== state.dailyKey) {
      // Fetch first, then reset from the callback. Resetting here directly would
      // rebuild the board before the new day's word has arrived, and
      // getDailyWord() returns null when the key does not match today.
      _fetchDailyWord();
      return;
    }
    if (state.nextMessagePoll && Date.now() > state.nextMessagePoll) {
      fetchAdminMessage();
      state.nextMessagePoll = Date.now() + 5 * 60 * 1000;
    }
    if (state.nextLeaderboardPoll && Date.now() > state.nextLeaderboardPoll) {
      state.nextLeaderboardPoll = Date.now() + 2 * 60 * 1000;
      fetchLeaderboard(state.dailyKey, 50).then((entries) => {
        if (entries) {
          const normalized = entries.map((row) => ({
            name: row.name,
            moves: row.moves,
            time: row.created_at ? Date.parse(row.created_at) : 0,
          }));
          const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
          state.leaderboard = filtered;
          saveLeaderboard(state.dailyKey, filtered);
        }
      });
    }
    if (state.mode === "play" && isSolved()) {
      if (state.customMode) {
        // Custom game solve — no daily progress
        if (!state.winAcknowledged) {
          playSolveFanfare();
          broadcastSolve();
          // Update own player record in multiplayer
          if (state.customMultiplayer && state.customPlayerId) {
            state.customSolveCount++;
            const self = state.customPlayers[state.customPlayerId];
            if (self && !self.solved) {
              self.solved = true;
              self.moves = state.moves;
              self.grid = gridToString(state.grid);
              self.finishOrder = state.customSolveCount;
            }
            finishCustomRoundIfComplete();
          }
          state.mode = "win";
          state.winAnimStart = performance.now();
          if (!state.customMultiplayer) {
            const mc = classifyMoves();
            state.sessionStats.games.push({
              moves: state.moves, minMoves: state.minMoves, minExact: state.minExact,
              green: mc.green, yellow: mc.yellow, red: mc.red,
            });
          }
          announceAlert(`Solved! ${state.targetWord} in ${state.moves} moves`);
          setCanvasLabel(`Puzzle solved! ${state.targetWord} in ${state.moves} moves.`);
        }
        state.solvedToday = true;
        return;
      }
      if (state.practiceMode && !state.winAcknowledged) {
        state.winAcknowledged = true;
        state.completed = true;
        state.mode = "win";
        state.winAnimStart = performance.now();
        const mc = classifyMoves();
        state.sessionStats.games.push({
          moves: state.moves, minMoves: state.minMoves, minExact: state.minExact,
          green: mc.green, yellow: mc.yellow, red: mc.red,
        });
        playSolveFanfare();
        // Archive (practiceMode) challenge solves exit here before the normal
        // challenge-redirect block below, so fire it explicitly. No-op when
        // there's no active challenge.
        handleChallengeSolveRedirect();
        return;
      }
      if (!state.winAcknowledged) {
        state.mode = "win";
        state.winAnimStart = performance.now();
        announceAlert(`Solved! ${state.targetWord} in ${state.moves} moves`);
        setCanvasLabel(`Puzzle solved! ${state.targetWord} in ${state.moves} moves.`);
        _winFocusIndex = -1;
        playSolveFanfare();
        if (state.redoMode) {
          const mc = classifyMoves();
          state.sessionStats.games.push({
            moves: state.moves, minMoves: state.minMoves, minExact: state.minExact,
            green: mc.green, yellow: mc.yellow, red: mc.red,
          });
        }
        setTimeout(checkAchievements, 500);
        // App Store review prompt (native app only, once)
        if (PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeReviewPrompt) {
          try {
            if (!localStorage.getItem("pivot_review_prompted")) {
              const stats = computePlayerStats();
              const streak = loadStreakState();
              if (stats.gamesPlayed >= 5 || (streak.count && streak.count >= 3)) {
                localStorage.setItem("pivot_review_prompted", "1");
                setTimeout(() => {
                  window.webkit.messageHandlers.nativeReviewPrompt.postMessage({});
                }, 2000);
              }
            }
          } catch (e) {}
        }
        // The "solve" event fires once, further down, keyed per daily_key. It
        // used to fire here too under a per-session key, so neither deduped the
        // other and every solve was counted twice: solves_today read 62 against
        // 27 actual plays, which halved every rate built on it (the 0.9% share
        // rate was really ~1.8%). duration_ms moved to that single event.
        // Lazily mint a player identity on first solve. Fire-and-forget;
        // failure is fine — submit will still work device-keyed.
        if (!state.practiceMode && !getPlayerId()) {
          ensurePlayerRegistered();
        }
        logEvent("share_loop_variant_seen", {
          ...getLandingTelemetryData(),
        }, `${state.dailyKey}_${state.sessionId}_share_loop_variant_seen`);
        // Check personal best
        if (!state.practiceMode && !state.redoMode) {
          const stats = computePlayerStats();
          state.isPersonalBest = stats.gamesPlayed > 1 && state.moves <= stats.personalBest;
        }
      }
      // Closing the result overlay returns to the solved board. Do not replay
      // persistence, analytics, completion, or name-entry side effects on
      // every animation frame while that solved board remains visible.
      if (state.winAcknowledged) return;
      state.solvedToday = true;
      if (!state.practiceMode) {
        const streak = loadStreakState();
        const todayKey = state.dailyKey;
        if (streak.lastKey !== todayKey) {
          const yesterdayKey = addDaysToKey(todayKey, -1);
          const graceUsed = canUseStreakGrace(streak, todayKey);
          const newCount = (streak.lastKey === yesterdayKey || graceUsed) ? streak.count + 1 : 1;
          state.streakCount = newCount;
          state.streakDisplay = streak.count;
          state.streakAnimating = true;
          state.streakAnimStart = performance.now();
          state.shieldCelebrationVisible = graceUsed;
          saveStreakState(newCount, todayKey, {
            graceUsedOn: graceUsed ? todayKey : streak.graceUsedOn,
          });
          if (graceUsed) {
            setShareToast("Streak freeze used: 1 miss forgiven.", 2800);
            logEvent("shield_consumed", {
              streak_count: newCount,
              previous_streak_count: streak.count,
              gap_days: daysBetweenKeys(streak.lastKey, todayKey),
            }, `${todayKey}_${state.sessionId}_shield_consumed`);
            logEvent("streak_grace_used", {
              streak_count: newCount,
            }, `${todayKey}_${state.sessionId}_streak_grace_used`);
          }
          // Milestone detection — fires once per (device, milestone). The
          // celebration banner shows on the win screen for ~6 seconds; the
          // persistent tier badge sticks based on the current streakCount.
          const crossed = getMilestoneCrossedToday(streak.count, newCount);
          if (crossed) {
            state.milestoneJustReached = crossed;
            state.milestoneJustReachedAt = performance.now();
            saveCelebratedMilestone(crossed);
            logEvent("streak_milestone_reached", {
              milestone: crossed,
              streak_count: newCount,
            }, `${todayKey}_${state.sessionId}_milestone_${crossed}`);
          }
        }
      }
      // Auto-redirect to comparison page after solving a challenge.
      // Do not require challengeResolved here; replay fetch may lag but the comparison page
      // can still load with inline data and fetch lazily.
      handleChallengeSolveRedirect();
      saveProgress(state.dailyKey, {
        grid: state.grid.map((row) => row.join("")),
        moves: state.moves,
        lastMove: state.lastMove,
        solvedToday: state.solvedToday,
        minMoves: state.minMoves,
        minExact: state.minExact,
        submitted: state.submitted,
        redoMode: state.redoMode,
        word: state.targetWord,
        moveHistory: state.moveHistory,
      }, state.practiceMode || state.redoMode);
      if (!state.practiceMode && !state.alreadyCompleted) {
        submitCompletion(state.dailyKey, state.moves, state.practiceMode);
        state.completed = true;
        state.alreadyCompleted = true;
        setTimeout(maybeShowInstallPrompt, 2500);
      }
      const solveMs = state.puzzleStartedAt > 0 ? Math.max(0, Date.now() - state.puzzleStartedAt) : null;
      const firstMoveMs = (state.firstMoveAt > 0 && state.puzzleStartedAt > 0)
        ? Math.max(0, state.firstMoveAt - state.puzzleStartedAt)
        : null;
      // duration_ms (first move -> solve) is distinct from solve_ms (start ->
      // solve). Carried over from the duplicate solve event removed above,
      // because the pivot_admin_stats RPC lives only in the database and isn't
      // versioned here, so which field it reads for median_solve_seconds can't
      // be checked from the repo. Keeping both means either still resolves.
      const durationMs = state.firstMoveAt
        ? Math.max(0, Date.now() - state.firstMoveAt)
        : null;
      logEvent(
        "solve",
        {
          moves: state.moves,
          solve_ms: solveMs,
          duration_ms: durationMs,
          first_move_ms: firstMoveMs,
          nudge_shown: state.activationNudgeShown ? 1 : 0,
          ...getLandingTelemetryData(),
        },
        `${state.dailyKey}_solve_${state.practiceMode ? "p" : "d"}`
      );
      if (!state.practiceMode && state.dailyKey && Number.isFinite(state.moves)) {
        fetchPercentileForMoves(state.dailyKey, state.moves);
      }
      if (!state.practiceMode && !state.customMode && !state.redoMode) growth?.solved();
      if (!state.practiceMode && !state.customMode && state.leaderboardEligible && !state.submitted) {
        clearPostSolveLeaderboardPrompt();
        _postSolveLeaderboardPromptTimer = setTimeout(() => {
          _postSolveLeaderboardPromptTimer = null;
          // The player may have closed the result, submitted from its inline
          // button, or navigated away while the celebration was visible.
          if (state.mode !== "win" || state.practiceMode || state.customMode) return;
          if (state.submitted || !state.leaderboardEligible) return;
          if (hasOpenOverlayForKeyboard() || growth?.busy() || document.hidden) {
            scheduleDeferredNamePrompt();
            return;
          }
          _promptReminderOnNameOverlayClose = true;
          openNameOverlay();
        }, POST_SOLVE_LEADERBOARD_PROMPT_DELAY_MS);
      }
    }
  }

  // Navigate to the challenge comparison page. Called once the challengee's
  // score submit resolves (so opponent_player_id has reached the server and the
  // rivalry row is written), or by the solve handler's fallback timer if the
  // player never submits. No-op for non-challenge solves (url is never set).
  function goToChallengeCompare() {
    if (!state._challengeCompareUrl) return;
    const url = state._challengeCompareUrl;
    state._challengeCompareUrl = "";
    if (state._challengeRedirectFallback) {
      clearTimeout(state._challengeRedirectFallback);
      state._challengeRedirectFallback = 0;
    }
    setTimeout(() => { window.location.href = url; }, 600);
  }

  // Record the head-to-head rivalry for an active challenge. Covers the gap the
  // score-submit path can't: a challengee who ALREADY played today (so no fresh
  // submit fires when they open the challenge link) — pivot-record-rivalry forms
  // the rivalry from both players' existing scores. Fire-and-forget, once/load.
  function recordChallengeRivalry() {
    if (state._rivalryRecorded) return;
    const cf = state.challengeFrom;
    const opponentId = cf && typeof cf.opponentPlayerId === "string" ? cf.opponentPlayerId : "";
    const myId = getPlayerId();
    if (!cf || !opponentId || !myId || opponentId === myId) return;
    if (state.practiceMode || !state.dailyKey) return;
    // Only meaningful once our own score for today exists server-side.
    if (!(state.submitted || state.alreadyCompleted || state.completed)) return;
    state._rivalryRecorded = true;
    try {
      fetch(`${SUPABASE_URL}/functions/v1/pivot-record-rivalry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          player_id: myId,
          opponent_player_id: opponentId,
          daily_key: state.dailyKey,
          device_id: getDeviceId(),
        }),
      }).catch(() => {});
    } catch (_) {}
  }

  // Build the challenge-compare redirect + record the rivalry on a solve of an
  // active challenge. Called from BOTH the practiceMode (archive) solve branch
  // AND the normal solve flow: archive solves return early before the normal
  // flow runs, so without this call they would never record a rivalry. Guarded
  // to run once per solve via _challengeRedirected; no-op when no challenge.
  function handleChallengeSolveRedirect() {
    if (!(state.challengeFrom && !state._challengeRedirected)) return;
    state._challengeRedirected = true;
    const cf = state.challengeFrom;
    const myName = String(state.playerName || safeGetItem("pivot_name_") || "You").trim().slice(0, 32) || "You";
    const dayNum = cf.dayNum || getDayNumber();
    const challengerMoves = Number.isFinite(cf.moves) ? cf.moves : null;
    const url = buildChallengeCompareUrl(cf.name, myName, dayNum, challengerMoves, state.moves);
    // Fire-and-forget push notification to challenger
    try {
      fetch(`${SUPABASE_URL}/functions/v1/pivot-challenge-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenger_name: cf.name,
          challengee_name: myName,
          day: dayNum,
          challenger_moves: challengerMoves,
          challengee_moves: state.moves,
          challengee_device_id: getDeviceId(),
          compare_url: url,
        }),
      }).catch(() => {});
    } catch (_) {}
    setShareToast("Challenge complete — opening comparison...", 1700);
    // Defer navigation until the challengee's score submit resolves. That
    // submit carries opponent_player_id, which is what writes the rivalry
    // row. The old blind 1.4s redirect raced — and beat — the user's submit
    // tap, so rivalry rows were never recorded. goToChallengeCompare() fires
    // from submitScore's success path; this fallback still navigates if the
    // player never submits (waiting out any in-flight submit, and any open
    // name entry, so typing a name is never cut off by the redirect).
    state._challengeCompareUrl = url;
    if (state._challengeRedirectFallback) clearTimeout(state._challengeRedirectFallback);
    const scheduleCompareFallback = () => {
      state._challengeRedirectFallback = setTimeout(() => {
        const nameEntryOpen = Boolean(nameOverlay && nameOverlay.classList.contains("show"));
        if (_scoreSubmitInFlight || nameEntryOpen) { scheduleCompareFallback(); return; }
        goToChallengeCompare();
      }, 6000);
    };
    scheduleCompareFallback();
    // Archive challenge solves can't submit normally (practiceMode); record
    // a hidden rivalry-only score so the head-to-head still forms. No-op for
    // live-edition challenges (self-gates on practiceMode).
    submitArchiveRivalryScore();
  }

  // Record a leaderboard-HIDDEN score for an archived day reached via a
  // challenge link, then let the server form the head-to-head rivalry. Archive
  // plays run in practiceMode so the normal submit never fires, and
  // pivot-record-rivalry needs a score row to exist — so we create one, flagged
  // ineligible (leaderboard_eligible:false). The server runs applyRivalryResult
  // because the payload carries opponent_player_id. Fire-and-forget, once per
  // archive solve. Deliberately NOT submitScore(): that path bails on
  // practiceMode and has leaderboard side effects (achievement, toast,
  // leaderboard refetch, OG image, retry queue) that must not fire here.
  async function submitArchiveRivalryScore() {
    if (state._archiveRivalrySubmitted) return;
    if (!state.practiceMode || !state.dailyKey) return;
    const cf = state.challengeFrom;
    const opponentId = cf && typeof cf.opponentPlayerId === "string" ? cf.opponentPlayerId : "";
    const myId = getPlayerId();
    if (!opponentId || !myId || opponentId === myId) return;
    const name = sanitizeName(state.playerName || loadName() || "");
    if (!name || !isNameAllowed(name)) return;
    if (!Number.isFinite(state.moves) || state.moves < 1 || state.moves > 300) return;
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length !== state.moves) return;
    const replay = replayAndVerify(state.canonicalStartStr, state.moveHistory, state.targetWord);
    if (!replay.valid) return;
    state._archiveRivalrySubmitted = true;
    const payload = {
      name: name.slice(0, 16),
      moves: state.moves,
      daily_key: state.dailyKey,
      device_id: getDeviceId(),
      move_history: state.moveHistory,
      start_state: state.canonicalStartStr,
      player_id: myId,
      opponent_player_id: opponentId,
      leaderboard_eligible: false,
    };
    // Mark a submit in flight so the existing compare-redirect fallback waits
    // for this POST before navigating (the same mechanism submitScore uses).
    _scoreSubmitInFlight = true;
    let landed = false;
    try {
      // postSubmitScoreWithRetry resolves {ok:false,...} on network/timeout
      // failure (it never throws); r.ok===true means an HTTP response came back.
      const r = await postSubmitScoreWithRetry(payload, 2);
      // 2xx = row written; 409 = a row already exists for this day (already-played
      // case) — also "landed" (server-side rivalry fallback handles it, retrying
      // here won't help). Any other status, or no response, is a real failure.
      landed = !!r && r.ok === true && !!r.res && (r.res.status < 400 || r.res.status === 409);
    } catch (_) {
    } finally {
      _scoreSubmitInFlight = false;
    }
    if (!landed) {
      // Roll back the once-guard so a re-solve can retry, and surface the failure.
      state._archiveRivalrySubmitted = false;
      logEvent("archive_rivalry_submit_failed", { daily_key: state.dailyKey });
    }
    // POST settled — open the comparison (degrades gracefully on failure).
    goToChallengeCompare();
  }

  function loop() {
    const frameNow = performance.now();
    if (!document.hidden) {
      if (!Number.isFinite(layout.cellSize) || layout.cellSize <= 0) {
        resizeCanvas();
      }
      if (state.streakAnimating) {
        const now = performance.now();
        const elapsed = now - state.streakAnimStart;
        const duration = 700;
        const t = Math.min(1, elapsed / duration);
        const eased = t * (2 - t);
        const start = state.streakDisplay;
        const target = state.streakCount;
        state.streakDisplay = Math.round(start + (target - start) * eased);
        if (t >= 1) {
          state.streakAnimating = false;
          state.streakDisplay = state.streakCount;
        }
      }
      update();
      const wallNow = Date.now();
      const highFps =
        Boolean(state.anim) ||
        Boolean(state.streakAnimating) ||
        Boolean(state.customReconnecting) ||
        Boolean(state._lastPivotPulse) ||
        Boolean(state._settleCells) ||
        Boolean(state._gameEntryAnimStart) ||
        (state.customCountdownUntil > 0 && frameNow < state.customCountdownUntil) ||
        (state.mode === "win" && state.winAnimStart && frameNow - state.winAnimStart < 2400) ||
        wallNow < state.shareToastUntil ||
        wallNow < state.achievementToastUntil ||
        wallNow < state.hintHighlightUntil ||
        wallNow < state.highlightUntil;
      const minFrameMs = highFps ? (1000 / 60) : (1000 / 12);
      if (!loop.lastRenderAt || (frameNow - loop.lastRenderAt) >= minFrameMs) {
        // Skip redundant redraws when nothing has changed (idle state)
        if (!highFps) {
          const leaderboardSignature = state.leaderboard.slice(0, 5)
            .map((entry) => `${entry.name}:${entry.moves}:${entry.time || 0}`)
            .join(",");
          const minuteBucket = Math.floor(wallNow / 60000);
          const hash = `${state.mode}|${state.moves}|${state.hoverPivot ? state.hoverPivot.r + "," + state.hoverPivot.c : ""}|${_currentTheme}|${leaderboardSignature}|${state.submitted}|${state.showTutorial}|${state.showLeaderboardOverlay}|${state.leaderboardTab}|${state.leaderboardPage}|${_winFocusIndex}|${minuteBucket}`;
          if (hash === loop._lastHash && loop.lastRenderAt) {
            // Still request next frame but don't redraw
          } else {
            loop._lastHash = hash;
            render();
            loop.lastRenderAt = frameNow;
          }
        } else {
          loop._lastHash = "";
          render();
          loop.lastRenderAt = frameNow;
        }
      }
    }
    requestAnimationFrame(loop);
  }
  loop.lastRenderAt = 0;

  function getMousePosition(event) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = layout.width / rect.width;
    const scaleY = layout.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  function isPointInRect(point, rect) {
    return (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    );
  }

  function isPointInRectPadded(point, rect, pad = 0) {
    return (
      point.x >= rect.x - pad &&
      point.x <= rect.x + rect.width + pad &&
      point.y >= rect.y - pad &&
      point.y <= rect.y + rect.height + pad
    );
  }

  function findPivotAt(point) {
    const { cellSize, offsetX, offsetY, pivotRadius } = layout;
    for (let r = 0; r < GRID_ROWS - 1; r += 1) {
      for (let c = 0; c < GRID_COLS - 1; c += 1) {
        const x = offsetX + (c + 1) * cellSize;
        const y = offsetY + (r + 1) * cellSize;
        const dx = point.x - x;
        const dy = point.y - y;
        if (Math.hypot(dx, dy) <= pivotRadius * 1.6) {
          return { r, c };
        }
      }
    }
    return null;
  }

  function buildCompactShareText() {
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    const day = isArchive ? getDayNumberForKey(state.dailyKey) : getDayNumber();
    // Broadcast shares link to the puzzle, never to a replay of the sender's
    // own solution -- that auto-plays the answer and spoils the puzzle the
    // share is inviting the recipient to solve. See getShareUrl().
    const url = getPlayableShareUrl();
    if (state.redoMode || (state.practiceMode && !isArchive)) {
      return `I'm practicing today's PIVOT (Edition No. ${day}). Try it: pivotthegame.com`;
    }
    if (isArchive) {
      return `I played PIVOT Edition No. ${day}. Try it: pivotthegame.com`;
    }
    const lines = [`PIVOT #${day} · ${state.moves} moves`, "I solved today’s PIVOT. Want to try it too?"];
    if (state.shareIncludeDetails) {
      const detailLine = buildOptionalShareDetailLine();
      if (detailLine) lines.push(detailLine);
    }
    lines.push("", url);
    return lines.join("\n");
  }

  function buildOptionalShareDetailLine() {
    if (!state.shareIncludeDetails) return "";
    const details = [];
    if (Number.isFinite(state.minMoves)) {
      const over = Math.max(0, state.moves - state.minMoves);
      details.push(`${over} over ${state.minExact ? "min" : "par"} ${state.minMoves}`);
    }
    if (Array.isArray(state.leaderboard) && state.leaderboard.length && state.playerName) {
      const entries = [...state.leaderboard].sort((a, b) => {
        if (a.moves !== b.moves) return a.moves - b.moves;
        return (a.time || 0) - (b.time || 0);
      });
      const rank = getPlayerRank(entries, state.playerName, state.moves);
      if (rank) {
        const tieCount = entries.filter((entry) => entry.moves === state.moves).length;
        details.push(`rank #${rank}${tieCount > 1 ? " tied" : ""}`);
      }
    }
    return details.join(" \u00b7 ");
  }

  async function shareResult() {
    const now = Date.now();
    if (now < state.shareCooldownUntil) return;
    state.shareCooldownUntil = now + 1500;
    const text = buildCompactShareText();
    unlockAchievement("share");
    if (PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeShare) {
      const shareCanvas = buildShareImageCanvas();
      const base64 = shareCanvas.toDataURL("image/png").split(",")[1];
      postNativeShare({
        title: `PIVOT Edition No. ${_currentDayNumber()}`,
        text,
        url: getPlayableShareUrl(),
        image: base64,
        eventName: "share_native",
        eventKey: `${state.dailyKey}_share_native`,
        eventData: getLandingTelemetryData(),
      });
      return;
    }
    const interaction = growth?.shareIntent("result");
    if (navigator.share) {
      try {
        setShareToast("Sharing...", 5000);
        await navigator.share({ text });
        logEvent("share_native", {
          ...getLandingTelemetryData(),
        }, `${state.dailyKey}_share_native`);
        growth?.shareOutcome(interaction, "completed");
        setShareToast("Shared!");
        return;
      } catch (err) {
        if (err.name === "AbortError") { growth?.shareOutcome(interaction, "canceled"); setShareToast(""); return; }
      }
    }
    setShareToast("Copying...", 5000);
    const copied = await copyTextToClipboard(text);
    growth?.shareOutcome(interaction, copied ? "copied" : "failed");
    if (copied) {
      logEvent("share_copy", {
        ...getLandingTelemetryData(),
      }, `${state.dailyKey}_share_copy`);
      setShareToast("Copied to clipboard \u2014 paste anywhere", 3500);
      return;
    }
    setShareToast("Copy this text.", 2400);
    openCopyOverlay(text, "Share result");
  }

  function getDayNumber() {
    const start = Date.UTC(2026, 0, 1);
    const { year, month, day } = getEasternDateParts();
    const todayUTC = Date.UTC(year, month - 1, day);
    return Math.floor((todayUTC - start) / (24 * 60 * 60 * 1000)) + 1;
  }

  function getDayNumberForKey(dateKey) {
    const start = Date.UTC(2026, 0, 1);
    const [y, m, d] = dateKey.split("-").map(Number);
    const keyUTC = Date.UTC(y, m - 1, d);
    return Math.floor((keyUTC - start) / (24 * 60 * 60 * 1000)) + 1;
  }

  function buildShareGrid() {
    // Per-move lower-bound analysis: walk move_history tracking distance changes.
    if (state.moveHistory && state.moveHistory.length > 0 && state.canonicalStartStr && state.targetWord) {
      try {
        let gridStr = state.canonicalStartStr;
        let prevDist = computeLowerBoundForStart(gridStr, state.targetWord);
        const squares = [];
        for (const moveIdx of state.moveHistory) {
          gridStr = applyMove(gridStr, moveIdx, true);
          const dist = computeLowerBoundForStart(gridStr, state.targetWord);
          if (dist === null || prevDist === null) {
            squares.push("\u2b1c\ufe0f"); // white — unknown
          } else if (dist < prevDist) {
            squares.push("\ud83d\udfe9"); // green — closer
          } else if (dist === prevDist) {
            squares.push("\ud83d\udfe8"); // yellow — neutral
          } else {
            squares.push("\ud83d\udfe5"); // red — farther
          }
          if (dist !== null) prevDist = dist;
        }
        return squares.join("");
      } catch (_) { /* fall through */ }
    }
    // Fallback: simple green/white grid
    const total = Math.min(10, Math.max(1, state.moves));
    const minMoves = Number.isFinite(state.minMoves) ? state.minMoves : total;
    const green = Math.max(0, Math.min(total, minMoves));
    const gray = Math.max(0, total - green);
    return "\ud83d\udfe9".repeat(green) + "\u2b1c\ufe0f".repeat(gray) + (state.moves > total ? "+" : "");
  }

  function setShareToast(message, duration = 1800) {
    state.shareToastMessage = message;
    state.shareToastUntil = Date.now() + duration;
    if (message) {
      haptic("light");
      announceAlert(message);
    }
  }

  async function copyTextToClipboard(text) {
    if (!text) return false;
    // iOS Safari often reports execCommand("copy") as successful without actually copying.
    // Only treat Clipboard API writeText as success on iOS Safari; otherwise fall back to manual copy overlay.
    if (isMobileSafari()) {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (err) {
          return false;
        }
      }
      return false;
    }
    if (navigator.clipboard?.writeText) {
      try {
        await Promise.race([
          navigator.clipboard.writeText(text),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1500)),
        ]);
        return true;
      } catch (err) {
        // Fall through to execCommand-based copy.
      }
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-1000px";
      textarea.style.left = "-1000px";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand("copy");
      textarea.remove();
      return ok;
    } catch (err) {
      return false;
    }
  }

  function isMobileSafari() {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIOS && isSafari;
  }

  async function copyImageToClipboard(blob) {
    if (!navigator.clipboard || !navigator.clipboard.write || !window.ClipboardItem) {
      return false;
    }
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      return true;
    } catch (err) {
      return false;
    }
  }

  async function downloadBlob(blob, filename) {
    // iOS: <a download> doesn't work for blob URLs — use share API instead
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: blob.type || "application/octet-stream" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
          return;
        }
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function openBlobInNewTab(blob) {
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  function shareImage() {
    setShareToast("Preparing image...", 5000);
    const imageCanvas = buildShareImageCanvas();
    imageCanvas.toBlob(async (blob) => {
      if (!blob) return;
      const fileName = `pivot-${state.dailyKey || getEasternKey()}.png`;
      try {
        const file = new File([blob], fileName, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          setShareToast("Sharing image...", 5000);
          await navigator.share({ files: [file] });
          logEvent("share_image_native", {}, `${state.dailyKey}_share_image_native`);
          setShareToast("Shared!");
          return;
        }
      } catch (err) {
        if (err.name === "AbortError") { setShareToast(""); return; }
      }
      if (isMobileSafari()) {
        const ok = await copyImageToClipboard(blob);
        if (ok) {
          setShareToast("Image copied. Paste to share.");
        } else {
          openBlobInNewTab(blob);
          setShareToast("Opened image. Long-press to save.");
        }
        return;
      }
      downloadBlob(blob, fileName);
      setShareToast("Downloading image...");
    }, "image/png");
  }

  function _currentDayNumber() {
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    return isArchive ? getDayNumberForKey(state.dailyKey) : getDayNumber();
  }

  function getReplayUrl() {
    const name = state.playerName || "Player";
    const day = _currentDayNumber();
    return `${CANONICAL_URL}/replay/#${encodeURIComponent(name)}/${day}`;
  }

  // A link to the player's replay. Correct when the user is deliberately
  // showing their solution ("See my solution"), wrong for a "can you beat
  // that?" broadcast — it auto-plays the answer and spoils the puzzle the
  // share is inviting the recipient to solve. Broadcast paths use
  // getPlayableShareUrl() instead.
  function getShareUrl() {
    const name = state.playerName || "Player";
    const day = _currentDayNumber();
    return `${CANONICAL_URL}/replay/#${encodeURIComponent(name)}/${day}`;
  }

  // Where a broadcast share should send someone: the puzzle itself.
  function getPlayableShareUrl() {
    return `${CANONICAL_URL}/?ref=friend`;
  }

  function getHallUrl() {
    if (PIVOT_NATIVE) return `${CANONICAL_URL}/hall/`;
    try {
      return new URL("hall/", window.location.href).toString();
    } catch {
      return `${CANONICAL_URL}/hall/`;
    }
  }

  let _hallNavLockUntil = 0;
  function openHallPage() {
    const now = Date.now();
    if (now < _hallNavLockUntil) return;
    _hallNavLockUntil = now + 2000;
    const url = getHallUrl();
    // Reliability-first: same-tab navigation avoids popup blockers and double-open races.
    window.location.assign(url);
  }

  function shareReplayLink() {
    if (!state.submitted && !state.playerName) {
      setShareToast("Submit your name first", 2000);
      return;
    }
    const url = getReplayUrl();
    logEvent("share_replay", {}, `${state.dailyKey}_share_replay`);
    // Navigate in same window — most reliable on mobile Safari
    window.location.href = url;
  }

  function shareOnBluesky() {
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    const day = isArchive ? getDayNumberForKey(state.dailyKey) : getDayNumber();
    let text;
    if (isArchive) {
      text = `PIVOT Edition No. ${day}: I played this past PIVOT in ${state.moves} moves. Try it: ${CANONICAL_URL}`;
    } else if (state.submitted || state.playerName) {
      text = `PIVOT Edition No. ${day}: I did today's PIVOT in ${state.moves} moves. See my solution:\n${getShareUrl()}`;
    } else {
      text = `PIVOT Edition No. ${day}: I did today's PIVOT in ${state.moves} moves. Let's see if you can beat that!\n${CANONICAL_URL}`;
    }
    const bskyUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`;
    const a = document.createElement("a");
    a.href = bskyUrl;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
    logEvent("share_bluesky", {}, `${state.dailyKey}_share_bluesky`);
  }

  async function generateAndDownloadVideo() {
    const loaded = await ensureVideoReplayLoaded();
    if (!loaded || !window.PivotVideo || !window.PivotVideo.isSupported()) {
      // Fallback: share replay link on unsupported browsers (e.g. iOS Safari)
      if (navigator.share) {
        const url = getReplayUrl();
        navigator.share({ title: "PIVOT Replay", url }).catch(() => {});
        return;
      }
      setShareToast("Video not supported in this browser.", 3000);
      return;
    }
    if (!state.canonicalStartStr || !Array.isArray(state.moveHistory) || !state.moveHistory.length) {
      setShareToast("No replay data available.", 2400);
      return;
    }
    setShareToast("Generating video...", 15000);
    try {
      const day = _currentDayNumber();
      const name = state.playerName || "Player";
      const blob = await window.PivotVideo.generate({
        startStr: state.canonicalStartStr,
        moveHistory: state.moveHistory,
        titleLine1: `${name}'s solution`,
        titleLine2: `PIVOT Edition No. ${day}`,
        moveCount: state.moves,
      });
      downloadBlob(blob, `pivot-day${day}.mp4`);
      setShareToast("Video downloaded!");
      logEvent("share_video", {}, `${state.dailyKey}_share_video`);
    } catch (err) {
      console.error("Video generation failed:", err);
      setShareToast("Video generation failed.", 3000);
    }
  }

  function shareResultsText() {
    const isArchive = state.practiceMode && state.dailyKey && state.dailyKey !== getEasternKey();
    const day = isArchive ? getDayNumberForKey(state.dailyKey) : getDayNumber();
    const nameEmojis = { "Geenybeans": "💚", "Abby": "🐰", "Jake": "⚖️" };
    const flair = nameEmojis[state.playerName] || "";
    const scoreLine = `PIVOT Edition No. ${day}: ${state.moves} moves${flair ? " " + flair : ""}`;
    const minLine = Number.isFinite(state.minMoves) ? `Min: ${state.minMoves}` : "";
    const entries = [...state.leaderboard].sort((a, b) => {
      if (a.moves !== b.moves) return a.moves - b.moves;
      return (a.time || 0) - (b.time || 0);
    });
    const best = entries.length ? entries[0]?.moves : null;
    const bestLine = Number.isFinite(best) ? `Best: ${best}` : "";
    const rank = getPlayerRank(entries, state.playerName, state.moves);
    const tieCount = entries.filter((entry) => entry.moves === state.moves).length;
    const placeLine = rank ? `Place: ${rank === 1 ? "👑 " : ""}#${rank}${tieCount > 1 ? " (tied)" : ""}` : "";
    const minCount =
      Number.isFinite(state.minMoves) && state.minExact
        ? entries.filter((entry) => entry.moves === state.minMoves).length
        : 0;
    const minHitLine = minCount ? `Min hit: ${minCount}` : "";
    const gridLine = buildShareGrid();
    // Broadcast path: link to the puzzle, not to a replay of our own solution.
    const url = getPlayableShareUrl();
    return [scoreLine, minLine, bestLine, placeLine, minHitLine, gridLine, url]
      .filter(Boolean)
      .join("\n");
  }

  function applySubmittedScore(name, responseBody = null) {
    const entry = { name: name.slice(0, 16), moves: state.moves, time: Date.now() };
    const updated = [...state.leaderboard, entry];
    state.leaderboard = updated;
    saveLeaderboard(state.dailyKey, updated);
    state.submitted = true;
    // The next My Circles visit should reflect this newly submitted score.
    state.friendCirclesFetchedAt = 0;
    applySubmissionReceiptFromResponse(responseBody);
    if (!state.submissionReceiptAt) {
      state.submissionReceiptAt = new Date().toISOString();
    }
    state.playerName = entry.name;
    saveName(entry.name);
    state.highlightUntil = Date.now() + 5000;
    saveProgress(state.dailyKey, {
      grid: state.grid.map((row) => row.join("")),
      moves: state.moves,
      lastMove: state.lastMove,
      solvedToday: state.solvedToday,
      minMoves: state.minMoves,
      minExact: state.minExact,
      submitted: true,
      redoMode: state.redoMode,
      word: state.targetWord,
      moveHistory: state.moveHistory,
      hintUsed: state.hintUsed,
    }, state.practiceMode || state.redoMode);
  }

  function mapSubmitError(statusCode, message, timedOut) {
    if (timedOut) return "Network timeout. Please try again.";
    const msg = (message || "").toLowerCase();
    if (statusCode === 409 || msg.includes("already submitted")) return "Score already submitted for today.";
    if (msg.includes("banned phrase")) return "Name contains a banned phrase. Please choose another.";
    if (msg.includes("invalid start_state")) {
      return "Puzzle sync issue. Refresh, then use Submit again from the sidebar.";
    }
    if (msg.includes("move_history")) return "Run could not be verified. Start a fresh puzzle and retry.";
    if (msg.includes("replay failed") || msg.includes("center word")) return "Move replay did not match. Try a fresh puzzle.";
    if (msg.includes("future date")) return "You can only submit today's puzzle.";
    if (msg.includes("no puzzle")) return "No puzzle found for this date.";
    if (msg.includes("rate") || statusCode === 429) return "Too many attempts. Please wait a moment.";
    // Surface unrecognized 400 errors with server detail
    if (statusCode === 400 && message) {
      const snippet = String(message).slice(0, 60);
      return `Submission rejected: ${snippet}`;
    }
    return "Could not submit score. Please try again.";
  }

  function logSubmitFailure(reason, data = {}) {
    const payload = { reason, ...data };
    if (typeof payload.server_error === "string") {
      payload.server_error = payload.server_error.slice(0, 120);
    }
    payload.referrer = buildSubmitReferrer("failed", {
      reason,
      status: Number.isFinite(Number(payload.status_code)) ? Number(payload.status_code) : 0,
      ms: Number.isFinite(Number(payload.submit_ms)) ? Number(payload.submit_ms) : null,
      attempts: Number.isFinite(Number(payload.attempts)) ? Number(payload.attempts) : 1,
      timedOut: Number.isFinite(Number(payload.timed_out)) ? Number(payload.timed_out) : 0,
      queued: Number.isFinite(Number(payload.queued)) ? Number(payload.queued) : 0,
    });
    logEvent("leaderboard_submit_failed", payload);
  }

  async function postSubmitScoreWithRetry(payload, maxAttempts = 2) {
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      let timedOut = false;
      let timeoutId = null;
      try {
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            timedOut = true;
            if (controller) controller.abort();
            reject(new Error("timeout"));
          }, 12000);
        });
        const requestPromise = fetch(SUPABASE_URL + "/functions/v1/pivot-submit-score", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": "Bearer " + SUPABASE_ANON_KEY },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
        });
        const res = await Promise.race([requestPromise, timeoutPromise]);
        if (timeoutId) clearTimeout(timeoutId);

        let responseBody = null;
        try { responseBody = await res.json(); } catch {}
        const serverError = responseBody && typeof responseBody.error === "string" ? responseBody.error : "";
        const retryableStatus = res.status === 429 || res.status >= 500;
        if (retryableStatus && attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
          continue;
        }
        return { ok: true, res, responseBody, serverError, timedOut: false, attempts: attempt };
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        const message = err && err.message ? String(err.message).toLowerCase() : "";
        const isTimeout = timedOut || message.includes("timeout") || (err && err.name === "AbortError");
        lastError = { err, timedOut: isTimeout };
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
          continue;
        }
      }
    }
    return {
      ok: false,
      error: lastError && lastError.err ? lastError.err : new Error("submit_failed"),
      timedOut: Boolean(lastError && lastError.timedOut),
      attempts: maxAttempts,
    };
  }

  function isRetryableSubmitStatus(statusCode) {
    return statusCode === 429 || statusCode >= 500;
  }

  function buildPendingSubmitPayload(name) {
    const safeName = sanitizeName(name);
    if (!safeName) return null;
    if (!state.dailyKey || !Number.isFinite(state.moves) || state.moves < 1 || state.moves > 300) return null;
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length !== state.moves) return null;
    if (!isValidGridString(state.canonicalStartStr)) return null;
    return {
      name: safeName.slice(0, 16),
      dailyKey: state.dailyKey,
      moves: state.moves,
      moveHistory: state.moveHistory.slice(),
      startState: state.canonicalStartStr,
      updatedAt: Date.now(),
    };
  }

  function queuePendingSubmit(name, reason = "") {
    const pending = buildPendingSubmitPayload(name);
    if (!pending) return false;
    const existing = loadPendingSubmit(pending.dailyKey);
    pending.retries = (existing && Number.isFinite(existing.retries) ? existing.retries : 0) + 1;
    if (!savePendingSubmit(pending)) return false;
    state.pendingSubmitRetryAt = Date.now() + 2000;
    if (reason) {
      logEvent(
        "leaderboard_submit_queued",
        {
          reason,
          referrer: buildSubmitReferrer("queued", { reason, queued: 1 }),
        },
        `${pending.dailyKey}_${state.sessionId}_submit_queued_${reason}`
      );
    }
    return true;
  }

  function completeQueuedSubmitLocally(name, autoRetry = false) {
    const safeName = sanitizeName(name || "");
    if (safeName) {
      state.playerName = safeName.slice(0, 16);
      state.nameInput = state.playerName;
      saveName(state.playerName);
    }
    state.leaderboardError = "";
    if (nameErrorEl) nameErrorEl.textContent = "";
    if (!autoRetry) {
      setShareToast("Score saved. Syncing in background.", 2400);
    }
    setReliabilityState("degraded", navigator.onLine ? "Queued score awaiting sync" : "Offline; score queued");
  }

  function pendingSubmitMatchesCurrentRun(pending) {
    if (!pending || pending.dailyKey !== state.dailyKey) return false;
    if (!Array.isArray(state.moveHistory) || pending.moveHistory.length !== state.moveHistory.length) return false;
    for (let i = 0; i < state.moveHistory.length; i += 1) {
      if (pending.moveHistory[i] !== state.moveHistory[i]) return false;
    }
    return pending.moves === state.moves && pending.startState === state.canonicalStartStr;
  }

  function maybeRetryPendingSubmit(reason = "manual", delayMs = 600) {
    if (_scoreSubmitInFlight) return;
    if (state.submitted || state.practiceMode || state.customMode || state.redoMode) return;
    if (!navigator.onLine) return;
    if (Date.now() < state.pendingSubmitRetryAt) return;

    const pending = loadPendingSubmit(state.dailyKey);
    if (!pending) return;
    if (!pendingSubmitMatchesCurrentRun(pending)) return;
    if (!canSubmitCurrentRun()) return;

    const pendingName = sanitizeName(pending.name || state.playerName || state.nameInput || "");
    if (!pendingName) return;
    state.playerName = pendingName;
    state.nameInput = pendingName;
    saveName(pendingName);

    if (_pendingSubmitRetryTimer) {
      clearTimeout(_pendingSubmitRetryTimer);
      _pendingSubmitRetryTimer = null;
    }
    _pendingSubmitRetryTimer = setTimeout(async () => {
      _pendingSubmitRetryTimer = null;
      if (_scoreSubmitInFlight || state.submitted || !navigator.onLine) return;
      if (!loadPendingSubmit(state.dailyKey)) return;
      const ok = await submitScore({ autoRetry: true, reason });
      if (!ok) {
        // Exponential-ish backoff for repeated reconnect attempts.
        state.pendingSubmitRetryAt = Date.now() + 15000;
      }
    }, Math.max(0, delayMs));
  }

  async function syncPendingSubmitNow(source = "manual_sync") {
    if (_scoreSubmitInFlight) {
      setShareToast("Submit already in progress.", 1800);
      return false;
    }
    const pending = loadPendingSubmit(state.dailyKey);
    if (!pending || !pendingSubmitMatchesCurrentRun(pending)) {
      setShareToast("No queued score for this puzzle.", 2200);
      return false;
    }
    if (!navigator.onLine) {
      setShareToast("Offline. We'll sync when you reconnect.", 2400);
      state.pendingSubmitRetryAt = Date.now() + 1200;
      return false;
    }
    const pendingName = sanitizeName(pending.name || "");
    if (!pendingName) {
      setShareToast("Queued score is missing a valid name.", 2600);
      return false;
    }
    state.nameInput = pendingName;
    state.playerName = pendingName;
    saveName(pendingName);

    const ok = await submitScore({ autoRetry: true, reason: source });
    if (ok && !loadPendingSubmit(state.dailyKey)) {
      setShareToast("Score synced.", 2000);
    }
    return ok;
  }

  async function submitScore(options = {}) {
    const autoRetry = Boolean(options && options.autoRetry);
    const retryReason = options && options.reason ? String(options.reason) : "";
    const submitSource = retryReason || (autoRetry ? "auto" : "manual");
    if (_scoreSubmitInFlight) return false;
    const name = sanitizeName(state.nameInput);
    // Client-side input validation runs BEFORE the attempt is logged.
    // Empty / banned names are UX friction, not real submit attempts,
    // so they don't count against pipeline success-rate metrics.
    if (!name) {
      if (nameErrorEl) nameErrorEl.textContent = "Please enter a name.";
      return false;
    }
    // Check the raw input too: sanitizeName strips symbols, so "f*ck" or
    // "r@pe" would otherwise be scanned (and posted) as "fck" / "rpe".
    if (!isNameAllowed(name) || !isNameAllowed(String(state.nameInput || ""))) {
      if (nameErrorEl) {
        nameErrorEl.textContent = "Name contains a banned phrase. Please choose another.";
      }
      return false;
    }
    logEvent("leaderboard_submit_attempt", {
      source: submitSource,
      auto_retry: autoRetry ? 1 : 0,
      ...getLandingTelemetryData(),
    });
    if (state.practiceMode) {
      logSubmitFailure("practice_mode");
      return false;
    }
    if (!state.leaderboardEligible) {
      logSubmitFailure("ineligible", { hint_used: state.hintUsed ? 1 : 0 });
      return false;
    }
    if (!Number.isFinite(state.moves) || state.moves < 1 || state.moves > 300) {
      state.leaderboardError = "Score rejected (invalid moves)";
      logSubmitFailure("moves_invalid", { moves: state.moves });
      return false;
    }
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length !== state.moves) {
      state.leaderboardError = "Score rejected (history mismatch)";
      logSubmitFailure("history_length_mismatch", {
        moves: state.moves,
        history_length: Array.isArray(state.moveHistory) ? state.moveHistory.length : -1,
      });
      return false;
    }
    const replay = replayAndVerify(state.canonicalStartStr, state.moveHistory, state.targetWord);
    if (!replay.valid) {
      state.leaderboardError = "Score rejected (replay failed)";
      logSubmitFailure("replay_invalid", { replayed_moves: replay.replayedMoves });
      return false;
    }
    if (Number.isFinite(state.lowerBound) && state.moves < state.lowerBound) {
      state.leaderboardError = "Score rejected (below bound)";
      logSubmitFailure("below_lower_bound", { moves: state.moves, lower_bound: state.lowerBound });
      return false;
    }
    if (Number.isFinite(state.minMoves) && state.minExact && state.moves < state.minMoves) {
      state.leaderboardError = "Score rejected (below min)";
      logSubmitFailure("below_min_exact", { moves: state.moves, min_moves: state.minMoves });
      return false;
    }

    const deviceId = getDeviceId();
    const submitPlayerId = getPlayerId();
    const opponentPid = state.challengeFrom && typeof state.challengeFrom.opponentPlayerId === "string"
      ? state.challengeFrom.opponentPlayerId
      : "";
    const payload = {
      name: name.slice(0, 16),
      moves: state.moves,
      daily_key: state.dailyKey,
      device_id: deviceId,
      move_history: state.moveHistory,
      start_state: state.canonicalStartStr,
    };
    if (submitPlayerId) payload.player_id = submitPlayerId;
    if (opponentPid && submitPlayerId && opponentPid !== submitPlayerId) {
      payload.opponent_player_id = opponentPid;
    }
    const queuedBeforeSubmit = loadPendingSubmit(state.dailyKey);
    const hadQueuedBeforeSubmit = Boolean(queuedBeforeSubmit);

    if (!navigator.onLine) {
      const queued = queuePendingSubmit(name, retryReason || "offline");
      if (queued) {
        completeQueuedSubmitLocally(name, autoRetry);
        setReliabilityState("degraded", "Offline; score queued");
        logSubmitFailure("offline_queued", { queued: 1 });
        return !autoRetry;
      }
      if (nameErrorEl) nameErrorEl.textContent = "You're offline.";
      state.leaderboardError = "Score submit failed";
      setReliabilityState("degraded", "Offline submit failed");
      logSubmitFailure("offline", { queued: 0 });
      return false;
    }

    _scoreSubmitInFlight = true;
    setNameSubmitPending(true);
    if (nameErrorEl) nameErrorEl.textContent = "";
    state.leaderboardError = "";

    let timedOut = false;
    const submitStartedAt = Date.now();
    try {
      const submitResult = await postSubmitScoreWithRetry(payload, 2);
      if (!submitResult.ok) {
        timedOut = submitResult.timedOut;
        const submitErr = submitResult.error || new Error("submit_failed");
        submitErr._timedOut = timedOut;
        throw submitErr;
      }

      const { res, responseBody, serverError, attempts } = submitResult;

      if (!res.ok && res.status !== 409) {
        const retryable = isRetryableSubmitStatus(res.status);
        if (retryable) {
          const queued = queuePendingSubmit(name, retryReason || `http_${res.status}`);
          if (queued) {
            state.pendingSubmitRetryAt = Date.now() + 15000;
            completeQueuedSubmitLocally(name, autoRetry);
            setReliabilityState("degraded", `Submit ${res.status}; queued for retry`);
            logSubmitFailure("http_error_queued", {
              status_code: res.status,
              attempts,
              timed_out: timedOut ? 1 : 0,
              server_error: serverError,
              queued: 1,
              submit_ms: Date.now() - submitStartedAt,
            });
            return !autoRetry;
          }
        }
        clearPendingSubmit(state.dailyKey);
        const msg = mapSubmitError(res.status, serverError, timedOut);
        if (nameErrorEl) nameErrorEl.textContent = msg;
        state.leaderboardError = "Score submit failed";
        setReliabilityState("degraded", `Submit failed (${res.status})`);
        logSubmitFailure("http_error", {
          status_code: res.status,
          attempts,
          timed_out: timedOut ? 1 : 0,
          server_error: serverError,
          queued: retryable ? 1 : 0,
          submit_ms: Date.now() - submitStartedAt,
        });
        return false;
      }

      if (res.status === 409) {
        state.submitted = true;
        applySubmissionReceiptFromResponse(responseBody);
        if (!state.playerName) {
          state.playerName = name.slice(0, 16);
          saveName(state.playerName);
        }
        if (!state.submissionReceiptAt) {
          state.submissionReceiptAt = new Date().toISOString();
        }
        saveProgress(state.dailyKey, {
          grid: state.grid.map((row) => row.join("")),
          moves: state.moves,
          lastMove: state.lastMove,
          solvedToday: state.solvedToday,
          minMoves: state.minMoves,
          minExact: state.minExact,
          submitted: true,
          redoMode: state.redoMode,
          word: state.targetWord,
          moveHistory: state.moveHistory,
          hintUsed: state.hintUsed,
        }, state.practiceMode || state.redoMode);
      } else {
        applySubmittedScore(name, responseBody);
      }
      clearPendingSubmit(state.dailyKey);
      state.pendingSubmitRetryAt = 0;
      setReliabilityState("healthy");
      const submitOutcome = res.status === 409 ? "duplicate" : "ok";
      const submitMs = Date.now() - submitStartedAt;
      const submitReferrer = buildSubmitReferrer(submitOutcome, {
        status: res.status,
        ms: submitMs,
        attempts,
        timedOut: 0,
        queued: 0,
      });
      logEvent("leaderboard_submit_result", { referrer: submitReferrer });
      logEvent("leaderboard_submit", {
        moves: state.moves,
        source: submitSource,
        ...getLandingTelemetryData(),
      }, `${state.dailyKey}_submit`);
      if (hadQueuedBeforeSubmit) {
        logEvent("leaderboard_submit_recovered", {
          source: submitSource,
          retries: queuedBeforeSubmit && Number.isFinite(Number(queuedBeforeSubmit.retries))
            ? Number(queuedBeforeSubmit.retries)
            : null,
          ...getLandingTelemetryData(),
        });
      }
      unlockAchievement("leaderboard");
      haptic("medium");
      if (res.status === 409) {
        setShareToast("Already submitted for today", 2200);
      } else {
        setShareToast("On the board. Tap Share.", 2200);
      }

      fetchLeaderboard(state.dailyKey, 50).then((entries) => {
        if (entries) {
          const normalized = entries.map((row) => ({
            name: row.name,
            moves: row.moves,
            time: row.created_at ? Date.parse(row.created_at) : 0,
          }));
          const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
          state.leaderboard = filtered;
          saveLeaderboard(state.dailyKey, filtered);
          fetchDailyMetrics(state.dailyKey);
        }
      });
      uploadOgImage(name, state.dailyKey);
      // Challenge solves: now that the score (with opponent_player_id) is in,
      // the server has written the rivalry row — safe to open the comparison.
      goToChallengeCompare();
      return true;
    } catch (err) {
      const timeoutFlag = timedOut || Boolean(err && err._timedOut);
      const queued = queuePendingSubmit(name, retryReason || (timeoutFlag ? "timeout" : "network"));
      if (queued) {
        state.pendingSubmitRetryAt = Date.now() + 15000;
        completeQueuedSubmitLocally(name, autoRetry);
        setReliabilityState("degraded", timeoutFlag ? "Submit timeout; queued" : "Submit network issue; queued");
        logSubmitFailure("network_error_queued", {
          timed_out: timeoutFlag ? 1 : 0,
          error: err && err.message ? String(err.message).slice(0, 120) : "",
          queued: 1,
          submit_ms: Date.now() - submitStartedAt,
        });
        return !autoRetry;
      }
      const msg = mapSubmitError(0, err && err.message ? String(err.message) : "", timeoutFlag);
      if (nameErrorEl) nameErrorEl.textContent = msg;
      state.leaderboardError = "Score submit failed";
      setReliabilityState("degraded", timeoutFlag ? "Submit timeout" : "Submit network failure");
      logSubmitFailure("network_error", {
        timed_out: timeoutFlag ? 1 : 0,
        error: err && err.message ? String(err.message).slice(0, 120) : "",
        queued: 0,
        submit_ms: Date.now() - submitStartedAt,
      });
      return false;
    } finally {
      _scoreSubmitInFlight = false;
      setNameSubmitPending(false);
    }
  }

  function uploadOgImage(name, dailyKey) {
    try {
      const canvasEl = buildShareImageCanvas();
      canvasEl.toBlob((blob) => {
        if (!blob) return;
        const path = `${encodeURIComponent(dailyKey)}/${encodeURIComponent(name)}.png`;
        const imgHeaders = { "Content-Type": "image/png", "x-upsert": "true", apikey: SUPABASE_ANON_KEY };
        if (looksLikeJwtToken(SUPABASE_ANON_KEY)) imgHeaders.Authorization = `Bearer ${SUPABASE_ANON_KEY}`;
        fetch(`${SUPABASE_URL}/storage/v1/object/og-images/${path}`, {
          method: "POST",
          headers: imgHeaders,
          body: blob,
        }).catch(() => {});
      }, "image/png");
    } catch {}
  }

  function openNameOverlay() {
    // Opening entry manually owns the interaction, so the delayed post-solve
    // prompt must not reopen after the player submits or dismisses it.
    clearPostSolveLeaderboardPrompt();
    if (!nameOverlay) return;
    if (state.submitted) return;
    if (!state.leaderboardEligible) return;
    if (!nameOverlay.classList.contains("show")) captureOverlayFocus();
    nameOverlay.classList.add("show");
    nameOverlay.setAttribute("aria-hidden", "false");
    if (nameErrorEl) {
      nameErrorEl.textContent = "";
    }
    const scoreSummary = document.getElementById("name-score-summary");
    if (scoreSummary) scoreSummary.textContent = `${state.targetWord} · ${state.moves} moves`;
    if (nameInputEl) {
      nameInputEl.value = state.playerName || state.nameInput;
      nameInputEl.focus();
      nameInputEl.select();
    }
  }

  function closeNameOverlay() {
    if (!nameOverlay) return;
    const hadFocus = nameOverlay.classList.contains("show");
    fadeOutOverlay(nameOverlay);
    if (hadFocus) restoreOverlayFocus();
    activePointerId = null;
    // Result-screen accessible buttons hide while an overlay is open and are
    // only re-shown on redraw, so repaint now that name entry has closed.
    scheduleRenderLoop();
    // Only set by the post-solve path; other close sites leave it false.
    if (_promptReminderOnNameOverlayClose) {
      _promptReminderOnNameOverlayClose = false;
      // The growth coordinator waits until name entry has closed.
    }
  }

  const NAME_SUBSTITUTIONS = [
    { match: "horny", replacement: "hungry\u2665" },
    { match: "jewlover", replacement: "jeeplover" },
  ];

  function sanitizeName(value) {
    if (typeof value !== "string") return "";
    // Keep this conservative: the name is used in URLs, storage keys, and UI.
    let out = value.trim().replace(/\s+/g, " ");
    const normalized = normalizeName(out);
    for (const { match, replacement } of NAME_SUBSTITUTIONS) {
      if (normalized.includes(match)) {
        out = replacement;
        break;
      }
    }
    out = out.replace(/[^A-Za-z0-9 _\-\u2665]/g, "");
    out = out.trim().replace(/\s+/g, " ");
    out = out.slice(0, 16);
    if (!out) return "";
    if (!/[A-Za-z0-9\u2665]/.test(out)) return "";
    return out;
  }

  function sanitizePlayerId(value) {
    if (typeof value !== "string") return "";
    const out = value.trim();
    if (!/^[A-Za-z0-9_-]{6,64}$/.test(out)) return "";
    return out;
  }

  function sanitizeRoomCode(value) {
    if (typeof value !== "string") return "";
    const out = value.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,5}$/.test(out)) return "";
    return out;
  }

  function sanitizeCustomPlayersMap(rawPlayers) {
    const out = {};
    if (!rawPlayers || typeof rawPlayers !== "object") return out;
    let count = 0;
    for (const [key, value] of Object.entries(rawPlayers)) {
      if (!value || typeof value !== "object") continue;
      const id = sanitizePlayerId(value.id || key);
      if (!id || out[id]) continue;
      const name = sanitizeName(value.name || "") || "Player";
      const moves = Number.isFinite(value.moves) ? Math.max(0, Math.min(999, Math.floor(value.moves))) : 0;
      const solved = !!value.solved;
      const grid = typeof value.grid === "string" && /^[A-Z]{18}$/.test(value.grid) ? value.grid : "";
      const finishOrder = Number.isFinite(value.finishOrder) ? Math.max(0, Math.min(999, Math.floor(value.finishOrder))) : 0;
      const wins = Number.isFinite(value.wins) ? Math.max(0, Math.min(9999, Math.floor(value.wins))) : 0;
      const losses = Number.isFinite(value.losses) ? Math.max(0, Math.min(9999, Math.floor(value.losses))) : 0;
      out[id] = {
        id,
        name,
        moves,
        solved,
        grid,
        finishOrder,
        wins,
        losses,
        isHost: !!value.isHost,
        isObserver: !!value.isObserver,
      };
      count += 1;
      if (count >= 40) break;
    }
    return out;
  }

  function sanitizePendingJoins(rawPending) {
    if (!Array.isArray(rawPending)) return [];
    const out = [];
    const seen = new Set();
    for (const row of rawPending) {
      if (!row || typeof row !== "object") continue;
      const id = sanitizePlayerId(row.id || "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        name: sanitizeName(row.name || "") || "Player",
        approved: !!row.approved,
      });
      if (out.length >= 40) break;
    }
    return out;
  }

  function normalizeName(value) {
    // Map look-alike symbols to letters before stripping, so "r@pe" and
    // "$hit" are scanned as words instead of collapsing to "rpe" / "hit".
    return value.toLowerCase()
      .replace(/@/g, "a").replace(/\$/g, "s").replace(/[!|]/g, "i").replace(/\+/g, "t")
      .replace(/[^a-z0-9]/g, "")
      .replace(/0/g, "o").replace(/1/g, "i").replace(/3/g, "e")
      .replace(/4/g, "a").replace(/5/g, "s").replace(/7/g, "t")
      .replace(/8/g, "b").replace(/9/g, "g");
  }

  // Spellings that dodge a single normalization: "*" standing in for a vowel
  // ("f*ck"), "4" or "v" standing in for "u" ("f4ck", "fvck").
  function nameScanForms(value) {
    const sources = value.includes("*")
      ? ["a", "e", "i", "o", "u"].map((vowel) => value.split("*").join(vowel))
      : [value];
    const forms = new Set();
    for (const source of sources) {
      const normalized = normalizeName(source);
      forms.add(normalized);
      forms.add(normalizeName(source.replace(/4/g, "u")));
      forms.add(normalized.replace(/v/g, "u"));
    }
    return [...forms];
  }

  function isNameAllowed(value) {
    const normalized = normalizeName(value);
    if (!normalized) return false;
    for (const form of nameScanForms(value)) {
      const substringScan = NAME_SUBSTRING_ALLOWLIST.reduce(
        (text, word) => text.split(word).join(" "),
        form
      );
      if (BANNED_SUBSTRINGS.some((token) => substringScan.includes(token))) return false;
      if (BANNED_EXACT.some((token) => form === token)) return false;
    }
    return true;
  }

  function openArchiveOverlay() {
    if (!archiveOverlay) return;
    if (!archiveOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    archiveOverlay.classList.add("show");
    archiveOverlay.setAttribute("aria-hidden", "false");
    if (archiveDateEl) {
      const { year, month, day } = getEasternDateParts();
      const today = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const startKey = getArchiveStartKey();
      archiveDateEl.min = startKey;
      archiveDateEl.max = today;
      if (!archiveDateEl.value) {
        archiveDateEl.value = today;
      }
      renderArchiveCalendar(today, startKey);
      if (!PIVOT_NATIVE) {
        setTimeout(() => archiveCalendarEl?.querySelector(".archive-calendar-day.today")?.focus(), 0);
      }
    }
    if (archiveErrorEl) {
      archiveErrorEl.textContent = "";
    }
  }

  function renderArchiveCalendar(todayKey = getEasternKey(), startKey = getArchiveStartKey()) {
    if (!archiveCalendarEl) return;
    archiveCalendarEl.replaceChildren();
    for (const label of ["S", "M", "T", "W", "T", "F", "S"]) {
      const weekday = document.createElement("div");
      weekday.className = "archive-calendar-weekday";
      weekday.textContent = label;
      weekday.setAttribute("aria-hidden", "true");
      archiveCalendarEl.appendChild(weekday);
    }

    const solvedByKey = new Map(computePlayerStats().history.map((row) => [row.dailyKey, row.moves]));
    const weekday = getWeekdayIndexForKey(todayKey);
    const firstKey = addDaysToKey(todayKey, -(weekday + 28));
    for (let offset = 0; offset < 35; offset += 1) {
      const key = addDaysToKey(firstKey, offset);
      const button = document.createElement("button");
      button.type = "button";
      button.className = "archive-calendar-day";
      if (key < startKey || key > todayKey) {
        button.disabled = true;
        button.setAttribute("aria-hidden", "true");
        archiveCalendarEl.appendChild(button);
        continue;
      }
      const dayNumber = Number(key.slice(8, 10));
      const moves = solvedByKey.get(key);
      button.textContent = String(dayNumber);
      button.dataset.key = key;
      button.title = moves ? `${key} · solved in ${moves} moves` : key;
      button.setAttribute("aria-label", moves ? `${key}, solved in ${moves} moves` : `Play puzzle from ${key}`);
      if (moves) button.classList.add("completed");
      if (key === todayKey) button.classList.add("today");
      button.addEventListener("click", () => {
        if (archiveDateEl) archiveDateEl.value = key;
        haptic("light");
        startArchive(key);
      });
      archiveCalendarEl.appendChild(button);
    }
  }

  function openFeedbackOverlay() {
    if (!feedbackOverlay) return;
    if (!feedbackOverlay.classList.contains("show")) captureOverlayFocus();
    feedbackOverlay.classList.add("show");
    feedbackOverlay.setAttribute("aria-hidden", "false");
    if (feedbackStatusEl) {
      feedbackStatusEl.textContent = "";
    }
    if (feedbackInputEl) {
      setTimeout(() => feedbackInputEl.focus(), 300);
    }
  }

  function closeFeedbackOverlay() {
    // Clear the source tag here, not only on submit. Opening the form from the
    // post-race prompt and closing it without sending would otherwise leave the
    // flag set, and the next unrelated feedback — days later, from the More
    // menu — would be filed as Live Race feedback.
    _feedbackSource = "";
    if (!feedbackOverlay) return;
    const hadFocus = feedbackOverlay.classList.contains("show");
    fadeOutOverlay(feedbackOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  const a11yOverlay = document.getElementById("a11y-overlay");

  function openAccessibilityOverlay() {
    if (!a11yOverlay) return;
    if (!a11yOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    a11yOverlay.classList.add("show");
    a11yOverlay.setAttribute("aria-hidden", "false");
    const dyslexicToggle = document.getElementById("a11y-dyslexic-toggle");
    if (dyslexicToggle) {
      dyslexicToggle.classList.toggle("on", _dyslexicFont);
      dyslexicToggle.setAttribute("aria-pressed", _dyslexicFont ? "true" : "false");
    }
  }

  function closeAccessibilityOverlay() {
    if (!a11yOverlay) return;
    const hadFocus = a11yOverlay.classList.contains("show");
    fadeOutOverlay(a11yOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  const a11yDyslexicToggle = document.getElementById("a11y-dyslexic-toggle");
  if (a11yDyslexicToggle) {
    a11yDyslexicToggle.addEventListener("click", () => {
      setDyslexicFont(!_dyslexicFont);
      a11yDyslexicToggle.classList.toggle("on", _dyslexicFont);
      a11yDyslexicToggle.setAttribute("aria-pressed", _dyslexicFont ? "true" : "false");
      render();
    });
  }
  if (a11yOverlay) {
    a11yOverlay.addEventListener("click", (e) => {
      if (e.target === a11yOverlay) closeAccessibilityOverlay();
    });
  }

  function computePlayerStats() {
    const completedByKey = new Map(loadRecoveredHistory().map((row) => [row.dailyKey, row]));
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(PROGRESS_PREFIX)) continue;
        const dailyKey = key.slice(PROGRESS_PREFIX.length);
        const raw = safeGetItem(key);
        if (!raw) continue;
        try {
          const data = JSON.parse(raw);
          // Legacy payloads written before redo had its own namespace can carry
          // redoMode:true in the daily key; a replay is not the day's result.
          if (data && data.solvedToday && !data.redoMode && Number.isFinite(data.moves) && data.moves > 0) {
            completedByKey.set(dailyKey, { dailyKey, moves: data.moves });
          }
        } catch (e) { /* skip */ }
      }
    } catch (e) { /* localStorage not available */ }
    const completed = [...completedByKey.values()].sort((a, b) => a.dailyKey.localeCompare(b.dailyKey));
    const gamesPlayed = completed.length;
    const streak = loadStreakState();
    const currentStreak = streak.count || 0;
    let maxStreak = 0;
    if (gamesPlayed > 0) {
      let run = 1;
      for (let i = 1; i < completed.length; i++) {
        const prev = completed[i - 1].dailyKey;
        const curr = completed[i].dailyKey;
        if (addDaysToKey(prev, 1) === curr) {
          run++;
        } else {
          if (run > maxStreak) maxStreak = run;
          run = 1;
        }
      }
      if (run > maxStreak) maxStreak = run;
    }
    const totalMoves = completed.reduce((sum, c) => sum + c.moves, 0);
    const avgMoves = gamesPlayed > 0 ? Math.round(totalMoves / gamesPlayed) : 0;
    const bestScore = gamesPlayed > 0 ? Math.min(...completed.map((c) => c.moves)) : 0;
    const buckets = [
      { label: "1-10", min: 1, max: 10, count: 0 },
      { label: "11-15", min: 11, max: 15, count: 0 },
      { label: "16-20", min: 16, max: 20, count: 0 },
      { label: "21-25", min: 21, max: 25, count: 0 },
      { label: "26-30", min: 26, max: 30, count: 0 },
      { label: "31+", min: 31, max: Infinity, count: 0 },
    ];
    for (const c of completed) {
      for (const b of buckets) {
        if (c.moves >= b.min && c.moves <= b.max) { b.count++; break; }
      }
    }
    const history = completed.map((c) => ({ dailyKey: c.dailyKey, moves: c.moves }));
    const personalBest = gamesPlayed > 0 ? bestScore : null;
    return { gamesPlayed, currentStreak, maxStreak, avgMoves, bestScore, buckets, history, personalBest };
  }

  function computeWeeklySummary(history = [], todayKey = getEasternKey()) {
    const movesByDay = new Map();
    for (const row of history) {
      if (!row || !isDayKey(row.dailyKey) || !Number.isFinite(row.moves)) continue;
      movesByDay.set(row.dailyKey, Number(row.moves));
    }
    let solved = 0;
    let moveTotal = 0;
    let best = null;
    let prevSolved = 0;
    let prevMoveTotal = 0;
    for (let i = 0; i < 7; i += 1) {
      const key = addDaysToKey(todayKey, -i);
      if (movesByDay.has(key)) {
        const moves = movesByDay.get(key);
        solved += 1;
        moveTotal += moves;
        if (best === null || moves < best) best = moves;
      }
    }
    for (let i = 7; i < 14; i += 1) {
      const key = addDaysToKey(todayKey, -i);
      if (movesByDay.has(key)) {
        prevSolved += 1;
        prevMoveTotal += movesByDay.get(key);
      }
    }
    const avgMoves = solved > 0 ? Math.round((moveTotal / solved) * 10) / 10 : null;
    const prevAvgMoves = prevSolved > 0 ? Math.round((prevMoveTotal / prevSolved) * 10) / 10 : null;
    return {
      solved,
      best,
      avgMoves,
      prevSolved,
      prevAvgMoves,
      solvedDelta: solved - prevSolved,
    };
  }

  function openStatsOverlay() {
    if (!statsOverlay || !statsContentEl) return;
    if (!statsOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    const s = computePlayerStats();
    const weekly = computeWeeklySummary(s.history);
    const streakGrace = getStreakGraceStatus(loadStreakState(), getEasternKey());
    const maxCount = Math.max(1, ...s.buckets.map((b) => b.count));
    let html = `<section id="stats-overview" class="stats-panel" role="tabpanel" aria-labelledby="stats-overview-tab"><div class="stats-numbers">`;
    growth?.event("weekly_progress_view", {}, "stats");
    html += `<div><div class="stats-num">${s.gamesPlayed}</div><div class="stats-label">Played</div></div>`;
    html += `<div><div class="stats-num">${s.currentStreak}</div><div class="stats-label">Streak</div></div>`;
    html += `<div><div class="stats-num">${s.maxStreak}</div><div class="stats-label">Max Streak</div></div>`;
    html += `<div><div class="stats-num">${s.avgMoves}</div><div class="stats-label">Avg Moves</div></div>`;
    html += `</div>`;
    html += growth?.weeklyHtml() || "";
    html += `<div class="stats-section-title">This Week</div>`;
    const weekParts = [`Solved ${weekly.solved}/7`];
    if (weekly.avgMoves !== null) weekParts.push(`Avg ${weekly.avgMoves}`);
    if (weekly.best !== null) weekParts.push(`Best ${weekly.best}`);
    html += `<div class="stats-week-line">${weekParts.join(" · ")}</div>`;
    const trendBits = [];
    if (weekly.solvedDelta > 0) trendBits.push(`+${weekly.solvedDelta} day vs last week`);
    else if (weekly.solvedDelta < 0) trendBits.push(`${weekly.solvedDelta} days vs last week`);
    else trendBits.push("Same solve count as last week");
    if (weekly.prevAvgMoves !== null && weekly.avgMoves !== null) {
      const deltaAvg = Math.round((weekly.avgMoves - weekly.prevAvgMoves) * 10) / 10;
      if (deltaAvg < 0) trendBits.push(`${Math.abs(deltaAvg)} fewer moves avg`);
      else if (deltaAvg > 0) trendBits.push(`${deltaAvg} more moves avg`);
    }
    html += `<div class="stats-week-detail">${trendBits.join(" · ")}</div>`;
    html += `<div class="stats-week-detail">Streak forgiveness: ${streakGrace.available ? `${getStreakGraceAllowanceLabel()} ready` : `Ready in ${streakGrace.daysUntil} day${streakGrace.daysUntil === 1 ? "" : "s"}`}</div>`;
    html += `<div class="stats-dist-title">Move Distribution</div>`;
    for (const b of s.buckets) {
      const pct = Math.max(4, Math.round((b.count / maxCount) * 100));
      html += `<div class="stats-dist-row">`;
      html += `<span class="stats-dist-label">${b.label}</span>`;
      html += `<span class="stats-dist-bar" style="width:${pct}%"></span>`;
      html += `<span class="stats-dist-count">${b.count}</span>`;
      html += `</div>`;
    }

    // Score trend sparkline (>=3 games)
    if (s.history.length >= 3) {
      const recent = s.history.slice(-30);
      const moves = recent.map((h) => h.moves);
      const minM = Math.min(...moves);
      const maxM = Math.max(...moves);
      const range = Math.max(1, maxM - minM);
      const svgW = 260;
      const svgH = 50;
      const pad = 4;
      const points = moves.map((m, i) => {
        const x = pad + (i / (moves.length - 1)) * (svgW - pad * 2);
        const y = pad + (1 - (m - minM) / range) * (svgH - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(" ");
      html += `<div class="stats-section-title">Score Trend</div>`;
      html += `<div class="stats-sparkline"><svg viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" height="${svgH}">`;
      html += `<polyline points="${points}" fill="none" stroke="#5ba85b" stroke-width="1.5" stroke-linejoin="round"/>`;
      html += `</svg></div>`;
      html += `<div class="stats-sparkline-legend">`;
      html += `<span>Best: ${s.personalBest}</span><span>Latest: ${moves[moves.length - 1]}</span>`;
      html += `</div>`;
    }

    // 30-day heatmap
    {
      const todayKey = getEasternKey();
      const playedSet = new Set(s.history.map((h) => h.dailyKey));
      const movesMap = {};
      s.history.forEach((h) => { movesMap[h.dailyKey] = h.moves; });
      const allMoves = s.history.map((h) => h.moves);
      const minH = allMoves.length ? Math.min(...allMoves) : 10;
      const maxH = allMoves.length ? Math.max(...allMoves) : 30;
      const rangeH = Math.max(1, maxH - minH);
      html += `<div class="stats-section-title">Last 30 Days</div>`;
      html += `<div class="stats-heatmap">`;
      for (let i = 29; i >= 0; i--) {
        const dk = addDaysToKey(todayKey, -i);
        if (playedSet.has(dk)) {
          const m = movesMap[dk] || 0;
          const intensity = 1 - Math.min(1, (m - minH) / rangeH);
          const g = Math.round(100 + intensity * 80);
          const color = `rgb(${100 - intensity * 40}, ${g}, ${100 - intensity * 40})`;
          html += `<div class="heatmap-cell" style="background:${color}" title="Day: ${dk}, ${m} moves"></div>`;
        } else {
          html += `<div class="heatmap-cell heatmap-empty" title="${dk}: not played"></div>`;
        }
      }
      html += `</div>`;
    }

    // Rivalries placeholder — filled in by a background fetch so the overlay
    // doesn't block waiting for the network. The container renders empty if
    // the player has no player_id or no rivalries.
    html += `<div id="stats-rivalries"></div>`;
    html += `</section><section id="stats-achievements" class="stats-panel" role="tabpanel" aria-labelledby="stats-achievements-tab" hidden>`;
    html += buildAchievementGridHtml();
    html += `</section>`;

    statsContentEl.innerHTML = html;
    selectStatsTab("overview");
    statsOverlay.classList.add("show");
    statsOverlay.setAttribute("aria-hidden", "false");

    // Fire the rivalries fetch async; render into the placeholder when it
    // resolves. Cached in state so re-opening the overlay is instant.
    loadAndRenderRivalries();
  }

  function selectStatsTab(tab) {
    for (const name of ["overview", "achievements"]) {
      const selected = name === tab;
      const button = document.getElementById(`stats-${name}-tab`);
      const panel = document.getElementById(`stats-${name}`);
      if (button) button.setAttribute("aria-selected", String(selected));
      if (panel) panel.hidden = !selected;
    }
    if (statsContentEl) statsContentEl.scrollTop = 0;
  }

  for (const name of ["overview", "achievements"]) {
    const tab = document.getElementById(`stats-${name}-tab`);
    tab?.addEventListener("click", () => selectStatsTab(name));
    tab?.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const next = name === "overview" ? "achievements" : "overview";
      const target = event.key === "Home" ? "overview" : event.key === "End" ? "achievements" : next;
      selectStatsTab(target);
      document.getElementById(`stats-${target}-tab`)?.focus();
    });
  }

  // --- Rivalries section in stats overlay ---------------------------------
  const RIVALRIES_URL = `${SUPABASE_URL}/functions/v1/pivot-player-rivalries`;
  let _rivalriesCache = null;
  let _rivalriesCacheAt = 0;
  const RIVALRIES_CACHE_TTL_MS = 60 * 1000;

  function loadAndRenderRivalries() {
    const container = document.getElementById("stats-rivalries");
    if (!container) return;
    const playerId = getPlayerId();
    if (!playerId) return; // player hasn't been registered yet — nothing to show

    // Show cached results immediately if fresh.
    const now = Date.now();
    if (_rivalriesCache && (now - _rivalriesCacheAt) < RIVALRIES_CACHE_TTL_MS) {
      renderRivalriesInto(container, _rivalriesCache);
      return;
    }

    container.innerHTML = `<div class="stats-section-title">Rivals</div><div style="font-size:12px;color:var(--muted)">Loading…</div>`;
    fetch(RIVALRIES_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ player_id: playerId, device_id: getDeviceId() }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const list = data && Array.isArray(data.rivalries) ? data.rivalries : [];
        _rivalriesCache = list;
        _rivalriesCacheAt = Date.now();
        // Re-render in case the overlay is still open.
        const target = document.getElementById("stats-rivalries");
        if (target) renderRivalriesInto(target, list);
      })
      .catch(() => {
        const target = document.getElementById("stats-rivalries");
        if (target) target.innerHTML = "";
      });
  }

  function escapeHtmlForRivals(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function renderRivalriesInto(container, rivalries) {
    if (!rivalries || rivalries.length === 0) {
      container.innerHTML = "";
      return;
    }
    let html = `<div class="stats-section-title">Rivals</div>`;
    html += `<ul class="rivals-list">`;
    for (const r of rivalries) {
      const name = r.opponent_name ? escapeHtmlForRivals(r.opponent_name) : "Anonymous";
      const mine = Number(r.my_wins) || 0;
      const theirs = Number(r.opponent_wins) || 0;
      const ties = Number(r.ties) || 0;
      const total = Number(r.total) || 0;
      const streak = Number(r.current_streak) || 0;

      let leadClass = "tie";
      let leadText;
      if (mine > theirs) {
        leadClass = "win";
        leadText = `You lead ${mine}-${theirs}`;
      } else if (mine < theirs) {
        leadClass = "loss";
        leadText = `${name} leads ${theirs}-${mine}`;
      } else if (mine === 0 && theirs === 0 && ties === total) {
        leadText = total === 1 ? `Drew once` : `${ties} draws`;
      } else {
        leadText = `Tied ${mine}-${theirs}`;
      }
      const tiesSuffix = ties > 0 && !(mine === 0 && theirs === 0) ? ` · ${ties}D` : "";
      const streakSuffix = streak >= 2
        ? r.streak_is_mine
          ? ` · ${streak} in a row 🔥`
          : ` · ${name} on ${streak}`
        : "";
      html += `<li class="rivals-row ${leadClass}">`;
      html += `<span class="rivals-name">${name}</span>`;
      html += `<span class="rivals-detail">${leadText}${tiesSuffix}${streakSuffix}</span>`;
      html += `<button class="rivals-rematch" type="button" data-rival-name="${name}" aria-label="Challenge ${name} to a rematch">Rematch</button>`;
      html += `</li>`;
    }
    html += `</ul>`;
    container.innerHTML = html;
    container.querySelectorAll(".rivals-rematch").forEach((button) => {
      button.addEventListener("click", () => {
        logUiEvent("rival_rematch_click", { rival_name: button.dataset.rivalName || "" });
        shareChallenge();
      });
    });
  }

  function closeStatsOverlay() {
    if (!statsOverlay) return;
    const hadFocus = statsOverlay.classList.contains("show");
    fadeOutOverlay(statsOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  function openMoreMenu() {
    if (!moreOverlay || !moreContentEl) return;
    if (!moreOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    logUiEvent("more_open");
    // Lazy-register if we don't have a player identity yet — covers users
    // who solved before the feature shipped, and iOS users whose initial
    // solve-time fetch may have been deferred by Safari background throttling.
    // Gate on player_id (not recovery_code): register only returns the code at
    // first mint, so a device with an id but no stored code must NOT re-register
    // (it can't recover the code, and the re-render-on-success would loop).
    if (!getPlayerId()) {
      ensurePlayerRegistered().then((id) => {
        // If More is still open, re-render to show the freshly minted code.
        if (id && moreOverlay && moreOverlay.classList.contains("show")) {
          openMoreMenu();
        }
      });
    }
    const pendingSummary = getPendingSubmitSummary();
    const pendingCount = pendingSummary.count;
    const currentPending = pendingSummary.current;
    const latestPending = pendingSummary.latest;
    const reliabilityBadge = getReliabilityBadge(currentPending);
    let html = "";
    // --- Settings ---
    html += `<div class="more-section-title">Settings</div>`;
    // Sound toggle
    html += `<div class="more-row">`;
    html += `<div><div class="more-row-label">Sound</div></div>`;
    html += `<button class="more-toggle${!_soundMuted ? " on" : ""}" id="more-sound-toggle" type="button" aria-label="Toggle sound"></button>`;
    html += `</div>`;
    // High contrast toggle
    html += `<div class="more-row">`;
    html += `<div><div class="more-row-label">High contrast</div>`;
    html += `<div class="more-row-desc">Maximum contrast for visibility</div></div>`;
    html += `<button class="more-toggle${_currentTheme === "highContrast" ? " on" : ""}" id="more-hc-toggle" type="button" aria-label="Toggle high contrast"></button>`;
    html += `</div>`;
    // Share details toggle (default off for ego-safe one-tap sharing)
    html += `<div class="more-row">`;
    html += `<div><div class="more-row-label">Detailed share</div>`;
    html += `<div class="more-row-desc">Include move grid and stats in share text</div>`;
    html += `</div>`;
    html += `<button class="more-toggle${state.shareIncludeDetails ? " on" : ""}" id="more-share-details-toggle" type="button" aria-label="Toggle share details"></button>`;
    html += `</div>`;
    // Notifications
    if (_remindersSupported) {
      html += `<div class="more-row">`;
      html += `<div><div class="more-row-label">Daily reminder</div>`;
      html += `<div class="more-row-desc">Get a gentle reminder when it's time to play</div></div>`;
      html += `<button class="more-inline-btn" id="more-notify-toggle" type="button" aria-label="Daily reminder settings">Settings</button>`;
      html += `</div>`;
    }
    // --- Play ---
    html += `<div class="more-section-title">Play</div>`;
    const streakGrace = getStreakGraceStatus(loadStreakState(), getEasternKey());
    html += `<div class="more-row">`;
    html += `<div><div class="more-row-label">Streak forgiveness</div>`;
    html += `<div class="more-row-desc">${streakGrace.available ? `Ready (${getStreakGraceAllowanceLabel()})` : `Recharges in ${streakGrace.daysUntil} day${streakGrace.daysUntil === 1 ? "" : "s"}`}</div>`;
    html += `</div></div>`;
    if (pendingCount > 0) {
      const pendingCountText = pendingCount === 1 ? "1 queued score" : `${pendingCount} queued scores`;
      const pendingAgeText = formatRelativeAgeShort(
        (currentPending && currentPending.updatedAt) || (latestPending && latestPending.updatedAt) || 0
      );
      const pendingDetailParts = [pendingCountText];
      if (pendingAgeText) pendingDetailParts.push(`updated ${pendingAgeText}`);
      html += `<div class="more-row">`;
      html += `<div><div class="more-row-label">Queued submit</div>`;
      html += `<div class="more-row-desc">${pendingDetailParts.join(" · ")}</div>`;
      html += `<div class="more-row-desc">${navigator.onLine ? "Tap Sync now to retry immediately" : "Offline. Will retry once connected."}</div>`;
      html += `</div>`;
      html += `<button class="more-inline-btn" id="more-sync-submit" type="button">Sync now</button>`;
      html += `</div>`;
    }
    if (reliabilityBadge && reliabilityBadge.kind !== "syncing" && reliabilityBadge.kind !== "offline_pending") {
      html += `<div class="more-row">`;
      html += `<div><div class="more-row-label">Reliability</div>`;
      html += `<div class="more-row-desc">${reliabilityBadge.text}</div>`;
      if (reliabilityBadge.subtext) {
        html += `<div class="more-row-desc">${reliabilityBadge.subtext}</div>`;
      }
      html += `</div></div>`;
    }
    // Social play is one progressive-disclosure entry. Daily play itself keeps
    // the same number of decisions as before.
    html += `<button class="more-link" id="more-play-together" type="button">Play together</button>`;
    // Custom game
    html += `<button class="more-link" id="more-custom-game" type="button">Practice</button>`;
    html += `<button class="more-link" id="more-hall" type="button">Hall of Fame</button>`;
    // --- Your account ---
    const currentCode = getRecoveryCode();
    html += `<div class="more-section-title">Your account</div>`;
    if (currentCode) {
      html += `<div class="more-row recovery-row" id="recovery-row">`;
      html += `<div style="flex:1;min-width:0">`;
      html += `<div class="more-row-label">Recovery code</div>`;
      html += `<div class="more-row-desc">Tap to copy. Use it on another device.</div>`;
      html += `<div id="recovery-code-display">${currentCode}</div>`;
      html += `</div>`;
      html += `<button class="more-inline-btn" id="recovery-copy-btn" type="button">Copy</button>`;
      html += `</div>`;
    } else if (getPlayerId()) {
      // Identity exists but the code isn't stored on this device (e.g. storage
      // cleared, or set up elsewhere). The code is shown only once at first mint
      // and can't be re-derived from a device_id, so guide the user to restore.
      html += `<div class="more-row" id="recovery-row-pending">`;
      html += `<div><div class="more-row-label">Recovery code</div>`;
      html += `<div class="more-row-desc">Not saved on this device. If you saved it elsewhere, use “Restore from another device” below.</div></div>`;
      html += `</div>`;
    } else {
      html += `<div class="more-row" id="recovery-row-pending">`;
      html += `<div><div class="more-row-label">Recovery code</div>`;
      html += `<div class="more-row-desc" id="recovery-pending-desc">Setting up…</div></div>`;
      html += `</div>`;
    }
    html += `<button class="more-link" id="more-recovery-restore" type="button">Restore from another device</button>`;
    // --- About ---
    const updatesDot = hasUnseenUpdates() ? `<span class="more-link-dot" aria-label="New"></span>` : "";
    html += `<button class="more-link" id="more-updates" type="button">What's New${updatesDot}</button>`;
    html += `<button class="more-link" id="more-feedback" type="button">Send feedback</button>`;
    html += `<button class="more-link" id="more-accessibility" type="button">Accessibility</button>`;
    moreContentEl.innerHTML = html;
    moreOverlay.classList.add("show");
    moreOverlay.setAttribute("aria-hidden", "false");
    // Wire toggles
    const soundToggleEl = document.getElementById("more-sound-toggle");
    if (soundToggleEl) {
      soundToggleEl.addEventListener("click", () => {
        toggleSound();
        soundToggleEl.classList.toggle("on", !_soundMuted);
      });
    }
    const hcToggleEl = document.getElementById("more-hc-toggle");
    if (hcToggleEl) {
      hcToggleEl.addEventListener("click", () => {
        if (_currentTheme === "highContrast") {
          // Revert to system preference
          const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
          applyTheme(prefersDark ? "dark" : "light", true);
        } else {
          applyTheme("highContrast", true);
        }
        hcToggleEl.classList.toggle("on", _currentTheme === "highContrast");
      });
    }
    const shareDetailsToggleEl = document.getElementById("more-share-details-toggle");
    if (shareDetailsToggleEl) {
      shareDetailsToggleEl.addEventListener("click", () => {
        setShareDetailPreference(!state.shareIncludeDetails);
        shareDetailsToggleEl.classList.toggle("on", state.shareIncludeDetails);
      });
    }
    const notifyToggleEl = document.getElementById("more-notify-toggle");
    if (notifyToggleEl) {
      notifyToggleEl.addEventListener("click", () => {
        togglePushSubscription();
        setTimeout(() => { notifyToggleEl.classList.toggle("on", _pushSubscribed); }, 500);
      });
    }
    const syncSubmitEl = document.getElementById("more-sync-submit");
    if (syncSubmitEl) {
      syncSubmitEl.addEventListener("click", async () => {
        if (_scoreSubmitInFlight) return;
        const originalLabel = syncSubmitEl.textContent;
        syncSubmitEl.disabled = true;
        syncSubmitEl.textContent = "Syncing...";
        await syncPendingSubmitNow("more_menu");
        syncSubmitEl.textContent = originalLabel || "Sync now";
        syncSubmitEl.disabled = false;
      });
    }
    const customGameEl = document.getElementById("more-custom-game");
    if (customGameEl) {
      customGameEl.addEventListener("click", () => {
        closeMoreMenu();
        const allowedDifficulties = new Set(["easy", "medium", "hard", "expert"]);
        const preferredDifficulty = allowedDifficulties.has(state.customDifficulty)
          ? state.customDifficulty
          : "medium";
        // Open in multiplayer-friendly setup so room creation + saving is immediately visible.
        openCustomGameOverlay(preferredDifficulty);
      });
    }
    const playTogetherEl = document.getElementById("more-play-together");
    if (playTogetherEl) {
      playTogetherEl.addEventListener("click", () => {
        closeMoreMenu();
        openPlayTogetherHub();
      });
    }
    const hallEl = document.getElementById("more-hall");
    if (hallEl) {
      hallEl.addEventListener("click", () => {
        closeMoreMenu();
        openHallPage();
      });
    }
    const feedbackEl = document.getElementById("more-feedback");
    if (feedbackEl) {
      feedbackEl.addEventListener("click", () => {
        closeMoreMenu();
        openFeedbackOverlay();
      });
    }
    const a11yEl = document.getElementById("more-accessibility");
    if (a11yEl) {
      a11yEl.addEventListener("click", () => {
        closeMoreMenu();
        openAccessibilityOverlay();
      });
    }
    const updatesEl = document.getElementById("more-updates");
    if (updatesEl) {
      updatesEl.addEventListener("click", () => {
        closeMoreMenu();
        openUpdatesOverlay();
      });
    }
    const recoveryRowEl = document.getElementById("recovery-row");
    const recoveryCopyBtnEl = document.getElementById("recovery-copy-btn");
    async function copyRecoveryCodeWithFeedback(triggerEl) {
      const code = getRecoveryCode();
      if (!code) return;
      let copied = false;
      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch {
        // iOS WKWebView and some Safari versions block async clipboard from
        // non-button events. Fall back to selecting a text range.
        try {
          const range = document.createRange();
          const sel = window.getSelection();
          const codeEl = document.getElementById("recovery-code-display");
          if (codeEl && sel) {
            range.selectNodeContents(codeEl);
            sel.removeAllRanges();
            sel.addRange(range);
            copied = document.execCommand && document.execCommand("copy");
            sel.removeAllRanges();
          }
        } catch {}
      }
      try { logEvent("recovery_code_copied", { ok: copied ? 1 : 0 }); } catch {}
      if (triggerEl) {
        const original = triggerEl.textContent;
        triggerEl.textContent = copied ? "Copied ✓" : "Long-press to copy";
        setTimeout(() => { if (triggerEl) triggerEl.textContent = original; }, 1600);
      }
    }
    if (recoveryRowEl) {
      recoveryRowEl.addEventListener("click", (e) => {
        // Don't double-trigger when the dedicated Copy button is tapped.
        if (recoveryCopyBtnEl && recoveryCopyBtnEl.contains(e.target)) return;
        copyRecoveryCodeWithFeedback(recoveryCopyBtnEl);
      });
    }
    if (recoveryCopyBtnEl) {
      recoveryCopyBtnEl.addEventListener("click", (e) => {
        e.stopPropagation();
        copyRecoveryCodeWithFeedback(recoveryCopyBtnEl);
      });
    }
    const recoveryRestoreEl = document.getElementById("more-recovery-restore");
    if (recoveryRestoreEl) {
      recoveryRestoreEl.addEventListener("click", () => {
        closeMoreMenu();
        openRecoveryOverlay();
      });
    }
  }

  function closeMoreMenu() {
    if (!moreOverlay) return;
    const hadFocus = moreOverlay.classList.contains("show");
    fadeOutOverlay(moreOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  // ---------------------------------------------------------------------------
  // What's New overlay
  // ---------------------------------------------------------------------------

  function getUpdatesLastSeenTs() {
    const raw = safeGetItem(UPDATES_LAST_SEEN_KEY);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function setUpdatesLastSeenTs(ts) {
    safeSetItem(UPDATES_LAST_SEEN_KEY, String(ts));
  }

  function hasUnseenUpdates() {
    return getNewestUpdatesTs() > getUpdatesLastSeenTs();
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatUpdateDate(ts) {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }

  function openUpdatesOverlay() {
    if (!updatesOverlay || !updatesListEl) return;
    document.getElementById("updates-banner")?.remove();
    if (!updatesOverlay.classList.contains("show")) captureOverlayFocus();
    _overlayOpenedAt = Date.now();
    const lastSeen = getUpdatesLastSeenTs();
    logUiEvent("updates_open", { unseen: hasUnseenUpdates() ? 1 : 0 });
    const sorted = [...UPDATES_ENTRIES].sort((a, b) => (b.ts || 0) - (a.ts || 0));
    let html = "";
    for (const entry of sorted) {
      const isNew = (entry.ts || 0) > lastSeen;
      const dateStr = formatUpdateDate(entry.ts);
      const emoji = entry.emoji ? `<span class="update-emoji">${escapeHtml(entry.emoji)}</span>` : "";
      const newBadge = isNew ? `<span class="update-new" aria-label="New">new</span>` : "";
      html += `<div class="update-entry${isNew ? " is-new" : ""}">`;
      html += `<div class="update-meta">${escapeHtml(dateStr)}${newBadge}</div>`;
      html += `<div class="update-title">${emoji}<span>${escapeHtml(entry.title || "")}</span></div>`;
      if (entry.body) {
        html += `<div class="update-body">${escapeHtml(entry.body)}</div>`;
      }
      html += `</div>`;
    }
    updatesListEl.innerHTML = html;
    updatesOverlay.classList.add("show");
    updatesOverlay.setAttribute("aria-hidden", "false");
    // Mark as seen — newest entry's timestamp.
    const newestTs = sorted.length ? Number(sorted[0].ts) || 0 : 0;
    if (newestTs > lastSeen) setUpdatesLastSeenTs(newestTs);
  }

  function closeUpdatesOverlay() {
    if (!updatesOverlay) return;
    const hadFocus = updatesOverlay.classList.contains("show");
    fadeOutOverlay(updatesOverlay);
    if (hadFocus) restoreOverlayFocus();
  }

  if (updatesOverlay) {
    // Tap-anywhere-to-close, matching leaderboard / accessibility overlays.
    // 400ms cooldown prevents the tap that *opens* the overlay from also
    // immediately closing it.
    updatesOverlay.addEventListener("click", (e) => {
      if (e.target === updatesOverlay && Date.now() - _overlayOpenedAt > 400) {
        closeUpdatesOverlay();
      }
    });
  }

  // --- Recovery restore overlay ---
  function openRecoveryOverlay() {
    if (!recoveryOverlay) return;
    if (!recoveryOverlay.classList.contains("show")) captureOverlayFocus();
    if (recoveryInputEl) recoveryInputEl.value = "";
    if (recoveryStatusEl) {
      recoveryStatusEl.textContent = "";
      recoveryStatusEl.className = "name-note";
    }
    _overlayOpenedAt = Date.now();
    logUiEvent("recovery_restore_open");
    recoveryOverlay.classList.add("show");
    recoveryOverlay.setAttribute("aria-hidden", "false");
    if (recoveryInputEl) {
      setTimeout(() => { try { recoveryInputEl.focus(); } catch {} }, 30);
    }
  }
  function closeRecoveryOverlay() {
    if (!recoveryOverlay) return;
    const hadFocus = recoveryOverlay.classList.contains("show");
    fadeOutOverlay(recoveryOverlay);
    if (hadFocus) restoreOverlayFocus();
  }
  async function submitRecoveryCode() {
    if (!recoveryInputEl) return;
    const raw = (recoveryInputEl.value || "").trim();
    if (!raw) {
      if (recoveryStatusEl) {
        recoveryStatusEl.textContent = "Paste your code first.";
        recoveryStatusEl.className = "name-note error";
      }
      return;
    }
    if (recoverySubmitEl) {
      recoverySubmitEl.disabled = true;
      recoverySubmitEl.textContent = "Restoring...";
    }
    if (recoveryStatusEl) {
      recoveryStatusEl.textContent = "";
      recoveryStatusEl.className = "name-note";
    }
    const result = await restorePlayerFromRecoveryCode(raw);
    if (recoverySubmitEl) {
      recoverySubmitEl.disabled = false;
      recoverySubmitEl.textContent = "Restore";
    }
    if (result.ok) {
      if (recoveryStatusEl) {
        const who = result.display_name ? ` Welcome back, ${esc(result.display_name)}.` : "";
        const historyNote = result.recovered_count > 0
          ? `<br>${result.recovered_count} past score${result.recovered_count === 1 ? "" : "s"} restored to Stats and Archive.`
          : "";
        if (result.today_completed && result.today_score) {
          const movesTxt = `${result.today_score.moves} move${result.today_score.moves === 1 ? "" : "s"}`;
          recoveryStatusEl.innerHTML = `Restored.${who}${historyNote}<br>You've already played today (${movesTxt}). New puzzle tomorrow.`;
        } else {
          recoveryStatusEl.innerHTML = `Restored.${who}${historyNote}`;
        }
        recoveryStatusEl.className = "name-note success";
      }
      // Reload after a moment so the rest of the UI picks up the player_id
      // (today's-completion state, name prefill, etc.).
      setTimeout(() => {
        try { closeRecoveryOverlay(); } catch {}
        try { window.location.reload(); } catch {}
      }, 1500);
    } else if (result.reason === "not_found") {
      if (recoveryStatusEl) {
        recoveryStatusEl.textContent = "We don't recognize that code.";
        recoveryStatusEl.className = "name-note error";
      }
    } else if (result.reason === "empty") {
      if (recoveryStatusEl) {
        recoveryStatusEl.textContent = "Paste your code first.";
        recoveryStatusEl.className = "name-note error";
      }
    } else {
      if (recoveryStatusEl) {
        recoveryStatusEl.textContent = "Something went wrong. Try again.";
        recoveryStatusEl.className = "name-note error";
      }
    }
  }
  if (recoverySubmitEl) recoverySubmitEl.addEventListener("click", submitRecoveryCode);
  if (recoveryCancelEl) recoveryCancelEl.addEventListener("click", closeRecoveryOverlay);
  if (recoveryInputEl) {
    recoveryInputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submitRecoveryCode();
      }
    });
  }
  if (recoveryOverlay) {
    recoveryOverlay.addEventListener("click", (e) => {
      if (e.target === recoveryOverlay) closeRecoveryOverlay();
    });
  }

  // ---------------------------------------------------------------------------
  // Custom Game
  // ---------------------------------------------------------------------------
  const FRIEND_CIRCLES_URL = `${SUPABASE_URL}/functions/v1/pivot-friend-circles`;
  const COMMUNITY_PUZZLE_URL = `${SUPABASE_URL}/functions/v1/pivot-community-puzzle`;
  const CUSTOM_ROOM_CHARS = "ABCDEFGHJKMNPQRTWXY23456789";

  function setCustomOverlayTitle(title, badge = "") {
    const overlay = document.getElementById("custom-game-overlay");
    const titleEl = overlay?.querySelector(".name-title");
    if (titleEl) titleEl.textContent = title;
    const badgeEl = overlay?.querySelector(".custom-beta-badge");
    if (badgeEl) {
      badgeEl.textContent = badge;
      badgeEl.style.display = badge ? "inline-block" : "none";
    }
  }

  function showCustomOverlay() {
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return;
    overlay.classList.add("show");
    overlay.setAttribute("aria-hidden", "false");
  }

  function playTogetherCard(id, eyebrow, title, description, disabled = false) {
    return `<button id="${id}" class="social-choice" type="button"${disabled ? " disabled" : ""}>` +
      `<span class="social-choice-eyebrow">${esc(eyebrow)}</span>` +
      `<span class="social-choice-title">${esc(title)}</span>` +
      `<span class="social-choice-desc">${esc(description)}</span></button>`;
  }

  function openPlayTogetherHub() {
    logUiEvent("play_together_open");
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    setCustomOverlayTitle("Play together");
    const canChallenge = state.mode === "win" || state.solvedToday || state.completed || state.submitted;
    content.innerHTML = `<div class="social-choice-list">` +
      playTogetherCard("social-challenge", "ASYNC", "Challenge a friend", canChallenge
        ? "They play the same edition against your move-by-move ghost."
        : "Finish today's PIVOT first, then send your move-by-move ghost.", !canChallenge) +
      playTogetherCard("social-circles", "WEEKLY", "Friend circles", "A private table for friends with one invite code and weekly standings.") +
      playTogetherCard("social-host", "LIVE OR CUSTOM", "Host a game", "Start a Live Race or make a PIVOT with your own word.") +
      `</div><nav class="custom-actions-nav"><a href="#" id="social-dismiss" class="custom-action-link custom-action-muted">Dismiss</a></nav>`;
    document.getElementById("social-challenge")?.addEventListener("click", () => {
      closeCustomGameOverlay();
      shareChallenge();
    });
    document.getElementById("social-circles")?.addEventListener("click", () => openFriendCircles());
    document.getElementById("social-host")?.addEventListener("click", openHostGameHub);
    document.getElementById("social-dismiss")?.addEventListener("click", (event) => {
      event.preventDefault();
      closeCustomGameOverlay();
    });
    showCustomOverlay();
  }

  function openHostGameHub() {
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    setCustomOverlayTitle("Host a game");
    content.innerHTML = `<div class="social-choice-list">` +
      playTogetherCard("host-race", "REAL TIME", "Live Race", "Invite friends, choose a best-of series, and race on the same board.") +
      playTogetherCard("host-puzzle", "YOUR WORD", "Make a PIVOT", "Choose a four-letter word and create a verified puzzle link.") +
      `</div><nav class="custom-actions-nav"><a href="#" id="host-back" class="custom-action-link custom-action-muted">Back</a></nav>`;
    document.getElementById("host-race")?.addEventListener("click", () => openLiveRaceOverlay(state.customDifficulty));
    document.getElementById("host-puzzle")?.addEventListener("click", () => openMakePuzzle());
    document.getElementById("host-back")?.addEventListener("click", (event) => { event.preventDefault(); openPlayTogetherHub(); });
  }

  async function circleRequest(action, fields = {}) {
    const playerId = await ensurePlayerRegistered();
    if (!playerId) throw new Error("Could not set up your player identity");
    const response = await fetch(FRIEND_CIRCLES_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action, player_id: playerId, device_id: getDeviceId(), ...fields }),
    });
    let data = null;
    try { data = await response.json(); } catch {}
    if (!response.ok) throw new Error(data?.error || "Circle request failed");
    return data || {};
  }

  function circleInviteText(circle) {
    return `Join my PIVOT circle “${circle.name}”\nCode: ${circle.invite_code}\n${buildCircleInviteUrl(circle)}`;
  }

  function buildCircleInviteUrl(circle) {
    return `${CANONICAL_URL}/join/?circle=${encodeURIComponent(circle.invite_code)}`;
  }

  function hasPlayedTodayForCircle() {
    if (state.solvedToday || state.completed || state.alreadyCompleted) return true;
    const saved = loadProgress(getEasternKey());
    return !!(saved && saved.solvedToday);
  }

  function setCircleStatus(message, isError = false) {
    const status = document.getElementById("circle-status");
    if (!status) return;
    status.textContent = message || "";
    status.className = `social-status${isError ? " error" : message ? " success" : ""}`;
  }

  async function shareCircleInvite(circle) {
    const url = buildCircleInviteUrl(circle);
    const text = circleInviteText(circle);
    if (postNativeShare({
      title: `${circle.name} on PIVOT`,
      text,
      url,
      eventName: "circle_invite_native",
    })) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${circle.name} on PIVOT`,
          text: `Join my PIVOT circle “${circle.name}”\nCode: ${circle.invite_code}`,
          url,
        });
        setCircleStatus("Invite shared. You are still joined to this circle.");
        logEvent("circle_invite_native");
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    if (await copyTextToClipboard(text)) {
      setCircleStatus("Invite link copied. Paste it into Messages, email, or a group chat.");
      logEvent("circle_invite_copy");
      return;
    }
    openCopyOverlay(text, "Circle invite");
  }

  function renderFriendCircles(circles = [], error = "", notice = "") {
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    // Keep the leaderboard's private circle view in sync after create, join,
    // leave, or refresh without introducing a second cache or score source.
    state.friendCircles = Array.isArray(circles) ? circles : [];
    state.friendCirclesError = error || "";
    if (!error) state.friendCirclesFetchedAt = Date.now();
    selectedFriendCircle();
    const savedName = sanitizeName(state.playerName || loadName() || "");
    const playedToday = hasPlayedTodayForCircle();
    const circlePlayHtml = circles.length
      ? `<section class="circle-play-row"><strong>Play with your circles</strong>` +
        `<span>Friend Circles use today's shared daily puzzle. Submit your score once and it counts automatically in every circle you've joined.</span>` +
        `<button class="name-submit secondary circle-play-today" type="button">${playedToday ? "View today's result" : "Play today's PIVOT"}</button></section>`
      : "";
    const circleHtml = circles.length ? circles.map((circle) => {
      const standings = Array.isArray(circle.standings) ? circle.standings : [];
      const rows = standings.map((row, index) => {
        const score = row.days_played ? `${row.days_played} day${row.days_played === 1 ? "" : "s"} · ${row.total_moves} moves` : "No games this week";
        const viewerLabel = row.is_viewer ? " (you)" : "";
        return `<div class="circle-standing${row.is_viewer ? " is-viewer" : ""}"><span>${index + 1}. ${esc(row.display_name)}${viewerLabel}</span><span>${esc(score)}</span></div>`;
      }).join("");
      const memberLabel = `${standings.length} member${standings.length === 1 ? "" : "s"}`;
      return `<section class="circle-card" data-circle-id="${escapeHtmlAttr(circle.id)}">` +
        `<div class="circle-card-head"><div><strong>${esc(circle.name)}</strong><small class="circle-membership">✓ Joined${circle.owner ? " · You created this" : ""}</small><small>Invite code ${esc(circle.invite_code)}</small></div>` +
        `<div class="circle-card-controls"><button class="more-inline-btn circle-share" type="button" data-code="${esc(circle.invite_code)}">Invite friends</button>` +
        `<button class="more-inline-btn circle-leave" type="button" data-circle-id="${escapeHtmlAttr(circle.id)}" data-owner="${circle.owner ? "1" : "0"}">${circle.owner ? "Close" : "Leave"}</button></div></div>` +
        `<div class="circle-week"><span>This week</span><span>${esc(memberLabel)}</span></div>` +
        `<div class="circle-ranking-rule">Ranked by days played, then fewest total moves.</div>` +
        `${rows || `<div class="more-row-desc">Invite friends to start the table.</div>`}</section>`;
    }).join("") : `<div class="social-empty">No circles yet. Create one or enter a friend's code.</div>`;
    content.innerHTML = `<div id="circle-status" class="social-status${error ? " error" : notice ? " success" : ""}">${esc(error || notice)}</div>${circlePlayHtml}${circleHtml}` +
      `<div class="custom-section-label">Your name</div>` +
      `<input id="circle-display-name" class="custom-name-input" maxlength="16" placeholder="Your name" value="${escapeHtmlAttr(savedName)}" />` +
      `<div class="circle-actions"><div><div class="custom-section-label">Create</div>` +
      `<input id="circle-name" class="custom-name-input" maxlength="32" placeholder="Circle name" />` +
      `<button id="circle-create" class="name-submit" type="button">Create circle</button></div>` +
      `<div><div class="custom-section-label">Join</div>` +
      `<input id="circle-code" class="custom-name-input" maxlength="6" placeholder="Invite code" autocapitalize="characters" />` +
      `<button id="circle-join" class="name-submit secondary" type="button">Join circle</button></div></div>` +
      `<nav class="custom-actions-nav"><a href="#" id="circle-back" class="custom-action-link custom-action-muted">Back</a></nav>`;
    for (const button of content.querySelectorAll(".circle-share")) {
      button.addEventListener("click", async () => {
        const circle = circles.find((item) => item.invite_code === button.dataset.code);
        if (!circle) return;
        await shareCircleInvite(circle);
      });
    }
    for (const button of content.querySelectorAll(".circle-play-today")) {
      button.addEventListener("click", () => {
        logUiEvent("circle_play_today", { already_played: playedToday ? 1 : 0 });
        if (_isCustomPage || state.customMode || state.practiceMode) {
          window.location.href = CANONICAL_URL;
          return;
        }
        closeCustomGameOverlay();
        if (!playedToday && state.mode === "menu") resetGame();
      });
    }
    for (const button of content.querySelectorAll(".circle-leave")) {
      button.addEventListener("click", async () => {
        const closesCircle = button.dataset.owner === "1";
        const prompt = closesCircle
          ? "Close this circle for everyone? Existing scores are not affected."
          : "Leave this circle? Existing scores are not affected.";
        if (!window.confirm(prompt)) return;
        try {
          await circleRequest("leave", { circle_id: button.dataset.circleId || "" });
          logEvent(closesCircle ? "circle_close_success" : "circle_leave_success");
          openFriendCircles();
        } catch (err) {
          logEvent(closesCircle ? "circle_close_failed" : "circle_leave_failed");
          renderFriendCircles(circles, err.message);
        }
      });
    }
    document.getElementById("circle-create")?.addEventListener("click", async () => {
      const name = document.getElementById("circle-name")?.value || "";
      const displayName = sanitizeName(document.getElementById("circle-display-name")?.value || "");
      if (!name.trim() || !displayName) {
        setCircleStatus("Enter your name and a circle name first.", true);
        return;
      }
      const createButton = document.getElementById("circle-create");
      if (createButton) {
        createButton.disabled = true;
        createButton.textContent = "Creating…";
      }
      logEvent("circle_create_attempt");
      try {
        const data = await circleRequest("create", { name, display_name: displayName });
        const created = data.circle;
        state.playerName = displayName;
        state.nameInput = displayName;
        saveName(displayName);
        logEvent("circle_create_success");
        const next = created ? [...circles.filter((circle) => circle.id !== created.id), created] : circles;
        renderFriendCircles(next, "", `Circle created. You joined as ${displayName}. Tap Invite friends to add someone.`);
      } catch (err) {
        logEvent("circle_create_failed");
        renderFriendCircles(circles, err.message);
      }
    });
    document.getElementById("circle-join")?.addEventListener("click", async () => {
      const inviteCode = document.getElementById("circle-code")?.value || "";
      const displayName = sanitizeName(document.getElementById("circle-display-name")?.value || savedName);
      if (!/^[A-Z2-9]{6}$/.test(inviteCode.trim().toUpperCase()) || !displayName) {
        setCircleStatus("Enter your name and a valid six-character invite code.", true);
        return;
      }
      const joinButton = document.getElementById("circle-join");
      if (joinButton) {
        joinButton.disabled = true;
        joinButton.textContent = "Joining…";
      }
      logEvent("circle_join_attempt");
      try {
        const data = await circleRequest("join", { invite_code: inviteCode, display_name: displayName });
        const joined = data.circle;
        state.playerName = displayName;
        state.nameInput = displayName;
        saveName(displayName);
        logEvent("circle_join_success");
        const next = joined ? [...circles.filter((circle) => circle.id !== joined.id), joined] : circles;
        renderFriendCircles(next, "", `Joined ${joined?.name || "the circle"} as ${displayName}.`);
      } catch (err) {
        logEvent("circle_join_failed");
        renderFriendCircles(circles, err.message);
      }
    });
    document.getElementById("circle-back")?.addEventListener("click", (event) => { event.preventDefault(); openPlayTogetherHub(); });
  }

  async function openFriendCircles(prefillCode = "") {
    logUiEvent("circle_open", { source: prefillCode ? "invite" : "menu" });
    if (prefillCode) { logUiEvent("circle_invite_open"); growth?.event("landing_view", { new_device: _isNewDevice ? 1 : 0, referral_kind: "circle" }, "circle"); growth?.event("referral_landing", { referral_kind: "circle" }, "circle"); }
    setCustomOverlayTitle("Friend circles");
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    content.innerHTML = `<div class="social-loading">Loading your circles…</div>`;
    showCustomOverlay();
    try {
      const data = await circleRequest("list");
      renderFriendCircles(
        Array.isArray(data.circles) ? data.circles : [],
        "",
        prefillCode ? "Invite received. Enter your name, then tap Join circle." : ""
      );
      if (prefillCode) {
        const input = document.getElementById("circle-code");
        if (input) input.value = String(prefillCode).toUpperCase();
        const nameInput = document.getElementById("circle-display-name");
        setTimeout(() => { try { (nameInput || input)?.focus(); } catch {} }, 30);
      }
    } catch (err) {
      renderFriendCircles([], err.message || "Could not load circles");
    }
  }

  const PLAYER_MADE_DIFFICULTIES = {
    easy: { minMoves: 2, maxMoves: 4, scrambleMoves: 5, attempts: 10 },
    medium: { minMoves: 5, maxMoves: 6, scrambleMoves: 8, attempts: 16 },
    hard: { minMoves: 7, maxMoves: 12, scrambleMoves: 24, attempts: 12 },
  };

  function normalizePlayerMadeDifficulty(value) {
    const difficulty = String(value || "medium").toLowerCase();
    return PLAYER_MADE_DIFFICULTIES[difficulty] ? difficulty : "medium";
  }

  function buildPlayerMadeSolvedGrid(word, rng) {
    // Excluding the target letters from filler cells keeps exactly one token for
    // each letter in the target. That makes the worker's minimum-move result
    // exact even for repeated-letter words such as MOON.
    const filler = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].filter((letter) => !word.includes(letter));
    const grid = Array.from({ length: GRID_ROWS }, () =>
      Array.from({ length: GRID_COLS }, () => filler[Math.floor(rng() * filler.length)])
    );
    for (let index = 0; index < CENTER_WORD_LENGTH; index += 1) {
      grid[CENTER_ROW][CENTER_START_COL + index] = word[index];
    }
    return grid;
  }

  function playerMadeCandidate(word, config, seed, attempt) {
    const rng = mulberry32(seed + attempt * 7919);
    let startState = gridToString(buildPlayerMadeSolvedGrid(word, rng));
    let previous = -1;
    let previousPrevious = -1;
    const scrambleMoves = config.scrambleMoves + (attempt % 3);
    for (let step = 0; step < scrambleMoves; step += 1) {
      let move = Math.floor(rng() * MOVE_MAPS.length);
      for (let retry = 0; retry < 12 && (move === previous || move === previousPrevious); retry += 1) {
        move = Math.floor(rng() * MOVE_MAPS.length);
      }
      previousPrevious = previous;
      previous = move;
      // Scramble counter-clockwise; the exact solver below returns the shortest
      // legal clockwise route from the resulting board.
      startState = applyMove(startState, move, false);
    }
    return { startState };
  }

  async function generatePlayerMadePuzzle(word, requestedDifficulty) {
    const difficulty = normalizePlayerMadeDifficulty(requestedDifficulty);
    const config = PLAYER_MADE_DIFFICULTIES[difficulty];
    const seed = hashString(`made-${word}-${difficulty}-${Date.now()}-${Math.random()}`);
    let closest = null;

    for (let attempt = 0; attempt < config.attempts; attempt += 1) {
      const candidate = playerMadeCandidate(word, config, seed, attempt);
      const result = await bfsMinToCenterAsync(candidate.startState, word, 120000, 1800, true);
      if (!result.exact || !Array.isArray(result.path) || !result.path.length) continue;
      const distance = result.moves < config.minMoves
        ? config.minMoves - result.moves
        : Math.max(0, result.moves - config.maxMoves);
      if (!closest || distance < closest.distance) {
        closest = { ...candidate, solution: result.path, moves: result.moves, distance };
      }
      if (distance === 0) {
        return { word, difficulty, startState: candidate.startState, solution: result.path };
      }
    }

    // Never silently label a board with a difficulty it did not meet. A retry
    // produces a new seed and is safer than publishing a misleading puzzle.
    if (closest) throw new Error(`Could not build a ${difficulty} board this time. Try again.`);
    throw new Error("Could not measure this board. Try again.");
  }

  function isKnownPlayerMadeWord(word) {
    return CUSTOM_WORDS.some((encoded, index) => _d(encoded, index) === word);
  }

  async function createPlayerMadePuzzle(word, creatorName, difficulty) {
    const playerId = await ensurePlayerRegistered();
    if (!playerId) throw new Error("Could not set up your player identity");
    const puzzle = await generatePlayerMadePuzzle(word, difficulty);
    const response = await fetch(COMMUNITY_PUZZLE_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        player_id: playerId, device_id: getDeviceId(), creator_name: creatorName,
        word: puzzle.word, difficulty: puzzle.difficulty,
        start_state: puzzle.startState, solution: puzzle.solution,
      }),
    });
    let data = null;
    try { data = await response.json(); } catch {}
    if (!response.ok) throw new Error(data?.error || "Could not create puzzle");
    return data.puzzle;
  }

  function openMakePuzzle(status = "") {
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    setCustomOverlayTitle("Make a PIVOT");
    const savedName = sanitizeName(state.playerName || loadName() || "");
    let selectedDifficulty = "medium";
    content.innerHTML = `<p class="social-intro">Choose a four-letter word from PIVOT's dictionary. The board is verified before its link is created.</p>` +
      `<input id="made-word" class="custom-name-input made-word" maxlength="4" placeholder="WORD" autocapitalize="characters" autocomplete="off" />` +
      `<input id="made-name" class="custom-name-input" maxlength="16" placeholder="Your name" value="${escapeHtmlAttr(savedName)}" />` +
      `<div class="custom-section-label">Difficulty</div><div class="custom-seg-row" role="group" aria-label="Puzzle difficulty">` +
      ["easy", "medium", "hard"].map((difficulty) => `<button class="custom-seg-btn${difficulty === selectedDifficulty ? " selected" : ""}" type="button" data-made-difficulty="${difficulty}" aria-pressed="${difficulty === selectedDifficulty}">${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</button>`).join("") +
      `</div><div id="made-difficulty-note" class="name-note name-help">Medium · 5–6 best moves</div>` +
      `<div id="made-status" class="social-status">${esc(status)}</div>` +
      `<nav class="custom-actions-nav"><a href="#" id="made-create" class="custom-action-link custom-action-gold">Create puzzle</a>` +
      `<span class="custom-sep">·</span><a href="#" id="made-back" class="custom-action-link custom-action-muted">Back</a></nav>`;
    document.getElementById("made-word")?.addEventListener("input", (event) => {
      event.target.value = event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
    });
    for (const button of content.querySelectorAll("[data-made-difficulty]")) {
      button.addEventListener("click", () => {
        selectedDifficulty = normalizePlayerMadeDifficulty(button.dataset.madeDifficulty);
        for (const option of content.querySelectorAll("[data-made-difficulty]")) {
          const selected = option.dataset.madeDifficulty === selectedDifficulty;
          option.classList.toggle("selected", selected);
          option.setAttribute("aria-pressed", String(selected));
        }
        const config = PLAYER_MADE_DIFFICULTIES[selectedDifficulty];
        const note = document.getElementById("made-difficulty-note");
        if (note) note.textContent = `${selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)} · ${config.minMoves}–${config.maxMoves} best moves`;
      });
    }
    document.getElementById("made-create")?.addEventListener("click", async (event) => {
      event.preventDefault();
      if (event.currentTarget.getAttribute("aria-disabled") === "true") return;
      const word = String(document.getElementById("made-word")?.value || "").toUpperCase();
      const creatorName = sanitizeName(document.getElementById("made-name")?.value || "");
      const statusEl = document.getElementById("made-status");
      if (!/^[A-Z]{4}$/.test(word) || !creatorName) {
        if (statusEl) statusEl.textContent = "Enter a four-letter word and your name.";
        return;
      }
      if (!isKnownPlayerMadeWord(word)) {
        if (statusEl) statusEl.textContent = "That word isn't in PIVOT's dictionary yet.";
        return;
      }
      const createButton = document.getElementById("made-create");
      if (createButton) createButton.setAttribute("aria-disabled", "true");
      if (statusEl) statusEl.textContent = `Building and measuring a ${selectedDifficulty} board…`;
      try {
        const made = await createPlayerMadePuzzle(word, creatorName, selectedDifficulty);
        const madeDifficulty = normalizePlayerMadeDifficulty(made.difficulty || selectedDifficulty);
        const difficultyLabel = madeDifficulty.charAt(0).toUpperCase() + madeDifficulty.slice(1);
        const url = `${CANONICAL_URL}/made/?puzzle=${encodeURIComponent(made.share_code)}`;
        content.innerHTML = `<div class="made-success"><strong>Your PIVOT is ready.</strong><span>${esc(word)} · ${esc(difficultyLabel)} · ${Number(made.solution_moves)} best moves</span>` +
          `<code>${esc(made.share_code)}</code></div><nav class="custom-actions-nav">` +
          `<a href="#" id="made-share" class="custom-action-link custom-action-gold">Share puzzle</a><span class="custom-sep">·</span>` +
          `<a href="${escapeHtmlAttr(url)}" class="custom-action-link">Play it</a><span class="custom-sep">·</span>` +
          `<a href="#" id="made-done" class="custom-action-link custom-action-muted">Done</a></nav>`;
        document.getElementById("made-share")?.addEventListener("click", async (shareEvent) => {
          shareEvent.preventDefault();
          const text = `${creatorName} made you a PIVOT: ${word}\n${url}`;
          if (postNativeShare({ title: `${creatorName} made a PIVOT`, text, url, eventName: "community_puzzle_share_native" })) return;
          if (await copyTextToClipboard(text)) setShareToast("Puzzle link copied!");
          else openCopyOverlay(text, "Puzzle link");
        });
        document.getElementById("made-done")?.addEventListener("click", (doneEvent) => { doneEvent.preventDefault(); openPlayTogetherHub(); });
      } catch (err) {
        if (statusEl) statusEl.textContent = err.message || "Could not create puzzle";
        if (createButton) createButton.removeAttribute("aria-disabled");
      }
    });
    document.getElementById("made-back")?.addEventListener("click", (event) => { event.preventDefault(); openHostGameHub(); });
    showCustomOverlay();
  }

  async function startCommunityPuzzle(code) {
    const normalizedCode = String(code || "").trim().toUpperCase();
    setCustomOverlayTitle("Made by a player");
    const content = document.getElementById("custom-game-content");
    if (content) content.innerHTML = `<div class="social-loading">Loading shared puzzle…</div>`;
    showCustomOverlay();
    try {
      const response = await fetch(`${COMMUNITY_PUZZLE_URL}?code=${encodeURIComponent(normalizedCode)}`, { cache: "no-store" });
      const data = await response.json();
      if (!response.ok || !data?.puzzle) throw new Error(data?.error || "Puzzle not found");
      const puzzle = data.puzzle;
      closeCustomGameOverlay();
      const difficulty = normalizePlayerMadeDifficulty(puzzle.difficulty);
      startCustomPuzzle(normalizedCode, difficulty, {
        word: puzzle.word,
        startState: puzzle.start_state,
        creatorName: String(puzzle.creator_name || "A player").slice(0, 16),
        communityCode: normalizedCode,
      }, false);
      setShareToast(`${state.customCreatorName} made this ${difficulty} PIVOT`, 2600);
    } catch (err) {
      if (content) content.innerHTML = `<div class="social-status error">${esc(err.message || "Puzzle not found")}</div>` +
        `<nav class="custom-actions-nav"><a href="#" id="community-back" class="custom-action-link custom-action-muted">Back</a></nav>`;
      document.getElementById("community-back")?.addEventListener("click", (event) => { event.preventDefault(); openPlayTogetherHub(); });
    }
  }

  async function shareCurrentCommunityPuzzle() {
    if (!state.customCommunityCode) return;
    const creator = state.customCreatorName || "A player";
    const url = `${CANONICAL_URL}/made/?puzzle=${encodeURIComponent(state.customCommunityCode)}`;
    const text = `${creator} made you a PIVOT: ${state.targetWord}\n${url}`;
    if (postNativeShare({ title: `${creator} made a PIVOT`, text, url, eventName: "community_puzzle_reshare_native" })) return;
    if (await copyTextToClipboard(text)) setShareToast("Puzzle link copied!");
    else openCopyOverlay(text, "Puzzle link");
  }

  function generateRoomCode() {
    let code = "";
    for (let i = 0; i < 5; i++) {
      code += CUSTOM_ROOM_CHARS[Math.floor(Math.random() * CUSTOM_ROOM_CHARS.length)];
    }
    return code;
  }

  // Anti-greedy scramble: each step picks the CW rotation that maximizes
  // the lower-bound distance from the solved state. Produces states that are
  // provably far from solution and consistently exceed BFS search budgets.
  function antiGreedyScramble(stateStr, word, rng, steps) {
    for (let s = 0; s < steps; s++) {
      let bestMoves = [];
      let bestLB = -1;
      for (let mi = 0; mi < MOVE_MAPS.length; mi++) {
        const next = applyMove(stateStr, mi, true);
        const lb = computeLowerBoundForStart(next, word) || 0;
        if (lb > bestLB) { bestLB = lb; bestMoves = [mi]; }
        else if (lb === bestLB) bestMoves.push(mi);
      }
      const chosen = bestMoves[Math.floor(rng() * bestMoves.length)];
      stateStr = applyMove(stateStr, chosen, true);
    }
    return stateStr;
  }

  function customPuzzleSeedKey(roomCode, roundNumber) {
    const round = Number.isFinite(roundNumber) ? Math.max(1, Math.floor(roundNumber)) : 1;
    return round > 1 ? `${roomCode}-round-${round}` : roomCode;
  }

  function generateCustomPuzzle(roomCode, difficulty, roundNumber) {
    const seed = hashString("custom-" + customPuzzleSeedKey(roomCode, roundNumber));
    const wordIndex = ((seed % CUSTOM_WORDS.length) + CUSTOM_WORDS.length) % CUSTOM_WORDS.length;
    const word = _d(CUSTOM_WORDS[wordIndex], wordIndex);

    if (difficulty === "easy") {
      // Quick fun puzzle, ~3-5 moves
      const rng = mulberry32(seed);
      const grid = buildSolvedGrid(word, rng);
      scrambleGrid(grid, rng, 15);
      return { word, startState: gridToString(grid), scrambleMoves: 15 };
    }

    if (difficulty === "medium") {
      // Moderate, ~5-7 moves
      const rng = mulberry32(seed);
      const grid = buildSolvedGrid(word, rng);
      scrambleGrid(grid, rng, 40);
      return { word, startState: gridToString(grid), scrambleMoves: 40 };
    }

    if (difficulty === "hard") {
      // Generate candidates and pick the highest lower-bound puzzle.
      // Avoid expensive synchronous BFS in the tap path.
      let bestState = "";
      let bestLB = -1;
      const candidates = [];
      for (let attempt = 0; attempt < 25; attempt++) {
        const rng = mulberry32(seed + attempt * 7919);
        const grid = buildSolvedGrid(word, rng);
        scrambleGrid(grid, rng, 120);
        const stateStr = gridToString(grid);
        const lb = computeLowerBoundForStart(stateStr, word) || 0;
        candidates.push({ stateStr, lb });
      }
      candidates.sort((a, b) => b.lb - a.lb);
      bestState = candidates[0]?.stateStr || "";
      bestLB = candidates[0]?.lb || 0;
      for (const cand of candidates.slice(1, 5)) {
        if ((cand.lb || 0) > bestLB) {
          bestLB = cand.lb || 0;
          bestState = cand.stateStr;
        }
      }
      if (!bestState) {
        const rng = mulberry32(seed);
        const grid = buildSolvedGrid(word, rng);
        scrambleGrid(grid, rng, 120);
        bestState = gridToString(grid);
      }
      return { word, startState: bestState, scrambleMoves: 120 };
    }

    // Expert: anti-greedy scramble — nearly impossible
    // Generate 20 candidates using anti-greedy scrambling with varying step counts.
    // Anti-greedy maximizes lower bound at each step, producing states that
    // consistently exceed BFS search budgets (require 8+ optimal CW moves).
    let bestState = null;
    let bestLB = -1;
    for (let attempt = 0; attempt < 20; attempt++) {
      const rng = mulberry32(seed + attempt * 7919);
      const grid = buildSolvedGrid(word, rng);
      const startStr = gridToString(grid);
      const steps = 30 + Math.floor(rng() * 30); // 30-60 anti-greedy steps
      const resultStr = antiGreedyScramble(startStr, word, rng, steps);
      const lb = computeLowerBoundForStart(resultStr, word) || 0;
      if (lb > bestLB) { bestLB = lb; bestState = resultStr; }
    }
    if (!bestState) {
      // Fallback: simple scramble
      const rng = mulberry32(seed);
      const grid = buildSolvedGrid(word, rng);
      scrambleGrid(grid, rng, 200);
      bestState = gridToString(grid);
    }
    return { word, startState: bestState, scrambleMoves: 200 };
  }

  function openCustomGameOverlay(preselectedDiff) {
    logUiEvent("custom_overlay_open");
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return;
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    setCustomOverlayTitle("Practice Game");
    let selectedDifficulty = preselectedDiff || "medium";

    function renderSetup() {
      const diffOptions = ["easy", "medium", "hard", "expert"];
      const diffLinks = diffOptions.map(d =>
        `<a href="#" class="custom-text-link${selectedDifficulty === d ? " custom-text-active" : ""}" data-diff="${d}">${d.charAt(0).toUpperCase() + d.slice(1)}</a>`
      ).join(' <span class="custom-sep">\u00b7</span> ');

      content.innerHTML = `
        <div class="custom-nav-group">
          <div class="custom-section-label">Difficulty</div>
          <div class="custom-text-nav">${diffLinks}</div>
        </div>
        <nav class="custom-actions-nav">
          <a href="#" id="custom-start" class="custom-action-link custom-action-gold">Start Game</a>
          <span class="custom-sep">\u00b7</span>
          <a href="#" id="custom-cancel" class="custom-action-link custom-action-muted">Dismiss</a>
        </nav>
      `;
      content.querySelectorAll("[data-diff]").forEach((btn) => {
        btn.addEventListener("click", (e) => { e.preventDefault(); selectedDifficulty = btn.dataset.diff; renderSetup(); });
      });
      const startBtn = document.getElementById("custom-start");
      if (startBtn) startBtn.addEventListener("click", (e) => { e.preventDefault();
        startSoloCustomGame(selectedDifficulty);
      });
      const cancelBtn = document.getElementById("custom-cancel");
      if (cancelBtn) cancelBtn.addEventListener("click", (e) => { e.preventDefault(); closeCustomGameOverlay(); });
    }
    renderSetup();
    overlay.classList.add("show");
    overlay.setAttribute("aria-hidden", "false");
  }

  function closeCustomGameOverlay() {
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return;
    // Escape or a backdrop tap can dismiss the hint confirmation; without this the
    // pending flag stuck and the Hint button did nothing for the rest of the session.
    _hintConfirmPending = false;
    fadeOutOverlay(overlay);
    scheduleRenderLoop();
  }

  function escapeHtmlAttr(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // Labs: Live Race setup. Deliberately a SEPARATE overlay from
  // openCustomGameOverlay (Practice) — Practice is in active use and must not
  // change. Solo play is intentionally absent here; this screen is only for
  // creating a private room to race a friend.
  function openLiveRaceOverlay(preselectedDiff) {
    logUiEvent("live_race_overlay_open");
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return;
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    setCustomOverlayTitle("Live Race", "BETA");
    let selectedDifficulty = normalizeCustomDifficulty(preselectedDiff || "medium");
    let selectedSeries = normalizeCustomSeriesBestOf(state.customSeriesBestOf || 1);
    // Keep edits local to this setup sheet while its difficulty/series links
    // rebuild the markup. Reading only the persisted name here used to erase
    // anything the player had just typed whenever either option changed.
    let draftName = state.customPlayerName || loadName() || "";

    function renderSetup() {
      const diffOptions = ["easy", "medium", "hard", "expert"];
      const diffLinks = diffOptions.map(d =>
        `<a href="#" class="custom-text-link${selectedDifficulty === d ? " custom-text-active" : ""}" data-diff="${d}">${d.charAt(0).toUpperCase() + d.slice(1)}</a>`
      ).join(' <span class="custom-sep">·</span> ');
      const seriesLinks = [1, 3, 5, 7].map((count) => {
        const label = count === 1 ? "One game" : `Best of ${count}`;
        return `<a href="#" class="custom-text-link${selectedSeries === count ? " custom-text-active" : ""}" data-series="${count}">${label}</a>`;
      }).join(' <span class="custom-sep">·</span> ');

      content.innerHTML = `
        <div class="custom-nav-group">
          <div class="custom-section-label">Your name</div>
          <input class="custom-name-input" id="race-name" type="text" maxlength="20" placeholder="Name" value="${escapeHtmlAttr(draftName)}" />
        </div>
        <div class="custom-nav-group" style="margin-top:12px">
          <div class="custom-section-label">Difficulty</div>
          <div class="custom-text-nav">${diffLinks}</div>
        </div>
        <div class="custom-nav-group" style="margin-top:12px">
          <div class="custom-section-label">Series</div>
          <div class="custom-text-nav">${seriesLinks}</div>
        </div>
        <nav class="custom-actions-nav">
          <a href="#" id="race-create" class="custom-action-link custom-action-gold">Create Live Race</a>
          <span class="custom-sep">·</span>
          <a href="#" id="race-cancel" class="custom-action-link custom-action-muted">Dismiss</a>
        </nav>
      `;

      const nameInput = document.getElementById("race-name");
      nameInput?.addEventListener("input", () => {
        draftName = nameInput.value;
      });

      content.querySelectorAll("[data-diff]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          selectedDifficulty = btn.dataset.diff;
          renderSetup();
        });
      });
      content.querySelectorAll("[data-series]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          selectedSeries = normalizeCustomSeriesBestOf(btn.dataset.series);
          renderSetup();
        });
      });

      const createBtn = document.getElementById("race-create");
      if (createBtn) createBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const nameEl = document.getElementById("race-name");
        const name = sanitizeName(nameEl ? nameEl.value : "") || "Host";
        state.customPassword = "";
        state.customPlayerName = name;
        state.customSeriesBestOf = selectedSeries;
        state.customSeriesWinsNeeded = Math.floor(selectedSeries / 2) + 1;
        state.customSeriesComplete = false;
        state.customSeriesWinnerId = "";
        logEvent("live_race_create_click");
        // Private room (isPublic=false), host plays. NOT startQuickPlay — that
        // one injects bots and auto-starts.
        createCustomRoom(selectedDifficulty, true, null, false);
      });

      const cancelBtn = document.getElementById("race-cancel");
      if (cancelBtn) cancelBtn.addEventListener("click", (e) => {
        e.preventDefault();
        closeCustomGameOverlay();
      });
    }

    renderSetup();
    overlay.classList.add("show");
    overlay.setAttribute("aria-hidden", "false");
  }

  function openCustomLobbyOverlay() {
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return false;
    overlay.classList.add("show");
    overlay.setAttribute("aria-hidden", "false");
    return true;
  }

  // --- Quick Play / Matchmaking ---
  const BOT_NAMES = ["Joe", "Bob", "Beth", "Sam", "Mia", "Alex", "Liv", "Max", "Zoe", "Kai", "Ava", "Leo"];
  let _quickPlayTimeout = null;
  let _botMoveTimer = null;

  function startQuickPlay(playerName, difficulty) {
    state.customPassword = "";
    state.customPlayerName = playerName;
    createCustomRoom(difficulty, true, null, false);
    // Wait for channel subscription, then add 3 bots and start
    const waitForChannel = setInterval(() => {
      if (_customChannel && state.customPhase === "lobby") {
        clearInterval(waitForChannel);
        // Add 3 bots
        const botNames = shuffleArray([...BOT_NAMES]).slice(0, 3);
        for (const name of botNames) {
          const botId = "bot_" + Math.random().toString(36).slice(2, 8);
          state.customPlayers[botId] = {
            id: botId, name, moves: 0,
            solved: false, grid: "", finishOrder: 0, wins: 0, losses: 0,
            isHost: false, isObserver: false, isBot: true,
          };
        }
        broadcastPlayerList();
        // Brief countdown then start
        setTimeout(() => {
          if (state.customIsHost && state.customPhase === "lobby") {
            startGameAsHost();
          }
        }, 1500);
      }
    }, 200);
    // Safety: stop waiting after 10s
    setTimeout(() => clearInterval(waitForChannel), 10000);
  }

  function shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function spawnBotAndStart() {
    const botId = "bot_" + Math.random().toString(36).slice(2, 8);
    const botName = BOT_NAMES[Math.floor(Math.random() * BOT_NAMES.length)];
    state.customPlayers[botId] = {
      id: botId, name: botName, moves: 0,
      solved: false, grid: "", finishOrder: 0, wins: 0, losses: 0, isHost: false, isObserver: false, isBot: true,
    };
    broadcastPlayerList();
    if (state.customIsHost) {
      startGameAsHost();
    }
  }

  function startBotOpponent(botId) {
    if (_botMoveTimer) clearInterval(_botMoveTimer);
    const bot = state.customPlayers[botId];
    if (!bot || !state.customStartState || !state.customWord) return;
    let botGrid = state.customStartState;
    let botMoves = 0;

    // Use BFS to get optimal moves
    computeHintSequenceAsync(botGrid, 20).then((sequence) => {
      if (!sequence || !sequence.length) return;
      let stepIdx = 0;
      _botMoveTimer = setInterval(() => {
        if (!state.customMode || !state.customMultiplayer) {
          clearInterval(_botMoveTimer);
          _botMoveTimer = null;
          return;
        }
        if (stepIdx >= sequence.length) {
          clearInterval(_botMoveTimer);
          _botMoveTimer = null;
          return;
        }
        const moveIdx = sequence[stepIdx];
        botGrid = applyMove(botGrid, moveIdx, true);
        botMoves++;
        stepIdx++;
        const botPlayer = state.customPlayers[botId];
        if (botPlayer) {
          botPlayer.moves = botMoves;
          botPlayer.grid = botGrid;
        }
        // Check if bot solved it
        if (checkCenterRow(botGrid, state.customWord || state.targetWord)) {
          if (botPlayer && !botPlayer.solved) {
            botPlayer.solved = true;
            state.customSolveCount++;
            botPlayer.finishOrder = state.customSolveCount;
            finishCustomRoundIfComplete();
          }
          clearInterval(_botMoveTimer);
          _botMoveTimer = null;
        }
      }, 1500 + Math.random() * 1500);
    });
  }

  function stopBotOpponent() {
    if (_botMoveTimer) {
      clearInterval(_botMoveTimer);
      _botMoveTimer = null;
    }
  }

  function checkCenterRow(gridStr, targetWord) {
    if (!isValidGridString(gridStr) || !targetWord) return false;
    const centerStart = CENTER_ROW * GRID_COLS + CENTER_START_COL;
    return gridStr.slice(centerStart, centerStart + CENTER_WORD_LENGTH) === targetWord;
  }

  let _lobbyRefreshInterval = null;
  function renderLobbyList() {
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    content.innerHTML = `
      <div style="font-size:15px;font-weight:600;margin-bottom:8px">Public Games</div>
      <div id="lobby-list-container" class="lobby-empty">Loading\u2026</div>
      <nav class="custom-actions-nav">
        <a href="#" id="lobby-back" class="custom-action-link custom-action-muted">Back</a>
      </nav>
    `;
    document.getElementById("lobby-back")?.addEventListener("click", (e) => { e.preventDefault();
      if (_lobbyRefreshInterval) { clearInterval(_lobbyRefreshInterval); _lobbyRefreshInterval = null; }
      openCustomGameOverlay("medium");
    });
    const PINNED_ROOM = { room_code: "ANDREWS", host_name: "Andrew's Room", difficulty: "medium", player_count: "?", max_players: 8, pinned: true };
    function refreshList() {
      lobbyFetch().then((rooms) => {
        const container = document.getElementById("lobby-list-container");
        if (!container) return;
        const allRooms = [PINNED_ROOM, ...rooms.filter(r => r.room_code !== PINNED_ROOM.room_code)];
        container.className = "lobby-list";
        container.innerHTML = allRooms.map((r) => `
          <div class="lobby-row${r.pinned ? " lobby-row-pinned" : ""}">
            <div class="lobby-row-info">
              <span class="lobby-row-host">${esc(r.host_name)}${r.pinned ? " ⭐" : ""}</span>
              <span class="lobby-row-meta">${esc(r.difficulty)} · ${r.pinned ? "always open" : `${r.player_count}/${r.max_players} players`}</span>
            </div>
            <button class="lobby-join-btn" data-code="${esc(r.room_code)}">Join</button>
          </div>
        `).join("");
        container.querySelectorAll(".lobby-join-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            if (_lobbyRefreshInterval) { clearInterval(_lobbyRefreshInterval); _lobbyRefreshInterval = null; }
            closeCustomGameOverlay();
            joinCustomRoom(btn.dataset.code, "medium");
          });
        });
      });
    }
    refreshList();
    _lobbyRefreshInterval = setInterval(refreshList, 5000);
  }

  function startSoloCustomGame(difficulty) {
    logEvent("custom_solo_start");
    closeCustomGameOverlay();
    const roomCode = generateRoomCode();
    const puzzle = generateCustomPuzzle(roomCode, difficulty);
    startCustomPuzzle(roomCode, difficulty, puzzle, false);
  }

  function startCustomPuzzle(roomCode, difficulty, puzzle, multiplayer) {
    _pendingMenuStart = false;
    setMenuStartLoadState("idle");
    state.customMode = true;
    state.customRoomCode = sanitizeRoomCode(roomCode) || roomCode;
    state.customDifficulty = normalizeCustomDifficulty(difficulty);
    state.customMultiplayer = multiplayer;
    state.customPhase = "playing";
    state.customStartState = puzzle.startState;
    state.customWord = puzzle.word;
    state.customCreatorName = sanitizeName(puzzle.creatorName || "");
    state.customCommunityCode = /^[A-Z0-9]{8}$/.test(String(puzzle.communityCode || ""))
      ? String(puzzle.communityCode).toUpperCase()
      : "";
    state.grid = stringToGrid(puzzle.startState);
    state.targetWord = puzzle.word;
    state.moves = 0;
    state.mode = "play";
    state.lastMove = null;
    state.solvedToday = false;
    state.submitted = false;
    clearSubmissionReceipt();
    state.leaderboardEligible = false;
    state.practiceMode = false;
    state.redoMode = false;
    state.completed = false;
    state.completionLocked = false;
    state.alreadyCompleted = false;
    state.winAcknowledged = false;
    state.hintUsed = false;
    state.hintDistanceVisible = false;
    state.hintDistance = null;
    state.hintPivotIndex = null;
    state.hintHighlightUntil = 0;
    state.hintSequence = null;
    state.hintSequenceUntil = 0;
    state.challengeFrom = null;
    state.challengeResolved = false;
    state.shareOnly = false;
    state.showReadme = false;
    state.showLeaderboardOverlay = false;
    state.moveHistory = [];
    state.canonicalStartStr = puzzle.startState;
    state.isPersonalBest = false;
    state.winAnimStart = 0;

    if (multiplayer) syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
    // Layout reserves differ in custom mode; recompute now instead of keeping
    // the previous mode's layout until the next window resize.
    resizeCanvas();
    // Compute min/par asynchronously to avoid blocking the main thread.
    state.minMoves = null;
    state.minExact = false;
    state.optimalPath = [];
    state.lowerBound = computeLowerBoundForStart(puzzle.startState, puzzle.word);
    state.leaderboard = [];
    state.metrics = createEmptyMetrics("");
    const customToken = `${roomCode}|${difficulty}|${puzzle.startState}|${puzzle.word}`;
    state.customMinToken = customToken;
    bfsMinToCenterAsync(puzzle.startState, puzzle.word, 600000, 0, true).then((result) => {
      if (!state.customMode) return;
      if (state.customMinToken !== customToken) return;
      if (state.customRoomCode !== roomCode) return;
      if (state.targetWord !== puzzle.word) return;
      if (Number.isFinite(result.moves)) {
        state.minMoves = result.moves;
        state.minExact = Boolean(result.exact);
        state.optimalPath = Array.isArray(result.path) ? result.path.slice() : [];
      } else {
        // BFS couldn't solve exactly — use lower bound as approximate par
        const lb = state.lowerBound;
        if (Number.isFinite(lb) && lb > 0) {
          state.minMoves = lb;
          state.minExact = false;
        }
      }
    });
  }

  function exitCustomMode(exitNotice = "", options = null) {
    const opts = options && typeof options === "object" ? options : {};
    const keepReturnSession = !!opts.keepReturnSession;
    const suppressNotice = !!opts.suppressNotice;
    const skipAutoOverlay = !!opts.skipAutoOverlay;
    if (!keepReturnSession) {
      try { sessionStorage.removeItem("pivot_custom_session"); } catch {}
    }
    // Broadcast leave before disconnecting
    if (_customChannel && state.customPlayerId) {
      try { _customChannel.send({ type: "broadcast", event: "player_leave", payload: { id: state.customPlayerId } }); } catch {}
    }
    if (_customChannel) {
      _customChannel.unsubscribe();
      _customChannel = null;
      _customChannelCode = "";
    }
    if (_kickedExitTimeout) { clearTimeout(_kickedExitTimeout); _kickedExitTimeout = null; }
    if (_heartbeatInterval) { clearInterval(_heartbeatInterval); _heartbeatInterval = null; }
    if (_playerPingInterval) { clearInterval(_playerPingInterval); _playerPingInterval = null; }
    if (_watchdogTimeout) { clearTimeout(_watchdogTimeout); _watchdogTimeout = null; }
    if (_countdownTimeout) { clearTimeout(_countdownTimeout); _countdownTimeout = null; }
    closeChatPanel();
    stopBotOpponent();
    if (_quickPlayTimeout) { clearTimeout(_quickPlayTimeout); _quickPlayTimeout = null; }
    if (state.customIsHost && state.customIsPublic) {
      lobbyRemove(state.customRoomCode, state.customPlayerId);
    }
    state.customIsPublic = false;
    state.customMode = false;
    state.customRoomCode = "";
    state.customDifficulty = "medium";
    state.customMultiplayer = false;
    state.customIsHost = false;
    state.customPlayerName = "";
    state.customPassword = "";
    state.customLobbyToken = "";
    state.customPlayerId = "";
    state.customPhase = "lobby";
    state.customPlayers = {};
    state.customSolveCount = 0;
    state.customRoundNumber = 1;
    state.customNextDifficulty = null;
    state.customIsObserver = false;
    state.customObserveMode = false;
    state.customObserveFilter = "all";
    state.customObserveFocusPlayer = null;
    state.customObserveFocusLocked = false;
    state.customJoinRequested = false;
    state.customJoinApproved = false;
    state.customPendingJoins = [];
    state.customRoomLocked = false;
    state.customCountdownUntil = 0;
    state.customCountdownPuzzle = null;
    state.customCountdownRoomCode = "";
    state.customClassroomMode = false;
    state.customStartState = "";
    state.customWord = "";
    state.customCreatorName = "";
    state.customCommunityCode = "";
    state.customRoundOutcomeKey = "";
    state.customSeriesBestOf = 1;
    state.customSeriesWinsNeeded = 1;
    state.customSeriesComplete = false;
    state.customSeriesWinnerId = "";
    state.customReconnecting = false;
    state.customReconnectUntil = 0;
    state.customMinToken = "";
    state.customLastKick = null;
    if (_isCustomPage) syncCustomRoomUrl("", "", "");
    hideReconnectChip();

    if (_isCustomPage) {
      state.mode = "menu";
      if (!skipAutoOverlay) setTimeout(() => openCustomGameOverlay(), 100);
    } else {
      resetGame();
    }
    resizeCanvas();
    const notice = typeof exitNotice === "string" ? exitNotice.trim() : "";
    if (notice && !suppressNotice) setShareToast(notice, 3600);
  }

  // --- Multiplayer via Supabase Broadcast (N-player lobby system) ---

  function resetWatchdog() {
    if (_watchdogTimeout) clearTimeout(_watchdogTimeout);
    if (state.customIsHost) return; // host doesn't watch itself
    // Clear reconnecting state if we got a fresh heartbeat
    if (state.customReconnecting) {
      state.customReconnecting = false;
      state.customReconnectUntil = 0;
      hideReconnectChip();
    }
    _watchdogTimeout = setTimeout(() => {
      // Don't immediately exit — try to reconnect
      if (state.customReconnecting) return; // already reconnecting
      state.customReconnecting = true;
      state.customReconnectUntil = performance.now() + CUSTOM_RECONNECT_GRACE_MS;
      setShareToast("Reconnecting (30s)", 1600);
      showReconnectChip();
      scheduleRenderLoop();
      // Attempt channel resubscribe
      if (_customChannel && supabase) {
        // Pin to the code the channel was created with. Session room codes are
        // stable across rounds, and the pin prevents stale client state from
        // ever moving a reconnect onto a channel the host did not create.
        const code = _customChannelCode || state.customRoomCode;
        try { _customChannel.unsubscribe(); } catch {}
        _customChannel = supabase.channel(`custom-${code}`);
        _customChannel
          .on("broadcast", { event: "player_list" }, (payload) => {
            const data = payload.payload || payload;
            if (isStaleCustomRoundUpdate(data)) return;
            state.customPlayers = sanitizeCustomPlayersMap(data.players);
            state.customPhase = data.phase || state.customPhase;
            if (data.roomCode) state.customRoomCode = sanitizeRoomCode(data.roomCode) || state.customRoomCode;
            if (data.difficulty) state.customDifficulty = data.difficulty;
            if (data.roundNumber) state.customRoundNumber = data.roundNumber;
            state.customNextDifficulty = data.nextDifficulty || null;
            applyCustomSeriesPayload(data);
            if (data.pendingJoins) state.customPendingJoins = sanitizePendingJoins(data.pendingJoins);
            if (data.classroomMode !== undefined) state.customClassroomMode = data.classroomMode;
            if (data.roomLocked !== undefined) state.customRoomLocked = !!data.roomLocked;
            const self = state.customPlayers[state.customPlayerId];
            if (self) {
              state.customIsObserver = !!self.isObserver;
              if (state.customIsObserver) state.customObserveMode = true;
            }
            syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
            resetWatchdog(); // clears reconnecting state
            scheduleObserverRepaint();
          })
          .on("broadcast", { event: "move" }, (payload) => handlePlayerMove(payload.payload || payload))
          .on("broadcast", { event: "solve" }, (payload) => handlePlayerSolve(payload.payload || payload))
          .on("broadcast", { event: "next_round" }, (payload) => {
            const data = payload.payload || payload;
            applyCustomSeriesPayload(data);
            const newCode = data.roomCode;
            const diff = data.difficulty || state.customDifficulty;
            if (!newCode) return;
            state.customDifficulty = diff;
            state.customRoundNumber = data.roundNumber || state.customRoundNumber + 1;
            state.customPhase = "playing";
            state.customSolveCount = 0;
            state.customCountdownUntil = 0;
            let puzzle;
            if (data.startState && data.word) {
              puzzle = { word: data.word, startState: data.startState };
            } else {
              puzzle = generateCustomPuzzle(newCode, diff, state.customRoundNumber);
            }
            resetWatchdog();
            startCustomPuzzle(newCode, diff, puzzle, true);
          })
          .on("broadcast", { event: "game_countdown" }, (payload) => {
            const data = payload.payload || payload;
            applyCustomSeriesPayload(data);
            if (data.roomCode) state.customRoomCode = sanitizeRoomCode(data.roomCode) || state.customRoomCode;
            if (data.difficulty) state.customDifficulty = data.difficulty;
            if (data.roundNumber) state.customRoundNumber = data.roundNumber;
            state.customCountdownUntil = performance.now() + (data.seconds || 3) * 1000;
            if (data.startState && data.word) {
              state.customCountdownPuzzle = { word: data.word, startState: data.startState };
            }
            state.customCountdownRoomCode = data.roomCode || state.customRoomCode;
            syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
            resetWatchdog();
            scheduleRenderLoop();
          })
          .on("broadcast", { event: "game_start" }, (payload) => {
            const data = payload.payload || payload;
            applyCustomSeriesPayload(data);
            if (data.difficulty) state.customDifficulty = data.difficulty;
            if (data.roundNumber) state.customRoundNumber = data.roundNumber;
            state.customPhase = "playing";
            state.customCountdownUntil = 0;
            const code = data.roomCode || state.customRoomCode;
            let puzzle;
            if (data.startState && data.word) {
              puzzle = { word: data.word, startState: data.startState };
            } else {
              puzzle = generateCustomPuzzle(code, state.customDifficulty, state.customRoundNumber);
            }
            resetWatchdog();
            startCustomPuzzle(code, state.customDifficulty, puzzle, true);
          })
          .on("broadcast", { event: "join_approved" }, (payload) => {
            const data = payload.payload || payload;
            if (data.targetId === state.customPlayerId) {
              state.customJoinApproved = true;
              state.customJoinRequested = false;
              setShareToast("Approved! You'll play next round", 3000);
            }
          })
          .on("broadcast", { event: "join_denied" }, (payload) => {
            const data = payload.payload || payload;
            if (data.targetId === state.customPlayerId) {
              state.customJoinRequested = false;
              state.customJoinApproved = false;
              setShareToast(data.reason || "Join request denied", 2600);
            }
          })
          .on("broadcast", { event: "player_kicked" }, (payload) => {
            const data = payload.payload || payload;
            if (data.targetId === state.customPlayerId) {
              handleSelfKicked(data.reason || "You've been removed from the room");
            }
          })
          .on("broadcast", { event: "player_kick_undo" }, (payload) => {
            handleKickUndo(payload.payload || payload);
          })
          .on("broadcast", { event: "observe_applied" }, (payload) => {
            handleObserveApplied(payload.payload || payload);
          })
          .subscribe((status) => {
            if (status === "SUBSCRIBED") {
              // Re-announce ourselves
              _customChannel.send({ type: "broadcast", event: "join_request", payload: {
                id: state.customPlayerId, name: state.customPlayerName, password: state.customPassword || ""
              }});
              startPlayerPing();
            } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
              // Keep trying until grace period expires.
              if (!state.customReconnecting) {
                state.customReconnecting = true;
                state.customReconnectUntil = performance.now() + CUSTOM_RECONNECT_GRACE_MS;
                setShareToast("Reconnecting (30s)", 1600);
                showReconnectChip();
                scheduleRenderLoop();
              }
            }
          });
      }
      // Grace period handled by render() — checks customReconnectUntil
      // renderLoop keeps animating while customReconnecting is true
    }, 15000);
  }

  function handleSelfKicked(reason = "You've been removed from the room") {
    if (_kickedExitTimeout) {
      clearTimeout(_kickedExitTimeout);
      _kickedExitTimeout = null;
    }
    const message = typeof reason === "string" && reason.trim()
      ? reason.trim()
      : "You've been removed from the room";
    setShareToast(message, 3000);
    _kickedExitTimeout = setTimeout(() => {
      _kickedExitTimeout = null;
      exitCustomMode(message);
    }, 3000);
  }

  function handleKickUndo(data) {
    const targetId = sanitizePlayerId(data && data.targetId ? String(data.targetId) : "");
    if (!targetId || targetId !== state.customPlayerId) return;
    if (_kickedExitTimeout) {
      clearTimeout(_kickedExitTimeout);
      _kickedExitTimeout = null;
      setShareToast("Host reversed the removal — you're still in the room", 3200);
    }
  }

  // --- Public Lobby API ---
  const LOBBY_ENDPOINT = `${SUPABASE_URL}/functions/v1/pivot-lobby`;
  const _lobbyHeaders = buildPublicSupabaseHeaders({ "Content-Type": "application/json" });

  function generateLobbyHostToken() {
    try {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
    } catch {
      return `${generatePlayerId().replace(/[^A-Za-z0-9_-]/g, "")}_${Date.now().toString(36)}`;
    }
  }

  function lobbyRegister(roomCode, hostName, hostId, difficulty, isPublic, maxPlayers) {
    if (!isPublic) return;
    fetch(LOBBY_ENDPOINT, {
      method: "POST",
      headers: _lobbyHeaders,
      body: JSON.stringify({
        room_code: roomCode,
        host_name: hostName,
        host_id: hostId,
        host_token: state.customLobbyToken,
        difficulty,
        is_public: true,
        max_players: maxPlayers || 8,
      }),
    }).then((res) => {
      // The server now refuses to overwrite a public listing owned by a
      // different host. Swallowing that would leave the UI claiming the room is
      // public when it was never listed, so say so and put the toggle back.
      if (res && res.status === 409 && state.customRoomCode === roomCode) {
        state.customIsPublic = false;
        const pubBtn = document.getElementById("custom-toggle-public");
        if (pubBtn) pubBtn.textContent = "Public";
        setShareToast("That code is already public under another host", 2600);
        logEvent("lobby_register_conflict");
      }
    }).catch(() => {});
  }

  function lobbyHeartbeat(roomCode, hostId, playerCount, phase) {
    if (!state.customIsPublic) return;
    fetch(LOBBY_ENDPOINT, {
      method: "PATCH",
      headers: _lobbyHeaders,
      body: JSON.stringify({ room_code: roomCode, host_id: hostId, host_token: state.customLobbyToken, player_count: playerCount, phase }),
    }).catch(() => {});
  }

  function lobbyRemove(roomCode, hostId) {
    if (!state.customIsPublic) return;
    fetch(LOBBY_ENDPOINT, {
      method: "DELETE",
      headers: _lobbyHeaders,
      body: JSON.stringify({ room_code: roomCode, host_id: hostId, host_token: state.customLobbyToken }),
    }).catch(() => {});
  }

  async function lobbyFetch() {
    try {
      const res = await fetch(LOBBY_ENDPOINT, { headers: buildPublicSupabaseHeaders() });
      const data = await res.json();
      return data.rooms || [];
    } catch { return []; }
  }

  function startHeartbeat() {
    if (_heartbeatInterval) clearInterval(_heartbeatInterval);
    _heartbeatInterval = setInterval(() => {
      const cutoff = Date.now() - 45000;
      const staleIds = Object.values(state.customPlayers)
        .filter((player) => player && !player.isHost && !player.isBot && Number(player.lastSeen || 0) < cutoff)
        .map((player) => player.id);
      for (const playerId of staleIds) handlePlayerLeave({ id: playerId, disconnected: true });
      broadcastPlayerList();
      if (state.customIsHost && state.customIsPublic) {
        lobbyHeartbeat(state.customRoomCode, state.customPlayerId, Object.keys(state.customPlayers).length, state.customPhase);
      }
    }, 10000);
  }

  function startPlayerPing() {
    if (_playerPingInterval) clearInterval(_playerPingInterval);
    const sendPing = () => {
      if (!_customChannel || !state.customMultiplayer || state.customIsHost || !state.customPlayerId) return;
      _customChannel.send({
        type: "broadcast",
        event: "player_ping",
        payload: { id: state.customPlayerId, roomCode: state.customRoomCode },
      });
    };
    sendPing();
    _playerPingInterval = setInterval(sendPing, 5000);
  }

  // --- Saved Rooms helpers ---
  const SAVED_ROOMS_KEY = "pivot_saved_rooms";
  const MAX_SAVED_ROOMS = 10;

  function getSavedRooms() {
    try { return JSON.parse(localStorage.getItem(SAVED_ROOMS_KEY)) || []; } catch { return []; }
  }

  function saveRoom(code, password, difficulty, name) {
    let rooms = getSavedRooms().filter((r) => r.code !== code);
    rooms.unshift({ code, password: password || "", difficulty, name: name || code, createdAt: Date.now(), lastUsedAt: Date.now() });
    if (rooms.length > MAX_SAVED_ROOMS) rooms = rooms.slice(0, MAX_SAVED_ROOMS);
    localStorage.setItem(SAVED_ROOMS_KEY, JSON.stringify(rooms));
  }

  function deleteSavedRoom(code) {
    const rooms = getSavedRooms().filter((r) => r.code !== code);
    localStorage.setItem(SAVED_ROOMS_KEY, JSON.stringify(rooms));
  }

  function isSavedRoom(code) {
    return getSavedRooms().some((r) => r.code === code);
  }

  function touchSavedRoom(code) {
    const rooms = getSavedRooms();
    const room = rooms.find((r) => r.code === code);
    if (room) { room.lastUsedAt = Date.now(); localStorage.setItem(SAVED_ROOMS_KEY, JSON.stringify(rooms)); }
  }

  function createCustomRoom(difficulty, hostPlays, savedRoomCode, isPublic) {
    if (!supabase) { setShareToast("Multiplayer unavailable", 2000); return; }
    const roomCode = sanitizeRoomCode(savedRoomCode || "") || generateRoomCode();
    const normalizedDifficulty = normalizeCustomDifficulty(difficulty);
    logEvent("custom_room_create", {}, `${roomCode}_${state.sessionId}_custom_room_create`);
    if (!hostPlays) {
      logEvent("classroom_room_create", {}, `${roomCode}_${state.sessionId}_classroom_room_create`);
    }
    if (savedRoomCode) touchSavedRoom(savedRoomCode);
    const playerId = generatePlayerId();
    state.customRoomCode = roomCode;
    state.customDifficulty = normalizedDifficulty;
    state.customIsHost = true;
    state.customMultiplayer = true;
    state.customPlayerId = playerId;
    state.customLobbyToken = generateLobbyHostToken();
    state.customPhase = "lobby";
    state.customSolveCount = 0;
    state.customRoundNumber = 1;
    state.customNextDifficulty = null;
    state.customIsObserver = !hostPlays;
    state.customObserveMode = !hostPlays;
    state.customObserveFilter = "all";
    state.customClassroomMode = !hostPlays;
    state.customRoomLocked = false;
    state.customRoundOutcomeKey = "";
    state.customIsPublic = !!isPublic;
    state.customSeriesBestOf = hostPlays ? normalizeCustomSeriesBestOf(state.customSeriesBestOf) : 1;
    state.customSeriesWinsNeeded = Math.floor(state.customSeriesBestOf / 2) + 1;
    state.customSeriesComplete = false;
    state.customSeriesWinnerId = "";

    // Add self to players map
    state.customPlayers = {};
    state.customPlayers[playerId] = {
      id: playerId, name: state.customPlayerName, moves: 0,
      solved: false, grid: "", finishOrder: 0, wins: 0, losses: 0, isHost: true, isObserver: !hostPlays,
      lastSeen: Date.now(),
    };

    state.customPendingJoins = [];
    state.customLastKick = null;
    syncCustomRoomUrl(roomCode, normalizedDifficulty, state.customPassword);

    _customChannelCode = sanitizeRoomCode(roomCode) || roomCode;
    _customChannel = supabase.channel(`custom-${roomCode}`);
    _customChannel
      .on("broadcast", { event: "join_request" }, (payload) => handleJoinRequest(payload.payload || payload))
      .on("broadcast", { event: "request_join" }, (payload) => handleRequestJoin(payload.payload || payload))
      .on("broadcast", { event: "request_observe" }, (payload) => handleObserveRequest(payload.payload || payload))
      .on("broadcast", { event: "move" }, (payload) => handlePlayerMove(payload.payload || payload))
      .on("broadcast", { event: "solve" }, (payload) => handlePlayerSolve(payload.payload || payload))
      .on("broadcast", { event: "player_leave" }, (payload) => handlePlayerLeave(payload.payload || payload))
      .on("broadcast", { event: "player_ping" }, (payload) => handlePlayerPing(payload.payload || payload))
      .on("broadcast", { event: "chat" }, (payload) => handleChatMessage(payload.payload || payload))
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          try { sessionStorage.setItem("pivot_custom_session", JSON.stringify({
            roomCode, difficulty: normalizedDifficulty, password: state.customPassword || "", playerName: state.customPlayerName, playerId: state.customPlayerId, lobbyToken: state.customLobbyToken, isHost: true, timestamp: Date.now()
          })); } catch {}
          showLobbyScreen(roomCode);
          startHeartbeat();
          broadcastPlayerList();
          if (state.customIsPublic) {
            lobbyRegister(roomCode, state.customPlayerName, playerId, normalizedDifficulty, true, 8);
          }
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setShareToast("Connection failed — try again", 3000);
          closeCustomGameOverlay();
          exitCustomMode();
        }
      });
  }

  function showLobbyScreen(roomCode) {
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
    const hasPassword = !!state.customPassword;
    const isHost = state.customIsHost;
    const playerCount = Object.keys(state.customPlayers).length;
    const inActiveSession = state.customMode && state.customMultiplayer && state.mode === "play";

    const diffOptions = ["easy", "medium", "hard", "expert"];
    const lobbyDiffLinks = diffOptions.map(d =>
      `<a href="#" class="custom-text-link${state.customDifficulty === d ? " custom-text-active" : ""}" data-diff="${d}">${d.charAt(0).toUpperCase() + d.slice(1)}</a>`
    ).join(' <span class="custom-sep">\u00b7</span> ');

    content.innerHTML = `
      <div class="custom-waiting">
        <div class="custom-room-code">${roomCode}${isHost ? `<button class="save-room-btn" id="save-room-toggle" title="${isSavedRoom(roomCode) ? "Unsave room" : "Save room"}" style="margin-left:8px;background:none;border:none;font-size:18px;cursor:pointer;vertical-align:middle;color:${isSavedRoom(roomCode) ? "#f5a623" : "#666"}">${isSavedRoom(roomCode) ? "\u2605" : "\u2606"}</button>` : ""}</div>
        <div style="font-size:12px;color:var(--muted-soft);margin-bottom:4px"><span id="lobby-meta-count">${lobbyMetaText(diffLabel, playerCount, hasPassword)}</span></div>
        <div id="lobby-player-list" class="lobby-player-list"></div>
        <div id="lobby-qr-container" style="display:none;margin-top:10px"></div>
        <div id="custom-lobby-status" class="custom-waiting-text" style="margin-top:8px;min-height:15px"></div>
        ${isHost ? `
          <div class="custom-nav-group" style="margin-top:12px">
            <div class="custom-section-label">Next round difficulty</div>
            <div class="custom-text-nav" id="lobby-diff-selector">${lobbyDiffLinks}</div>
          </div>
          <input class="custom-name-input" id="lobby-set-password" type="text" maxlength="20" placeholder="${hasPassword ? "Change access code" : "Set access code (optional)"}" style="margin-top:8px" value="${esc(state.customPassword || "")}" />
        ` : `
          <div class="custom-waiting-text" style="margin-top:12px">Watching as observer. Waiting for host\u2026</div>
        `}
        <nav class="custom-actions-nav">
          ${isHost ? `
            <a href="#" id="custom-start-game" class="custom-action-link custom-action-gold"${playerCount < 1 ? ' style="opacity:0.4;pointer-events:none"' : ""}>Start Game</a>
            <span class="custom-sep">\u00b7</span>
            <a href="#" id="custom-copy-link" class="custom-action-link">${PIVOT_NATIVE ? "Invite" : "Copy Link"}</a>
            <span class="custom-sep">\u00b7</span>
            <a href="#" id="custom-show-qr" class="custom-action-link">QR Code</a>
            <span class="custom-sep">\u00b7</span>
            <a href="#" id="custom-toggle-lock" class="custom-action-link">${state.customRoomLocked ? "Unlock" : "Lock"}</a>
            <span class="custom-sep">\u00b7</span>
            <a href="#" id="custom-toggle-public" class="custom-action-link">${state.customIsPublic ? "Private" : "Public"}</a>
            <span class="custom-sep">\u00b7</span>
          ` : ""}
          <a href="#" id="custom-lobby-chat" class="custom-action-link">${lobbyChatLabel()}</a>
          <span class="custom-sep">·</span>
          <a href="#" id="custom-cancel-lobby" class="custom-action-link custom-action-muted">${inActiveSession ? "Close" : "Dismiss"}</a>
        </nav>
      </div>
    `;

    renderLobbyPlayerList();
    if (isHost && state.customClassroomMode) {
      const unapprovedCount = state.customPendingJoins.filter((j) => !j.approved).length;
      setCustomLobbyStatus(
        unapprovedCount > 0
          ? `${unapprovedCount} waiting for approval`
          : "Classroom lobby is open",
        unapprovedCount > 0 ? "warn" : "muted"
      );
    } else if (!isHost) {
      setCustomLobbyStatus("You are watching until host starts the round.", "muted");
    } else {
      setCustomLobbyStatus("", "muted");
    }

    document.getElementById("custom-copy-link")?.addEventListener("click", (e) => {
      e.preventDefault();
      shareCustomJoinLink(roomCode, state.customDifficulty, state.customPassword, "custom_lobby_copy_link");
    });

    document.getElementById("custom-show-qr")?.addEventListener("click", (e) => {
      e.preventDefault();
      logEvent("custom_lobby_show_qr");
      const container = document.getElementById("lobby-qr-container");
      if (!container) return;
      if (container.style.display !== "none") {
        container.style.display = "none";
        return;
      }
      container.style.display = "block";
      // QR scanners drop hash fragments — use query params through the
      // purpose-specific /race/ preview and redirect page.
      const qrUrl = buildCustomShareUrl(roomCode, state.customDifficulty);
      renderQRCode(container, qrUrl);
    });

    if (isHost) {
      document.getElementById("custom-start-game")?.addEventListener("click", (e) => { e.preventDefault();
        if (!startGameAsHost()) {
          if (state.customClassroomMode) {
            setCustomLobbyStatus("Approve at least one observer first.", "warn");
          } else {
            setCustomLobbyStatus("Still connecting to room...", "warn");
          }
        }
      });
      document.getElementById("custom-toggle-lock")?.addEventListener("click", (e) => {
        e.preventDefault();
        toggleCustomRoomLock();
        const lockBtn = document.getElementById("custom-toggle-lock");
        if (lockBtn) lockBtn.textContent = state.customRoomLocked ? "Unlock" : "Lock";
      });
      document.getElementById("custom-toggle-public")?.addEventListener("click", (e) => {
        e.preventDefault();
        const wasPublic = state.customIsPublic;
        if (wasPublic) {
          // De-list BEFORE flipping the flag. lobbyRemove self-cancels on
          // `if (!state.customIsPublic) return`, so clearing it first meant the
          // DELETE never fired: the host saw "Room is now private" while the
          // room stayed publicly listed until the heartbeat cutoff expired it.
          lobbyRemove(state.customRoomCode, state.customPlayerId);
        }
        state.customIsPublic = !wasPublic;
        const pubBtn = document.getElementById("custom-toggle-public");
        if (pubBtn) pubBtn.textContent = state.customIsPublic ? "Private" : "Public";
        if (state.customIsPublic) {
          lobbyRegister(state.customRoomCode, state.customPlayerName, state.customPlayerId, state.customDifficulty, true, 8);
          setShareToast("Room is now public", 1500);
        } else {
          setShareToast("Room is now private", 1500);
        }
      });
      document.getElementById("lobby-set-password")?.addEventListener("change", (e) => {
        state.customPassword = e.target.value.trim();
        syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
        setShareToast(state.customPassword ? "Access code set" : "Access code removed", 1500);
      });
      document.querySelectorAll("#lobby-diff-selector [data-diff]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const diff = btn.dataset.diff;
          state.customNextDifficulty = diff;
          // Update visual selection
          document.querySelectorAll("#lobby-diff-selector [data-diff]").forEach((b) => { b.classList.remove("custom-text-active"); b.style.fontWeight = ""; });
          btn.classList.add("custom-text-active");
          btn.style.fontWeight = "600";
          // Update current difficulty for this round if still in lobby
          if (state.customPhase === "lobby") {
            state.customDifficulty = diff;
            state.customNextDifficulty = null;
            // Update header — re-render lobby to reflect new difficulty
            showLobbyScreen(roomCode);
          }
          broadcastPlayerList();
          if (_customChannel) {
            _customChannel.send({ type: "broadcast", event: "difficulty_change", payload: { difficulty: diff } });
          }
        });
      });
    }

    document.getElementById("custom-lobby-chat")?.addEventListener("click", (e) => { e.preventDefault();
      if (!state.customChatOpen) logEvent("custom_chat_open_lobby");
      toggleChatPanel();
    });

    document.getElementById("custom-cancel-lobby")?.addEventListener("click", (e) => { e.preventDefault();
      if (inActiveSession) {
        closeCustomGameOverlay();
      } else {
        exitCustomMode();
        closeCustomGameOverlay();
      }
    });

    // Save room toggle
    document.getElementById("save-room-toggle")?.addEventListener("click", () => {
      if (isSavedRoom(roomCode)) {
        deleteSavedRoom(roomCode);
        setShareToast("Room unsaved", 1500);
      } else {
        const label = prompt("Room name (optional):", roomCode);
        if (label === null) return; // cancelled
        saveRoom(roomCode, state.customPassword, state.customDifficulty, label || roomCode);
        setShareToast("Room saved!", 1500);
      }
      // Update the star icon
      const btn = document.getElementById("save-room-toggle");
      if (btn) {
        const saved = isSavedRoom(roomCode);
        btn.textContent = saved ? "\u2605" : "\u2606";
        btn.style.color = saved ? "#f5a623" : "#666";
        btn.title = saved ? "Unsave room" : "Save room";
      }
    });
  }

  // Lobby header line: "Medium · 3 players · 🔒".
  function lobbyMetaText(diffLabel, playerCount, hasPassword) {
    const series = state.customSeriesBestOf > 1 ? ` · Best of ${state.customSeriesBestOf}` : "";
    return `${diffLabel}${series} · ${playerCount} player${playerCount !== 1 ? "s" : ""}${hasPassword ? " · 🔒" : ""}`;
  }

  function renderLobbyPlayerList() {
    const container = document.getElementById("lobby-player-list");
    if (!container) return;
    const players = Object.values(state.customPlayers);
    // The count is baked into the lobby HTML at first render, which for a joiner
    // happens before the first player_list arrives — so it must be refreshed here
    // or it stays stuck on "0 players" while rows appear beneath it.
    const metaEl = document.getElementById("lobby-meta-count");
    if (metaEl) {
      const diffLabel = state.customDifficulty.charAt(0).toUpperCase() + state.customDifficulty.slice(1);
      metaEl.textContent = lobbyMetaText(diffLabel, players.length, !!state.customPassword);
    }
    const pendingById = new Map(state.customPendingJoins.map((j) => [j.id, j]));
    container.textContent = "";
    for (const p of players) {
      ensurePlayerRecordShape(p);
      const row = document.createElement("div");
      row.className = "lobby-player-row";
      const youTag = p.id === state.customPlayerId ? " (you)" : "";
      const botTag = p.isBot ? " (Robot)" : "";
      row.appendChild(document.createTextNode(`${sanitizeName(p.name || "") || "Player"}${youTag}${botTag}`));
      const wl = document.createElement("span");
      wl.style.color = "#777";
      wl.style.marginLeft = "6px";
      wl.style.fontSize = "11px";
      wl.textContent = `W-L ${p.wins}-${p.losses}`;
      row.appendChild(wl);
      if (p.isObserver) {
        const observer = document.createElement("span");
        observer.style.color = "#999";
        observer.style.fontStyle = "italic";
        observer.textContent = " watching";
        row.appendChild(observer);
      }
      if (p.isHost) {
        row.appendChild(document.createTextNode(" "));
        const badge = document.createElement("span");
        badge.className = "lobby-host-badge";
        badge.textContent = "HOST";
        row.appendChild(badge);
      }
      if (state.customIsHost && state.customClassroomMode && !p.isHost) {
        const pending = pendingById.get(p.id);
        if (pending && pending.approved) {
          const allowed = document.createElement("span");
          allowed.style.marginLeft = "8px";
          allowed.style.color = "#5ba85b";
          allowed.style.fontSize = "11px";
          allowed.textContent = "allowed";
          row.appendChild(allowed);
        } else if (p.isObserver) {
          const allowBtn = document.createElement("button");
          allowBtn.className = "lobby-approve-btn";
          allowBtn.dataset.approveId = p.id;
          allowBtn.textContent = "Allow";
          row.appendChild(allowBtn);

          const denyBtn = document.createElement("button");
          denyBtn.className = "lobby-deny-btn";
          denyBtn.dataset.denyId = p.id;
          denyBtn.textContent = "Deny";
          row.appendChild(denyBtn);
        }
      }
      if (state.customIsHost && !p.isHost) {
        const kickBtn = document.createElement("button");
        kickBtn.className = "lobby-kick-btn";
        kickBtn.dataset.kickId = p.id;
        kickBtn.title = "Remove player";
        kickBtn.textContent = "\u00d7";
        row.appendChild(kickBtn);
      }
      container.appendChild(row);
    }
    if (state.customIsHost && !container.dataset.boundHostActions) {
      container.dataset.boundHostActions = "1";
      container.addEventListener("click", (event) => {
        const target = event.target && typeof event.target.closest === "function"
          ? event.target.closest("button")
          : null;
        if (!target || !container.contains(target)) return;
        event.stopPropagation();
        const kickId = target.dataset.kickId;
        if (kickId) {
          const player = state.customPlayers[kickId];
          if (!player) return;
          if (!confirm(`Remove ${player.name}?`)) return;
          hostKickPlayer(kickId, "You were removed by the host");
          return;
        }
        const approveId = target.dataset.approveId;
        if (approveId) {
          approveJoinRequest(approveId);
          return;
        }
        const denyId = target.dataset.denyId;
        if (denyId) {
          denyJoinRequest(denyId, "Not approved this round");
        }
      });
    }
    // Update start button state
    const startBtn = document.getElementById("custom-start-game");
    if (startBtn) {
      const shouldDisable = players.length < 1;
      startBtn.style.pointerEvents = shouldDisable ? "none" : "";
      startBtn.style.opacity = shouldDisable ? "0.4" : "";
      if (state.customClassroomMode) {
        const hasPlayableParticipant = hasPlayableClassroomParticipant();
        startBtn.title = hasPlayableParticipant ? "Start round" : "Approve at least one observer to play";
      }
    }
  }

  function setCustomLobbyStatus(message, tone = "muted") {
    const el = document.getElementById("custom-lobby-status");
    if (!el) return;
    const text = typeof message === "string" ? message.trim() : "";
    el.textContent = text;
    if (!text) {
      el.style.color = "#999";
      el.style.fontStyle = "italic";
      return;
    }
    if (tone === "warn") {
      el.style.color = "#8a6a00";
      el.style.fontStyle = "normal";
    } else if (tone === "success") {
      el.style.color = "#4f7f4f";
      el.style.fontStyle = "normal";
    } else {
      el.style.color = "#777";
      el.style.fontStyle = "italic";
    }
  }

  function esc(s) {
    const el = document.createElement("span");
    el.textContent = s;
    return el.innerHTML;
  }

  function normalizeWinLoss(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(9999, Math.floor(value)));
  }

  function ensurePlayerRecordShape(player) {
    if (!player || typeof player !== "object") return;
    player.wins = normalizeWinLoss(player.wins);
    player.losses = normalizeWinLoss(player.losses);
  }

  function isVerifiedCustomSolve(player) {
    const targetWord = state.targetWord || state.customWord;
    return !!(player && player.solved && checkCenterRow(player.grid, targetWord));
  }

  // The host is authoritative for round completion, but only a solved grid for
  // the current target counts. This prevents stale or malformed broadcasts from
  // ending a later round, and avoids Array#every's empty-list=true edge case.
  function finishCustomRoundIfComplete() {
    if (!state.customIsHost || state.customPhase !== "playing") return false;
    const activePlayers = Object.values(state.customPlayers).filter((p) => p && !p.isObserver);
    if (activePlayers.length === 0) return false;
    if (!state.customObserveMode) {
      const self = state.customPlayers[state.customPlayerId];
      if (!self || self.isObserver) return false;
    }
    if (!activePlayers.every(isVerifiedCustomSolve)) return false;
    state.customPhase = "results";
    scoreRoundOutcomeIfNeeded();
    broadcastPlayerList();
    return true;
  }

  function isCurrentCustomPlayerObserver() {
    if (!state.customMode || !state.customMultiplayer || !state.customPlayerId) return false;
    const self = state.customPlayers[state.customPlayerId];
    return !!(self && self.isObserver);
  }

  function applyObserverSwitchByHost(playerId) {
    if (!state.customIsHost) return { changed: false, reason: "not_host" };
    const targetId = sanitizePlayerId(playerId || "");
    if (!targetId) return { changed: false, reason: "bad_id" };
    const player = state.customPlayers[targetId];
    if (!player) return { changed: false, reason: "missing_player" };
    if (player.isObserver) {
      return {
        changed: false,
        reason: "already_observer",
        playerId: targetId,
        playerName: sanitizeName(player.name || "") || "Player",
      };
    }

    const participantsBefore = Object.values(state.customPlayers).filter((p) => p && !p.isObserver);
    const wasPlaying = state.customPhase === "playing";

    ensurePlayerRecordShape(player);
    player.isObserver = true;
    player.solved = false;
    player.finishOrder = 0;
    player.grid = "";
    player.moves = 0;
    state.customPendingJoins = state.customPendingJoins.filter((j) => j.id !== targetId);

    if (targetId === state.customPlayerId) {
      state.customObserveMode = true;
      state.customObserveFocusLocked = false;
      state.customObserveFocusPlayer = null;
    }

    const participantsAfter = Object.values(state.customPlayers).filter((p) => p && !p.isObserver);
    const outcome = {
      changed: true,
      playerId: targetId,
      playerName: sanitizeName(player.name || "") || "Player",
      forfeitWin: false,
      winnerId: "",
      winnerName: "",
    };

    if (wasPlaying && participantsBefore.length === 2 && participantsAfter.length === 1) {
      const winner = participantsAfter[0];
      ensurePlayerRecordShape(winner);
      winner.wins += 1;
      player.losses += 1;
      winner.solved = true;
      winner.finishOrder = winner.finishOrder > 0 ? winner.finishOrder : 1;
      winner.moves = Number.isFinite(winner.moves) ? Math.max(0, Math.floor(winner.moves)) : 0;
      state.customSolveCount = Math.max(Number(state.customSolveCount) || 0, winner.finishOrder || 1);
      state.customPhase = "results";
      state.customRoundOutcomeKey = `${state.customRoomCode || "room"}:${state.customRoundNumber || 0}`;
      updateCustomSeriesWinner();
      outcome.forfeitWin = true;
      outcome.winnerId = winner.id;
      outcome.winnerName = sanitizeName(winner.name || "") || "Player";
      return outcome;
    }

    if (wasPlaying && participantsAfter.length > 0 && participantsAfter.every(isVerifiedCustomSolve)) {
      finishCustomRoundIfComplete();
    } else if (wasPlaying && participantsAfter.length === 0) {
      state.customPhase = "lobby";
      state.customRoundOutcomeKey = "";
      state.customSolveCount = 0;
    }

    return outcome;
  }

  function handleObserveRequest(data) {
    if (!state.customIsHost) return;
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    if (!playerId) return;
    const outcome = applyObserverSwitchByHost(playerId);
    if (!outcome.changed) return;
    if (_customChannel) {
      _customChannel.send({
        type: "broadcast",
        event: "observe_applied",
        payload: {
          targetId: outcome.playerId,
          forfeitWin: !!outcome.forfeitWin,
          winnerId: outcome.winnerId || "",
          winnerName: outcome.winnerName || "",
        },
      });
    }
    if (outcome.forfeitWin && outcome.winnerName) {
      setCustomLobbyStatus(`${outcome.winnerName} wins by forfeit.`, "warn");
    }
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  function handleObserveApplied(data) {
    const targetId = sanitizePlayerId(data && data.targetId ? String(data.targetId) : "");
    if (!targetId || targetId !== state.customPlayerId) return;
    state.customIsObserver = true;
    state.customObserveMode = true;
    state.customObserveFocusLocked = false;
    state.customObserveFocusPlayer = null;
    state.customJoinRequested = false;
    state.customJoinApproved = false;
    const winnerName = sanitizeName(data && data.winnerName ? String(data.winnerName) : "");
    if (data && data.forfeitWin && winnerName) {
      setShareToast(`${winnerName} wins by forfeit. You're now watching.`, 3200);
    } else {
      setShareToast("You're now watching this round.", 2200);
    }
  }

  function requestObserveModeSwitch() {
    if (!state.customMode || !state.customMultiplayer) return false;
    if (!state.customIsHost && (state.customIsObserver || isCurrentCustomPlayerObserver())) return true;
    if (state.customIsHost) {
      const outcome = applyObserverSwitchByHost(state.customPlayerId);
      if (!outcome.changed) return false;
      if (outcome.forfeitWin && outcome.winnerName) {
        setShareToast(`${outcome.winnerName} wins by forfeit.`, 3000);
      } else {
        setShareToast("Now watching this round.", 2000);
      }
      broadcastPlayerList();
      renderLobbyPlayerList();
      return true;
    }
    if (!_customChannel || !state.customPlayerId) {
      setShareToast("Still connecting to room...", 2000);
      return false;
    }
    state.customObserveMode = true;
    state.customObserveFocusLocked = false;
    state.customObserveFocusPlayer = null;
    state.customJoinRequested = false;
    state.customJoinApproved = false;
    logEvent("custom_player_observe_request");
    _customChannel.send({
      type: "broadcast",
      event: "request_observe",
      payload: { id: state.customPlayerId },
    });
    setShareToast("Switching to observer mode...", 2000);
    return true;
  }

  function hasPlayableClassroomParticipant() {
    const players = Object.values(state.customPlayers || {});
    if (!players.length) return false;
    const approvedIds = new Set((state.customPendingJoins || []).filter((j) => j.approved).map((j) => j.id));
    return players.some((p) => {
      if (!p) return false;
      if (p.isHost && state.customObserveMode) return false;
      if (p.isHost && !state.customObserveMode) return true;
      if (!p.isObserver) return true;
      return approvedIds.has(p.id);
    });
  }

  function updateCustomSeriesWinner() {
    const needed = Math.max(1, Number(state.customSeriesWinsNeeded) || 1);
    const leaders = Object.values(state.customPlayers).filter((player) =>
      player && !player.isObserver && normalizeWinLoss(player.wins) >= needed
    );
    state.customSeriesComplete = leaders.length > 0;
    state.customSeriesWinnerId = leaders.length > 1 ? "tie" : (leaders[0]?.id || "");
    return state.customSeriesComplete;
  }

  function scoreRoundOutcomeIfNeeded() {
    if (!state.customIsHost) return;
    if (state.customPhase !== "results") return;
    const roundKey = `${state.customRoomCode || "room"}:${state.customRoundNumber || 0}`;
    if (state.customRoundOutcomeKey === roundKey) return;

    const participants = Object.values(state.customPlayers).filter((p) => p && !p.isObserver);
    if (!participants.length) {
      state.customRoundOutcomeKey = roundKey;
      return;
    }
    for (const player of participants) ensurePlayerRecordShape(player);

    const solvedParticipants = participants.filter((p) => p.solved && Number.isFinite(p.moves));
    if (!solvedParticipants.length) {
      state.customRoundOutcomeKey = roundKey;
      return;
    }
    const bestMoves = Math.min(...solvedParticipants.map((p) => p.moves));
    const winnerIds = new Set(solvedParticipants.filter((p) => p.moves === bestMoves).map((p) => p.id));

    for (const player of participants) {
      if (winnerIds.has(player.id)) player.wins += 1;
      else player.losses += 1;
    }
    updateCustomSeriesWinner();
    state.customRoundOutcomeKey = roundKey;
  }

  function handleJoinRequest(data) {
    if (!state.customIsHost) return;
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    const playerName = sanitizeName(data && data.name ? String(data.name) : "");
    if (!playerId || !playerName) return;
    const existingPlayer = state.customPlayers[playerId];
    if (existingPlayer) {
      existingPlayer.name = playerName;
      ensurePlayerRecordShape(existingPlayer);
      existingPlayer.lastSeen = Date.now();
      const gameActiveNow = state.customPhase === "playing" || state.customPhase === "results";
      if (state.customClassroomMode || gameActiveNow) {
        if (!state.customPendingJoins.some((j) => j.id === playerId)) {
          state.customPendingJoins.push({ id: playerId, name: playerName, approved: false });
        }
      }
      broadcastPlayerList();
      renderLobbyPlayerList();
      return;
    }
    // Max 20 players
    if (Object.keys(state.customPlayers).length >= 20) {
      if (_customChannel) {
        _customChannel.send({ type: "broadcast", event: "join_rejected", payload: { targetId: playerId, reason: "Room full" } });
      }
      return;
    }
    if (state.customRoomLocked && !state.customPlayers[playerId]) {
      if (_customChannel) {
        _customChannel.send({ type: "broadcast", event: "join_rejected", payload: { targetId: playerId, reason: "Room is locked by host" } });
      }
      return;
    }
    // Password check
    if (state.customPassword && data.password !== state.customPassword) {
      if (_customChannel) {
        _customChannel.send({ type: "broadcast", event: "join_rejected", payload: { targetId: playerId, reason: "Wrong password" } });
      }
      return;
    }
    // Add player — in classroom mode everyone starts as observer and host approves who plays.
    const gameActive = state.customPhase === "playing" || state.customPhase === "results";
    const observerFirst = gameActive || state.customClassroomMode;
    state.customPlayers[playerId] = {
      id: playerId, name: playerName, moves: 0,
      solved: false, grid: "", finishOrder: 0, wins: 0, losses: 0, isHost: false, isObserver: observerFirst,
      lastSeen: Date.now(),
    };
    // Classroom mode and late-joins both use host approvals.
    if (state.customClassroomMode || gameActive) {
      if (!state.customPendingJoins.some((j) => j.id === playerId)) {
        state.customPendingJoins.push({ id: playerId, name: playerName, approved: false });
      }
    }
    playJoinSound();
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  function handlePlayerLeave(data) {
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    if (!playerId) return;
    if (state.customIsHost) {
      const departing = state.customPlayers[playerId];
      let outcome = null;
      if (departing && !departing.isObserver && state.customPhase === "playing") {
        outcome = applyObserverSwitchByHost(playerId);
      }
      delete state.customPlayers[playerId];
      // Also remove from pending joins
      state.customPendingJoins = state.customPendingJoins.filter((j) => j.id !== playerId);
      if (state.customLastKick && state.customLastKick.player && state.customLastKick.player.id === playerId) {
        state.customLastKick = null;
      }
      if (outcome?.forfeitWin && outcome.winnerName) {
        setShareToast(`${outcome.winnerName} wins by forfeit`, 2400);
      } else if (state.customPhase === "playing") {
        finishCustomRoundIfComplete();
      }
      broadcastPlayerList();
      renderLobbyPlayerList();
    }
  }

  function handlePlayerPing(data) {
    if (!state.customIsHost) return;
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    const player = playerId ? state.customPlayers[playerId] : null;
    if (player && !player.isHost) player.lastSeen = Date.now();
  }

  function handleChatMessage(data) {
    if (!data || typeof data.text !== "string") return;
    const id = String(data.id || "").slice(0, 40);
    const name = String(data.name || "").slice(0, 16);
    const text = String(data.text).slice(0, 120);
    if (!text.trim()) return;
    // Ignore own messages (we already added them locally)
    if (id === state.customPlayerId) return;
    state.customChatMessages.push({ id, name, text, ts: Date.now() });
    if (state.customChatMessages.length > 50) state.customChatMessages.shift();
    if (!state.customChatOpen) state.customChatUnread++;
    renderChatMessages();
    updateChatBadge();
  }

  function sendChatMessage(text) {
    if (!_customChannel || !state.customMultiplayer) return;
    text = String(text || "").trim().slice(0, 120);
    if (!text) return;
    const now = Date.now();
    if (now - state.customChatLastSendAt < 1000) {
      setShareToast("Slow down", 1000);
      return;
    }
    const normalized = text.toLowerCase();
    const normalizedChat = normalizeName(text);
    if (BANNED_SUBSTRINGS.some((t) => normalizedChat.includes(t)) ||
        BANNED_EXACT.some((t) => normalizedChat.includes(t))) {
      setShareToast("Message blocked", 1200);
      return;
    }
    state.customChatLastSendAt = now;
    const msg = { id: state.customPlayerId, name: state.customPlayerName, text, ts: now };
    state.customChatMessages.push(msg);
    if (state.customChatMessages.length > 50) state.customChatMessages.shift();
    renderChatMessages();
    try {
      _customChannel.send({ type: "broadcast", event: "chat", payload: msg });
    } catch {}
    logEvent("custom_chat_message_sent", { phase: state.customPhase || "" });
  }

  function toggleChatPanel() {
    state.customChatOpen = !state.customChatOpen;
    const panel = document.getElementById("chat-panel");
    if (!panel) return;
    if (state.customChatOpen) {
      // Clear any dragged position so CSS defaults apply
      panel.style.cssText = "";
      panel.classList.add("show");
      state.customChatUnread = 0;
      updateChatBadge();
      renderChatMessages();
      const input = document.getElementById("chat-input");
      if (input) input.focus();
    } else {
      panel.classList.remove("show");
    }
  }

  function renderChatMessages() {
    const list = document.getElementById("chat-messages");
    if (!list) return;
    list.innerHTML = state.customChatMessages.map((m) => {
      const time = new Date(m.ts);
      const timeStr = time.getHours().toString().padStart(2, "0") + ":" + time.getMinutes().toString().padStart(2, "0");
      const nameHtml = `<span class="chat-msg-name">${esc(m.name)}</span>`;
      const textHtml = `<span class="chat-msg-text">${esc(m.text)}</span>`;
      const timeHtml = `<span class="chat-msg-time">${timeStr}</span>`;
      return `<div class="chat-msg">${nameHtml} ${textHtml} ${timeHtml}</div>`;
    }).join("");
    list.scrollTop = list.scrollHeight;
  }

  // Label for the lobby's Chat link. The canvas HUD Chat button is hidden behind
  // the lobby overlay, so between rounds this is the only unread indicator.
  function lobbyChatLabel() {
    return state.customChatUnread > 0
      ? `Chat (${Math.min(state.customChatUnread, 99)})`
      : "Chat";
  }

  function updateChatBadge() {
    // Keep the lobby link's unread count live without re-rendering the lobby.
    const lobbyChatEl = document.getElementById("custom-lobby-chat");
    if (lobbyChatEl) lobbyChatEl.textContent = lobbyChatLabel();
    const badge = document.getElementById("chat-badge");
    if (!badge) return;
    if (state.customChatUnread > 0) {
      badge.textContent = state.customChatUnread > 9 ? "9+" : String(state.customChatUnread);
      badge.style.display = "";
    } else {
      badge.style.display = "none";
    }
  }

  function closeChatPanel() {
    state.customChatOpen = false;
    state.customChatMessages = [];
    state.customChatUnread = 0;
    const panel = document.getElementById("chat-panel");
    if (panel) panel.classList.remove("show");
  }

  function customMessageMatchesCurrentRound(data) {
    if (!data || typeof data !== "object") return false;
    if (data.roomCode !== undefined) {
      const roomCode = sanitizeRoomCode(String(data.roomCode || ""));
      if (!roomCode || roomCode !== sanitizeRoomCode(state.customRoomCode || "")) return false;
    }
    // Messages from a client opened before this fix have no round number. Keep
    // those rooms interoperable; their solve grid is still verified below.
    if (data.roundNumber !== undefined) {
      const roundNumber = Number(data.roundNumber);
      if (!Number.isInteger(roundNumber) || roundNumber !== state.customRoundNumber) return false;
    }
    return true;
  }

  function isStaleCustomRoundUpdate(data) {
    if (!data || data.roundNumber === undefined) return false;
    const roundNumber = Number(data.roundNumber);
    return Number.isInteger(roundNumber) && roundNumber < state.customRoundNumber;
  }

  function handlePlayerMove(data) {
    if (state.customPhase !== "playing" || !customMessageMatchesCurrentRound(data)) return;
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    if (!playerId || playerId === state.customPlayerId) return;
    const player = state.customPlayers[playerId];
    if (!player || player.isObserver) return;
    if (Number.isFinite(data.moves)) player.moves = Math.max(0, Math.min(9999, Math.floor(data.moves)));
    if (isValidGridString(data.grid)) player.grid = data.grid;
    player.lastSeen = Date.now();
    scheduleObserverRepaint();
  }

  function handlePlayerSolve(data) {
    if (state.customPhase !== "playing" || !customMessageMatchesCurrentRound(data)) return;
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    if (!playerId || playerId === state.customPlayerId) return;
    const player = state.customPlayers[playerId];
    if (!player || player.isObserver || player.solved) return;
    if (!checkCenterRow(data.grid, state.targetWord || state.customWord)) return;
    if (Number.isFinite(data.moves)) player.moves = Math.max(0, Math.min(9999, Math.floor(data.moves)));
    player.grid = data.grid;
    player.solved = true;
    player.lastSeen = Date.now();
    state.customSolveCount++;
    player.finishOrder = state.customSolveCount;
    finishCustomRoundIfComplete();
    scheduleObserverRepaint();
  }

  // Observer requests to join next round
  function handleRequestJoin(data) {
    const playerId = sanitizePlayerId(data && data.id ? String(data.id) : "");
    if (!state.customIsHost || !playerId) return;
    const existing = state.customPendingJoins.find((j) => j.id === playerId);
    if (existing) return; // already requested
    const player = state.customPlayers[playerId];
    if (!player) return;
    state.customPendingJoins.push({ id: playerId, name: sanitizeName(player.name || "") || "Player", approved: false });
    playJoinSound();
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  // Host approves a pending join request
  function approveJoinRequest(playerId) {
    if (!state.customIsHost) return;
    let pending = state.customPendingJoins.find((j) => j.id === playerId);
    if (!pending) {
      const player = state.customPlayers[playerId];
      if (!player) return;
      pending = { id: playerId, name: sanitizeName(player.name || "") || "Player", approved: false };
      state.customPendingJoins.push(pending);
    }
    logEvent("custom_host_join_approve", {
      referrer: buildModerationReferrer("join_approve", {
        target: pending.name || "Player",
      }),
    });
    pending.approved = true;
    setCustomLobbyStatus(`${pending.name || "Player"} approved for next round.`, "success");
    // Notify the specific player
    if (_customChannel) {
      _customChannel.send({ type: "broadcast", event: "join_approved", payload: { targetId: playerId } });
    }
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  function denyJoinRequest(playerId, reason = "Not approved this round") {
    if (!state.customIsHost) return;
    const pending = state.customPendingJoins.find((j) => j.id === playerId);
    const before = state.customPendingJoins.length;
    state.customPendingJoins = state.customPendingJoins.filter((j) => j.id !== playerId);
    if (before === state.customPendingJoins.length) return;
    setCustomLobbyStatus(`${(pending && pending.name) || "Player"} denied.`, "warn");
    logEvent("custom_host_join_deny", {
      referrer: buildModerationReferrer("join_deny", {
        target: (pending && pending.name) || "Player",
        reason,
      }),
    });
    if (_customChannel) {
      _customChannel.send({ type: "broadcast", event: "join_denied", payload: { targetId: playerId, reason } });
    }
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  // Host approves all pending join requests
  function approveAllJoinRequests() {
    if (!state.customIsHost) return;
    let approvedCount = 0;
    for (const pending of state.customPendingJoins) {
      if (pending.approved) continue;
      approvedCount += 1;
      pending.approved = true;
      if (_customChannel) {
        _customChannel.send({ type: "broadcast", event: "join_approved", payload: { targetId: pending.id } });
      }
    }
    if (approvedCount > 0) {
      logEvent("custom_host_join_approve", {
        referrer: buildModerationReferrer("join_approve_all", { count: approvedCount }),
      });
    }
    broadcastPlayerList();
    renderLobbyPlayerList();
  }

  function denyAllJoinRequests(reason = "Lobby closed for this round") {
    if (!state.customIsHost) return;
    const denyIds = state.customPendingJoins
      .filter((pending) => !pending.approved)
      .map((pending) => pending.id);
    for (const playerId of denyIds) {
      denyJoinRequest(playerId, reason);
    }
  }

  function toggleCustomRoomLock(forceValue = null) {
    if (!state.customIsHost) return;
    if (typeof forceValue === "boolean") state.customRoomLocked = forceValue;
    else state.customRoomLocked = !state.customRoomLocked;
    if (state.customRoomLocked) {
      logEvent("custom_room_lock_on", {
        referrer: buildModerationReferrer("room_lock_on"),
      });
    } else {
      logEvent("custom_room_lock_off", {
        referrer: buildModerationReferrer("room_lock_off"),
      });
    }
    broadcastPlayerList();
    setCustomLobbyStatus(state.customRoomLocked ? "Room is locked." : "Room is unlocked.", "muted");
    setShareToast(state.customRoomLocked ? "Room locked" : "Room unlocked", 1800);
  }

  function hostKickPlayer(playerId, reason = "You were removed by the host") {
    if (!state.customIsHost) return false;
    const targetId = sanitizePlayerId(playerId || "");
    if (!targetId) return false;
    const player = state.customPlayers[targetId];
    if (!player || player.isHost) return false;
    logEvent("custom_host_player_kick", {
      referrer: buildModerationReferrer("kick_player", {
        target: player.name || "Player",
        reason,
      }),
    });
    const now = Date.now();
    const pendingSnapshot = state.customPendingJoins
      .filter((j) => j.id === targetId)
      .map((j) => ({ id: j.id, name: sanitizeName(j.name || "") || "Player", approved: !!j.approved }));
    state.customLastKick = {
      player: {
        id: player.id,
        name: sanitizeName(player.name || "") || "Player",
        moves: Number.isFinite(player.moves) ? player.moves : 0,
        solved: !!player.solved,
        grid: typeof player.grid === "string" ? player.grid : "",
        finishOrder: Number.isFinite(player.finishOrder) ? player.finishOrder : 0,
        wins: normalizeWinLoss(player.wins),
        losses: normalizeWinLoss(player.losses),
        isHost: false,
        isObserver: !!player.isObserver,
      },
      pendingJoins: pendingSnapshot,
      kickedAt: now,
      expiresAt: now + 12000,
    };
    delete state.customPlayers[targetId];
    state.customPendingJoins = state.customPendingJoins.filter((j) => j.id !== targetId);
    if (_customChannel) {
      _customChannel.send({ type: "broadcast", event: "player_kicked", payload: { targetId, reason } });
    }
    if (state.customPhase === "playing") {
      finishCustomRoundIfComplete();
    }
    broadcastPlayerList();
    renderLobbyPlayerList();
    return true;
  }

  function undoLastKick() {
    if (!state.customIsHost || !state.customLastKick) return false;
    const kick = state.customLastKick;
    if (!kick.player || !kick.player.id || Date.now() > kick.expiresAt) {
      state.customLastKick = null;
      return false;
    }
    if (state.customPlayers[kick.player.id]) {
      state.customLastKick = null;
      return false;
    }
    const restored = {
      id: kick.player.id,
      name: sanitizeName(kick.player.name || "") || "Player",
      moves: Number.isFinite(kick.player.moves) ? kick.player.moves : 0,
      solved: !!kick.player.solved,
      grid: typeof kick.player.grid === "string" ? kick.player.grid : "",
      finishOrder: Number.isFinite(kick.player.finishOrder) ? kick.player.finishOrder : 0,
      wins: normalizeWinLoss(kick.player.wins),
      losses: normalizeWinLoss(kick.player.losses),
      isHost: false,
      isObserver: !!kick.player.isObserver,
    };
    // Mid-round restores rejoin as observers to avoid changing active race state.
    if (state.customPhase === "playing") {
      restored.moves = 0;
      restored.solved = false;
      restored.grid = "";
      restored.finishOrder = 0;
      restored.isObserver = true;
    }
    state.customPlayers[restored.id] = restored;
    const pending = Array.isArray(kick.pendingJoins) ? kick.pendingJoins : [];
    for (const item of pending) {
      if (!item || !item.id) continue;
      if (state.customPendingJoins.some((j) => j.id === item.id)) continue;
      state.customPendingJoins.push({ id: item.id, name: sanitizeName(item.name || "") || "Player", approved: !!item.approved });
    }
    state.customLastKick = null;
    if (_customChannel) {
      _customChannel.send({ type: "broadcast", event: "player_kick_undo", payload: { targetId: restored.id } });
    }
    logEvent("custom_host_kick_undo", {
      referrer: buildModerationReferrer("kick_undo", {
        target: restored.name || "Player",
      }),
    });
    broadcastPlayerList();
    renderLobbyPlayerList();
    return true;
  }

  function startGameAsHost() {
    if (!state.customIsHost) return false;
    if (!_customChannel) {
      setShareToast("Still connecting to room...", 1800);
      return false;
    }
    if (state.customClassroomMode && !hasPlayableClassroomParticipant()) {
      setShareToast("Approve at least one observer first", 2200);
      return false;
    }
    logEvent("custom_round_start_host", {}, `${state.customRoomCode}_${state.customRoundNumber}_round_start`);
    state.customLastKick = null;
    state.customPhase = "playing";
    state.customSolveCount = 0;
    const approvedIds = new Set(state.customPendingJoins.filter((j) => j.approved).map((j) => j.id));
    // Reset all players for this round
    for (const p of Object.values(state.customPlayers)) {
      ensurePlayerRecordShape(p);
      if (p.isHost && state.customObserveMode) {
        // Host stays as observer in classroom mode
        p.moves = 0;
        p.solved = false;
        p.grid = "";
        p.finishOrder = 0;
        p.isObserver = true;
      } else if (state.customClassroomMode) {
        p.moves = 0;
        p.solved = false;
        p.grid = "";
        p.finishOrder = 0;
        if (p.isObserver && !approvedIds.has(p.id)) p.isObserver = true;
        else p.isObserver = false;
      } else {
        p.moves = 0;
        p.solved = false;
        p.grid = "";
        p.finishOrder = 0;
        p.isObserver = false;
      }
    }
    if (state.customClassroomMode) {
      state.customPendingJoins = state.customPendingJoins.filter((j) => !j.approved);
    }
    const puzzle = generateCustomPuzzle(state.customRoomCode, state.customDifficulty, state.customRoundNumber);
    state.customStartState = puzzle.startState;
    state.customWord = puzzle.word;
    // Broadcast countdown first, then game_start after 3s
    _customChannel.send({ type: "broadcast", event: "game_countdown", payload: {
      roomCode: state.customRoomCode,
      difficulty: state.customDifficulty,
      roundNumber: state.customRoundNumber,
      startState: puzzle.startState,
      word: puzzle.word,
      seconds: 3,
      seriesBestOf: state.customSeriesBestOf,
      seriesComplete: state.customSeriesComplete,
      seriesWinnerId: state.customSeriesWinnerId,
    }});
    broadcastPlayerList();
    closeCustomGameOverlay();
    state.customCountdownUntil = performance.now() + 3000;
    state.customCountdownPuzzle = puzzle;
    scheduleRenderLoop();
    _countdownTimeout = setTimeout(() => {
      _countdownTimeout = null;
      if (!state.customMultiplayer || !_customChannel) return;
      _customChannel.send({ type: "broadcast", event: "game_start", payload: {
        roomCode: state.customRoomCode,
        difficulty: state.customDifficulty,
        roundNumber: state.customRoundNumber,
        startState: puzzle.startState,
        word: puzzle.word,
        seriesBestOf: state.customSeriesBestOf,
        seriesComplete: state.customSeriesComplete,
        seriesWinnerId: state.customSeriesWinnerId,
      }});
      state.customCountdownUntil = 0;
      startCustomPuzzle(state.customRoomCode, state.customDifficulty, puzzle, true);
      // Start any bot opponents
      for (const p of Object.values(state.customPlayers)) {
        if (p.isBot && !p.isObserver) startBotOpponent(p.id);
      }
    }, 3000);
    return true;
  }

  function handleNextRound() {
    if (!state.customIsHost || !_customChannel) return;
    if (state.customSeriesComplete) {
      for (const player of Object.values(state.customPlayers)) {
        ensurePlayerRecordShape(player);
        player.wins = 0;
        player.losses = 0;
      }
      state.customRoundNumber = 0;
      state.customRoundOutcomeKey = "";
      state.customSeriesComplete = false;
      state.customSeriesWinnerId = "";
    }
    logEvent("custom_round_start_host", {}, `${state.customRoomCode}_${state.customRoundNumber + 1}_round_start`);
    state.customLastKick = null;
    const roomCode = state.customRoomCode;
    const diff = state.customNextDifficulty || state.customDifficulty;
    state.customDifficulty = diff;
    state.customNextDifficulty = null;
    state.customRoundNumber++;
    state.customPhase = "playing";
    state.customSolveCount = 0;
    // Promote observers: classroom mode requires approval, default auto-promotes all
    const approvedIds = new Set(state.customPendingJoins.filter((j) => j.approved).map((j) => j.id));
    // Reset all players
    for (const p of Object.values(state.customPlayers)) {
      ensurePlayerRecordShape(p);
      p.moves = 0;
      p.solved = false;
      p.grid = "";
      p.finishOrder = 0;
      if (p.isHost && state.customObserveMode) {
        // Host stays as observer in classroom mode
        p.isObserver = true;
      } else if (state.customClassroomMode) {
        // Classroom mode: only approved observers get promoted
        if (p.isObserver && !approvedIds.has(p.id)) {
          p.isObserver = true;
        } else {
          p.isObserver = false;
        }
      } else {
        // Default: all observers auto-promote to players
        p.isObserver = false;
      }
    }
    state.customPendingJoins = state.customClassroomMode
      ? state.customPendingJoins.filter((j) => !j.approved)
      : [];
    const puzzle = generateCustomPuzzle(roomCode, diff, state.customRoundNumber);
    state.customStartState = puzzle.startState;
    state.customWord = puzzle.word;
    // Countdown then start
    _customChannel.send({ type: "broadcast", event: "game_countdown", payload: {
      roomCode, difficulty: diff, roundNumber: state.customRoundNumber,
      startState: puzzle.startState, word: puzzle.word, seconds: 3,
      seriesBestOf: state.customSeriesBestOf,
      seriesComplete: state.customSeriesComplete,
      seriesWinnerId: state.customSeriesWinnerId,
    }});
    broadcastPlayerList();
    state.customCountdownUntil = performance.now() + 3000;
    state.customCountdownPuzzle = puzzle;
    state.mode = "play"; // exit results screen for countdown
    scheduleRenderLoop();
    _countdownTimeout = setTimeout(() => {
      _countdownTimeout = null;
      if (!state.customMultiplayer || !_customChannel) return;
      _customChannel.send({ type: "broadcast", event: "next_round", payload: {
        roomCode, difficulty: diff, roundNumber: state.customRoundNumber,
        startState: puzzle.startState, word: puzzle.word,
        seriesBestOf: state.customSeriesBestOf,
        seriesComplete: state.customSeriesComplete,
        seriesWinnerId: state.customSeriesWinnerId,
      }});
      state.customCountdownUntil = 0;
      startCustomPuzzle(roomCode, diff, puzzle, true);
    }, 3000);
  }

  function playJoinSound() {
    if (_soundMuted) return;
    const ac = ensureAudioCtx();
    if (!ac) return;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "sine";
    osc.frequency.value = 660;
    gain.gain.setValueAtTime(0.28, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.15);
    osc.connect(gain).connect(_masterGain);
    osc.start();
    osc.stop(ac.currentTime + 0.15);
  }

  // QR Code generator (minimal implementation for room links)
  function renderQRCode(container, url) {
    // Use a QR code canvas approach - encode URL into a QR image via a small inline generator
    // For simplicity, render an img tag pointing to a public QR API
    container.innerHTML = `<img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}" alt="QR Code" style="width:200px;height:200px;image-rendering:pixelated;border:8px solid white;display:block;margin:0 auto" />`;
  }

  // Prefer the iOS wrapper's native share sheet for room invites. Async
  // clipboard writes are unreliable inside WKWebView, and an invite that
  // silently fails is the difference between a race and a dead lobby.
  function shareCustomJoinLink(roomCode, difficulty, password, eventName = "custom_room_copy_link") {
    const joinUrl = buildCustomShareUrl(roomCode, difficulty);
    const seriesLine = state.customSeriesBestOf > 1 ? ` — best of ${state.customSeriesBestOf}` : "";
    const inviteText = `Race me in PIVOT${seriesLine}\n${joinUrl}${password ? `\nAccess code: ${password}` : ""}`;
    if (PIVOT_NATIVE && window.webkit?.messageHandlers?.nativeShare) {
      postNativeShare({
        title: "Race me in PIVOT",
        text: inviteText,
        url: joinUrl,
        eventName,
        eventData: { via: "native" },
      });
      return;
    }
    copyCustomJoinLink(roomCode, difficulty, password, eventName, { via: "clipboard" });
  }

  function copyCustomJoinLink(roomCode, difficulty, password, eventName = "custom_room_copy_link", eventData) {
    const joinUrl = buildCustomShareUrl(roomCode, difficulty);
    const inviteText = `${joinUrl}${password ? `\nAccess code: ${password}` : ""}`;
    logEvent(eventName, eventData);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(inviteText).then(() => {
        setShareToast("Room link copied!", 2000);
      }).catch(() => {
        openCopyOverlay(inviteText, "Room invite");
      });
      return;
    }
    openCopyOverlay(inviteText, "Room invite");
  }

  function normalizeCustomDifficulty(value) {
    const diff = String(value || "medium").toLowerCase();
    if (diff === "easy" || diff === "medium" || diff === "hard" || diff === "expert") return diff;
    return "medium";
  }

  function normalizeCustomSeriesBestOf(value) {
    const count = Number(value);
    return count === 3 || count === 5 || count === 7 ? count : 1;
  }

  function applyCustomSeriesPayload(data) {
    if (!data || typeof data !== "object") return;
    const bestOf = normalizeCustomSeriesBestOf(data.seriesBestOf);
    state.customSeriesBestOf = bestOf;
    state.customSeriesWinsNeeded = Math.floor(bestOf / 2) + 1;
    state.customSeriesComplete = !!data.seriesComplete;
    const winnerId = String(data.seriesWinnerId || "");
    state.customSeriesWinnerId = winnerId === "tie" ? "tie" : sanitizePlayerId(winnerId);
  }

  // --- One-time What's New banner -------------------------------------------
  // This is tied to the newest What's New timestamp, so a future entry can
  // reuse the same calm, one-time notice without inventing a new storage key.
  // Showing the banner and reading the full list are deliberately separate:
  // dismissing this notice must not clear the unread dot in More.
  const UPDATES_BANNER_TEXT = "New in PIVOT: clearer Friend Circles, reliable challenge links, and honest daily counts.";
  const UPDATES_BANNER_CTA = "See updates";

  // Shared bottom-banner builder. Used by What's New and the post-race
  // feedback prompt so they look and behave identically.
  // Every bottom banner sits at `position:fixed; bottom:0`, so two visible at
  // once overlap rather than stack. Only one may exist at a time.
  const BOTTOM_BANNER_IDS = ["updates-banner", "race-feedback-banner", "install-banner"];

  function bottomBannerVisible() {
    return BOTTOM_BANNER_IDS.some((id) => document.getElementById(id));
  }

  function showBottomBanner({ id, text, ctaLabel, ctaAriaLabel, onCta, onDismiss }) {
    if (bottomBannerVisible()) return null;
    const banner = document.createElement("div");
    banner.id = id;
    banner.setAttribute("role", "status");
    banner.style.cssText = `position:fixed;bottom:0;left:0;right:0;background:${C.bg};border-top:1px solid ${C.panelStroke};padding:12px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;z-index:9998;font-family:Georgia,'Times New Roman',serif;font-size:13px;line-height:1.35;color:${C.ink};box-shadow:0 -2px 8px ${_currentTheme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.06)"};padding-bottom:calc(12px + env(safe-area-inset-bottom));`;

    const msg = document.createElement("span");
    msg.textContent = text;
    msg.style.cssText = "min-width:0";

    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;gap:8px;flex-shrink:0";

    const cta = document.createElement("button");
    cta.type = "button";
    cta.textContent = ctaLabel;
    if (ctaAriaLabel) cta.setAttribute("aria-label", ctaAriaLabel);
    cta.style.cssText = `font-family:inherit;font-size:12px;min-height:44px;padding:6px 14px;background:${C.btnPrimaryBg};color:${C.btnPrimaryText};border:none;border-radius:2px;cursor:pointer`;
    cta.addEventListener("click", () => { if (onCta) onCta(banner); });

    const close = document.createElement("button");
    close.type = "button";
    close.textContent = "×";
    close.setAttribute("aria-label", "Dismiss");
    close.style.cssText = `font-family:inherit;font-size:12px;min-width:44px;min-height:44px;padding:6px 10px;background:none;border:1px solid ${C.btnStroke};border-radius:2px;cursor:pointer;color:${C.muted}`;
    close.addEventListener("click", () => { if (onDismiss) onDismiss(banner); });

    actions.appendChild(cta);
    actions.appendChild(close);
    banner.appendChild(msg);
    banner.appendChild(actions);
    document.body.appendChild(banner);
    return banner;
  }

  function getNewestUpdatesTs() {
    if (!Array.isArray(UPDATES_ENTRIES) || !UPDATES_ENTRIES.length) return 0;
    return UPDATES_ENTRIES.reduce((max, entry) => Math.max(max, Number(entry.ts) || 0), 0);
  }

  function showUpdatesBanner() {
    if (growth?.followupShown() || state.mode === "win") return;
    const newestTs = getNewestUpdatesTs();
    const lastShownTs = Number(safeGetItem(UPDATES_BANNER_LAST_SHOWN_KEY)) || 0;
    if (!newestTs || newestTs <= lastShownTs || !hasUnseenUpdates()) return;
    // Social deep links, tutorials, overlays, and an active puzzle already give
    // the player a clear primary task. The update notice can wait for a calmer
    // visit instead of competing for attention or covering game controls.
    if (_isCustomPage || state.customMode || state.showTutorial || _challengeLandingEl) return;
    if (hasOpenOverlayForKeyboard() || document.hidden) return;
    if (state.mode !== "menu" && !state.solvedToday) return;

    const banner = showBottomBanner({
      id: "updates-banner",
      text: UPDATES_BANNER_TEXT,
      ctaLabel: UPDATES_BANNER_CTA,
      ctaAriaLabel: "See what’s new in PIVOT",
      onCta: (el) => {
        logUiEvent("updates_banner_open", { newest_ts: newestTs });
        el.remove();
        openUpdatesOverlay();
      },
      onDismiss: (el) => {
        logUiEvent("updates_banner_dismiss", { newest_ts: newestTs });
        el.remove();
      },
    });
    if (!banner) return;

    // Burn the one-time marker only after the notice is actually on screen.
    // The What's New unread marker remains until the full list is opened.
    safeSetItem(UPDATES_BANNER_LAST_SHOWN_KEY, String(newestTs));
    logUiEvent("updates_banner_shown", { newest_ts: newestTs });
    setTimeout(() => {
      if (!banner.isConnected) return;
      banner.remove();
      logUiEvent("updates_banner_timeout", { newest_ts: newestTs });
    }, UPDATES_BANNER_AUTO_DISMISS_MS);
  }

  // --- Post-race feedback prompt ---------------------------------------------
  // Asked once, after a player's FIRST completed Live Race round, while the
  // experience is fresh. Live Race is the part of the product we have the least
  // visibility into — telemetry says whether people reached it, not whether it
  // actually worked for them.
  const RACE_FEEDBACK_KEY = "pivot_race_feedback_prompted_v1";

  function maybeShowRaceFeedbackPrompt() {
    if (!state.customMultiplayer) return;
    if (safeGetItem(RACE_FEEDBACK_KEY)) return;
    if (document.getElementById("race-feedback-banner")) return;

    const banner = showBottomBanner({
      id: "race-feedback-banner",
      text: "How was Live Race? It's brand new — tell us what broke or felt off.",
      ctaLabel: "Tell us",
      onCta: (el) => {
        logEvent("race_feedback_accepted");
        el.remove();
        _feedbackSource = "live_race";
        openFeedbackOverlay();
      },
      onDismiss: (el) => {
        logEvent("race_feedback_dismissed");
        el.remove();
      },
    });
    // Only burn the one-time flag once the banner is actually on screen.
    // showBottomBanner returns null when another bottom banner holds the slot,
    // and consuming the flag before knowing that meant a player who happened to
    // have the install banner up when they finished their first race would
    // never be asked for feedback at all.
    if (banner) {
      safeSetItem(RACE_FEEDBACK_KEY, "1");
      logEvent("race_feedback_prompt_shown");
    }
  }

  function getCustomBasePath() {
    try {
      const base = new URL(window.location.href);
      base.hash = "";
      base.search = "";
      const path = base.pathname || "/";
      const match = path.match(/^(.*?\/custom)\/?(?:.*)$/i);
      if (match && match[1]) {
        base.pathname = `${match[1]}/`;
      } else {
        base.pathname = new URL("custom/", window.location.href).pathname;
      }
      return base;
    } catch {
      return new URL(`${CANONICAL_URL}/custom/`);
    }
  }

  function buildCustomJoinUrl(roomCode, difficulty, password) {
    const url = getCustomBasePath();
    const normalizedCode = sanitizeRoomCode(roomCode || "");
    if (normalizedCode) url.searchParams.set("code", normalizedCode);
    url.searchParams.set("diff", normalizeCustomDifficulty(difficulty));
    return url.toString();
  }

  function buildCustomShareUrl(roomCode, difficulty) {
    const url = new URL(`${CANONICAL_URL}/race/`);
    const normalizedCode = sanitizeRoomCode(roomCode || "");
    if (normalizedCode) url.searchParams.set("code", normalizedCode);
    url.searchParams.set("diff", normalizeCustomDifficulty(difficulty));
    return url.toString();
  }

  function syncCustomRoomUrl(roomCode, difficulty, password) {
    if (!_isCustomPage) return;
    if (!state.customMode || !state.customMultiplayer) {
      const base = getCustomBasePath();
      const target = `${base.pathname}${base.search}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (target !== current) history.replaceState(null, "", target);
      return;
    }
    const targetUrl = new URL(buildCustomJoinUrl(roomCode, difficulty, password));
    const target = `${targetUrl.pathname}${targetUrl.search}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (target !== current) history.replaceState(null, "", target);
  }

  let _classroomQrPanel = null;
  let _classroomQrKey = "";

  function ensureClassroomQrPanel() {
    if (_classroomQrPanel) return _classroomQrPanel;
    const panel = document.createElement("div");
    panel.className = "classroom-qr-panel";
    panel.innerHTML = `
      <div class="classroom-qr-kicker">Classroom Join</div>
      <div class="classroom-qr-room"></div>
      <img class="classroom-qr-image" alt="Room QR code" />
      <div class="classroom-qr-link"></div>
    `;
    document.body.appendChild(panel);
    _classroomQrPanel = panel;
    return panel;
  }

  function updateClassroomQrPanel() {
    const shouldShow = !!(
      state.customMode &&
      state.customMultiplayer &&
      state.customIsHost &&
      state.customClassroomMode &&
      state.mode === "play" &&
      state.customRoomCode
    );
    if (!shouldShow) {
      if (_classroomQrPanel) _classroomQrPanel.classList.remove("show");
      _classroomQrKey = "";
      return;
    }

    const panel = ensureClassroomQrPanel();
    const joinUrl = buildCustomShareUrl(state.customRoomCode, state.customDifficulty);
    const qrKey = `${state.customRoomCode}|${state.customDifficulty}|${state.customPassword || ""}`;
    if (_classroomQrKey !== qrKey) {
      const roomEl = panel.querySelector(".classroom-qr-room");
      const imageEl = panel.querySelector(".classroom-qr-image");
      const linkEl = panel.querySelector(".classroom-qr-link");
      if (roomEl) roomEl.textContent = state.customRoomCode;
      if (imageEl) {
        imageEl.src = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(joinUrl)}`;
      }
      if (linkEl) linkEl.textContent = "Scan or visit /race/";
      _classroomQrKey = qrKey;
    }

    const mobile = !!layout.panelBelow;
    panel.classList.toggle("mobile", mobile);
    panel.classList.toggle("docked", !mobile);
    if (mobile) {
      panel.style.left = "";
      panel.style.top = "";
      panel.style.right = "8px";
      panel.style.bottom = "8px";
    } else if (canvas && typeof canvas.getBoundingClientRect === "function") {
      const rect = canvas.getBoundingClientRect();
      const panelW = panel.offsetWidth || 148;
      const panelH = panel.offsetHeight || 190;
      const gap = 10;
      let left = Math.round(rect.right + gap);
      let top = Math.round(rect.top + 26);
      if (left + panelW > window.innerWidth - 8) {
        left = Math.round(rect.right - panelW - 8);
      }
      if (top + panelH > window.innerHeight - 8) {
        top = Math.max(8, Math.round(window.innerHeight - panelH - 8));
      }
      left = Math.max(8, left);
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.right = "auto";
      panel.style.bottom = "auto";
    }
    panel.classList.add("show");
  }

  function generatePlayerId() {
    return (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `pid_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  }

  // Reuse this tab's player id when rejoining the same room. A fresh id on every
  // join makes the host add a SECOND row for the same person on any reload, and
  // nothing ever prunes the old one — so reloads leave ghost players behind.
  // sessionStorage is per-tab, so two tabs on one device stay two players.
  function playerIdForRoom(roomCode) {
    try {
      const raw = sessionStorage.getItem("pivot_custom_session");
      if (raw) {
        const session = JSON.parse(raw);
        const sameRoom = sanitizeRoomCode(session.roomCode || "") === sanitizeRoomCode(roomCode || "");
        const id = sanitizePlayerId(String(session.playerId || ""));
        if (sameRoom && !session.isHost && id) return id;
      }
    } catch {}
    return generatePlayerId();
  }

  function broadcastPlayerList() {
    if (!_customChannel || !state.customIsHost) return;
    const payload = {
      players: state.customPlayers,
      phase: state.customPhase,
      difficulty: state.customDifficulty,
      roundNumber: state.customRoundNumber,
      nextDifficulty: state.customNextDifficulty,
      roomCode: state.customRoomCode,
      pendingJoins: state.customPendingJoins,
      classroomMode: !!state.customClassroomMode,
      roomLocked: !!state.customRoomLocked,
      seriesBestOf: normalizeCustomSeriesBestOf(state.customSeriesBestOf),
      seriesComplete: !!state.customSeriesComplete,
      seriesWinnerId: state.customSeriesWinnerId || "",
    };
    // Include puzzle data for late joiners
    if (state.customPhase === "playing" && state.customStartState) {
      payload.startState = state.customStartState;
      payload.word = state.customWord;
    }
    _customChannel.send({ type: "broadcast", event: "player_list", payload });
  }

  function broadcastMove() {
    if (!_customChannel || !state.customMultiplayer) return;
    _customChannel.send({ type: "broadcast", event: "move", payload: {
      id: state.customPlayerId,
      roomCode: state.customRoomCode,
      roundNumber: state.customRoundNumber,
      moves: state.moves,
      grid: gridToString(state.grid),
    } });
  }

  function broadcastSolve() {
    if (!_customChannel || !state.customMultiplayer) return;
    _customChannel.send({ type: "broadcast", event: "solve", payload: {
      id: state.customPlayerId,
      roomCode: state.customRoomCode,
      roundNumber: state.customRoundNumber,
      moves: state.moves,
      grid: gridToString(state.grid),
    } });
    // Ask for feedback once, after the celebration has had a moment to land.
    setTimeout(maybeShowRaceFeedbackPrompt, 3500);
  }

  function joinCustomRoom(roomCode, difficulty, prefillPassword) {
    if (!supabase) { setShareToast("Multiplayer unavailable", 2000); return; }
    const normalizedRoomCode = sanitizeRoomCode(roomCode || "");
    if (!normalizedRoomCode) {
      setShareToast("Invalid room code", 2200);
      return;
    }
    const normalizedDifficulty = normalizeCustomDifficulty(difficulty);
    logEvent("custom_room_join_attempt");
    const overlay = document.getElementById("custom-game-overlay");
    if (!overlay) return;
    const content = document.getElementById("custom-game-content");
    if (!content) return;
    const diffLabel = normalizedDifficulty.charAt(0).toUpperCase() + normalizedDifficulty.slice(1);
    const hasPrefilledPass = !!prefillPassword;
    content.innerHTML = `
      <div class="custom-waiting">
        <div style="font-size:11px;color:#999;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:2px">Custom game</div>
        <div class="custom-room-code">${normalizedRoomCode}</div>
        <div style="font-size:13px;color:#777;margin:4px 0 12px">Difficulty: <strong>${diffLabel}</strong></div>
          <input class="custom-name-input" id="custom-join-name" type="text" maxlength="16" placeholder="Your name" value="${esc(sanitizeName(state.playerName || ""))}" />
        ${hasPrefilledPass ? "" : `<input class="custom-name-input" id="custom-join-password" type="text" maxlength="20" placeholder="Access code (if required)" style="margin-top:6px" />`}
        <nav class="custom-actions-nav">
          <a href="#" id="custom-join-btn" class="custom-action-link custom-action-gold">Join Game</a>
          <span class="custom-sep">\u00b7</span>
          <a href="#" id="custom-join-cancel" class="custom-action-link custom-action-muted">Dismiss</a>
        </nav>
      </div>
    `;
    overlay.classList.add("show");
    overlay.setAttribute("aria-hidden", "false");

    document.getElementById("custom-join-btn")?.addEventListener("click", (e) => { e.preventDefault();
      const nameEl = document.getElementById("custom-join-name");
      const name = sanitizeName(nameEl ? nameEl.value : "");
      if (!name) { setShareToast("Enter your name first", 2000); return; }
      const passEl = document.getElementById("custom-join-password");
      const password = hasPrefilledPass ? prefillPassword : (passEl ? passEl.value.trim() : "");

      const playerId = playerIdForRoom(normalizedRoomCode);
      state.customPlayerName = name;
      state.customPassword = password;
      state.customPlayerId = playerId;
      state.customIsHost = false;
      state.customMultiplayer = true;
      state.customRoomCode = normalizedRoomCode;
      state.customDifficulty = normalizedDifficulty;
      state.customPhase = "lobby";
      state.customPlayers = {};
      state.customSolveCount = 0;
      state.customRoundNumber = 1;
      state.customNextDifficulty = null;
      state.customIsObserver = false;
      state.customObserveMode = false;
      state.customObserveFilter = "all";
      state.customRoomLocked = false;
      state.customLastKick = null;
      state.customRoundOutcomeKey = "";
      state.customSeriesBestOf = 1;
      state.customSeriesWinsNeeded = 1;
      state.customSeriesComplete = false;
      state.customSeriesWinnerId = "";
      syncCustomRoomUrl(normalizedRoomCode, normalizedDifficulty, password);

      const joinBtn = document.getElementById("custom-join-btn");
      if (joinBtn) { joinBtn.textContent = "Connecting\u2026"; joinBtn.style.pointerEvents = "none"; joinBtn.style.opacity = "0.5"; }

      _customChannelCode = normalizedRoomCode;
      _customChannel = supabase.channel(`custom-${normalizedRoomCode}`);
      _customChannel
        .on("broadcast", { event: "player_list" }, (payload) => {
          const data = payload.payload || payload;
          if (isStaleCustomRoundUpdate(data)) return;
          state.customPlayers = sanitizeCustomPlayersMap(data.players);
          const prevPhase = state.customPhase;
          state.customPhase = data.phase || state.customPhase;
          if (data.roomCode) state.customRoomCode = sanitizeRoomCode(data.roomCode) || state.customRoomCode;
          if (data.difficulty) state.customDifficulty = data.difficulty;
          if (data.roundNumber) state.customRoundNumber = data.roundNumber;
          state.customNextDifficulty = data.nextDifficulty || null;
          applyCustomSeriesPayload(data);
          // Sync pending joins list from host (for display purposes)
          if (data.pendingJoins) state.customPendingJoins = sanitizePendingJoins(data.pendingJoins);
          if (data.classroomMode !== undefined) state.customClassroomMode = data.classroomMode;
          if (data.roomLocked !== undefined) state.customRoomLocked = !!data.roomLocked;
          // Check if we're an observer
          const self = state.customPlayers[state.customPlayerId];
          if (self) {
            const wasObserver = state.customIsObserver;
            state.customIsObserver = !!self.isObserver;
            // If promoted from observer to player, ensure observe mode is off
            if (wasObserver && !state.customIsObserver) {
              state.customObserveMode = false;
            }
            // If became observer, ensure observe mode is on
            if (state.customIsObserver) {
              state.customObserveMode = true;
            }
          }
          const selfPending = state.customPendingJoins.find((j) => j.id === state.customPlayerId);
          if (selfPending && selfPending.approved) {
            state.customJoinApproved = true;
            state.customJoinRequested = false;
          } else if (selfPending && state.customIsObserver) {
            state.customJoinRequested = true;
            if (!state.customJoinApproved) state.customJoinApproved = false;
          } else if (!state.customIsObserver) {
            state.customJoinRequested = false;
            state.customJoinApproved = false;
          }
          syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
          resetWatchdog();
          // Late joiner: first player_list arrives with phase=playing → enter observation mode
          if (data.phase === "playing" && state.customIsObserver && state.mode !== "play") {
            closeCustomGameOverlay();
            // Use host-authoritative puzzle data if available
            const code = data.roomCode || normalizedRoomCode;
            let puzzle;
            if (data.startState && data.word) {
              puzzle = { word: data.word, startState: data.startState };
            } else {
              puzzle = generateCustomPuzzle(code, state.customDifficulty, state.customRoundNumber);
            }
            startCustomPuzzle(code, state.customDifficulty, puzzle, true);
            state.customIsObserver = true;
            state.customObserveMode = true;
          } else {
            renderLobbyPlayerList();
          }
          // If phase changed to results, trigger results view
          if (data.phase === "results" && state.customPhase === "results" && state.mode === "play") {
            state.mode = "win";
            state.winAnimStart = performance.now();
          }
          scheduleObserverRepaint();
        })
        .on("broadcast", { event: "join_rejected" }, (payload) => {
          const data = payload.payload || payload;
          if (data.targetId && data.targetId !== state.customPlayerId) return;
          logEvent("custom_room_join_rejected");
          setShareToast(data.reason || "Rejected", 2000);
          if (joinBtn) { joinBtn.textContent = "Join Game"; joinBtn.style.pointerEvents = ""; joinBtn.style.opacity = ""; }
          exitCustomMode();
        })
        .on("broadcast", { event: "game_countdown" }, (payload) => {
          const data = payload.payload || payload;
          applyCustomSeriesPayload(data);
          if (data.roomCode) state.customRoomCode = sanitizeRoomCode(data.roomCode) || state.customRoomCode;
          if (data.difficulty) state.customDifficulty = data.difficulty;
          if (data.roundNumber) state.customRoundNumber = data.roundNumber;
          closeCustomGameOverlay();
          state.customCountdownUntil = performance.now() + (data.seconds || 3) * 1000;
          // Store puzzle data for when countdown ends
          if (data.startState && data.word) {
            state.customCountdownPuzzle = { word: data.word, startState: data.startState };
          } else {
              state.customCountdownPuzzle = generateCustomPuzzle(data.roomCode || normalizedRoomCode, state.customDifficulty, state.customRoundNumber);
          }
          state.customCountdownRoomCode = data.roomCode || normalizedRoomCode;
          syncCustomRoomUrl(state.customRoomCode, state.customDifficulty, state.customPassword);
          scheduleRenderLoop();
        })
        .on("broadcast", { event: "game_start" }, (payload) => {
          const data = payload.payload || payload;
          applyCustomSeriesPayload(data);
          if (data.roomCode) state.customRoomCode = sanitizeRoomCode(data.roomCode) || state.customRoomCode;
          if (data.difficulty) state.customDifficulty = data.difficulty;
          if (data.roundNumber) state.customRoundNumber = data.roundNumber;
          state.customPhase = "playing";
          state.customCountdownUntil = 0;
          closeCustomGameOverlay();
          const code = data.roomCode || normalizedRoomCode;
          let puzzle;
          if (data.startState && data.word) {
            puzzle = { word: data.word, startState: data.startState };
          } else {
            puzzle = generateCustomPuzzle(code, state.customDifficulty, state.customRoundNumber);
          }
          startCustomPuzzle(code, state.customDifficulty, puzzle, true);
        })
        .on("broadcast", { event: "move" }, (payload) => handlePlayerMove(payload.payload || payload))
        .on("broadcast", { event: "solve" }, (payload) => handlePlayerSolve(payload.payload || payload))
        .on("broadcast", { event: "next_round" }, (payload) => {
          const data = payload.payload || payload;
          applyCustomSeriesPayload(data);
          const newCode = data.roomCode;
          const diff = data.difficulty || state.customDifficulty;
          if (!newCode) return;
          state.customDifficulty = diff;
          state.customRoundNumber = data.roundNumber || state.customRoundNumber + 1;
          state.customPhase = "playing";
          state.customSolveCount = 0;
          state.customJoinRequested = false;
          state.customJoinApproved = false;
          state.customCountdownUntil = 0;
          // Use host-authoritative puzzle data
          let puzzle;
          if (data.startState && data.word) {
            puzzle = { word: data.word, startState: data.startState };
          } else {
            puzzle = generateCustomPuzzle(newCode, diff, state.customRoundNumber);
          }
          startCustomPuzzle(newCode, diff, puzzle, true);
        })
        .on("broadcast", { event: "difficulty_change" }, (payload) => {
          const data = payload.payload || payload;
          if (data.difficulty) state.customNextDifficulty = data.difficulty;
        })
        .on("broadcast", { event: "join_approved" }, (payload) => {
          const data = payload.payload || payload;
          if (data.targetId === state.customPlayerId) {
            state.customJoinApproved = true;
            state.customJoinRequested = false;
            setShareToast("Approved! You'll play next round", 3000);
          }
        })
        .on("broadcast", { event: "join_denied" }, (payload) => {
          const data = payload.payload || payload;
          if (data.targetId === state.customPlayerId) {
            state.customJoinRequested = false;
            state.customJoinApproved = false;
            setShareToast(data.reason || "Join request denied", 2600);
          }
        })
        .on("broadcast", { event: "player_kicked" }, (payload) => {
          const data = payload.payload || payload;
          if (data.targetId === state.customPlayerId) {
            handleSelfKicked(data.reason || "You've been removed from the room");
          }
        })
        .on("broadcast", { event: "player_kick_undo" }, (payload) => {
          handleKickUndo(payload.payload || payload);
        })
        .on("broadcast", { event: "observe_applied" }, (payload) => {
          handleObserveApplied(payload.payload || payload);
        })
        .on("broadcast", { event: "chat" }, (payload) => handleChatMessage(payload.payload || payload))
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            logEvent("custom_room_join_success", {}, `${normalizedRoomCode}_${state.sessionId}_custom_room_join_success`);
            try { sessionStorage.setItem("pivot_custom_session", JSON.stringify({
              roomCode: normalizedRoomCode, difficulty: normalizedDifficulty, password, playerName: name, playerId, isHost: false, timestamp: Date.now()
            })); } catch {}
            // Send join request to host
            _customChannel.send({ type: "broadcast", event: "join_request", payload: { id: playerId, name, password } });
            // Show lobby (will be populated by player_list events from host)
            showLobbyScreen(normalizedRoomCode);
            resetWatchdog();
            startPlayerPing();
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            logEvent("custom_room_join_failed");
            setShareToast("Connection failed — try again", 3000);
            if (joinBtn) { joinBtn.textContent = "Join Game"; joinBtn.style.pointerEvents = ""; joinBtn.style.opacity = ""; }
          }
        });
    });
    document.getElementById("custom-join-cancel")?.addEventListener("click", (e) => {
      e.preventDefault();
      closeCustomGameOverlay();
    });
  }

  function parseRoomCode() {
    // Format: #custom/G7K3P/hard  or  ?code=G7K3P&diff=hard (legacy: ?room=...)
    // Also:   #create-custom/hard  → opens custom overlay with multiplayer pre-selected
    //         #custom-solo/easy    → starts solo custom game immediately
    const hash = window.location.hash;

    // Create custom room with pre-selected difficulty
    const createMatch = hash.match(/^#create-custom\/(\w+)$/i);
    if (createMatch) {
      history.replaceState(null, "", window.location.pathname);
      return { action: "create", difficulty: createMatch[1].toLowerCase() };
    }

    // Solo custom game
    const soloMatch = hash.match(/^#custom-solo\/(\w+)$/i);
    if (soloMatch) {
      history.replaceState(null, "", window.location.pathname);
      return { action: "solo", difficulty: soloMatch[1].toLowerCase() };
    }

    // Join custom room: #custom/G7K3P/hard  or  #custom/G7K3P/hard/password123
    const customMatch = hash.match(/^#custom\/([A-Z0-9]{3,5})\/?(\w+)?(?:\/(.+))?$/i);
    if (customMatch) {
      history.replaceState(null, "", window.location.pathname);
      return { code: customMatch[1].toUpperCase(), difficulty: customMatch[2] || "medium", password: customMatch[3] ? decodeURIComponent(customMatch[3]) : "" };
    }
    // Legacy query param format
    const params = new URLSearchParams(window.location.search);
    const room = params.get("code") || params.get("room");
    const diff = normalizeCustomDifficulty(params.get("diff") || "medium");
    const pass = params.get("pass") || "";
    const code = sanitizeRoomCode(room || "");
    if (!code) return null;
    history.replaceState(null, "", window.location.pathname);
    return { code, difficulty: diff, password: pass };
  }

  // Return session: saved when player joins a multiplayer room, cleared on exit
  function getReturnSession() {
    try {
      const raw = sessionStorage.getItem("pivot_custom_session");
      if (!raw) return null;
      const session = JSON.parse(raw);
      if (Date.now() - session.timestamp > 3 * 60 * 1000) {
        sessionStorage.removeItem("pivot_custom_session");
        return null;
      }
      if (session.isHost) {
        sessionStorage.removeItem("pivot_custom_session");
        return null;
      }
      return session;
    } catch { return null; }
  }

  function showReturnBanner(session, options = null) {
    const opts = options && typeof options === "object" ? options : {};
    const bannerId = String(opts.id || "pivot-return-banner");
    const message = String(opts.message || "You're in a multiplayer game");
    const actionLabel = String(opts.actionLabel || "Return");
    const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Math.max(2000, Number(opts.timeoutMs)) : 15000;
    const clearOnDismiss = opts.clearOnDismiss !== false;
    const existing = document.getElementById(bannerId);
    if (existing) existing.remove();
    const banner = document.createElement("div");
    banner.id = bannerId;
    banner.style.cssText = `position:fixed;top:0;left:0;right:0;z-index:9999;background:${_currentTheme === "dark" ? "rgba(212,175,55,0.15)" : "rgba(244,211,94,0.25)"};color:${C.ink};display:flex;align-items:center;justify-content:center;gap:12px;padding:10px 16px;font-family:Georgia,'Times New Roman',serif;font-size:14px;border-bottom:1px solid ${_currentTheme === "dark" ? "rgba(212,175,55,0.3)" : "rgba(244,211,94,0.5)"};`;
    banner.innerHTML = `<span>${message}</span><a href="#" id="pivot-return-link" style="color:${C.ink};text-decoration:underline;font-weight:bold">${actionLabel}</a><button style="background:none;border:none;color:${C.mutedSoft};font-size:18px;cursor:pointer;padding:0 4px;font-family:inherit" aria-label="Dismiss">\u00d7</button>`;
    const returnLink = banner.querySelector("#pivot-return-link");
    const dismissBtn = banner.querySelector("button");
    if (returnLink) {
      returnLink.addEventListener("click", (e) => {
        e.preventDefault();
        banner.remove();
        try { sessionStorage.removeItem("pivot_custom_session"); } catch {}
        if (typeof opts.onAction === "function") {
          opts.onAction(session);
          return;
        }
        joinCustomRoom(session.roomCode, session.difficulty || "medium", session.password || "");
      });
    }
    if (dismissBtn) {
      dismissBtn.addEventListener("click", () => {
        if (clearOnDismiss) {
          try { sessionStorage.removeItem("pivot_custom_session"); } catch {}
        }
        banner.remove();
      });
    }
    document.body.appendChild(banner);
    setTimeout(() => { if (banner.parentNode) banner.remove(); }, timeoutMs);
  }

  function attemptRejoinFromSavedSession() {
    const session = getReturnSession();
    if (!session || !session.roomCode) return false;
    logEvent("custom_reconnect_retry_click", {
      referrer: buildModerationReferrer("reconnect_retry", {
        room: session.roomCode || "",
      }),
    });
    if (state.customMode) {
      exitCustomMode("", { keepReturnSession: true, suppressNotice: true, skipAutoOverlay: true });
    }
    hideReconnectChip();
    setTimeout(() => {
      joinCustomRoom(session.roomCode, session.difficulty || "medium", session.password || "");
    }, 80);
    return true;
  }

  function showReconnectChip() {
    let chip = document.getElementById("pivot-reconnect-chip");
    if (!chip) {
      chip = document.createElement("div");
      chip.id = "pivot-reconnect-chip";
      chip.style.cssText = `position:fixed;right:12px;top:12px;z-index:9998;display:flex;align-items:center;gap:8px;padding:7px 10px;background:${_currentTheme === "dark" ? "rgba(30,30,30,0.92)" : "rgba(27,27,27,0.88)"};color:${_currentTheme === "dark" ? C.ink : "#fdf8f1"};border:1px solid ${_currentTheme === "dark" ? "rgba(236,233,225,0.2)" : "rgba(253,248,241,0.25)"};font:12px Georgia,'Times New Roman',serif;`;
      const text = document.createElement("span");
      text.id = "pivot-reconnect-chip-text";
      chip.appendChild(text);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "Retry";
      btn.style.cssText = "background:transparent;color:#fdf8f1;border:1px solid rgba(253,248,241,0.38);padding:2px 7px;font:12px Georgia,'Times New Roman',serif;cursor:pointer;";
      btn.addEventListener("click", () => {
        if (!attemptRejoinFromSavedSession()) {
          setShareToast("Room session expired", 2200);
          hideReconnectChip();
        }
      });
      chip.appendChild(btn);
      document.body.appendChild(chip);
    }
    updateReconnectChip();
  }

  function updateReconnectChip() {
    const chip = document.getElementById("pivot-reconnect-chip");
    if (!chip) return;
    if (!state.customReconnecting) {
      chip.remove();
      return;
    }
    const text = document.getElementById("pivot-reconnect-chip-text");
    if (!text) return;
    const secs = Math.max(0, Math.ceil((state.customReconnectUntil - performance.now()) / 1000));
    text.textContent = `Reconnecting (${secs}s)`;
  }

  function hideReconnectChip() {
    const chip = document.getElementById("pivot-reconnect-chip");
    if (chip) chip.remove();
  }

  // Parse URL params for /custom/ page (query params from QR, hash routes, etc.)
  function parseCustomPageParams() {
    // Canonical share format: /custom/?code=X&diff=Y&pass=Z
    const params = new URLSearchParams(window.location.search);
    const puzzleCode = String(params.get("puzzle") || "").trim().toUpperCase();
    if (/^[A-Z2-9]{8}$/.test(puzzleCode)) {
      return { action: "community", puzzleCode };
    }
    const queryCode = sanitizeRoomCode(params.get("code") || params.get("room") || "");
    if (queryCode) {
      const diff = normalizeCustomDifficulty(params.get("diff") || "medium");
      const pass = params.get("pass") || "";
      const canonical = new URL(buildCustomJoinUrl(queryCode, diff, pass));
      const target = `${canonical.pathname}${canonical.search}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (target !== current) history.replaceState(null, "", target);
      return { code: queryCode, difficulty: diff, password: pass };
    }

    const basePath = getCustomBasePath().pathname;

    // Optional path style support: /custom/CODE[/diff[/password]]. Checked
    // before the race branch below, so a real room link (path-style or
    // query-style) always wins over create-intent.
    const path = window.location.pathname || "";
    const baseLower = basePath.toLowerCase();
    const pathLower = path.toLowerCase();
    if (pathLower.startsWith(baseLower)) {
      const suffix = path.slice(basePath.length).replace(/^\/+/, "");
      if (suffix) {
        const parts = suffix.split("/").filter(Boolean);
        const code = sanitizeRoomCode(parts[0] || "");
        if (code) {
          const diff = normalizeCustomDifficulty(parts[1] || "medium");
          let pass = "";
          if (parts.length > 2) {
            const rawPass = parts.slice(2).join("/");
            try { pass = decodeURIComponent(rawPass); } catch { pass = rawPass; }
          }
          const canonical = new URL(buildCustomJoinUrl(code, diff, pass));
          history.replaceState(null, "", `${canonical.pathname}${canonical.search}`);
          return { code, difficulty: diff, password: pass };
        }
      }
    }

    // Labs: /custom/?race=1 opens the Live Race setup. Checked after both the
    // query-string and path-style invite code branches above, so a real room
    // link always wins over create-intent.
    if (params.get("race") === "1") {
      return { action: "race", difficulty: normalizeCustomDifficulty(params.get("diff") || "medium") };
    }

    const hash = window.location.hash.slice(1);
    if (!hash) return null;

    // #create/diff
    const createMatch = hash.match(/^create\/(\w+)$/i);
    if (createMatch) {
      history.replaceState(null, "", basePath);
      return { action: "create", difficulty: createMatch[1].toLowerCase() };
    }
    // #solo/diff
    const soloMatch = hash.match(/^solo\/(\w+)$/i);
    if (soloMatch) {
      history.replaceState(null, "", basePath);
      return { action: "solo", difficulty: soloMatch[1].toLowerCase() };
    }
    // #CODE/diff  or  #CODE/diff/password
    const joinMatch = hash.match(/^([A-Z0-9]{3,5})\/?(\w+)?(?:\/(.+))?$/i);
    if (joinMatch) {
      const code = sanitizeRoomCode(joinMatch[1] || "");
      if (!code) return null;
      const diff = normalizeCustomDifficulty(joinMatch[2] || "medium");
      let pass = "";
      if (joinMatch[3]) {
        try { pass = decodeURIComponent(joinMatch[3]); } catch { pass = joinMatch[3]; }
      }
      const canonical = new URL(buildCustomJoinUrl(code, diff, pass));
      history.replaceState(null, "", `${canonical.pathname}${canonical.search}`);
      return { code, difficulty: diff, password: pass };
    }

    return null;
  }

  // Redirect from main page hash routes to /custom/ page
  function redirectToCustomPage(params) {
    const baseUrl = getCustomBasePath();
    if (params.action === "create") {
      window.location.href = `${baseUrl.pathname}#create/${normalizeCustomDifficulty(params.difficulty || "medium")}`;
    } else if (params.action === "solo") {
      window.location.href = `${baseUrl.pathname}#solo/${normalizeCustomDifficulty(params.difficulty || "medium")}`;
    } else if (params.code) {
      window.location.href = buildCustomJoinUrl(params.code, params.difficulty || "medium", params.password || "");
    }
  }

  // Clean up multiplayer on page unload
  window.addEventListener("beforeunload", () => {
    if (_customChannel && state.customPlayerId) {
      try { _customChannel.send({ type: "broadcast", event: "player_leave", payload: { id: state.customPlayerId } }); } catch {}
    }
    if (_customChannel) {
      _customChannel.unsubscribe();
      _customChannel = null;
      _customChannelCode = "";
    }
    if (_heartbeatInterval) { clearInterval(_heartbeatInterval); _heartbeatInterval = null; }
    if (_watchdogTimeout) { clearTimeout(_watchdogTimeout); _watchdogTimeout = null; }
  });

  function gatherDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      viewport: `${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)}`,
      dpr: window.devicePixelRatio || 1,
    };
  }

  async function submitFeedback() {
    if (!supabase) return;
    const message = feedbackInputEl ? feedbackInputEl.value.trim() : "";
    if (!message) {
      if (feedbackStatusEl) {
        feedbackStatusEl.textContent = "Please add a message.";
      }
      return;
    }
    const includeDevice = feedbackDeviceEl ? feedbackDeviceEl.checked : false;
    const payload = {
      message,
      device_id: getDeviceId(),
      daily_key: state.dailyKey || getEasternKey(),
      mode: state.practiceMode ? "practice" : "daily",
      device_info: includeDevice ? gatherDeviceInfo() : null,
    };
    try {
      // Tag feedback that came from the post-race prompt so it is separable in
      // the admin panel. pivot_feedback predates the migrations directory, so
      // whether `mode` is constrained cannot be checked from source — if the
      // tagged insert is rejected, fall back to the untagged one rather than
      // losing what someone took the trouble to write.
      let error = null;
      if (_feedbackSource) {
        ({ error } = await supabase.from("pivot_feedback").insert({ ...payload, mode: _feedbackSource }));
        if (error) ({ error } = await supabase.from("pivot_feedback").insert(payload));
      } else {
        ({ error } = await supabase.from("pivot_feedback").insert(payload));
      }
      _feedbackSource = "";
      if (error) {
        if (feedbackStatusEl) {
          feedbackStatusEl.textContent = "Could not send feedback.";
        }
        return;
      }
      if (feedbackStatusEl) {
        feedbackStatusEl.textContent = "Thanks for the feedback!";
      }
      if (feedbackInputEl) {
        feedbackInputEl.value = "";
      }
      if (feedbackDeviceEl) {
        feedbackDeviceEl.checked = false;
      }
      setTimeout(() => {
        closeFeedbackOverlay();
      }, 900);
    } catch (err) {
      if (feedbackStatusEl) {
        feedbackStatusEl.textContent = "Could not send feedback.";
      }
    }
  }

function closeArchiveOverlay() {
  if (!archiveOverlay) return;
  const hadFocus = archiveOverlay.classList.contains("show");
  fadeOutOverlay(archiveOverlay);
  if (hadFocus) restoreOverlayFocus();
}

  function _initArchiveGame(dateKey, word, startState) {
    const seed = hashString(`${dateKey}-${word}`);
    const rng = mulberry32(seed);
    const scrambleMoves = getScrambleMovesForKey(dateKey);
    const solvedGrid = buildSolvedGrid(word, rng);
    let newGrid = solvedGrid.map((row) => row.slice());
    if (startState && startState.length === GRID_ROWS * GRID_COLS) {
      newGrid = stringToGrid(startState);
    } else {
      scrambleGrid(newGrid, rng, scrambleMoves);
    }
    return newGrid;
  }

  function startArchive(dateKey) {
    if (!isDayKey(dateKey)) {
      if (archiveErrorEl) archiveErrorEl.textContent = "Choose a valid archive date.";
      return;
    }
    const startKey = getArchiveStartKey();
    const todayKey = getEasternKey();
    if (dateKey < startKey) {
      if (archiveErrorEl) {
        archiveErrorEl.textContent = `Archive starts ${startKey}.`;
      }
      return;
    }
    if (dateKey > todayKey) {
      if (archiveErrorEl) {
        archiveErrorEl.textContent = "Archive date cannot be in the future.";
      }
      return;
    }
    _pendingMenuStart = false;
    setMenuStartLoadState("idle");

    const showOfflineError = () => {
      if (archiveErrorEl) {
        archiveErrorEl.textContent = "Couldn't load puzzle. Check your connection and try again.";
      }
    };

    const setupGame = (word, startState) => {
      if (!word || !/^[A-Z]{4,}$/.test(word)) {
        if (archiveErrorEl) {
          archiveErrorEl.textContent = "No puzzle available for this date.";
        }
        return;
      }
      const newGrid = _initArchiveGame(dateKey, word, startState);
      const startStr = gridToString(newGrid);
      state.lowerBound = computeLowerBoundForStart(startStr, word);

      // Archive moves have always been saved but never read back, so leaving a
      // half-finished archive puzzle silently threw the progress away. Restore
      // it, validated the same way the daily path validates its own payload.
      // Practice namespace only — this must never read the daily record.
      let archiveGrid = newGrid;
      let archiveMoves = 0;
      let archiveLastMove = null;
      let archiveSolved = false;
      let archiveHistory = [];
      const savedArchive = loadProgress(dateKey, true);
      if (savedArchive && Array.isArray(savedArchive.grid) && savedArchive.word === word) {
        const savedGridStr = savedArchive.grid.join("");
        if (isValidGridString(savedGridStr)) {
          archiveGrid = stringToGrid(savedGridStr);
          archiveMoves = Number.isFinite(savedArchive.moves) && savedArchive.moves >= 0 ? savedArchive.moves : 0;
          archiveLastMove = savedArchive.lastMove || null;
          archiveSolved = Boolean(savedArchive.solvedToday);
          const hist = Array.isArray(savedArchive.moveHistory) ? savedArchive.moveHistory.slice() : [];
          // A history that does not account for exactly the restored move count
          // cannot be replayed, and a bad one would fail verification later, so
          // drop it rather than carry something unusable.
          archiveHistory = hist.length === archiveMoves ? hist : [];
        }
      } else if (savedArchive) {
        // Wrong word for this date, or an unusable grid: the stored record does
        // not describe this puzzle. Discard it rather than start from it.
        clearProgressState(dateKey, true);
      }

      state.grid = archiveGrid;
      state.targetWord = word;
      state.moves = archiveMoves;
      state.mode = "play";
      state.lastMove = archiveLastMove;
      state.solvedToday = archiveSolved;
      state.minMoves = null;
      state.minExact = false;
      state.nameInput = "";
      state.submitted = false;
      state._archiveRivalrySubmitted = false;
      clearSubmissionReceipt();
      state.dailyKey = dateKey;
      // If this archive load came from a past-edition challenge link, restore
      // the challenge context so the solve handler can record the rivalry.
      if (_pendingArchiveChallenge && _pendingArchiveChallenge.archiveKey === dateKey) {
        state.challengeFrom = _pendingArchiveChallenge;
        resolveChallenge(_pendingArchiveChallenge);
        // Start the ghost when the archived board is actually playable. Network
        // loading time must never give the ghost an artificial head start.
        state.ghostStartedAt = performance.now();
        state.ghostMoveIndex = 0;
        state.ghostGrid = _pendingArchiveChallenge.ghostStartState || startStr;
      }
      state.leaderboard = filterLeaderboardByLowerBound(loadLeaderboard(dateKey), state.lowerBound);
      state.showReadme = false;
      state.practiceMode = true;
      state.completed = false;
      state.completionLocked = false;
      // Mirrors resetGame/startCustomPuzzle. The practice win path sets
      // winAcknowledged=true on the first archive solve, so without this the
      // SECOND archive puzzle in a session renders no win screen at all, and
      // the unguarded solved-block tail keeps rewriting progress every frame.
      state.winAcknowledged = false;
      const streak = loadStreakState();
      state.streakCount = streak.count;
      state.streakDisplay = streak.count;
      state.streakAnimating = false;
      state.redoMode = false;
      state.leaderboardEligible = false;
      state.canonicalStartStr = startStr;
      state.moveHistory = archiveHistory;
      announceStatus(`Spell ${state.targetWord} by rotating tile blocks. Grid is ${GRID_ROWS} by ${GRID_COLS}.`);
      setCanvasLabel(`PIVOT puzzle. Target word: ${state.targetWord}. Moves: ${state.moves}. Use pivot dots to rotate 2x2 tile blocks.`);

      fetchLeaderboard(dateKey, 30).then((entries) => {
        if (entries) {
          const normalized = entries.map((entry) => ({
            name: entry.name,
            moves: entry.moves,
            time: entry.created_at ? Date.parse(entry.created_at) : 0,
          }));
          const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
          state.leaderboard = filtered;
          saveLeaderboard(dateKey, filtered);
        }
      });

      fetchDailyMetrics(dateKey);
      fetchAdminMessage();
      state.nextMessagePoll = Date.now() + 5 * 60 * 1000;
      state.nextLeaderboardPoll = Date.now() + 2 * 60 * 1000;
      logEvent("archive_play", { archive_key: dateKey, server_backed: startState ? 1 : 0 }, `${dateKey}_archive_play`);

      fetchCompletion(dateKey).then((done) => {
        // Late replies must not apply to a board the player has since left.
        if (state.dailyKey !== dateKey || !state.practiceMode || state.customMode) return;
        if (done) {
          // Record that this edition was completed, but deliberately do NOT set
          // state.completed. Archive exists to replay old puzzles, and
          // state.completed blocks every pivot (see the input guard), so opening
          // a date you had solved as the daily produced a fresh-looking board
          // that silently stopped responding when this reply landed.
          state.completionLocked = true;
        }
      });

      fetchDailyMeta(dateKey, word).then((meta) => {
        if (!meta) return;
        if (Number.isFinite(meta.min_moves) && meta.min_moves > 0) {
          state.minMoves = meta.min_moves;
          state.minExact = Boolean(meta.min_exact);
        }
      });
      bfsMinToCenterAsync(startStr, word, 600000, 0, true).then((result) => {
        if (state.dailyKey !== dateKey) return;
        if (state.targetWord !== word) return;
        if (!state.practiceMode) return;
        state.minMoves = result.moves;
        state.minExact = Boolean(result.exact);
        state.optimalPath = Array.isArray(result.path) ? result.path.slice() : [];
      });
    };

    // Attempt server fetch for archive word + start state
    closeArchiveOverlay();
    if (SUPABASE_URL) {
      const _abortCtl = new AbortController();
      const _abortTimer = setTimeout(() => _abortCtl.abort(), 4000);
      fetch(`${SUPABASE_URL}/functions/v1/pivot-daily-word?key=${encodeURIComponent(dateKey)}`, { signal: _abortCtl.signal })
        .then(r => { clearTimeout(_abortTimer); return r.ok ? r.json() : null; })
        .then(data => {
          if (data && typeof data.word === "string" && /^[A-Z]{4,}$/.test(data.word)) {
            setupGame(data.word, data.start_state || "");
          } else {
            showOfflineError();
          }
        })
        .catch(() => {
          showOfflineError();
        });
    } else {
      showOfflineError();
    }
  }

  function startRedo() {
    const dailyKey = getEasternKey();
    const word = getDailyWord();
    if (!word) return;
    const seed = hashString(`${dailyKey}-${word}`);
    const rng = mulberry32(seed);
    const scrambleMoves = getScrambleMovesForKey(dailyKey);
    const solvedGrid = buildSolvedGrid(word, rng);
    let newGrid = solvedGrid.map((row) => row.slice());
    const startState = window.PivotDaily?.getStartStateForKey ? window.PivotDaily.getStartStateForKey(dailyKey) : "";
    if (startState && startState.length === GRID_ROWS * GRID_COLS) {
      newGrid = stringToGrid(startState);
    } else {
      scrambleGrid(newGrid, rng, scrambleMoves);
    }

    const startStr = gridToString(newGrid);
    state.lowerBound = computeLowerBoundForStart(startStr, word);

    state.grid = newGrid;
    state.targetWord = word;
    state.moves = 0;
    state.mode = "play";
    state.lastMove = null;
    state.solvedToday = false;
    state.minMoves = null;
    state.minExact = false;
    state.nameInput = "";
    state.submitted = true;
    clearSubmissionReceipt();
    state.dailyKey = dailyKey;
    state.showReadme = false;
    state.practiceMode = false;
    state.completed = true;
    state.alreadyCompleted = true;
    state.completionLocked = false;
    state.winAcknowledged = false;
    state.redoMode = true;
    state.leaderboardEligible = false;
    state.isPersonalBest = false;
    state.leaderboard = filterLeaderboardByLowerBound(loadLeaderboard(dailyKey), state.lowerBound);
    state.metrics = createEmptyMetrics(dailyKey);
    state.canonicalStartStr = startStr;
    state.moveHistory = [];
    announceStatus(`Spell ${state.targetWord} by rotating tile blocks. Grid is ${GRID_ROWS} by ${GRID_COLS}.`);
    setCanvasLabel(`PIVOT puzzle. Target word: ${state.targetWord}. Moves: 0. Use pivot dots to rotate 2x2 tile blocks.`);

    saveProgress(state.dailyKey, {
      grid: state.grid.map((row) => row.join("")),
      moves: state.moves,
      lastMove: state.lastMove,
      solvedToday: state.solvedToday,
      minMoves: state.minMoves,
      minExact: state.minExact,
      minKind: MIN_KIND,
      submitted: state.submitted,
      redoMode: state.redoMode,
      word: state.targetWord,
      moveHistory: state.moveHistory,
      hintUsed: state.hintUsed,
    }, state.practiceMode || state.redoMode);

    fetchLeaderboard(dailyKey, 30).then((entries) => {
      if (entries) {
        const normalized = entries.map((entry) => ({
          name: entry.name,
          moves: entry.moves,
          time: entry.created_at ? Date.parse(entry.created_at) : 0,
        }));
        const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
        state.leaderboard = filtered;
        saveLeaderboard(dailyKey, filtered);
      }
    });

    fetchDailyMetrics(dailyKey);
    fetchAdminMessage();
    state.nextMessagePoll = Date.now() + 5 * 60 * 1000;
    state.nextLeaderboardPoll = Date.now() + 2 * 60 * 1000;

    fetchDailyMeta(dailyKey, word).then((meta) => {
      if (!meta) return;
      // Same scoping the bfs call below already applies: a late reply must not
      // stamp this date's par onto whatever board is showing now.
      if (state.dailyKey !== dailyKey || state.targetWord !== word) return;
      if (Number.isFinite(meta.min_moves) && meta.min_moves > 0) {
        state.minMoves = meta.min_moves;
        state.minExact = Boolean(meta.min_exact);
      }
    });
    bfsMinToCenterAsync(startStr, word, 600000, 0, true).then((result) => {
      if (state.dailyKey !== dailyKey) return;
      if (state.targetWord !== word) return;
      if (state.redoMode !== true) return;
      state.minMoves = result.moves;
      state.minExact = Boolean(result.exact);
      state.optimalPath = Array.isArray(result.path) ? result.path.slice() : [];
    });
  }

  function openShareOverlay() {
    if (state.practiceMode) return;
    if (!state.completed && !state.submitted && !state.alreadyCompleted) return;
    closeNameOverlay();
    closeArchiveOverlay();
    logUiEvent("share_open");
    state.shareOnly = true;
    state.mode = "win";
  }

  function getRating(delta) {
    if (delta <= 0) return "Perfect!";
    if (delta <= 3) return "Awesome!";
    if (delta <= 8) return "Not bad!";
    return "I'll do better tomorrow!";
  }

  function handleCanvasMouseMove(event) {
    _winFocusIndex = -1;
    _canvasLastMouse = getMousePosition(event);
    if (state.mode !== "play") {
      state.hoverPivot = null;
      return;
    }
    const hovered = findPivotAt(_canvasLastMouse);
    state.hoverPivot = hovered;
    if (hovered) _keyboardPivot = hovered;
  }

  function handleCanvasMouseDown(event) {
    const pos = getMousePosition(event);

    // Block all clicks during countdown
    if (state.customCountdownUntil > 0 && performance.now() < state.customCountdownUntil) return;

    // Reconnecting: block interactions until reconnect succeeds or timeout exits room.
    if (state.customReconnecting) {
      return;
    }

    if (state.showReadme) {
      if (layout.readmeFeedbackButton && isPointInRectPadded(pos, layout.readmeFeedbackButton, 4)) {
        state.showReadme = false;
        openFeedbackOverlay();
        return;
      }
      if (layout.readmeMailRect && isPointInRectPadded(pos, layout.readmeMailRect, 4)) {
        window.location.href = "mailto:andrew@leahey.org";
        return;
      }
      if (layout.readmeMastoRect && isPointInRectPadded(pos, layout.readmeMastoRect, 4)) {
        const a = document.createElement("a");
        a.href = "https://esq.social/@andrew";
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
        return;
      }
      if (layout.readmeBlueRect && isPointInRectPadded(pos, layout.readmeBlueRect, 4)) {
        const a = document.createElement("a");
        a.href = "https://bsky.app/profile/esq.social";
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
        return;
      }
      state.showReadme = false;
      return;
    }
    if (state.showLeaderboardOverlay) {
      return;
    }
    if (state.showTutorial || growth?.blocked()) {
      return;
    }

    if (state.mode === "win") {
      if (!state.customMode && isWinOverlayPending()) return;
      if (layout.growthStats && isPointInRect(pos, layout.growthStats)) { openStatsOverlay(); return; }
      if (layout.growthCircle && isPointInRect(pos, layout.growthCircle)) { state.leaderboardScope = "circles"; growth?.event("circle_summary_opened"); openLeaderboardOverlay(); return; }
      // Custom game win buttons — solo
      if (state.customMode && !state.customMultiplayer && layout.customNextPuzzle && isPointInRectPadded(pos, layout.customNextPuzzle, 6)) {
        if (state.customCommunityCode) {
          shareCurrentCommunityPuzzle();
          return;
        }
        const newCode = generateRoomCode();
        const puzzle = generateCustomPuzzle(newCode, state.customDifficulty);
        startCustomPuzzle(newCode, state.customDifficulty, puzzle, false);
        return;
      }
      if (state.customMode && !state.customMultiplayer && layout.customChangeDifficulty && isPointInRectPadded(pos, layout.customChangeDifficulty, 6)) {
        if (state.customCommunityCode) {
          openMakePuzzle();
          return;
        }
        openCustomGameOverlay(state.customDifficulty);
        return;
      }
      if (state.customMode && !state.customMultiplayer && layout.customBackToToday && isPointInRectPadded(pos, layout.customBackToToday, 6)) {
        state.sessionStats = { games: [] };
        exitCustomMode();
        return;
      }
      // N-player: Next round (host only)
      if (state.customMode && state.customMultiplayer && state.customIsHost && layout.customNextRoundButton && isPointInRectPadded(pos, layout.customNextRoundButton, 6)) {
        handleNextRound();
        return;
      }
      if (state.customMode && layout.customExitWinButton && isPointInRectPadded(pos, layout.customExitWinButton, 6)) {
        exitCustomMode();
        return;
      }
      if (state.practiceMode && layout.winPlayAnother && isPointInRectPadded(pos, layout.winPlayAnother, 6)) {
        openArchiveOverlay();
        return;
      }
      if (state.practiceMode && layout.winBackToToday && isPointInRectPadded(pos, layout.winBackToToday, 6)) {
        state.sessionStats = { games: [] };
        resetGame();
        return;
      }
      if (state.redoMode && layout.winBackToToday && isPointInRectPadded(pos, layout.winBackToToday, 6)) {
        state.sessionStats = { games: [] };
        // Clear BEFORE resetGame, or the reload restores redo and it sticks.
        state.redoMode = false;
        resetGame();
        return;
      }
      if (!state.practiceMode && !state.shareOnly && layout.winResubmit && isPointInRectPadded(pos, layout.winResubmit, 6)) {
        if (_scoreSubmitInFlight) return;
        const resendName = sanitizeName(state.playerName || state.nameInput || "");
        if (!resendName) {
          openNameOverlay();
          return;
        }
        state.nameInput = resendName;
        state.winIgnoreCloseUntil = Date.now() + 700;
        (async () => {
          if (await submitScore()) {
            closeNameOverlay();
            return;
          }
          // One-tap posting has no visible form, so reopen name entry to show
          // why the server or name check refused it and let the player edit.
          const errorText = nameErrorEl ? nameErrorEl.textContent : "";
          if (!errorText || state.submitted) return;
          openNameOverlay();
          if (nameErrorEl) nameErrorEl.textContent = errorText;
        })();
        return;
      }
      if (!state.practiceMode && isPointInRectPadded(pos, layout.winShareResults, 6)) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        haptic("light");
        logUiEvent("share_result_btn_click");
        shareResult();
        return;
      }
      if (!state.practiceMode && isPointInRectPadded(pos, layout.winShareVideo, 6)) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        haptic("light");
        logUiEvent("share_replay_click");
        shareReplayLink();
        return;
      }
      if (layout.challengeButton && isPointInRectPadded(pos, layout.challengeButton, 6)) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        haptic("light");
        logUiEvent("challenge_click");
        shareChallenge();
        return;
      }
      if (state.challengeResolved && layout.challengeReplayLink && isPointInRectPadded(pos, layout.challengeReplayLink, 6)) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        const cf = state.challengeFrom;
        if (cf && state.playerName) {
          const url = buildChallengeCompareUrl(cf.name, state.playerName, cf.dayNum, cf.moves, state.moves);
          navigateTo(url, true);
        } else if (cf) {
          const url = `${CANONICAL_URL}/replay/#${encodeURIComponent(cf.name)}/${cf.dayNum}`;
          navigateTo(url, true);
        }
        return;
      }
      // The text links sit 18px apart with 16px rects, so their padded hit areas
      // overlap. Give a tap in the overlap to the line it is closest to.
      const winLinkCenterDist = (rect) => Math.abs(pos.y - (rect.y + rect.height / 2));
      const winLinkAbove = layout.winWatchOptimal.width > 0 ? layout.winWatchOptimal : layout.winWatchBest;
      const tapNearerLinkAbove = winLinkAbove.width > 0 && isPointInRectPadded(pos, winLinkAbove, 6) &&
        winLinkCenterDist(winLinkAbove) < winLinkCenterDist(layout.winPlayYesterday);
      if (!tapNearerLinkAbove && isPointInRectPadded(pos, layout.winPlayYesterday, 6) && layout.winPlayYesterday.width > 0) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        const prevKey = getPreviousDailyKey(state.dailyKey || getEasternKey());
        logUiEvent("play_yesterday_click", { daily_key: prevKey });
        // startArchive sets mode="play" and practiceMode=true itself. It leaves
        // winAcknowledged alone, which is correct here: it is still false, so
        // the archive puzzle's own win screen fires. practiceMode also stops
        // this link from re-rendering, so the chain can't loop.
        state.shareOnly = false;
        startArchive(prevKey);
        return;
      }
      if (isPointInRectPadded(pos, layout.winWatchBest, 6) && state.leaderboard.length > 0) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        logUiEvent("watch_best_click");
        const best = [...state.leaderboard].sort((a, b) => {
          if (a.moves !== b.moves) return a.moves - b.moves;
          return (a.time || 0) - (b.time || 0);
        })[0];
        const day = getDayNumber();
        const url = `${CANONICAL_URL}/replay/#${encodeURIComponent(best.name)}/${day}`;
        navigateTo(url, true);
        return;
      }
      if (isPointInRectPadded(pos, layout.winWatchOptimal, 6) && state.optimalPath.length > 0) {
        state.winIgnoreCloseUntil = Date.now() + 600;
        logUiEvent("watch_optimal_click", { moves: state.optimalPath.length });
        const day = state.dailyKey ? getDayNumberForKey(state.dailyKey) : getDayNumber();
        const path = state.optimalPath.join(",");
        navigateTo(`${CANONICAL_URL}/replay/#optimal/${day}/${path}`, true);
        return;
      }
      if (layout.winIosAppCard.width > 0) {
        if (isPointInRectPadded(pos, layout.winIosAppDismiss, 4)) {
          state.winIgnoreCloseUntil = Date.now() + 400;
          logUiEvent("win_ios_app_dismiss");
          dismissIosAppCard();
          layout.winIosAppCard = { x: 0, y: 0, width: 0, height: 0 };
          layout.winIosAppDismiss = { x: 0, y: 0, width: 0, height: 0 };
          return;
        }
        if (isPointInRectPadded(pos, layout.winIosAppCard, 6)) {
          state.winIgnoreCloseUntil = Date.now() + 600;
          logUiEvent("win_ios_app_click");
          window.location.href = IOS_CARD_APP_STORE_URL;
          return;
        }
      }
      // Text-nav buttons (Archive, Leader, Stats, etc.) on win screen. The daily
      // result paints an opaque backdrop over the sidebar, so its Leader link is
      // hidden there and must not stay tappable; only the custom win shows it.
      if (state.customMode && isPointInRectPadded(pos, layout.fullLeaderboardLink, 6)) {
        logUiEvent("leader_open");
        openLeaderboardOverlay();
        if (!state.leaderboardLoading) {
          fetchLeaderboard(state.dailyKey, 200).then((entries) => {
            if (entries) {
              const normalized = entries.map((row) => ({
                name: row.name,
                moves: row.moves,
                time: row.created_at ? Date.parse(row.created_at) : 0,
              }));
              const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
              state.leaderboard = filtered;
              saveLeaderboard(state.dailyKey, filtered);
              if (state.showLeaderboardOverlay) renderLeaderboardEntries();
            }
          });
        }
        return;
      }
      if (Date.now() < state.winIgnoreCloseUntil) return;
      // The drawn X is 26px; pad its hit area to a 44px touch target.
      if (isPointInRectPadded(pos, layout.winClose, 9)) {
        state.winAcknowledged = true;
        state.mode = "play";
        state.shareOnly = false;
        state.highlightUntil = Date.now() + 5000;
        closeNameOverlay();
        return;
      }
      return;
    }

    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.shareButton, 6)) {
      openShareOverlay();
      return;
    }

    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.leaderboardRetry, 6)) {
      retryLeaderboardFetch(state.showLeaderboardOverlay ? 200 : 50);
      return;
    }

    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.fullLeaderboardLink, 6)) {
      logUiEvent("leader_open");
      openLeaderboardOverlay();
      if (!state.leaderboardLoading) {
        fetchLeaderboard(state.dailyKey, 200).then((entries) => {
          if (entries) {
            const normalized = entries.map((row) => ({
              name: row.name,
              moves: row.moves,
              time: row.created_at ? Date.parse(row.created_at) : 0,
            }));
            const filtered = filterLeaderboardByLowerBound(normalized, state.lowerBound);
            state.leaderboard = filtered;
            saveLeaderboard(state.dailyKey, filtered);
            if (state.showLeaderboardOverlay) renderLeaderboardEntries();
          }
        });
      }
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.submitButton, 6)) {
      if (_scoreSubmitInFlight) return;
      const pending = loadPendingSubmit(state.dailyKey);
      const pendingNow = pending && pendingSubmitMatchesCurrentRun(pending) ? pending : null;
      if (pendingNow && !state.practiceMode && !state.customMode && !state.submitted) {
        syncPendingSubmitNow("retry_chip").then(() => {});
        return;
      }
      openNameOverlay();
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.statsButton, 6)) {
      logUiEvent("stats_open");
      openStatsOverlay();
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.soundButton, 6)) {
      toggleSound();
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.hintButton, 6)) {
      useHint();
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.undoButton, 6)) {
      undoMove();
      return;
    }
    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.moreButton, 6)) {
      openMoreMenu();
      return;
    }
    if (state.mode !== "menu" && state.customMode && layout.exitCustomButton && isPointInRectPadded(pos, layout.exitCustomButton, 6)) {
      exitCustomMode();
      return;
    }
    if (state.mode !== "menu" && state.customMode && state.customMultiplayer && layout.customObserveModeButton && isPointInRectPadded(pos, layout.customObserveModeButton, 6)) {
      const effectivelyObserver = state.customIsObserver || (!state.customIsHost && isCurrentCustomPlayerObserver());
      if (effectivelyObserver) {
        if (openCustomLobbyOverlay()) {
          showLobbyScreen(state.customRoomCode || "");
        }
        return;
      }
      if (!state.customIsHost) {
        requestObserveModeSwitch();
        return;
      }
      if (requestObserveModeSwitch()) return;
      const enteringObserve = !state.customObserveMode;
      state.customObserveMode = enteringObserve;
      if (enteringObserve) {
        state.mode = "play";
        state.customObserveFocusLocked = false;
        state.customObserveFocusPlayer = null;
      }
      return;
    }
    if (state.mode !== "menu" && state.customMode && state.customMultiplayer && layout.chatButton && isPointInRectPadded(pos, layout.chatButton, 6)) {
      toggleChatPanel();
      return;
    }
    // Observation mode: "Request to join" button (observer)
    if (state.customMode && state.customMultiplayer && (state.customIsObserver || isCurrentCustomPlayerObserver()) && !state.customIsHost && layout.observeRequestJoinButton && isPointInRectPadded(pos, layout.observeRequestJoinButton, 4)) {
      if (!state.customJoinRequested && _customChannel) {
        logEvent("custom_observer_request_join");
        state.customJoinRequested = true;
        _customChannel.send({ type: "broadcast", event: "request_join", payload: { id: state.customPlayerId } });
        setShareToast("Join request sent!", 2000);
      }
      return;
    }
    // Observation mode: host action bar buttons
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeHostActionButtons) {
      for (const btn of layout.observeHostActionButtons) {
        if (!isPointInRectPadded(pos, btn, 4)) continue;
        if (btn.action === "start_round") startGameAsHost();
        else if (btn.action === "next_round") handleNextRound();
        else if (btn.action === "approve_all") approveAllJoinRequests();
        else if (btn.action === "copy_link") copyCustomJoinLink(state.customRoomCode, state.customDifficulty, state.customPassword, "custom_host_copy_link");
        else if (btn.action === "toggle_lock") toggleCustomRoomLock();
        else if (btn.action === "exit_room") exitCustomMode();
        return;
      }
    }
    // Observation mode: undo last host kick
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeUndoKickButton && isPointInRectPadded(pos, layout.observeUndoKickButton, 4)) {
      if (!undoLastKick()) {
        setShareToast("Undo window expired", 2000);
      } else {
        setShareToast("Player restored to the lobby", 2200);
      }
      return;
    }
    // Observation mode: roster-level deny/approve/kick controls (classroom host layout)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeRosterDenyButtons) {
      for (const btn of layout.observeRosterDenyButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          denyJoinRequest(btn.playerId);
          return;
        }
      }
    }
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeRosterApproveButtons) {
      for (const btn of layout.observeRosterApproveButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          approveJoinRequest(btn.playerId);
          return;
        }
      }
    }
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeRosterKickButtons) {
      for (const btn of layout.observeRosterKickButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          const player = state.customPlayers[btn.playerId];
          if (!player || player.isHost) return;
          if (!confirm(`Remove ${player.name}?`)) return;
          hostKickPlayer(btn.playerId, "You were removed by the host");
          return;
        }
      }
    }
    // Observation mode: "Approve all" button (host)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeApproveAllButton && isPointInRectPadded(pos, layout.observeApproveAllButton, 4)) {
      approveAllJoinRequests();
      return;
    }
    // Observation mode: "Deny all" button (host)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeDenyAllButton && isPointInRectPadded(pos, layout.observeDenyAllButton, 4)) {
      denyAllJoinRequests();
      return;
    }
    // Observation mode: individual deny buttons (host)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeDenyButtons) {
      for (const btn of layout.observeDenyButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          denyJoinRequest(btn.playerId);
          return;
        }
      }
    }
    // Observation mode: individual approve buttons (host)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeApproveButtons) {
      for (const btn of layout.observeApproveButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          approveJoinRequest(btn.playerId);
          return;
        }
      }
    }
    // Observation mode: live kick buttons on player tiles (host)
    if (state.customMode && state.customMultiplayer && state.customIsHost && layout.observeKickButtons) {
      for (const btn of layout.observeKickButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          const player = state.customPlayers[btn.playerId];
          if (!player || player.isHost) return;
          if (!confirm(`Remove ${player.name}?`)) return;
          hostKickPlayer(btn.playerId, "You were removed by the host");
          return;
        }
      }
    }
    // Observation mode: filter chips
    if (state.customMode && state.customMultiplayer && (state.customObserveMode || state.customIsObserver || isCurrentCustomPlayerObserver() || state.customClassroomMode) && layout.observeFilterButtons) {
      for (const btn of layout.observeFilterButtons) {
        if (isPointInRectPadded(pos, btn, 4)) {
          state.customObserveFilter = btn.filter || "all";
          scheduleRenderLoop();
          return;
        }
      }
    }
    // Observation mode: player list — click to focus
    if (state.customMode && state.customMultiplayer && (state.customObserveMode || state.customIsObserver || isCurrentCustomPlayerObserver()) && layout.observePlayerRects) {
      for (const rect of layout.observePlayerRects) {
        if (isPointInRectPadded(pos, rect, 4)) {
          if (state.customObserveFocusLocked && state.customObserveFocusPlayer === rect.playerName) {
            state.customObserveFocusLocked = false;
            state.customObserveFocusPlayer = null;
          } else {
            state.customObserveFocusPlayer = rect.playerName;
            state.customObserveFocusLocked = true;
          }
          scheduleRenderLoop();
          return;
        }
      }
    }
    // Observation mode: "Auto" button — unlock focus
    if (state.customMode && state.customMultiplayer && (state.customObserveMode || state.customIsObserver || isCurrentCustomPlayerObserver()) && layout.observeAutoButton && isPointInRectPadded(pos, layout.observeAutoButton, 4)) {
      state.customObserveFocusLocked = false;
      state.customObserveFocusPlayer = null;
      scheduleRenderLoop();
      return;
    }

    if (state.mode !== "menu" && state.alreadyCompleted && !state.practiceMode && layout.hudPracticeLink && isPointInRectPadded(pos, layout.hudPracticeLink, 6)) {
      logUiEvent("hud_practice_link_click");
      openCustomGameOverlay();
      return;
    }

    if (state.mode !== "menu" && isPointInRectPadded(pos, layout.archiveButton, 6)) {
      logUiEvent("archive_open");
      openArchiveOverlay();
      return;
    }
    if (
      state.mode !== "menu" &&
      !state.practiceMode &&
      (state.alreadyCompleted || state.submitted || state.redoMode) &&
      isPointInRectPadded(pos, layout.redoButton, 6)
    ) {
      logUiEvent("redo_start");
      startRedo();
      return;
    }
    if (state.mode !== "menu" && state.practiceMode && isPointInRectPadded(pos, layout.todayButton, 6)) {
      resetGame();
      return;
    }

    if (state.mode === "menu") {
      const hasWord = !!getDailyWord();
      const streakBanner = getStreakRiskBannerState();
      const logStreakBannerClick = () => {
        if (!streakBanner) return;
        logEvent("streak_banner_clicked", {
          streak_count: streakBanner.streakCount,
          grace_available: streakBanner.tone.indexOf("shield") === 0 ? 1 : 0,
          hours_left: streakBanner.hoursLeft,
          gap_days: streakBanner.gap,
        }, `${state.dailyKey || getEasternKey()}_${state.sessionId}_streak_banner_clicked`);
      };
      if (isPointInRectPadded(pos, layout.menuPlayToday, 8)) {
        playMenuTap();
        logStreakBannerClick();
        logUiEvent("landing_play_click", getLandingTelemetryData());
        if (!hasWord && isDailyWordLoadingForMenuStart()) return;
        if (!hasWord && _menuStartLoadState === "failed") {
          retryMenuStartFetch();
          return;
        }
        startDailyWithIntroduction();
        return;
      }
      if (isPointInRectPadded(pos, layout.menuCreateCustom, 6)) {
        logUiEvent("landing_create_custom_click", getLandingTelemetryData());
        openCustomGameOverlay();
        return;
      }
      if (isPointInRectPadded(pos, layout.menuHall, 6)) {
        logUiEvent("landing_hall_click", getLandingTelemetryData());
        openHallPage();
        return;
      }
      if (layout.menuAppStore.width > 0 && isPointInRectPadded(pos, layout.menuAppStore, 6)) {
        logUiEvent("landing_appstore_click", getLandingTelemetryData());
        window.location.href = "https://apps.apple.com/app/id6759224219";
        return;
      }
      playMenuTap();
      logStreakBannerClick();
      logUiEvent("landing_tap_start", getLandingTelemetryData());
      if (!hasWord && isDailyWordLoadingForMenuStart()) return;
      if (!hasWord && _menuStartLoadState === "failed") {
        retryMenuStartFetch();
        return;
      }
      startDailyWithIntroduction();
      return;
    }
    // Medal tooltip on tap (mobile)
    if (state.mode === "play" && layout.yesterdayMedalRect && isPointInRectPadded(pos, layout.yesterdayMedalRect, 6)) {
      _medalTooltipUntil = performance.now() + 2000;
      return;
    }
    if (state.mode !== "play") return;
    if (state.completed && !state.redoMode) return;
    if (state.anim) return;
    // Block pivot clicks for observers and observe mode
    if (state.customIsObserver || state.customObserveMode || isCurrentCustomPlayerObserver()) return;

    const pivot = findPivotAt(pos);
    if (!pivot) return;

    const now = performance.now();
    if (now - lastMoveTime < 150) return;
    lastMoveTime = now;

    performPivotMove(pivot);
  }

  function canUndoMove() {
    // Allow undo only in solo custom/practice modes (not daily, not multiplayer)
    if (state.mode !== "play") return false;
    if (state.anim) return false;
    if (!Array.isArray(state.moveHistory) || state.moveHistory.length === 0) return false;
    if (state.customMode && state.customMultiplayer) return false;
    return state.practiceMode || state.customMode;
  }

  function undoMove() {
    if (!canUndoMove()) return;
    const lastIdx = state.moveHistory[state.moveHistory.length - 1];
    if (!Number.isInteger(lastIdx) || lastIdx < 0 || lastIdx >= (GRID_ROWS - 1) * (GRID_COLS - 1)) return;
    state.moveHistory.pop();
    const pivotRow = Math.floor(lastIdx / (GRID_COLS - 1));
    const pivotCol = lastIdx % (GRID_COLS - 1);
    // Reverse the CW rotation that was applied
    rotateBlock(state.grid, pivotRow, pivotCol, false);
    state.moves = Math.max(0, state.moves - 1);
    state.hintPivotIndex = null;
    state.hintDistanceVisible = false;
    state.hintSequence = null;
    state.hintSequenceUntil = 0;
    haptic("light");
    playPivotClick();
    state.lastMove = state.moveHistory.length
      ? `CW rotate at (${Math.floor(state.moveHistory.at(-1) / (GRID_COLS - 1)) + 1}, ${state.moveHistory.at(-1) % (GRID_COLS - 1) + 1})`
      : null;
    const center = state.grid[CENTER_ROW].slice(CENTER_START_COL, CENTER_START_COL + CENTER_WORD_LENGTH).join("");
    announceStatus(`Undo. Move ${state.moves}. Center: ${center}`);
    setCanvasLabel(`PIVOT puzzle. Target word: ${state.targetWord}. Moves: ${state.moves}. Center: ${center}.`);
    if (!state.customMode) persistCurrentRun();
    scheduleRenderLoop();
  }

  function performPivotMove(pivot) {
    const animCells = [
      { r: pivot.r, c: pivot.c },
      { r: pivot.r, c: pivot.c + 1 },
      { r: pivot.r + 1, c: pivot.c },
      { r: pivot.r + 1, c: pivot.c + 1 },
    ];
    const fromLetters = animCells.map((cell) => ({
      ...cell,
      letter: state.grid[cell.r][cell.c],
    }));
    rotateBlock(state.grid, pivot.r, pivot.c, true);
    state.anim = {
      start: performance.now(),
      duration: 260,
      pivot: { r: pivot.r, c: pivot.c },
      clockwise: true,
      cells: fromLetters,
    };
    state.moves += 1;
    growth?.activity();
    haptic("medium");
    playPivotClick();
    announceStatus(`Move ${state.moves}. Center: ${state.grid[CENTER_ROW].slice(CENTER_START_COL, CENTER_START_COL + CENTER_WORD_LENGTH).join("")}`);
    setCanvasLabel(`PIVOT puzzle. Target word: ${state.targetWord}. Moves: ${state.moves}. Center: ${state.grid[CENTER_ROW].slice(CENTER_START_COL, CENTER_START_COL + CENTER_WORD_LENGTH).join("")}.`);
    if (state.customMode) broadcastMove();
    state.hintPivotIndex = null;
    state.hintDistanceVisible = false;
    state.hintSequence = null;
    state.hintSequenceUntil = 0;
    const pivotIndex = pivot.r * (GRID_COLS - 1) + pivot.c;
    if (Array.isArray(state.moveHistory)) {
      state.moveHistory.push(pivotIndex);
    }

    if (state.customMode) return; // skip daily progress saving
    if (state.moves === 1) {
      clearActivationNudgeTimer();
      state.firstMoveAt = Date.now();
      const msFromStart = state.puzzleStartedAt > 0 ? Math.max(0, state.firstMoveAt - state.puzzleStartedAt) : null;
      logEvent(
        "first_move",
        {
          ms_from_start: msFromStart,
          nudge_shown: state.activationNudgeShown ? 1 : 0,
          ...getLandingTelemetryData(),
        },
        `${state.dailyKey}_${state.sessionId}_first_move_${state.practiceMode ? "p" : "d"}`
      );
    }
    state.lastMove = `CW rotate at (${pivot.r + 1}, ${pivot.c + 1})`;
    persistCurrentRun();
  }

  function persistCurrentRun() {
    saveProgress(state.dailyKey, {
      grid: state.grid.map((row) => row.join("")),
      moves: state.moves,
      lastMove: state.lastMove,
      solvedToday: state.solvedToday,
      minMoves: state.minMoves,
      minExact: state.minExact,
      minKind: MIN_KIND,
      submitted: state.submitted,
      redoMode: state.redoMode,
      word: state.targetWord,
      moveHistory: state.moveHistory,
      hintUsed: state.hintUsed,
    }, state.practiceMode || state.redoMode);
  }


  function handleCanvasContextMenu(event) {
    event.preventDefault();
  }

  let lastPointerType = "";
  let lastTapTime = 0;
  let lastMoveTime = 0;
  let activePointerId = null;

  function handlePointerDown(event) {
    lastPointerType = event.pointerType || "";
    if (lastPointerType === "touch") {
      event.preventDefault();
    }
    if (event.button && event.button !== 0) return;
    if (event.isPrimary === false) return;
    const now = performance.now();
    if (now - lastTapTime < 120) return;
    if (activePointerId !== null && activePointerId !== event.pointerId) return;
    activePointerId = event.pointerId;
    lastTapTime = now;
    try {
      handleCanvasMouseDown(event);
    } catch (e) {
      console.error("Click handler error:", e);
      render();
    }
  }

  function handlePointerUp(event) {
    if (event.isPrimary !== false) {
      activePointerId = null;
    }
  }

  function addCanvasListeners() {
    canvas.addEventListener("mousemove", handleCanvasMouseMove);
    canvas.addEventListener("pointerdown", handlePointerDown, { passive: false });
    canvas.addEventListener("contextmenu", handleCanvasContextMenu);
  }

  function rebuildCanvas() {
    const parent = canvas.parentElement;
    if (!parent) return;
    const newCanvas = document.createElement("canvas");
    newCanvas.id = "game";
    newCanvas.setAttribute("aria-label", canvas.getAttribute("aria-label") || "PIVOT — A daily tile puzzle. Press Play to start.");
    newCanvas.setAttribute("role", "application");
    newCanvas.setAttribute("tabindex", "0");
    parent.replaceChild(newCanvas, canvas);
    canvas = newCanvas;
    ctx = canvas.getContext("2d");
    addCanvasListeners();
    resizeCanvas();
    render();
  }

  let _resumeRefreshTimer = null;
  let _lastCanvasRebuildAt = 0;
  const MIN_CANVAS_REBUILD_INTERVAL_MS = 1200;

  function scheduleResumeRefresh(reason = "resume", opts = {}) {
    const forceRebuild = !!opts.forceRebuild;
    const delayMs = Number.isFinite(Number(opts.delayMs)) ? Math.max(0, Number(opts.delayMs)) : 120;
    if (_resumeRefreshTimer) {
      clearTimeout(_resumeRefreshTimer);
      _resumeRefreshTimer = null;
    }
    _resumeRefreshTimer = setTimeout(() => {
      _resumeRefreshTimer = null;
      const now = Date.now();
      const canRebuild = now - _lastCanvasRebuildAt >= MIN_CANVAS_REBUILD_INTERVAL_MS;
      const missingCanvas = !canvas || !canvas.isConnected;
      const missingContext = !ctx || typeof ctx.clearRect !== "function";
      const shouldRebuild = (forceRebuild || missingCanvas || missingContext) && canRebuild;
      if (shouldRebuild) {
        _lastCanvasRebuildAt = now;
        rebuildCanvas();
        return;
      }
      resizeCanvas();
      render();
    }, delayMs);
  }

  addCanvasListeners();
  document.addEventListener("pointerup", handlePointerUp);
  document.addEventListener("pointercancel", handlePointerUp);

  window.addEventListener("keydown", async (event) => {
    const tag = document.activeElement?.tagName;
    const inputFocused = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.isContentEditable;
    const gameFocused = document.activeElement === canvas || document.activeElement === document.body;
    const key = event.key;
    const keyLower = key.toLowerCase();
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (key === "Tab" && trapOverlayTab(event)) return;

    if (
      state.mode === "menu" &&
      !inputFocused &&
      !hasOpenOverlayForKeyboard() &&
      (document.activeElement === canvas || document.activeElement === document.body) &&
      !event.repeat &&
      (key === "Enter" || key === " ")
    ) {
      playMenuTap();
      logUiEvent("landing_keyboard_start", getLandingTelemetryData());
      const hasWord = !!getDailyWord();
      if (!hasWord && isDailyWordLoadingForMenuStart()) {
        event.preventDefault();
        return;
      }
      if (!hasWord && _menuStartLoadState === "failed") {
        retryMenuStartFetch();
        event.preventDefault();
        return;
      }
      startDailyWithIntroduction();
      event.preventDefault();
      return;
    }

    if (keyLower === "g" && gameFocused && !inputFocused && !hasOpenOverlayForKeyboard() && state.mode === "play") {
      announceAlert(getGridDescription());
      event.preventDefault();
      return;
    }

    if (keyLower === "f" && gameFocused && !inputFocused && !hasOpenOverlayForKeyboard() && !event.repeat && !event.metaKey && !event.ctrlKey && !event.altKey) {
      if (!document.fullscreenElement) {
        canvas.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    }

    if (state.showReadme && event.key === "Escape") {
      state.showReadme = false;
      closeNameOverlay();
      closeArchiveOverlay();
      return;
    }
    if (event.key === "Escape") {
      const overlay = getTopOpenOverlay();
      const closers = {
        // Escape on name entry is "Not now", including its cancelled-challenge toast.
        "name-overlay": () => nameSkipEl?.click(),
        "archive-overlay": closeArchiveOverlay,
        "feedback-overlay": closeFeedbackOverlay,
        "a11y-overlay": closeAccessibilityOverlay,
        "copy-overlay": closeCopyOverlay,
        "updates-overlay": closeUpdatesOverlay,
        "recovery-restore-overlay": closeRecoveryOverlay,
      };
      const close = overlay && closers[overlay.id];
      if (close) {
        event.preventDefault();
        close();
        return;
      }
    }
    if (state.showTutorial && event.key === "Escape") {
      closeTutorialOverlay();
      return;
    }
    if (state.showLeaderboardOverlay && event.key === "Escape") {
      closeLeaderboardOverlay();
      return;
    }
    if (event.key === "Escape" && statsOverlay?.classList.contains("show")) {
      closeStatsOverlay();
      return;
    }
    if (event.key === "Escape" && moreOverlay?.classList.contains("show")) {
      closeMoreMenu();
      return;
    }
    if (event.key === "Escape" && document.getElementById("custom-game-overlay")?.classList.contains("show")) {
      closeCustomGameOverlay();
      return;
    }

    // Native controls and dialogs own their keys. Never rotate or share behind them.
    if (inputFocused || !gameFocused || hasOpenOverlayForKeyboard() || event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.repeat && (key === "Enter" || key === " " || /^[123]$/.test(key) || keyLower === "u")) {
      event.preventDefault();
      return;
    }

    // Enter opens explicit entry, including after "Not now"; only the form submits.
    if (state.mode === "win" && !state.practiceMode && !state.customMode &&
        !state.shareOnly && !state.submitted && state.leaderboardEligible && !state.hintUsed &&
        (key === "Enter" || key === " ")) {
      event.preventDefault();
      openNameOverlay();
      return;
    }

    // The result's close X is canvas-only; Escape is its keyboard equivalent.
    if (state.mode === "win" && !state.customMode && key === "Escape" && !isWinOverlayPending()) {
      state.winAcknowledged = true;
      state.mode = "play";
      state.shareOnly = false;
      state.highlightUntil = Date.now() + 5000;
      closeNameOverlay();
      event.preventDefault();
      return;
    }

    if (state.mode === "win" && keyLower === "s") { openStatsOverlay(); event.preventDefault(); return; }
    if (state.mode === "win" && keyLower === "c") { state.leaderboardScope = "circles"; openLeaderboardOverlay(); event.preventDefault(); return; }

    // Win screen keyboard navigation: 1/2/3, Tab, arrows, Enter/Space
    // Only activate when not in name-entry mode (already submitted or not eligible)
    if (state.mode === "win" && !inputFocused && !state.practiceMode && (state.submitted || !state.leaderboardEligible || state.hintUsed)) {
      const winBtnCount = 3; // Share, My Replay, Challenge
      const winActions = [() => { logUiEvent("share_result_btn_click"); shareResult(); },
                          () => { logUiEvent("share_replay_click"); shareReplayLink(); },
                          () => { logUiEvent("challenge_click"); shareChallenge(); }];
      if (key === "1" || key === "2" || key === "3") {
        const idx = Number(key) - 1;
        haptic("light");
        winActions[idx]();
        event.preventDefault();
        return;
      }
      if (key === "Tab") {
        event.preventDefault();
        if (event.shiftKey) {
          _winFocusIndex = _winFocusIndex <= 0 ? winBtnCount - 1 : _winFocusIndex - 1;
        } else {
          _winFocusIndex = _winFocusIndex >= winBtnCount - 1 ? 0 : _winFocusIndex + 1;
        }
        render();
        return;
      }
      if (key === "ArrowRight") { _winFocusIndex = Math.min(winBtnCount - 1, _winFocusIndex + 1); render(); event.preventDefault(); return; }
      if (key === "ArrowLeft") { _winFocusIndex = Math.max(0, _winFocusIndex - 1); render(); event.preventDefault(); return; }
      if ((key === "Enter" || key === " ") && _winFocusIndex >= 0 && _winFocusIndex < winBtnCount) {
        haptic("light");
        winActions[_winFocusIndex]();
        event.preventDefault();
        return;
      }
    }

    if (state.mode === "play" && !inputFocused && !hasOpenOverlayForKeyboard()) {
      // Multiplayer only — solo custom games are keyboard-playable like the daily.
      if (state.customCountdownUntil > 0 || state.customReconnecting || state.customMultiplayer) return;
      if (state.anim || (state.completed && !state.redoMode)) return;
      if (state.customIsObserver || state.customObserveMode || isCurrentCustomPlayerObserver()) return;
      if (keyLower === "u") {
        undoMove();
        event.preventDefault();
        return;
      }
      const pivot = _getKeyboardPivot();
      if (key === "ArrowUp" || keyLower === "w") {
        _setKeyboardPivot(pivot.r - 1, pivot.c);
        event.preventDefault();
        return;
      }
      if (key === "ArrowDown" || keyLower === "s") {
        _setKeyboardPivot(pivot.r + 1, pivot.c);
        event.preventDefault();
        return;
      }
      if (key === "ArrowLeft" || keyLower === "a") {
        _setKeyboardPivot(pivot.r, pivot.c - 1);
        event.preventDefault();
        return;
      }
      if (key === "ArrowRight" || keyLower === "d") {
        _setKeyboardPivot(pivot.r, pivot.c + 1);
        event.preventDefault();
        return;
      }
      if (key === "Enter" || key === " ") {
        const now = performance.now();
        if (now - lastMoveTime < 150) return;
        lastMoveTime = now;
        performPivotMove(pivot);
        event.preventDefault();
      }
    }
  });

  window.render_game_to_text = () => {
    const customPlayers = Object.values(state.customPlayers || {});
    const activeCustomPlayers = customPlayers.filter((p) => p && !p.isObserver);
    const observingCustomPlayers = customPlayers.filter((p) => p && p.isObserver);
    const menuWord = state.mode === "menu" ? (getDailyWord() || "") : state.targetWord;
    const payload = {
      mode: state.mode,
      grid: state.grid.map((row) => row.join("")),
      targetWord: menuWord,
      targetCells: { row: CENTER_ROW, cols: [1, 2, 3, 4] },
      canvasSize: { width: layout.width, height: layout.height },
      panelBelow: layout.panelBelow,
      gridRect: { x: layout.offsetX, y: layout.offsetY, width: layout.gridWidth, height: layout.gridHeight },
      controls: { more: layout.moreButton, stats: layout.statsButton },
      moves: state.moves,
      lastMove: state.lastMove,
      solvedToday: state.solvedToday,
      nameInput: state.nameInput,
      leaderboard: state.leaderboard,
      dailyKey: state.mode === "menu" ? getEasternKey() : state.dailyKey,
      leaderboardLoading: state.leaderboardLoading,
      leaderboardError: state.leaderboardError,
      metrics: {
        solvers: state.metrics.solvers,
        boardEntries: state.metrics.boardEntries,
        median: state.metrics.median,
      },
      showReadme: state.showReadme,
      minMoves: state.minMoves,
      minExact: state.minExact,
      submitted: state.submitted,
      submissionReceiptAt: state.submissionReceiptAt,
      submissionReceiptId: state.submissionReceiptId,
      playerName: state.playerName,
      practiceMode: state.practiceMode,
      completed: state.completed,
      completionLocked: state.completionLocked,
      showLeaderboardOverlay: state.showLeaderboardOverlay,
      leaderboardScope: state.leaderboardScope,
      leaderboardPage: state.leaderboardPage,
      circleLeaderboards: (() => {
        const circles = Array.isArray(state.friendCircles) ? state.friendCircles : [];
        const selected = selectedFriendCircle();
        const standings = Array.isArray(selected?.standings) ? selected.standings : [];
        const viewerIndex = standings.findIndex((row) => row?.is_viewer);
        return {
          loading: state.friendCirclesLoading,
          error: state.friendCirclesError,
          count: circles.length,
          selectedId: selected?.id || "",
          selectedName: selected?.name || "",
          viewerRank: viewerIndex >= 0 ? viewerIndex + 1 : null,
          memberCount: standings.length,
        };
      })(),
      winAcknowledged: state.winAcknowledged,
      streakCount: state.streakCount,
      streakDisplay: state.streakDisplay,
      redoMode: state.redoMode,
      leaderboardEligible: state.leaderboardEligible,
      showTutorial: state.showTutorial,
      onboarding: growth?.state() || null,
      weeklyActivity: growth?.week() || null,
      nameOverlayVisible: !!nameOverlay?.classList.contains("show"),
      updatesBannerVisible: !!document.getElementById("updates-banner"),
      hasUnseenUpdates: hasUnseenUpdates(),
      shareIncludeDetails: state.shareIncludeDetails,
      challenge: state.challengeFrom ? {
        id: state.challengeFrom.id || "",
        name: state.challengeFrom.name || "A friend",
        day: Number(state.challengeFrom.dayNum) || 0,
        moves: Number.isFinite(Number(state.challengeFrom.moves)) ? Number(state.challengeFrom.moves) : null,
        resolved: !!state.challengeResolved,
        landingVisible: !!_challengeLandingEl,
        ghostMove: Number(state.ghostMoveIndex) || 0,
        ghostMoves: Array.isArray(state.challengeFrom.ghostHistory) ? state.challengeFrom.ghostHistory.length : 0,
      } : null,
      custom: state.customMode ? {
        roomCode: state.customRoomCode || "",
        difficulty: state.customDifficulty || "",
        phase: state.customPhase || "",
        multiplayer: !!state.customMultiplayer,
        classroomMode: !!state.customClassroomMode,
        isHost: !!state.customIsHost,
        isObserver: !!state.customIsObserver,
        observeMode: !!state.customObserveMode,
        roomLocked: !!state.customRoomLocked,
        roundNumber: Number.isFinite(state.customRoundNumber) ? state.customRoundNumber : 0,
        seriesBestOf: normalizeCustomSeriesBestOf(state.customSeriesBestOf),
        seriesComplete: !!state.customSeriesComplete,
        seriesWinnerId: state.customSeriesWinnerId || "",
        creatorName: state.customCreatorName || "",
        communityCode: state.customCommunityCode || "",
        pendingCount: (state.customPendingJoins || []).filter((j) => j && !j.approved).length,
        activeCount: activeCustomPlayers.length,
        watchingCount: observingCustomPlayers.length,
        players: customPlayers
          .map((p) => ({
            id: p.id,
            name: p.name,
            moves: p.moves,
            solved: !!p.solved,
            observer: !!p.isObserver,
            host: !!p.isHost,
            wins: Number.isFinite(p.wins) ? p.wins : 0,
            losses: Number.isFinite(p.losses) ? p.losses : 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      } : null,
      selectedPivot: state.mode === "play" ? state.hoverPivot : null,
      canUndo: canUndoMove(),
      moveHistory: Array.isArray(state.moveHistory) ? state.moveHistory.slice() : [],
      coordinates: "origin top-left; row increases downward; col increases rightward",
    };
    return JSON.stringify(payload);
  };

  window.__pivot_get_menu_hitboxes = () => ({
    mode: state.mode,
    menuPlayToday: { ...layout.menuPlayToday },
    menuHall: { ...layout.menuHall },
    layoutWidth: layout.width,
    layoutHeight: layout.height,
  });

  window.__pivot_get_win_hitboxes = () => {
    const keys = ["winClose", "winShareResults", "winShareVideo", "challengeButton", "winResubmit",
      "winPlayAnother", "winBackToToday", "winWatchOptimal", "winWatchBest", "winPlayYesterday",
      "growthStats", "growthCircle", "winIosAppCard", "winIosAppDismiss", "hintButton", "redoButton"];
    const out = { mode: state.mode, width: layout.width, height: layout.height };
    for (const key of keys) out[key] = layout[key] ? { ...layout[key] } : null;
    return out;
  };

  window.__pivot_get_board_geometry = () => ({
    width: layout.width,
    height: layout.height,
    offsetX: layout.offsetX,
    offsetY: layout.offsetY,
    cellSize: layout.cellSize,
    gridWidth: layout.gridWidth,
    gridHeight: layout.gridHeight,
    panelBelow: layout.panelBelow,
    shortLandscape: layout.shortLandscape,
    fullLeaderboardLink: { ...layout.fullLeaderboardLink },
    moreButton: { ...layout.moreButton },
  });

  window.advanceTime = (ms) => {
    const steps = Math.max(1, Math.round(ms / (1000 / 60)));
    for (let i = 0; i < steps; i += 1) {
      update();
    }
    render();
  };

  let _viewportRefreshTimer = null;
  let _viewportResizeBurstStartedAt = 0;
  let _viewportResizeBurstCount = 0;

  function trackViewportResizeBurst() {
    const now = performance.now();
    if (!Number.isFinite(_viewportResizeBurstStartedAt) || now - _viewportResizeBurstStartedAt > 1200) {
      _viewportResizeBurstStartedAt = now;
      _viewportResizeBurstCount = 0;
    }
    _viewportResizeBurstCount += 1;
    if (_viewportResizeBurstCount === 14) {
      logRuntimeAnomaly("viewport_resize_burst", {
        count: _viewportResizeBurstCount,
        hidden: !!document.hidden,
        mode: state.mode,
      }, 180000);
    }
  }

  function scheduleViewportRefresh(delayMs = 0) {
    if (_viewportRefreshTimer) {
      clearTimeout(_viewportRefreshTimer);
      _viewportRefreshTimer = null;
    }
    _viewportRefreshTimer = setTimeout(() => {
      _viewportRefreshTimer = null;
      resizeCanvas();
      if (state.showTutorial) {
        resizeTutorialCanvas();
      }
      render();
    }, Math.max(0, delayMs));
  }

  window.addEventListener("resize", () => {
    scheduleViewportRefresh(0);
  });
  window.addEventListener("pageshow", (event) => {
    const persisted = !!(event && event.persisted);
    if (persisted) {
      logRuntimeAnomaly("pageshow_persisted", { persisted: 1, mode: state.mode }, 300000);
    }
    scheduleResumeRefresh("pageshow", {
      forceRebuild: persisted,
      delayMs: persisted ? 140 : 100,
    });
  });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", () => {
      // iOS Safari can fire rapid viewport resize bursts during pull-to-refresh/address bar animation.
      // Coalescing these avoids repeated canvas clears that look like "blink" flicker.
      trackViewportResizeBurst();
      scheduleViewportRefresh(70);
    });
  }
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      // Resume AudioContext — iOS suspends it when app is backgrounded
      if (!_soundMuted && _audioCtx && _audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
      // Date rollover check — if backgrounded past midnight, load new puzzle
      const nowKey = getEasternKey();
      // state.archiveMode was checked here but is never assigned anywhere, so
      // the guard did nothing: backgrounding an archive puzzle past midnight
      // would resetGame() out from under the player. Archive and replay both
      // run in practiceMode, which is the flag that actually tracks this.
      if (state.dailyKey && state.dailyKey !== nowKey && !state.customMode && !state.practiceMode) {
        _fetchDailyWord();
        resetGame();
      }
      scheduleResumeRefresh("visible", { forceRebuild: false, delayMs: 110 });
      // Reset stale loading states on resume (in-flight fetches may have been killed)
      if (state.leaderboardLoading) {
        state.leaderboardLoading = false;
      }
      // Refresh leaderboard on resume — catches new scores while backgrounded
      if (state.dailyKey && !state.customMode) {
        retryLeaderboardFetch(300);
      }
      if (state.weeklyLeaderboardLoading) state.weeklyLeaderboardLoading = false;
      if (state.monthlyLeaderboardLoading) state.monthlyLeaderboardLoading = false;
      setTimeout(() => {
        maybeRetryPendingSubmit("visible", 1200);
        maybePollChallengeFeed(true);
      }, 140);
    } else {
      logSessionEnd("hidden");
      flushTelemetryNow();
    }
  });
  window.addEventListener("online", () => {
    const pending = loadPendingSubmit(state.dailyKey);
    if (!pending) {
      setReliabilityState("healthy");
      return;
    }
    setReliabilityState("degraded", "Connection restored; syncing queued score");
    setShareToast("Back online. Retrying submit…", 2000);
    maybeRetryPendingSubmit("online", 700);
    maybePollChallengeFeed(true);
  });
  window.addEventListener("offline", () => {
    setReliabilityState("degraded", "Offline");
  });
  setInterval(async () => {
    if (document.hidden) return;
    if (_reliabilityTickInFlight) return;
    if (state.customMode || state.practiceMode || !state.dailyKey) return;
    _reliabilityTickInFlight = true;
    try {
      maybeRetryPendingSubmit("heartbeat", 400);
      if (state.reliabilityMode === "degraded" && navigator.onLine && !state.leaderboardLoading) {
        await retryLeaderboardFetch(state.showLeaderboardOverlay ? 120 : 50);
      }
      maybePollChallengeFeed(false);
      maybePromptReminderOptIn("heartbeat");
    } catch {
      // best-effort reliability tick only
    } finally {
      _reliabilityTickInFlight = false;
    }
  }, 30000);
  window.addEventListener("error", (event) => {
    const message = event && event.message ? String(event.message) : "";
    logRuntimeAnomaly("window_error", {
      message,
      source: event && event.filename ? String(event.filename).slice(0, 100) : "",
      line: event && Number.isFinite(Number(event.lineno)) ? Number(event.lineno) : 0,
      col: event && Number.isFinite(Number(event.colno)) ? Number(event.colno) : 0,
    }, 120000);
  });
  window.addEventListener("unhandledrejection", (event) => {
    let reason = "";
    try {
      reason = event && event.reason && event.reason.message
        ? String(event.reason.message)
        : String(event && event.reason ? event.reason : "");
    } catch {}
    logRuntimeAnomaly("unhandled_rejection", { reason }, 120000);
  });
  window.addEventListener("pagehide", () => {
    logSessionEnd("pagehide");
    flushTelemetryNow();
  });

  let _deferredInstallPrompt = null;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredInstallPrompt = e;
  });
  window.addEventListener("appinstalled", () => {
    _deferredInstallPrompt = null;
    try { localStorage.setItem("pivot_installed", "1"); } catch {}
  });

  function maybeShowInstallPrompt() {
    if (growth?.followupShown()) return;
    if (!_deferredInstallPrompt) return;
    // Don't show while win screen is active — it covers share buttons on small screens
    if (state.mode === "win") {
      setTimeout(maybeShowInstallPrompt, 5000);
      return;
    }
    try { if (localStorage.getItem("pivot_install_prompted")) return; } catch {}
    try { if (localStorage.getItem("pivot_installed")) return; } catch {}
    const progressKeys = Object.keys(localStorage).filter((k) => k.startsWith("pivot_progress_"));
    if (progressKeys.length > 1) return;
    const toast = document.createElement("div");
    toast.style.cssText =
      `position:fixed;bottom:0;left:0;right:0;padding:14px 20px;background:${C.btnPrimaryBg};color:${C.btnPrimaryText};` +
      "font:14px Georgia,'Times New Roman',serif;display:flex;align-items:center;justify-content:space-between;" +
      "z-index:10000;gap:12px;";
    toast.innerHTML =
      `<span>Add PIVOT to your home screen</span><button style="background:#f4d35e;color:#1b1b1b;border:none;` +
      'padding:8px 16px;font:bold 13px Georgia,serif;cursor:pointer;border-radius:2px;">Install</button>';
    document.body.appendChild(toast);
    const btn = toast.querySelector("button");
    btn.addEventListener("click", () => {
      _deferredInstallPrompt.prompt();
      _deferredInstallPrompt.userChoice.then(() => { _deferredInstallPrompt = null; });
      toast.remove();
      try { localStorage.setItem("pivot_install_prompted", "1"); } catch {}
    });
    setTimeout(() => { if (toast.parentNode) toast.remove(); }, 8000);
    try { localStorage.setItem("pivot_install_prompted", "1"); } catch {}
  }

  // Prefetch today's word from server (non-blocking, with retry)
  function _fetchDailyWord() {
    if (!SUPABASE_URL) return;
    if (_fetchingDailyWord) return;
    const now = Date.now();
    if (now < _nextDailyWordFetchAt) return;
    _nextDailyWordFetchAt = now + 3000;
    _fetchingDailyWord = true;
    fetch(SUPABASE_URL + "/functions/v1/pivot-daily-word", { signal: AbortSignal.timeout(10000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && typeof data.word === "string" && /^[A-Z]{4,}$/.test(data.word) && data.daily_key === getEasternKey()) {
          const hasWord = Boolean(_serverDailyWord);
          const shouldRefreshAfterDayChange = !state.customMode && !state.practiceMode && state.dailyKey && state.dailyKey !== data.daily_key;
          _serverDailyWord = data;
          try { localStorage.setItem("pivot_server_word", JSON.stringify(data)); } catch {}
          // An explicit start can be waiting while yesterday's word is still
          // cached (landing page left open overnight). It must not depend on
          // whether a previous day's word was present.
          const wasWaitingForWord =
            _pendingMenuStart ||
            (!hasWord && (state.targetWord === "null" || state.targetWord === null || !state.targetWord));
          const shouldResetGame = shouldRefreshAfterDayChange || wasWaitingForWord;
          if (shouldResetGame) {
            _pendingMenuStart = false;
            resetGame();
          } else {
            scheduleRenderLoop();
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        _fetchingDailyWord = false;
      });
  }
  function startDailyWithIntroduction() {
    if (growth?.maybeOnboard(() => resetGame())) return;
    if (_menuStartLoadState === "failed" && !getDailyWord()) { retryMenuStartFetch(); return; }
    resetGame();
  }

  function scheduleDeferredNamePrompt() {
    _postSolveLeaderboardPromptTimer = setTimeout(() => {
      _postSolveLeaderboardPromptTimer = null;
      if (state.mode !== "win" || state.submitted || !state.leaderboardEligible) return;
      if (hasOpenOverlayForKeyboard() || growth?.busy() || document.hidden) { scheduleDeferredNamePrompt(); return; }
      _promptReminderOnNameOverlayClose = true;
      openNameOverlay();
    }, 500);
  }

  growth = window.PivotGrowth?.create({
    get: safeGetItem, set: safeSetItem, today: getEasternKey, state: () => state,
    log: logEvent, build: () => document.querySelector('script[src*="game.min.js"]')?.src.split("?v=")[1] || "dev",
    canvas: () => canvas, size: () => layout, render: scheduleRenderLoop,
    blocked: () => hasOpenOverlayForKeyboard() || !!_challengeLandingEl,
    shareFriend: shareResult, openStats: openStatsOverlay, openCircleStandings: () => { state.leaderboardScope = "circles"; openLeaderboardOverlay(); },
    start: startDailyWithIntroduction, loading: () => _menuStartLoadState === "loading" && !getDailyWord(),
    entryLabel: () => {
      if (_menuStartLoadState === "failed" && !getDailyWord()) return "Try again";
      if (_menuStartLoadState === "loading" && !getDailyWord()) return "Loading today's puzzle…";
      const saved = loadProgress(getEasternKey());
      return saved?.solvedToday ? "View today's result" : saved?.moves > 0 ? "Continue today's puzzle" : "Play today's puzzle";
    },
    saved: () => loadProgress(getEasternKey()), hasSeenTutorial, markTutorialSeen,
    history: () => computePlayerStats().history, newDevice: () => _isNewDevice,
    loadCircles: () => loadCircleLeaderboards(), circles: () => state.friendCircles || [], circleError: () => state.friendCirclesError,
    openCircles: async (action) => { await openFriendCircles(); document.getElementById(action === "join" ? "circle-code" : "circle-name")?.focus(); },
    remind: () => maybePromptReminderOptIn("post_solve"),
  });

  handlePushAttributionFromUrl();
  _fetchDailyWord();
  // Retry once after 5s in case of transient failure
  setTimeout(() => { if (!_serverDailyWord) _fetchDailyWord(); }, 5000);

  resizeCanvas();
  // WKWebView (iOS app) may not have its viewport settled on first paint.
  // Retry a few times to catch late layout changes.
  const _resizeRetryDelays = [100, 300, 1000];
  _resizeRetryDelays.forEach((ms) => setTimeout(resizeCanvas, ms));
  setTimeout(() => {
    if (state.mode === "menu") {
      maybePromptReminderOptIn("landing_idle");
    }
  }, 2200);

  // Parse challenge from URL params
  const _challenge = parseChallenge();
  const _circleInvite = (() => {
    try {
      const value = new URLSearchParams(window.location.search).get("circle") || "";
      return /^[A-Z2-9]{6}$/i.test(value) ? value.toUpperCase() : "";
    } catch { return ""; }
  })();
  const _hadSeenTutorialAtBoot = hasSeenTutorial();
  if (_challenge) {
    state.challengeFrom = _challenge;
    // Keep every inbound challenge on an explicit invitation screen, including
    // older editions. The player chooses when to enter the puzzle; acceptance
    // routes past editions into the archive without losing rivalry context.
    showChallengeLanding(_challenge);
    resolveChallenge(_challenge);
  }

  // Challenge notifications for challengers (top grey banner + comparison link).
  // Poll once shortly after boot and periodically afterwards.
  setTimeout(() => maybePollChallengeFeed(true), 1400);

  // One calm, nonmodal release notice for web and the iOS WKWebView. It is
  // skipped on social deep links and disappears as soon as play or a modal
  // begins. A timestamp marker makes it eligible again only for newer updates.
  if (_hadSeenTutorialAtBoot && !_challenge && !_circleInvite) setTimeout(showUpdatesBanner, 3200);

  // Route custom game URLs
  if (_isCustomPage) {
    const _customParams = parseCustomPageParams();
    if (_customParams) {
      try { sessionStorage.removeItem("pivot_custom_session"); } catch {}
      if (_customParams.action === "race") {
        setTimeout(() => openLiveRaceOverlay(_customParams.difficulty), 200);
      } else if (_customParams.action === "community") {
        setTimeout(() => startCommunityPuzzle(_customParams.puzzleCode), 200);
      } else if (_customParams.action === "create") {
        setTimeout(() => openCustomGameOverlay(_customParams.difficulty), 200);
      } else if (_customParams.action === "solo") {
        setTimeout(() => startSoloCustomGame(_customParams.difficulty), 200);
      } else if (_customParams.code) {
        setTimeout(() => joinCustomRoom(_customParams.code, _customParams.difficulty, _customParams.password), 200);
      }
    } else {
      const _returnSession = getReturnSession();
      if (_returnSession) {
        showReturnBanner(_returnSession);
      } else {
        setTimeout(() => openPlayTogetherHub(), 200);
      }
    }
  } else {
    // Main page — handle #custom/... hashes inline (old cached /custom/ redirects here)
    if (_circleInvite && !_challenge) {
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("circle");
        history.replaceState(null, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      } catch {}
      setTimeout(() => openFriendCircles(_circleInvite), 200);
    } else {
      const _roomCodeParam = parseRoomCode();
      if (_roomCodeParam) {
      if (_roomCodeParam.action === "create") {
        setTimeout(() => openCustomGameOverlay(_roomCodeParam.difficulty), 200);
      } else if (_roomCodeParam.action === "solo") {
        setTimeout(() => startSoloCustomGame(_roomCodeParam.difficulty), 200);
      } else if (_roomCodeParam.code) {
        setTimeout(() => joinCustomRoom(_roomCodeParam.code, _roomCodeParam.difficulty, _roomCodeParam.password), 200);
      }
      } else if (!_challenge) {
      // If user already solved today, skip landing page and go straight to the game (shows congrats)
      const _todayKey = getEasternKey();
      const _todaySaved = loadProgress(_todayKey);
      if (_todaySaved && _todaySaved.solvedToday && !_todaySaved.practiceMode) {
        resetGame();
      } else {
        const _returnSession = getReturnSession();
        if (_returnSession) showReturnBanner(_returnSession);
        // The interactive introduction is offered after the player chooses Play.
      }
      }
    }
  }
  // --- Install prompt (mobile PWA) ---
  (function initInstallPrompt() {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) return; // already installed
    if (!_detectedPlatform || _detectedPlatform !== "mobile") return;
    if (safeGetItem("pivot_install_dismissed")) return;

    let _deferredPrompt = null;
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      _deferredPrompt = e;
    });

    function showInstallBanner() {
      if (state.mode === "win" || growth?.followupShown() || hasOpenOverlayForKeyboard()) return;
      if (PIVOT_NATIVE) return;
      if (safeGetItem("pivot_install_dismissed")) return;
      if (document.getElementById("install-banner")) return;
      // No captured beforeinstallprompt means there is nothing to install, and
      // the Add button would silently dismiss itself and set the dismissed flag
      // forever — offering durable presence and delivering none. iOS Safari
      // never fires the event; those users get the iOS app card on the win
      // screen, which is a real path.
      if (!_deferredPrompt) return;
      const banner = document.createElement("div");
      banner.id = "install-banner";
      banner.style.cssText = `position:fixed;bottom:0;left:0;right:0;background:${C.bg};border-top:1px solid ${C.panelStroke};padding:12px 16px;display:flex;align-items:center;justify-content:space-between;z-index:9999;font-family:Georgia,'Times New Roman',serif;font-size:13px;color:${C.ink};box-shadow:0 -2px 8px ${_currentTheme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.06)"};`;
      banner.innerHTML = `<span>Add PIVOT to your home screen for the best experience</span><div style="display:flex;gap:8px;flex-shrink:0;margin-left:12px"><button id="install-add" style="font-family:inherit;font-size:12px;min-height:44px;padding:6px 14px;background:${C.btnPrimaryBg};color:${C.btnPrimaryText};border:none;border-radius:2px;cursor:pointer">Add</button><button id="install-dismiss" style="font-family:inherit;font-size:12px;min-width:44px;min-height:44px;padding:6px 10px;background:none;border:1px solid ${C.btnStroke};border-radius:2px;cursor:pointer;color:${C.muted}">\u00d7</button></div>`;
      document.body.appendChild(banner);
      const installDismiss = banner.querySelector("#install-dismiss");
      const installAdd = banner.querySelector("#install-add");
      if (installDismiss) {
        installDismiss.addEventListener("click", () => {
          banner.remove();
          try { localStorage.setItem("pivot_install_dismissed", "1"); } catch (_) {}
        });
      }
      if (installAdd) {
        installAdd.addEventListener("click", async () => {
          if (_deferredPrompt) {
            _deferredPrompt.prompt();
            const { outcome } = await _deferredPrompt.userChoice;
            if (outcome === "accepted") {
              try { localStorage.setItem("pivot_install_dismissed", "1"); } catch (_) {}
            }
            _deferredPrompt = null;
          }
          banner.remove();
          try { localStorage.setItem("pivot_install_dismissed", "1"); } catch (_) {}
        });
      }
    }

  // Show after first solve (listen for mode change to "win")
  const _origCheckAchievements = checkAchievements;
  checkAchievements = function () {
    _origCheckAchievements();
    setTimeout(showInstallBanner, 2000);
  };
})();

  loop();
})();
